/* eslint-disable no-unused-vars */
import {
  collection,
  query,
  where,
  getDocs
 } from 'firebase/firestore';
 import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { CheckCircle, Edit2, Trash2, Save, X, BarChart2, Target, Sparkles, TrendingDown, Calculator, Scale, Keyboard, Mic, Upload, ChevronDown, ChevronUp, Users, Briefcase, Plus, FileText, Download } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
 import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, setAuthPersistence, app } from "./firebase";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import ReportExport from './ReportExport';
import { canPersistLedgerDataset, createLedgerLoadTracker } from './lib/ledgerAccess';
 import CurrencySelector from './components/CurrencySelector';
 import CountrySelector from './components/CountrySelector';
 import BudgetSection from './BudgetSection';
 import PricingCalculator from './PricingCalculator';
 import AIHelper from './components/AIHelper/AIHelper';
 import { CurrencyProvider, useCurrency } from './Contexts/CurrencyContext';
import logo from './src/images/70Lives Beauty Ledger Logo.png';
 import EnhancedAuthCheck from './components/EnhancedAuthCheck';
 import SimpleSignIn from './components/SimpleSignIn';
import { db } from './firebase';
import { SubscriptionProvider, useSubscription } from './Contexts/SubscriptionContext';
import {
  createUserWithTrial,
  createFreeUser,
  createTrialUser,
  isLegacyAuthorizedUser,
  getUserSubscription,
  hasEmailUsedTrial,
  migrateLegacyUserDoc,
  hasFeatureAccess,
  PREMIUM_FEATURES
} from './services/subscriptionService';
import { ensureStripeCustomer, createCheckoutSession, createCustomerPortalSession } from './services/stripeService';
 import SubscriptionStatus from './components/Subscription/SubscriptionStatus';
import { VoiceToTextGate, ReceiptScannerGate, AdvancedReportsGate } from './components/Subscription/FeatureGate';
import { getCurrentCountry, getCurrentTaxRates, getDefaultTaxRate, detectCountryFromIP } from './lib/countryUtils';
import { getBusinessTypeLabelForCountry, getCountryById, getCountryTerminology } from './lib/countrySettings';
  import {
   initDriveService,
   signInWithGoogle,
   signOut as signOutFromDrive,
   saveTransactionsToDrive,
   saveDepreciationToDrive,
   saveServicePricesToDrive,
   loadTransactionsFromDrive,
   loadDepreciationFromDrive,
   loadServicePricesFromDrive,
   isSignedIn as isDriveSignedIn,
   ensureDriveSession
  } from './googleDriveService';
import CountrySelectionModal from './components/Onboarding/CountrySelectionModal';
 // Import the new hybrid storage utility
import {
  saveTransactions as saveTransactionsToHybrid,
  loadTransactions as loadTransactionsFromHybrid,
  saveDepreciation as saveDepreciationToHybrid,
  loadDepreciation as loadDepreciationFromHybrid,
  saveServicePrices as saveServicePricesToHybrid,
  loadServicePrices as loadServicePricesFromHybrid,
  saveCostItems as saveCostItemsToHybrid,
  loadCostItems as loadCostItemsFromHybrid,
  saveExtraCategories as saveExtraCategoriesToHybrid,
  loadExtraCategories as loadExtraCategoriesFromHybrid,
  saveGoals as saveGoalsToHybrid,
  loadGoals as loadGoalsFromHybrid,
  saveVatSettings as saveVatSettingsToHybrid,
  loadVatSettings as loadVatSettingsFromHybrid,
  saveClients as saveClientsToHybrid,
  loadClients as loadClientsFromHybrid,
  saveBusinessProfile as saveBusinessProfileToHybrid,
  loadBusinessProfile as loadBusinessProfileFromHybrid,
  saveBankAccounts as saveBankAccountsToHybrid,
  loadBankAccounts as loadBankAccountsFromHybrid,
  normalizeBankAccounts,
  saveJournalEntries as saveJournalEntriesToHybrid,
  loadJournalEntries as loadJournalEntriesFromHybrid,
  normalizeJournalEntries,
  clearBrowserLedgerCache
} from './hybridStorage';
 import TaxCalculation from './TaxCalculation';
 import VatContext from './Contexts/VatContext';
import ReceiptScannerIntegration from './components/ReceiptScanner/ReceiptScannerIntegration';
import { ReceiptScannerProvider } from './Contexts/ReceiptScannerContext';
import ReceiptManagement from './pages/ReceiptManagement';
import { updateReceipt } from './services/receiptManagementService';
 import VoiceInput from './components/VoiceInput';
 // PWA Enhancement Imports
import SyncStatus from './components/SyncStatus';
import SetupChecklistModal from './components/Onboarding/SetupChecklistModal';
import { initializeEncryption } from './utils/encryption';
import { initializePerformanceOptimizations } from './utils/lazyComponents';
import { restoreFirebaseAuthFromGoogle } from './utils/authSync';
import CustomSelect from './components/CustomSelect';
import ClientDatabasePanel from './components/ClientDatabasePanel';
import { CATEGORY_OPTIONS, normalizeCategoryId } from './lib/categories';
import { guardAutoDetectedReceiptExpenseCategory } from './lib/receiptAutoCategoryGuard';
import { parseLineItemPackDetails } from './lib/receiptLineItemUtils';
import { parseSpreadsheetTransactions } from './services/spreadsheetImportService';
import {
  getClientAllocationLabel,
  getClientDisplayName,
  getTransactionClientAllocations,
  mergeClientLists,
  normalizeClientAllocations,
  normalizeClientRecord
} from './lib/clientUtils';
import {
  buildTransactionVatEntries as buildTransactionVatEntriesForReporting,
  getTransactionVatAmount as getTransactionVatAmountForReporting
} from './lib/vatReportingUtils';
import {
  formatDateForInput,
  formatDisplayDate,
  getDateLocaleForCountry,
  parseFlexibleDateValue
} from './lib/dateUtils';
import {
  buildLatestVatFiledSnapshotMap,
  calculateVatFiledNetForTransactions,
  sanitizeVatFiledPeriods
} from './lib/vatFilingUtils';
import {
  buildStaffPerformanceRows,
  getStaffPerformancePeriodMeta,
  getStaffMemberOptions,
  normalizeStaffMemberName,
  STAFF_PERFORMANCE_PERIOD_OPTIONS
} from './lib/staffPerformance';
import AccountantAccessModal from './components/AccountantAccessModal';
import AccountantWorkspacePanel from './components/AccountantWorkspacePanel';
import {
  clearVatFromTransaction,
  getIrishBlockedExpenseVatWarning,
  materializeExpenseVatFromRate
} from './lib/blockedVatExpenseWarning';
import {
  SHARED_WORKSPACE_DATASETS,
  acceptAccountantInvite,
  createAccountantInvite,
  getCollaborationStateForUser,
  loadWorkspaceDatasets,
  revokeAccountantInvite,
  saveWorkspaceDataset,
  syncAcceptedInviteMemberships
} from './services/collaborationService';
import {
  DEFAULT_INDUSTRY_ID,
  INDUSTRY_IDS,
  INDUSTRY_OPTIONS,
  getIndustryProfile,
  normalizeIndustryId
} from './lib/industryProfiles';
import { downloadInvoiceHtml } from './lib/invoiceDownload';

const DEFAULT_SETUP_PROGRESS = { install: false, drive: false, basics: false };
const ONBOARDING_GUIDE_PROGRESS_KEY = 'onboarding_guide_completed_steps';
const PERSONAL_WORKSPACE_SELECTION = '__personal__';
const LEDGER_CACHE_OWNER_KEY = 'ledger_cache_owner_uid';
const STAFF_HR_RECORDS_STORAGE_KEY = 'staff_hr_records';
const DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT = 8;
const STAFF_HOURS_PERIOD_OPTIONS = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' }
];

const toStaffPlanNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const createStaffPlanMember = (index = 0, overrides = {}) => ({
  id: overrides.id || `staff-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
  name: overrides.name ?? '',
  hoursPeriod: STAFF_HOURS_PERIOD_OPTIONS.some((option) => option.id === overrides.hoursPeriod)
    ? overrides.hoursPeriod
    : 'weekly',
  hours: overrides.hours ?? '',
  hourlyPay: overrides.hourlyPay ?? '',
  employerCostPercent: overrides.employerCostPercent ?? '',
  holidayPayPercent: overrides.holidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT
});

const getStaffPlanWeeklyHours = (period, hours, openDaysPerWeek) => {
  const numericHours = toStaffPlanNumber(hours);
  if (numericHours <= 0) return 0;
  if (period === 'daily') return numericHours * Math.max(0, toStaffPlanNumber(openDaysPerWeek));
  if (period === 'monthly') return (numericHours * 12) / 52;
  return numericHours;
};

const getStaffPlanLoadedHourlyCost = (hourlyPay, employerCostPercent, holidayPayPercent = 0) => {
  const base = toStaffPlanNumber(hourlyPay);
  const loading = Math.max(0, toStaffPlanNumber(employerCostPercent));
  const holidayReserve = Math.max(0, toStaffPlanNumber(holidayPayPercent));
  return base * (1 + (loading + holidayReserve) / 100);
};

const buildStaffPlanFromTargetIncome = (targetIncomeData = {}) => {
  const defaultEmployerCostPercent = targetIncomeData.defaultEmployerCostPercent ?? '';
  const defaultHolidayPayPercent = targetIncomeData.defaultHolidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT;
  const sourceMembers = Array.isArray(targetIncomeData.staffMembers) ? targetIncomeData.staffMembers : [];
  return {
    defaultEmployerCostPercent,
    defaultHolidayPayPercent,
    staffMembers: sourceMembers.map((member, index) => createStaffPlanMember(index, {
      ...member,
      employerCostPercent: member?.employerCostPercent ?? defaultEmployerCostPercent,
      holidayPayPercent: member?.holidayPayPercent ?? defaultHolidayPayPercent
    }))
  };
};

const buildLabourProfilesForStaffPlan = (staffPlan, targetIncomeData = {}) => {
  const openDaysPerWeek = targetIncomeData.openDaysPerWeek ?? targetIncomeData.workDaysPerWeek ?? 5;
  const staffMembers = Array.isArray(staffPlan?.staffMembers) ? staffPlan.staffMembers : [];
  return staffMembers
    .map((member, index) => {
      const weeklyHours = getStaffPlanWeeklyHours(member.hoursPeriod, member.hours, openDaysPerWeek);
      const baseHourlyPay = toStaffPlanNumber(member.hourlyPay);
      const employerCostPercent = toStaffPlanNumber(member.employerCostPercent);
      const holidayPayPercent = toStaffPlanNumber(member.holidayPayPercent);
      const baseWeeklyPay = weeklyHours * baseHourlyPay;
      const weeklyEmployerLoading = baseWeeklyPay * Math.max(0, employerCostPercent) / 100;
      const weeklyHolidayPayReserve = baseWeeklyPay * Math.max(0, holidayPayPercent) / 100;
      const loadedHourlyCost = getStaffPlanLoadedHourlyCost(member.hourlyPay, member.employerCostPercent, member.holidayPayPercent);
      const weeklyCost = weeklyHours * loadedHourlyCost;

      return {
        id: member.id || `staff-${index + 1}`,
        type: 'staff',
        name: member.name?.trim() || `Staff ${index + 1}`,
        weeklyHours: Number(weeklyHours.toFixed(2)),
        baseHourlyPay: Number(baseHourlyPay.toFixed(2)),
        employerCostPercent: Number(employerCostPercent.toFixed(2)),
        holidayPayPercent: Number(holidayPayPercent.toFixed(2)),
        baseWeeklyPay: Number(baseWeeklyPay.toFixed(2)),
        weeklyEmployerLoading: Number(weeklyEmployerLoading.toFixed(2)),
        weeklyHolidayPayReserve: Number(weeklyHolidayPayReserve.toFixed(2)),
        loadedHourlyCost: Number(loadedHourlyCost.toFixed(2)),
        weeklyCost: Number(weeklyCost.toFixed(2))
      };
    })
    .filter((profile) => profile.loadedHourlyCost > 0 || profile.weeklyHours > 0 || profile.baseHourlyPay > 0);
};

const buildStaffPlanSummary = (labourProfiles = []) => {
  const totalWeeklyLabourHours = labourProfiles.reduce((sum, profile) => sum + Math.max(0, Number(profile.weeklyHours) || 0), 0);
  const totalWeeklyLabourCost = labourProfiles.reduce((sum, profile) => sum + Math.max(0, Number(profile.weeklyCost) || 0), 0);
  const totalWeeklyHolidayPayReserve = labourProfiles.reduce((sum, profile) => sum + Math.max(0, Number(profile.weeklyHolidayPayReserve) || 0), 0);
  const weightedLoadedHourlyCost = totalWeeklyLabourHours > 0
    ? labourProfiles.reduce((sum, profile) => sum + ((Number(profile.loadedHourlyCost) || 0) * (Number(profile.weeklyHours) || 0)), 0) / totalWeeklyLabourHours
    : 0;

  return {
    totalWeeklyLabourHours,
    totalWeeklyLabourCost,
    totalMonthlyLabourCost: (totalWeeklyLabourCost * 52) / 12,
    totalYearlyLabourCost: totalWeeklyLabourCost * 52,
    totalWeeklyHolidayPayReserve,
    totalMonthlyHolidayPayReserve: (totalWeeklyHolidayPayReserve * 52) / 12,
    totalYearlyHolidayPayReserve: totalWeeklyHolidayPayReserve * 52,
    weightedLoadedHourlyCost
  };
};

const getCurrentWeekStartDateString = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date.toISOString().split('T')[0];
};
const BUSINESS_TYPES = Object.freeze({
  SOLE_TRADER: 'sole_trader',
  LIMITED_COMPANY: 'limited_company'
});
const BUSINESS_TYPE_STORAGE_KEY = '70livesbeautyledgerBusinessType';
const INDUSTRY_STORAGE_KEY = '70livesbeautyledgerIndustry';
const BUSINESS_DETAILS_STORAGE_KEY = '70livesbeautyledgerBusinessDetails';
const BUSINESS_TYPE_CONFIRMED_STORAGE_KEY = '70livesbeautyledgerBusinessTypeConfirmed';
const INDUSTRY_CONFIRMED_STORAGE_KEY = '70livesbeautyledgerIndustryConfirmed';
const VAT_STATUS_CONFIRMED_STORAGE_KEY = '70livesbeautyledgerVatStatusConfirmed';
const VAT_ACCOUNTING_BASIS_CONFIRMED_STORAGE_KEY = '70livesbeautyledgerVatAccountingBasisConfirmed';
const FUEL_VAT_REVIEW_PREFERENCE_STORAGE_KEY = '70livesFuelVatReviewPreference_ie';
const BUSINESS_TYPE_OPTIONS = [
  { id: BUSINESS_TYPES.SOLE_TRADER },
  { id: BUSINESS_TYPES.LIMITED_COMPANY }
];

const getIndustryLabel = (value) => getIndustryProfile(value).shortLabel || getIndustryProfile(value).label;

const getCollaborationWorkspaceStorageKey = (userId) => `active_shared_workspace_${userId}`;

const readFuelVatReviewPreference = () => {
  if (typeof window === 'undefined') return '';
  try {
    const stored = localStorage.getItem(FUEL_VAT_REVIEW_PREFERENCE_STORAGE_KEY);
    return stored === 'diesel' ? stored : '';
  } catch {
    return '';
  }
};

const ensureIndustrySetupForExistingUsers = () => {
  if (typeof window === 'undefined') return false;
  try {
    const hasCompletedPreviousRequiredSetup = Boolean(localStorage.getItem('selectedCountry')) &&
      localStorage.getItem(BUSINESS_TYPE_CONFIRMED_STORAGE_KEY) === 'true' &&
      localStorage.getItem(VAT_STATUS_CONFIRMED_STORAGE_KEY) === 'true';

    if (!hasCompletedPreviousRequiredSetup) return false;

    if (!localStorage.getItem(INDUSTRY_STORAGE_KEY)) {
      localStorage.setItem(INDUSTRY_STORAGE_KEY, DEFAULT_INDUSTRY_ID);
    }
    if (localStorage.getItem(INDUSTRY_CONFIRMED_STORAGE_KEY) !== 'true') {
      localStorage.setItem(INDUSTRY_CONFIRMED_STORAGE_KEY, 'true');
    }
    return true;
  } catch {
    return false;
  }
};

const isRequiredBusinessSetupComplete = () => {
  if (typeof window === 'undefined') return false;
  try {
    ensureIndustrySetupForExistingUsers();
    let requiresVatBasisConfirmation = false;
    try {
      const storedVatSettings = localStorage.getItem('vat_settings');
      const parsedVatSettings = storedVatSettings ? JSON.parse(storedVatSettings) : null;
      requiresVatBasisConfirmation = Boolean(parsedVatSettings?.enabled && parsedVatSettings?.isRegistered);
    } catch {
      requiresVatBasisConfirmation = false;
    }
    return Boolean(localStorage.getItem('selectedCountry')) &&
      localStorage.getItem(BUSINESS_TYPE_CONFIRMED_STORAGE_KEY) === 'true' &&
      localStorage.getItem(INDUSTRY_CONFIRMED_STORAGE_KEY) === 'true' &&
      localStorage.getItem(VAT_STATUS_CONFIRMED_STORAGE_KEY) === 'true' &&
      (!requiresVatBasisConfirmation || localStorage.getItem(VAT_ACCOUNTING_BASIS_CONFIRMED_STORAGE_KEY) === 'true');
  } catch {
    return false;
  }
};

const prepareBrowserLedgerCacheForUser = async (nextUserId, onAccountSwitch = null) => {
  const normalizedUserId = String(nextUserId || '').trim();
  let previousCacheOwnerId = '';

  try {
    previousCacheOwnerId = localStorage.getItem(LEDGER_CACHE_OWNER_KEY) || '';
  } catch {
    previousCacheOwnerId = '';
  }

  if (normalizedUserId && previousCacheOwnerId && previousCacheOwnerId !== normalizedUserId) {
    if (typeof onAccountSwitch === 'function') {
      onAccountSwitch();
    }
    await clearBrowserLedgerCache();
  }

  try {
    if (normalizedUserId) {
      localStorage.setItem(LEDGER_CACHE_OWNER_KEY, normalizedUserId);
    } else {
      localStorage.removeItem(LEDGER_CACHE_OWNER_KEY);
    }
  } catch {}
};

const computeSetupProgressFromStorage = () => {
  if (typeof window === 'undefined') return DEFAULT_SETUP_PROGRESS;
  const isStandalone = typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
     window.matchMedia('(display-mode: minimal-ui)').matches);
  const installComplete = isStandalone || localStorage.getItem('pwa-install-complete') === 'true';
  const driveComplete = Boolean(localStorage.getItem('google_access_token')) &&
    localStorage.getItem('driveAuthState') === 'authenticated' &&
    localStorage.getItem('hasDriveConsent') === 'true';
  const basicsComplete = localStorage.getItem('setup_step_basics') === 'true' && isRequiredBusinessSetupComplete();
  return {
    install: Boolean(installComplete),
    drive: Boolean(driveComplete),
    basics: Boolean(basicsComplete)
  };
};

const resolveAuthViewFromPath = (path, hasSignedInBefore) => {
  const normalizedPath = (path || '/').toLowerCase();
  if (normalizedPath === '/signin' || normalizedPath === '/login') return 'signin';
  if (normalizedPath === '/signup' || normalizedPath === '/register') return 'signup';
  return hasSignedInBefore ? 'signin' : 'signup';
};

const readHasSignedInBefore = () => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('has_signed_in_before') === 'true';
  } catch {
    return false;
  }
};

const shouldPreferSignIn = () => {
  if (typeof window === 'undefined') return false;
  const isStandalone = window.navigator.standalone ||
    (typeof window.matchMedia === 'function' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
       window.matchMedia('(display-mode: minimal-ui)').matches));
  return isStandalone || readHasSignedInBefore();
};

const DEFAULT_TARGET_INCOME_DATA = {
  amount: '',
  period: 'weekly',
  businessProfitGoalAmount: '',
  businessProfitGoalPeriod: 'weekly',
  openDaysPerWeek: 5,
  totalWeeklyOpenHours: 30,
  workDaysPerWeek: 5,
  workHoursPerWeek: 30,
  goalHourly: null,
  ownerRole: 'service_team',
  ownerPayMethod: 'drawings',
  ownerServiceHourlyRate: '',
  ownerEmployerCostPercent: '',
  ownerHoursPeriod: 'weekly',
  ownerHours: '',
  teamMode: 'solo',
  defaultEmployerCostPercent: '',
  defaultHolidayPayPercent: '8',
  staffCount: 0,
  staffMembers: [],
  rctSubcontractors: [],
  desiredProfitPerStaffMember: null,
  labourProfiles: [],
  pricingLaborHourlyCost: null,
  businessProfitHourlyTarget: null
};

const normalizeTargetIncomeData = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const amount = source.businessProfitGoalAmount ?? source.amount ?? '';
  const period = source.businessProfitGoalPeriod ?? source.period ?? 'weekly';
  const openDaysPerWeek = source.openDaysPerWeek ?? source.workDaysPerWeek ?? 5;
  const totalWeeklyOpenHours = source.totalWeeklyOpenHours ?? source.openHoursPerWeek ?? source.workHoursPerWeek ?? 30;
  const sourceStaffMembers = Array.isArray(source.staffMembers) ? source.staffMembers : [];
  const staffCount = Math.max(0, Number(source.staffCount) || sourceStaffMembers.length || 0);
  const teamMode = source.teamMode === 'staff' || staffCount > 0 ? 'staff' : 'solo';
  const defaultHolidayPayPercent = source.defaultHolidayPayPercent ?? '8';
  const ownerRole = ['service_team', 'occasional', 'non_service'].includes(source.ownerRole)
    ? source.ownerRole
    : (teamMode === 'staff' ? 'non_service' : 'service_team');
  const normalizedStaffMembers = teamMode === 'staff'
    ? sourceStaffMembers.slice(0, staffCount || sourceStaffMembers.length).map((member, index) => ({
        id: member?.id || `staff-${index + 1}`,
        name: member?.name || '',
        hoursPeriod: ['daily', 'weekly', 'monthly'].includes(member?.hoursPeriod) ? member.hoursPeriod : 'weekly',
        hours: member?.hours ?? '',
        hourlyPay: member?.hourlyPay ?? '',
        employerCostPercent: member?.employerCostPercent ?? source.defaultEmployerCostPercent ?? '',
        holidayPayPercent: member?.holidayPayPercent ?? defaultHolidayPayPercent
      }))
    : [];
  const normalizedRctSubcontractors = (Array.isArray(source.rctSubcontractors) ? source.rctSubcontractors : [])
    .map((subcontractor, index) => {
      const rate = Number(subcontractor?.rctDeductionRate ?? subcontractor?.deductionRate ?? 0.2);
      const safeRate = [0, 0.2, 0.35].includes(rate) ? rate : 0.2;
      const name = String(subcontractor?.name || subcontractor?.subcontractorName || '').trim();
      const supplierType = subcontractor?.supplierType === 'standard' ? 'standard' : 'rct';
      return {
        id: subcontractor?.id || `rct-subcontractor-${index + 1}`,
        name,
        supplierType,
        rctDeductionRate: safeRate
      };
    })
    .filter((subcontractor) => subcontractor.name);
  const labourProfiles = Array.isArray(source.labourProfiles) ? source.labourProfiles : [];

  return {
    amount,
    period,
    businessProfitGoalAmount: amount,
    businessProfitGoalPeriod: period,
    openDaysPerWeek,
    totalWeeklyOpenHours,
    workDaysPerWeek: openDaysPerWeek,
    workHoursPerWeek: totalWeeklyOpenHours,
    goalHourly: source.goalHourly ?? null,
    ownerRole,
    ownerPayMethod: source.ownerPayMethod || (ownerRole === 'non_service' ? 'not_planned' : 'drawings'),
    ownerServiceHourlyRate: source.ownerServiceHourlyRate ?? source.ownerHourlyRate ?? source.goalHourly ?? '',
    ownerEmployerCostPercent: source.ownerEmployerCostPercent ?? '',
    ownerHoursPeriod: ['daily', 'weekly', 'monthly'].includes(source.ownerHoursPeriod) ? source.ownerHoursPeriod : 'weekly',
    ownerHours: source.ownerHours ?? '',
    defaultEmployerCostPercent: source.defaultEmployerCostPercent ?? '',
    defaultHolidayPayPercent,
    teamMode,
    staffCount: teamMode === 'staff' ? (staffCount || normalizedStaffMembers.length) : 0,
    staffMembers: normalizedStaffMembers,
    rctSubcontractors: normalizedRctSubcontractors,
    desiredProfitPerStaffMember: source.desiredProfitPerStaffMember ?? null,
    labourProfiles,
    pricingLaborHourlyCost: source.pricingLaborHourlyCost ?? null,
    businessProfitHourlyTarget: source.businessProfitHourlyTarget ?? null,
    staffPlanSummary: source.staffPlanSummary && typeof source.staffPlanSummary === 'object'
      ? source.staffPlanSummary
      : {}
  };
};

const createEmptyTransactionDraft = (type = '', overrides = {}) => ({
  type,
  date: new Date().toISOString().split('T')[0],
  amount: '',
  amountBasis: 'gross',
  discountAmount: '',
  category: '',
  description: '',
  paymentMethod: 'cash',
  paymentBreakdown: [],
  amountPaid: null,
  outstandingAmount: 0,
  paymentStatus: 'paid',
  saleTotalAmount: null,
  isPartPaymentSale: false,
  isPartPaymentInstallment: false,
  linkedPartPaymentSaleId: '',
  service: '',
  vatRate: null,
  vatAmount: null,
  vatComponents: [],
  saveToCost: false,
  clientId: '',
  clientName: '',
  clientAllocations: [],
  allocateToClient: false,
  jobReference: '',
  billableToClient: false,
  supplierId: '',
  supplierName: '',
  staffMemberId: '',
  staffMemberName: '',
  rctSubcontractorId: '',
  rctSubcontractorName: '',
  rctDeductionRate: 0.2,
  rctReverseCharge: false,
  notes: '',
  ...overrides
});

const RECEIPT_ATTACHMENT_ACCEPT = 'image/*,application/pdf';
const MAX_RECEIPT_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const isAllowedReceiptAttachmentFile = (file) => {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  return type.startsWith('image/') || type === 'application/pdf' || /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(name);
};

const sanitizeStorageFileName = (fileName = 'receipt') => {
  const cleanName = String(fileName || 'receipt')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return cleanName || 'receipt';
};

const buildClearedReceiptAttachmentPatch = () => ({
  receiptImageUrl: '',
  receiptUrl: '',
  imageUrl: '',
  receiptFileName: '',
  receiptFilename: '',
  receiptName: '',
  receiptFileType: '',
  receiptStoragePath: '',
  receiptAttachedAt: '',
  receiptAttachmentSource: '',
  receiptAttachmentWorkspaceId: ''
});

const getTransactionDateValue = (transaction) => {
  if (!transaction || typeof transaction !== 'object') return null;

  const candidates = [
    transaction.date,
    transaction.createdAt,
    transaction.updatedAt,
    transaction.timestamp
  ];

  for (const candidate of candidates) {
    const parsed = parseFlexibleDateValue(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const getTransactionFinancialDateValue = (transaction) => {
  if (!transaction || typeof transaction !== 'object') return null;
  return parseFlexibleDateValue(transaction.date);
};

const isReasonableFinancialYear = (year) => {
  const currentYear = new Date().getFullYear();
  return Number.isFinite(year) && year >= currentYear - 10 && year <= currentYear + 1;
};

const getTransactionYearValue = (transaction) => {
  const dateValue = getTransactionFinancialDateValue(transaction);
  const year = dateValue ? dateValue.getFullYear() : null;
  return isReasonableFinancialYear(year) ? year : null;
};

const getTransactionEntryDateValue = (transaction) => {
  if (!transaction || typeof transaction !== 'object') return null;

  const candidates = [
    transaction.receiptUploadedAt,
    transaction.recordedAt,
    transaction.updatedAt,
    transaction.createdAt,
    transaction.timestamp,
    transaction.date
  ];

  for (const candidate of candidates) {
    const parsed = parseFlexibleDateValue(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const getTransactionEntryTime = (transaction) => {
  const entryDate = getTransactionEntryDateValue(transaction);
  return entryDate ? entryDate.getTime() : 0;
};

const getTransactionOrderTime = (transaction) => {
  const orderCandidates = [
    transaction.receiptUploadOrder,
    transaction.entryOrder,
    transaction.id
  ];

  for (const candidate of orderCandidates) {
    const numericValue = Number(candidate);
    if (Number.isFinite(numericValue)) return numericValue;
  }

  return 0;
};

const compareTransactionsByEntryDateDesc = (a, b) => {
  const timeComparison = getTransactionEntryTime(b) - getTransactionEntryTime(a);
  if (timeComparison !== 0) return timeComparison;

  return getTransactionOrderTime(b) - getTransactionOrderTime(a);
};

const DEMO_DASHBOARD_DATA = {
  snapshots: [
    { label: "Today's Income", value: 1050 },
    { label: "This Week's Income", value: 6825 },
    { label: "This Month's Income", value: 18940 }
  ],
  revenueVsExpenses: { income: 22800, expenses: 8900 },
  kpis: [
    { label: 'Average Ticket', value: '145' },
    { label: 'Rebook Rate', value: '78%' },
    { label: 'Retail Mix', value: '18%' }
  ],
  services: [
    { name: 'Luxury Facial', total: '7,320', count: 48 },
    { name: 'Balayage Package', total: '5,460', count: 26 },
    { name: 'Brow Lamination', total: '2,140', count: 36 }
  ],
  expenses: [
    { name: 'Supplies / Retail Stock', total: '1,980' },
    { name: 'Rent & Utilities', total: '3,200' },
    { name: 'Education & Training', total: '860' }
  ]
};

const DemoDashboardModal = ({ onClose, onDontShow }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1050] px-4 py-8">
    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-2xl font-semibold text-gray-900">Sample Dashboard</h3>
          <p className="text-sm text-gray-500">Here's how the dashboard looks with a thriving business.</p>
        </div>
        <div className="flex items-center gap-2">
          {onDontShow && (
            <button
              onClick={onDontShow}
              className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              Don't show again
            </button>
          )}
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close sample dashboard">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {DEMO_DASHBOARD_DATA.snapshots.map((card) => (
          <div key={card.label} className="bg-accent-gradient rounded-xl p-4">
            <div className="text-xs uppercase text-gray-500">{card.label}</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">{card.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-3">Revenue vs Expenses</h4>
          <div className="space-y-3">
            {['income', 'expenses'].map((key) => {
              const value = DEMO_DASHBOARD_DATA.revenueVsExpenses[key];
              const percent = Math.min(100, (value / Math.max(...Object.values(DEMO_DASHBOARD_DATA.revenueVsExpenses))) * 100);
              return (
                <div key={key}>
                  <div className="flex justify-between text-sm text-gray-700 capitalize">
                    <span>{key}</span>
                    <span>{value.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-1">
                    <div
                      className={`h-2 rounded-full ${key === 'income' ? 'bg-[color:var(--color-accent)]' : 'bg-[color:var(--color-primary)]'}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-3">Signature KPIs</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {DEMO_DASHBOARD_DATA.kpis.map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                <div className="text-xs uppercase text-gray-500">{kpi.label}</div>
                <div className="text-xl font-semibold text-gray-900 mt-1">{kpi.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Most-Booked Services</h4>
          <div className="space-y-3">
            {DEMO_DASHBOARD_DATA.services.map((service) => (
              <div key={service.name} className="flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-gray-900">{service.name}</div>
                  <div className="text-gray-500">{service.count} bookings</div>
                </div>
                <div className="font-semibold text-gray-900">{service.total}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Spending Highlights</h4>
          <div className="space-y-3">
            {DEMO_DASHBOARD_DATA.expenses.map((expense) => (
              <div key={expense.name} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{expense.name}</span>
                <span className="font-semibold text-gray-900">{expense.total}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SetupChecklistCard = ({
  progress,
  onInstall,
  onDrive,
  onBasics,
  onDismiss,
  onDemo,
  hideDemoDashboard,
  onHideDemoDashboard
}) => {
  const steps = [
    {
      id: 'install',
      title: 'Add App to Home Screen (Optional)',
      description: 'Works on Safari and Chrome. Install to home screen for faster access.',
      action: onInstall,
      actionLabel: 'Install'
    },
    {
      id: 'drive',
      title: 'Connect Google Drive',
      description: 'Sync receipts and backups with your business Drive.',
      action: onDrive,
      actionLabel: 'Connect'
    },
    {
      id: 'basics',
      title: 'Open Guided Walkthrough',
      description: 'Follow a step-by-step guide across setup, pricing, receipts, imports, and reports.',
      action: onBasics,
      actionLabel: 'Open guide'
    }
  ];

  return (
    <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h4 className="text-xl font-semibold text-gray-900">Setup Your Business (3 Steps)</h4>
          <p className="text-sm text-gray-500">Complete these essentials to personalize your analytics.</p>
        </div>
        <div className="flex items-center gap-2">
          {!hideDemoDashboard && (
            <>
              <button
                type="button"
                onClick={onDemo}
                className="px-3 py-1.5 text-xs font-semibold rounded-full border border-gray-200 hover:bg-gray-50"
              >
                See example dashboard
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onHideDemoDashboard) onHideDemoDashboard();
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Don't show again
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Skip for now
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {steps.map((step) => {
          const completed = progress[step.id];
          return (
            <div
              key={step.id}
              className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border ${
                completed ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-[200px]">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${completed ? 'bg-green-500 text-white' : 'bg-white border border-gray-300 text-gray-400'}`}>
                  {completed ? '?' : ''}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{step.title}</div>
                  <p className="text-xs text-gray-600">{step.description}</p>
                </div>
              </div>
              {!completed && (
                <button
                  type="button"
                  onClick={step.action}
                  className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[color:var(--color-accent)] text-[color:var(--color-text)] hover:brightness-95"
                >
                  {step.actionLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Categories with services (shared across app, voice, and receipt scanner)
const LIMITED_COMPANY_CATEGORY_OPTIONS = {
  income: [{ id: 'director_loan', name: "Director's Loan" }],
  expenses: [{ id: 'director_loan', name: "Director's Loan" }]
};

const normalizeBusinessType = (value) => (
  value === BUSINESS_TYPES.LIMITED_COMPANY
    ? BUSINESS_TYPES.LIMITED_COMPANY
    : BUSINESS_TYPES.SOLE_TRADER
);

const DEFAULT_BUSINESS_PROFILE_DETAILS = Object.freeze({
  businessName: '',
  tradingName: '',
  businessAddress: '',
  companyRegistrationNumber: '',
  incorporationDate: '',
  taxReference: '',
  directorDetails: {
    name: '',
    address: '',
    role: '',
    appointedDate: ''
  }
});

const normalizeBusinessProfileDetails = (value = {}, options = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const directorDetails = source.directorDetails && typeof source.directorDetails === 'object' && !Array.isArray(source.directorDetails)
    ? source.directorDetails
    : {};
  const normalizeText = (textValue) => {
    const stringValue = String(textValue || '');
    return options.preserveTypingWhitespace ? stringValue : stringValue.trim();
  };

  return {
    ...DEFAULT_BUSINESS_PROFILE_DETAILS,
    businessName: normalizeText(source.businessName),
    tradingName: normalizeText(source.tradingName),
    businessAddress: normalizeText(source.businessAddress),
    companyRegistrationNumber: normalizeText(source.companyRegistrationNumber),
    incorporationDate: normalizeText(source.incorporationDate),
    taxReference: normalizeText(source.taxReference),
    directorDetails: {
      name: normalizeText(directorDetails.name),
      address: normalizeText(directorDetails.address),
      role: normalizeText(directorDetails.role),
      appointedDate: normalizeText(directorDetails.appointedDate)
    }
  };
};

const buildBusinessProfileSettings = (businessType, industryId, details = {}) => ({
  businessType: normalizeBusinessType(businessType),
  industryId: normalizeIndustryId(industryId),
  ...normalizeBusinessProfileDetails(details)
});

const readLocalBusinessProfileSettings = () => {
  if (typeof window === 'undefined') {
    return buildBusinessProfileSettings(BUSINESS_TYPES.SOLE_TRADER, DEFAULT_INDUSTRY_ID);
  }

  try {
    return buildBusinessProfileSettings(
      localStorage.getItem(BUSINESS_TYPE_STORAGE_KEY),
      localStorage.getItem(INDUSTRY_STORAGE_KEY),
      JSON.parse(localStorage.getItem(BUSINESS_DETAILS_STORAGE_KEY) || '{}')
    );
  } catch {
    return buildBusinessProfileSettings(BUSINESS_TYPES.SOLE_TRADER, DEFAULT_INDUSTRY_ID);
  }
};

const writeLocalBusinessProfileSettings = (profile = {}) => {
  if (typeof window === 'undefined') return;
  try {
    const normalizedProfile = buildBusinessProfileSettings(profile.businessType, profile.industryId, profile);
    localStorage.setItem(BUSINESS_TYPE_STORAGE_KEY, normalizedProfile.businessType);
    localStorage.setItem(INDUSTRY_STORAGE_KEY, normalizedProfile.industryId);
    localStorage.setItem(BUSINESS_DETAILS_STORAGE_KEY, JSON.stringify(normalizeBusinessProfileDetails(normalizedProfile)));
    localStorage.setItem(BUSINESS_TYPE_CONFIRMED_STORAGE_KEY, 'true');
    localStorage.setItem(INDUSTRY_CONFIRMED_STORAGE_KEY, 'true');
  } catch {}
};

const getBusinessTypeLabel = (value, countryInput = 'ie') => (
  getBusinessTypeLabelForCountry(normalizeBusinessType(value), countryInput)
);

const mergeCategoryOptions = (...categoryLists) => {
  const merged = new Map();

  categoryLists.flat().forEach((category) => {
    if (!category?.id) return;
    if (!merged.has(category.id)) {
      merged.set(category.id, { icon: '', ...category });
      return;
    }

    merged.set(category.id, {
      ...merged.get(category.id),
      ...category,
      icon: category.icon ?? merged.get(category.id).icon ?? ''
    });
  });

  return Array.from(merged.values());
};

const getCategoriesForBusinessProfile = (businessType, industryId = DEFAULT_INDUSTRY_ID) => {
  const isLimitedCompany = normalizeBusinessType(businessType) === BUSINESS_TYPES.LIMITED_COMPANY;
  const industryProfile = getIndustryProfile(industryId);
  const industryCategories = industryProfile.categories || {};
  const shouldIncludeLegacyBeautyDefaults = industryProfile.id === DEFAULT_INDUSTRY_ID;

  return {
    income: mergeCategoryOptions(
      industryCategories.income || [],
      shouldIncludeLegacyBeautyDefaults ? CATEGORY_OPTIONS.income || [] : [],
      isLimitedCompany ? LIMITED_COMPANY_CATEGORY_OPTIONS.income : []
    ),
    expenses: mergeCategoryOptions(
      industryCategories.expenses || [],
      shouldIncludeLegacyBeautyDefaults ? CATEGORY_OPTIONS.expenses || [] : [],
      isLimitedCompany ? LIMITED_COMPANY_CATEGORY_OPTIONS.expenses : []
    )
  };
};

const SERVICE_OFFERINGS_STORAGE_KEY = '70livesbeautyledgerServiceOfferings';
const HAIR_VAT_REDUCTION_EFFECTIVE_DATE = '2026-07-01';
const IRISH_HAIR_SALES_VAT_RATE = 0.09;
const IRISH_STANDARD_VAT_RATE = 0.23;
const IRISH_REDUCED_VAT_RATE = 0.135;
const IRISH_ZERO_VAT_RATE = 0;

const SERVICE_OFFERING_OPTIONS = [
  { id: 'hair', label: 'Hair' },
  { id: 'makeup_beauty', label: 'Makeup / Beauty' },
  { id: 'brows_lashes', label: 'Brows / Lashes' },
  { id: 'nails', label: 'Nails' },
  { id: 'aesthetics_dermatology', label: 'Aesthetics / Dermatology' },
  { id: 'massage_acupuncture', label: 'Massage / Acupuncture' },
  { id: 'other', label: 'Other' }
];

const SERVICE_OFFERING_IDS = new Set(SERVICE_OFFERING_OPTIONS.map((option) => option.id));

const BEAUTY_COMMON_INCOME_CATEGORY_IDS = new Set([
  'gift_card',
  'training',
  'gov_funding',
  'loan',
  'director_loan'
]);

const BEAUTY_COMMON_EXPENSE_CATEGORY_IDS = new Set([
  'general_expenses',
  'utilities',
  'internet_telephone',
  'rent',
  'staffCosts',
  'employer_prsi',
  'employee_holiday_pay',
  'owner_pension',
  'naersa_employer_contributions',
  'naersa_employee_contributions',
  'insurance',
  'marketing',
  'travel_expenses',
  'meals',
  'uniform_costs',
  'equipment',
  'furniture',
  'electronics',
  'vehicle',
  'vehicle_fuel',
  'vehicle_repairs_licensing',
  'accounting',
  'legal',
  'interest',
  'bank_charges',
  'loan_repayment',
  'training',
  'repairs',
  'cleaning',
  'maintenance',
  'software',
  'office',
  'misc',
  'director_loan'
]);

const SERVICE_OFFERING_CATEGORY_MAP = {
  hair: {
    income: ['hair'],
    expenses: ['supplies', 'retail_stock', 'salon_tools']
  },
  makeup_beauty: {
    income: ['facial', 'makeup', 'brows', 'lashes', 'nails', 'massage', 'body_treatments', 'injectables', 'retail', 'packages', 'consulting'],
    expenses: ['supplies', 'retail_stock', 'salon_tools', 'medical_expenses']
  },
  brows_lashes: {
    income: ['brows', 'lashes', 'waxing_services'],
    expenses: ['supplies', 'retail_stock', 'salon_tools']
  },
  nails: {
    income: ['nails'],
    expenses: ['supplies', 'retail_stock', 'salon_tools']
  },
  aesthetics_dermatology: {
    income: ['facial', 'injectables', 'body_treatments', 'consulting', 'retail'],
    expenses: ['supplies', 'retail_stock', 'salon_tools', 'medical_expenses']
  },
  massage_acupuncture: {
    income: ['massage', 'body_treatments'],
    expenses: ['supplies', 'retail_stock', 'salon_tools']
  },
  other: {
    income: ['consulting', 'packages', 'retail'],
    expenses: ['supplies', 'retail_stock', 'salon_tools']
  }
};

const sanitizeServiceOfferings = (value) => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(new Set(source.filter((entry) => SERVICE_OFFERING_IDS.has(entry))));
};

const getBeautyServiceScopedCategories = (categories, selectedOfferings, type) => {
  const offerings = sanitizeServiceOfferings(selectedOfferings);
  if (!offerings.length) return categories;

  const allowedIds = new Set(type === 'income' ? BEAUTY_COMMON_INCOME_CATEGORY_IDS : BEAUTY_COMMON_EXPENSE_CATEGORY_IDS);
  offerings.forEach((offeringId) => {
    (SERVICE_OFFERING_CATEGORY_MAP[offeringId]?.[type] || []).forEach((categoryId) => allowedIds.add(categoryId));
  });

  return categories.filter((category) => {
    if (!category?.id) return false;
    if (allowedIds.has(category.id)) return true;
    return !CATEGORY_OPTIONS[type]?.some((defaultCategory) => defaultCategory.id === category.id);
  });
};

const normalizeDateString = (value) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  const direct = String(value).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString().slice(0, 10) : parsed.toISOString().slice(0, 10);
};

const normalizeOptionalDateString = (value) => {
  if (!value) return '';
  const direct = String(value).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const isIrishTaxJurisdiction = (taxJurisdiction, country) => {
  const countryId = String(country?.id || '').toLowerCase();
  const countryName = String(country?.name || '').toLowerCase();
  return taxJurisdiction === 'Ireland' || countryId === 'ie' || countryName.includes('ireland');
};

const isHairSalesCategory = (category) => {
  const normalized = normalizeCategoryId(category || '');
  const compact = String(normalized || '').toLowerCase().replace(/[\s_-]+/g, '');
  return compact === 'hair' || compact === 'hairservices';
};

const BEAUTY_REDUCED_RATE_INCOME_CATEGORY_IDS = new Set([
  'facial',
  'injectables',
  'body_treatments',
  'massage',
  'massageServices',
  'nails',
  'nailServices',
  'brows',
  'browServices',
  'lashes',
  'lashServices',
  'makeup',
  'makeupServices',
  'skinServices',
  'packages',
  'consulting',
  'waxing_services'
]);

const CONSTRUCTION_REDUCED_RATE_INDUSTRY_IDS = new Set([
  INDUSTRY_IDS.LANDSCAPING,
  INDUSTRY_IDS.CARPENTRY,
  INDUSTRY_IDS.PAINTING_DECORATING,
  INDUSTRY_IDS.ELECTRICAL
]);

const NON_TAXABLE_INCOME_CATEGORY_IDS = new Set([
  'gift_card',
  'giftcard',
  'gift_voucher',
  'giftvoucher',
  'gov_funding',
  'loan',
  'director_loan'
]);

const normalizeVatRateSetting = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed > 1 ? parsed / 100 : parsed;
};

const getDefaultIrishIncomeCategoryVatRate = ({
  category,
  industryId = DEFAULT_INDUSTRY_ID,
  transactionDate = null,
  isProductCategory = () => false
}) => {
  const normalizedCategory = normalizeCategoryId(category || '');
  const compactCategory = String(normalizedCategory || '').toLowerCase().replace(/[\s_-]+/g, '');

  if (NON_TAXABLE_INCOME_CATEGORY_IDS.has(compactCategory) || NON_TAXABLE_INCOME_CATEGORY_IDS.has(normalizedCategory)) {
    return IRISH_ZERO_VAT_RATE;
  }

  if (isHairSalesCategory(normalizedCategory)) {
    return normalizeDateString(transactionDate) >= HAIR_VAT_REDUCTION_EFFECTIVE_DATE
      ? IRISH_HAIR_SALES_VAT_RATE
      : IRISH_REDUCED_VAT_RATE;
  }

  if (normalizeIndustryId(industryId) === DEFAULT_INDUSTRY_ID && BEAUTY_REDUCED_RATE_INCOME_CATEGORY_IDS.has(normalizedCategory)) {
    return IRISH_REDUCED_VAT_RATE;
  }

  if (CONSTRUCTION_REDUCED_RATE_INDUSTRY_IDS.has(normalizeIndustryId(industryId)) || normalizedCategory === 'rct_contract_income') {
    return IRISH_REDUCED_VAT_RATE;
  }

  return isProductCategory(normalizedCategory)
    ? IRISH_STANDARD_VAT_RATE
    : IRISH_STANDARD_VAT_RATE;
};

const sanitizeCategoryVatRates = (value = {}) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [categoryId, rate]) => {
    const normalizedCategory = normalizeCategoryId(categoryId || '');
    const normalizedRate = normalizeVatRateSetting(rate);
    if (normalizedCategory && normalizedRate !== null) {
      acc[normalizedCategory] = normalizedRate;
    }
    return acc;
  }, {});
};

 // Default service offerings by category
 const DEFAULT_SERVICES = {
   facial: [
     { name: 'Basic Facial', duration: 30, cost: 25, price: 60 },
     { name: 'Deep Cleansing Facial', duration: 60, cost: 35, price: 85 },
     { name: 'Anti-Aging Facial', duration: 75, cost: 45, price: 110 }
   ],
   massage: [
     { name: 'Relaxing Massage', duration: 30, cost: 20, price: 50 },
     { name: 'Deep Tissue Massage', duration: 60, cost: 30, price: 80 },
     { name: 'Hot Stone Therapy', duration: 90, cost: 40, price: 120 }
   ],
   hair: [
     { name: 'Haircut', duration: 30, cost: 15, price: 45 },
     { name: 'Color Treatment', duration: 90, cost: 40, price: 120 },
     { name: 'Highlights', duration: 120, cost: 60, price: 150 }
   ],
   nails: [
     { name: 'Manicure', duration: 30, cost: 10, price: 35 },
     { name: 'Pedicure', duration: 45, cost: 15, price: 45 },
     { name: 'Gel Nails', duration: 60, cost: 25, price: 70 }
   ],
   makeup: [
     { name: 'Day Makeup', duration: 30, cost: 20, price: 50 },
     { name: 'Evening Makeup', duration: 45, cost: 30, price: 75 },
     { name: 'Bridal Makeup', duration: 90, cost: 60, price: 150 }
   ]
 };

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: '' },
  { id: 'card', name: 'Card', icon: '' },
  { id: 'revolut', name: 'Revolut', icon: '' },
  { id: 'transfer', name: 'Bank Transfer', icon: '' },
  { id: 'voucher', name: 'Gift Voucher', icon: '' },
  { id: 'unpaid', name: 'Unpaid', icon: '' }
 ];

const RCT_DEDUCTION_RATES = [
  { value: 0, label: '0%' },
  { value: 0.2, label: '20%' },
  { value: 0.35, label: '35%' }
];

const DEFAULT_RCT_VAT_RATE = 0.135;
const CONSTRUCTION_ONLY_CATEGORY_IDS = new Set([
  'rct_contract_income',
  'rct_tax_income',
  'subcontractor_rct',
  'outsourced_labour_non_rct',
  'rct_tax'
]);

const SPREADSHEET_IMPORT_CATEGORY_ALIASES = {
  income: {
    hair: 'hair',
    haircut: 'hair',
    barber: 'hair',
    barbers: 'hair',
    barbering: 'hair',
    'barber services': 'hair',
    colour: 'hair',
    color: 'hair',
    nails: 'nails',
    nail: 'nails',
    shellac: 'nails',
    shelac: 'nails',
    gel: 'nails',
    polish: 'nails',
    acrylic: 'nails',
    acrylics: 'nails',
    file: 'nails',
    manicure: 'nails',
    pedicure: 'nails',
    lashes: 'lashes',
    lash: 'lashes',
    eyelash: 'lashes',
    eyelashes: 'lashes',
    brows: 'brows',
    brow: 'brows',
    eyebrow: 'brows',
    eyebrows: 'brows',
    facial: 'facial',
    skin: 'facial',
    injectables: 'injectables',
    injectable: 'injectables',
    botox: 'injectables',
    filler: 'injectables',
    fillers: 'injectables',
    'dermal filler': 'injectables',
    'anti wrinkle': 'injectables',
    'anti-wrinkle': 'injectables',
    'skin booster': 'injectables',
    body: 'body_treatments',
    'body treatment': 'body_treatments',
    'body treatments': 'body_treatments',
    'body contouring': 'body_treatments',
    'body sculpting': 'body_treatments',
    wax: 'waxing_services',
    waxing: 'waxing_services',
    threading: 'waxing_services',
    bikini: 'waxing_services',
    spraytan: 'spray_tan',
    'spray tan': 'spray_tan',
    tanning: 'spray_tan',
    massage: 'massage',
    makeup: 'makeup',
    'make up': 'makeup',
    retail: 'retail',
    products: 'retail',
    candle: 'retail',
    serum: 'retail',
    cream: 'retail',
    cleanser: 'retail',
    shampoo: 'retail',
    conditioner: 'retail',
    mask: 'retail',
    moisturizer: 'retail',
    moisturiser: 'retail',
    lotion: 'retail',
    scrub: 'retail',
    toner: 'retail',
    'gift card': 'gift_card',
    giftcard: 'gift_card',
    'gift voucher': 'gift_card',
    giftvoucher: 'gift_card',
    packages: 'packages',
    package: 'packages',
    training: 'training',
    consulting: 'consulting',
    consultation: 'consulting',
    'consulting fees': 'consulting_fees',
    'consultant fees': 'consulting_fees',
    'management consulting': 'management_consulting',
    'business advisory': 'business_advisory',
    'project management': 'project_management',
    'medical consulting': 'medical_consulting',
    retainer: 'retainer_income',
    'retainer income': 'retainer_income',
    'workshop training': 'workshops_training',
    workshops: 'workshops_training',
    'live production': 'live_production',
    'sound engineering': 'sound_engineering',
    'lighting design': 'lighting_design',
    'film crew': 'film_crew',
    'stage crew': 'stage_crew',
    roadie: 'stage_crew',
    roadies: 'stage_crew',
    'roadie work': 'stage_crew',
    'equipment hire income': 'equipment_hire_income',
    'production management': 'production_management',
    'gov funding': 'gov_funding',
    'government funding': 'gov_funding',
    'government grant': 'gov_funding',
    grant: 'gov_funding',
    grants: 'gov_funding',
    loan: 'loan',
    'business loan': 'loan',
    'loan income': 'loan',
    'loan received': 'loan',
    'director loan': 'director_loan',
    "director's loan": 'director_loan',
    'director loan received': 'director_loan',
    'director loan introduced': 'director_loan',
    rct: 'rct_contract_income',
    'rct contract': 'rct_contract_income',
    'rct contract income': 'rct_contract_income',
    'rct income': 'rct_contract_income',
    'rct tax income': 'rct_tax_income'
  },
  expenses: {
    supplies: 'supplies',
    materials: 'supplies',
    general: 'general_expenses',
    'general expense': 'general_expenses',
    'general expenses': 'general_expenses',
    stock: 'retail_stock',
    inventory: 'retail_stock',
    retail: 'retail_stock',
    utilities: 'utilities',
    internet: 'internet_telephone',
    'internet/telephone': 'internet_telephone',
    telephone: 'internet_telephone',
    broadband: 'internet_telephone',
    wifi: 'internet_telephone',
    'wi-fi': 'internet_telephone',
    telecom: 'internet_telephone',
    telecommunications: 'internet_telephone',
    'phone bill': 'internet_telephone',
    'mobile bill': 'internet_telephone',
    rent: 'rent',
    lease: 'rent',
    staff: 'staffCosts',
    payroll: 'staffCosts',
    prsi: 'employer_prsi',
    'employer prsi': 'employer_prsi',
    "employer's prsi": 'employer_prsi',
    'holiday pay': 'employee_holiday_pay',
    'employee holiday pay': 'employee_holiday_pay',
    'staff holiday pay': 'employee_holiday_pay',
    'holiday pay reserve': 'employee_holiday_pay',
    'holiday reserve': 'employee_holiday_pay',
    'owner pension': 'owner_pension',
    'owners pension': 'owner_pension',
    "owner's pension": 'owner_pension',
    'naersa employer contributions': 'naersa_employer_contributions',
    'naersa employer contribution': 'naersa_employer_contributions',
    'naersa employer': 'naersa_employer_contributions',
    'naersa employee contributions': 'naersa_employee_contributions',
    'naersa employee contribution': 'naersa_employee_contributions',
    'naersa employee': 'naersa_employee_contributions',
    labour: 'staffCosts',
    labor: 'staffCosts',
    wages: 'staffCosts',
    salary: 'staffCosts',
    contractor: 'staffCosts',
    subcontractor: 'outsourced_labour_non_rct',
    'subcontractor rct': 'subcontractor_rct',
    'subcontractor (rct)': 'subcontractor_rct',
    'rct subcontractor': 'subcontractor_rct',
    'outsourced labour': 'outsourced_labour_non_rct',
    'outsourced labor': 'outsourced_labour_non_rct',
    'outsourced labour non rct': 'outsourced_labour_non_rct',
    'outsourced labour (non rct)': 'outsourced_labour_non_rct',
    'outsourced labor non rct': 'outsourced_labour_non_rct',
    'rct tax': 'rct_tax',
    'professional memberships': 'professional_memberships',
    'professional indemnity': 'professional_indemnity',
    'research reports': 'research_reports',
    'client meetings': 'client_meetings',
    'contractor support': 'contractor_support',
    'medical equipment': 'medical_equipment',
    'crew labour': 'crew_labour',
    'crew labor': 'crew_labour',
    'audio equipment': 'audio_equipment',
    'lighting equipment': 'lighting_equipment',
    'camera equipment': 'camera_equipment',
    'production consumables': 'production_consumables',
    'venue costs': 'venue_site_costs',
    'site costs': 'venue_site_costs',
    'venue site costs': 'venue_site_costs',
    'permits licensing': 'permits_licensing',
    'permits & licensing': 'permits_licensing',
    'freight transport': 'freight_transport',
    insurance: 'insurance',
    marketing: 'marketing',
    travel: 'travel_expenses',
    'vehicle fuel': 'vehicle_fuel',
    fuel: 'vehicle_fuel',
    petrol: 'vehicle_fuel',
    diesel: 'vehicle_fuel',
    gasoline: 'vehicle_fuel',
    meals: 'meals',
    tips: 'tips',
    tip: 'tips',
    gratuity: 'tips',
    gratuities: 'tips',
    uniform: 'uniform_costs',
    equipment: 'equipment',
    furniture: 'furniture',
    electronics: 'electronics',
    tools: 'salon_tools',
    'vehicle repair': 'vehicle_repairs_licensing',
    'vehicle repairs': 'vehicle_repairs_licensing',
    'vehicle licensing': 'vehicle_repairs_licensing',
    'vehicle licence': 'vehicle_repairs_licensing',
    'vehicle license': 'vehicle_repairs_licensing',
    'motor tax': 'vehicle_repairs_licensing',
    'road tax': 'vehicle_repairs_licensing',
    nct: 'vehicle_repairs_licensing',
    mot: 'vehicle_repairs_licensing',
    accounting: 'accounting',
    legal: 'legal',
    interest: 'interest',
    'interest paid': 'interest',
    'bank interest': 'interest',
    'loan interest': 'interest',
    'bank charge': 'bank_charges',
    'bank charges': 'bank_charges',
    'bank fee': 'bank_charges',
    'bank fees': 'bank_charges',
    'merchant fee': 'bank_charges',
    'merchant fees': 'bank_charges',
    'director loan': 'director_loan',
    "director's loan": 'director_loan',
    'director loan repayment': 'director_loan',
    'director loan payment': 'director_loan',
    'loan repayment': 'loan_repayment',
    'loan repayments': 'loan_repayment',
    'loan payment': 'loan_repayment',
    'loan payments': 'loan_repayment',
    repayment: 'loan_repayment',
    repairs: 'repairs',
    cleaning: 'cleaning',
    maintenance: 'maintenance',
    software: 'software',
    office: 'office',
    medical: 'medical_expenses',
    miscellaneous: 'misc',
    misc: 'misc'
  }
};

const IMPORT_SUGGESTED_INCOME_CATEGORIES = {
  waxing_services: { id: 'waxing_services', name: 'Waxing Services', type: 'income' },
  spray_tan: { id: 'spray_tan', name: 'Spray Tan', type: 'income' }
};

const BANK_STATEMENT_CATEGORY_HINTS = {
  rent: ['rent', 'lease', 'landlord', 'property'],
  utilities: ['electric', 'electricity', 'gas', 'water', 'utility'],
  internet_telephone: ['internet', 'wifi', 'wi-fi', 'broadband', 'phone', 'mobile', 'telephone', 'telecom', 'eir', 'vodafone', 'three', 'virgin media', 'sky'],
  software: ['software', 'subscription', 'saas', 'adobe', 'canva', 'google', 'microsoft', 'apple', 'dropbox'],
  marketing: ['meta', 'facebook', 'instagram', 'google ads', 'tiktok', 'ads', 'advertising', 'marketing'],
  insurance: ['insurance', 'policy'],
  accounting: ['accountant', 'bookkeeping', 'xero', 'quickbooks', 'sage'],
  legal: ['legal', 'solicitor', 'law'],
  interest: ['interest', 'interest paid', 'bank interest', 'loan interest', 'finance interest'],
  bank_charges: ['bank charge', 'bank charges', 'bank fee', 'bank fees', 'merchant fee', 'merchant fees', 'account fee', 'transaction fee'],
  director_loan: ['director loan', "director's loan", 'director loan repayment', 'director loan payment', 'paid back director'],
  supplies: ['supply', 'supplies', 'beauty', 'cosmetic', 'wholesale', 'amazon', 'shop', 'store'],
  vehicle_fuel: ['fuel', 'petrol', 'diesel', 'gasoline', 'unleaded', 'fuel station', 'circle k', 'applegreen', 'topaz', 'maxol'],
  vehicle_repairs_licensing: ['garage', 'mechanic', 'tyre', 'tire', 'car service', 'vehicle service', 'oil change', 'brake', 'windscreen', 'wheel alignment', 'motor tax', 'road tax', 'vehicle licensing', 'vehicle licence', 'vehicle license', 'nct', 'mot'],
  travel_expenses: ['uber', 'lyft', 'taxi', 'train', 'bus', 'travel', 'flight', 'hotel', 'parking', 'toll', 'mileage', 'car rental'],
  meals: ['restaurant', 'cafe', 'coffee', 'meal', 'lunch', 'dinner', 'takeaway'],
  tips: ['tip', 'tips', 'gratuity', 'gratuities'],
  employer_prsi: ['prsi', 'employer prsi', "employer's prsi", 'employer paye prsi'],
  employee_holiday_pay: ['holiday pay', 'employee holiday pay', 'staff holiday pay', 'holiday pay reserve', 'holiday reserve'],
  owner_pension: ['owner pension', 'owners pension', "owner's pension"],
  naersa_employer_contributions: ['naersa employer contributions', 'naersa employer contribution', 'naersa employer'],
  naersa_employee_contributions: ['naersa employee contributions', 'naersa employee contribution', 'naersa employee'],
  staffCosts: ['salary', 'wage', 'wages', 'payroll', 'employee', 'staff', 'labour', 'labor', 'contractor'],
  subcontractor_rct: ['subcontractor rct', 'subcontractor (rct)', 'rct subcontractor'],
  outsourced_labour_non_rct: ['outsourced labour', 'outsourced labor', 'outsourced labour non rct', 'outsourced labor non rct', 'subcontractor'],
  rct_tax: ['rct tax'],
  office: ['office', 'stationery', 'printer'],
  repairs: ['repair', 'service call'],
  maintenance: ['maintenance'],
  loan_repayment: ['loan repayment', 'loan payment', 'repayment', 'finance repayment'],
  equipment: ['equipment', 'device', 'machine', 'tool'],
  cleaning: ['cleaning', 'laundry', 'janitorial'],
  medical_expenses: ['medical', 'pharmacy', 'clinic']
};

const TIMEFRAMES = [
  { id: 'financialYear', name: 'Current Financial Year' },
  { id: 'all', name: 'All Time' },
  { id: 'thisWeek', name: 'This Week' },
  { id: 'thisMonth', name: 'This Month' },
  { id: 'thisYear', name: 'Last Year' },
  { id: 'custom', name: 'Custom Date Range' }
];
const ALL_TIMEFRAME_ID = 'all';
const DEFAULT_TIMEFRAME_ID = 'financialYear';

// Makeup service templates (ballpark presets). Update here to add more presets.
const MAKEUP_TEMPLATE_DEFINITIONS = [
  {
    name: 'Bridal Makeup',
    durationMinutes: 90,
    defaultHourlyPay: 35,
    defaultProfitMarginPct: 20,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.75, notes: 'Medium coverage default', coveragePreset: 'medium' },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.25 },
      { productName: 'Powder', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.3 },
      { productName: 'Blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.2 },
      { productName: 'Bronzer / Contour', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.2 },
      { productName: 'Highlighter', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.1 },
      { productName: 'Eyeshadow', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.2 },
      { productName: 'Eyeliner', unitType: 'piece', packSize: 1, packPrice: '', usagePerService: 0.02 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.1 },
      { productName: 'Lip product', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.1 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 1.5 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1, notes: 'cotton pads, spoolie, lip wand, tissues, wipes' },
      { productName: 'Strip lashes (optional)', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1, optionalToggleGroup: 'Lashes' }
    ]
  },
  {
    name: 'Evening / Full Glam',
    durationMinutes: 60,
    defaultHourlyPay: 35,
    defaultProfitMarginPct: 20,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.65, notes: 'Medium to full' },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.22 },
      { productName: 'Powder', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.28 },
      { productName: 'Blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.18 },
      { productName: 'Bronzer / Contour', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.18 },
      { productName: 'Highlighter', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.1 },
      { productName: 'Eyeshadow', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.18 },
      { productName: 'Eyeliner', unitType: 'piece', packSize: 1, packPrice: '', usagePerService: 0.02 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.09 },
      { productName: 'Lip product', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.1 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 1.3 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 },
      { productName: 'Strip lashes (optional)', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1, optionalToggleGroup: 'Lashes' }
    ]
  },
  {
    name: 'Soft Glam Makeup',
    durationMinutes: 45,
    defaultHourlyPay: 35,
    defaultProfitMarginPct: 20,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.6 },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.2 },
      { productName: 'Powder', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.2 },
      { productName: 'Blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.15 },
      { productName: 'Eyeshadow', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.15 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.08 },
      { productName: 'Lip product', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.08 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 1.0 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 }
    ]
  },
  {
    name: 'Natural / Day Makeup',
    durationMinutes: 30,
    defaultHourlyPay: 35,
    defaultProfitMarginPct: 15,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation / Skin tint', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.45 },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.1 },
      { productName: 'Cream blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.1 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.05 },
      { productName: 'Lip balm / gloss', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.05 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 0.7 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 }
    ]
  },
  {
    name: 'Mature Skin Makeup',
    durationMinutes: 60,
    defaultHourlyPay: 35,
    defaultProfitMarginPct: 20,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Hydrating primer', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.5 },
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.6 },
      { productName: 'Powder (minimal)', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.15 },
      { productName: 'Cream blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.12 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.06 },
      { productName: 'Lip product', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.08 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 1.0 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 }
    ]
  },
  {
    name: 'Photoshoot / Editorial Makeup',
    durationMinutes: 75,
    defaultHourlyPay: 40,
    defaultProfitMarginPct: 25,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.8 },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.25 },
      { productName: 'Powder', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.35 },
      { productName: 'Bronzer / Contour', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.22 },
      { productName: 'Blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.18 },
      { productName: 'Highlighter', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.12 },
      { productName: 'Eyeshadow', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.25 },
      { productName: 'Eyeliner', unitType: 'piece', packSize: 1, packPrice: '', usagePerService: 0.03 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.12 },
      { productName: 'Lip product (multiple)', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.15 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 2.0 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 }
    ]
  },
  {
    name: 'Makeup Lesson (1:1)',
    durationMinutes: 90,
    defaultHourlyPay: 40,
    defaultProfitMarginPct: 20,
    vatMode: 'exclusive',
    wastageBufferPct: 10,
    productItems: [
      { productName: 'Foundation', unitType: 'ml', packSize: 30, packPrice: '', usagePerService: 0.4 },
      { productName: 'Concealer', unitType: 'ml', packSize: 10, packPrice: '', usagePerService: 0.15 },
      { productName: 'Powder', unitType: 'g', packSize: 10, packPrice: '', usagePerService: 0.18 },
      { productName: 'Blush', unitType: 'g', packSize: 8, packPrice: '', usagePerService: 0.12 },
      { productName: 'Mascara', unitType: 'ml', packSize: 8, packPrice: '', usagePerService: 0.05 },
      { productName: 'Lip product', unitType: 'g', packSize: 3, packPrice: '', usagePerService: 0.08 },
      { productName: 'Setting spray', unitType: 'ml', packSize: 100, packPrice: '', usagePerService: 1.0 },
      { productName: 'Disposable supplies bundle', unitType: 'set', packSize: 1, packPrice: '', usagePerService: 1 }
    ]
  }
];

const BASE_VAT_SETTINGS = {
  enabled: false,
  isRegistered: false,
  registrationDate: '',
  registrationPeriods: [],
  accountingBasis: 'cash',
  productsRate: 0.23,
  servicesRate: 0.135,
  salesRate: 0.23,
  categoryVatRates: {},
  vatSettlementOffset: 0,
  vatFiledPeriods: []
};

const getDefaultVatSettingsForCountry = (country) => {
  const countryId = String(country?.id || '').toLowerCase();
  const isUk = countryId === 'uk' || countryId === 'gb';
  const isUs = countryId === 'us' || String(country?.taxSystem || '').toLowerCase() === 'sales';
  return {
    ...BASE_VAT_SETTINGS,
    productsRate: isUs ? 0 : (isUk ? 0.2 : 0.23),
    servicesRate: isUs ? 0 : (isUk ? 0.2 : 0.135),
    salesRate: isUs ? 0 : (isUk ? 0.2 : 0.23)
  };
};

const UK_ZERO_RATED_CATEGORY_IDS = new Set([
  'medical_expenses',
  'insurance',
  'rent'
]);

const UK_REDUCED_RATED_CATEGORY_IDS = new Set([
  'utilities'
]);

const sanitizeVatRegistrationPeriods = (rawPeriods = [], fallbackRegistrationDate = '') => {
  const periods = Array.isArray(rawPeriods)
    ? rawPeriods
        .map((period) => {
          const startDate = normalizeOptionalDateString(period?.startDate || period?.registrationDate);
          const endDate = normalizeOptionalDateString(period?.endDate || period?.deregistrationDate);
          if (!startDate) return null;
          return {
            id: String(period?.id || `vat-period-${startDate}-${endDate || 'open'}`),
            startDate,
            endDate: endDate && endDate >= startDate ? endDate : ''
          };
        })
        .filter(Boolean)
    : [];

  if (periods.length === 0) {
    const legacyStartDate = normalizeOptionalDateString(fallbackRegistrationDate);
    if (legacyStartDate) {
      periods.push({
        id: `vat-period-${legacyStartDate}-open`,
        startDate: legacyStartDate,
        endDate: ''
      });
    }
  }

  return periods.sort((a, b) => a.startDate.localeCompare(b.startDate));
};

const sanitizeVatSettings = (rawSettings = {}, fallbackSettings = BASE_VAT_SETTINGS) => {
  const registrationDate = normalizeOptionalDateString(rawSettings?.registrationDate);
  const shouldUseLegacyRegistrationDate = rawSettings?.isRegistered !== false && (
    rawSettings?.isRegistered === true ||
    fallbackSettings?.isRegistered === true
  );
  const sanitizedRegistrationPeriods = sanitizeVatRegistrationPeriods(
    rawSettings?.registrationPeriods ?? fallbackSettings.registrationPeriods,
    shouldUseLegacyRegistrationDate ? (registrationDate || fallbackSettings.registrationDate) : ''
  );
  const registrationPeriods = rawSettings?.isRegistered === false
    ? sanitizedRegistrationPeriods.filter((period) => period.endDate)
    : sanitizedRegistrationPeriods;
  const hasOpenRegistrationPeriod = registrationPeriods.some((period) => !period.endDate);
  const isRegistered = rawSettings?.isRegistered === undefined
    ? Boolean(fallbackSettings?.isRegistered || hasOpenRegistrationPeriod)
    : Boolean(rawSettings?.isRegistered);
  const enabled = rawSettings?.enabled === undefined
    ? Boolean(isRegistered || fallbackSettings?.enabled || registrationPeriods.length > 0)
    : Boolean(rawSettings.enabled);

  return {
    enabled,
    isRegistered,
    registrationDate: registrationDate || registrationPeriods[0]?.startDate || '',
    registrationPeriods,
    accountingBasis: rawSettings?.accountingBasis === 'invoice'
      ? 'invoice'
      : (fallbackSettings?.accountingBasis === 'invoice' ? 'invoice' : 'cash'),
    productsRate: Number.isFinite(Number(rawSettings?.productsRate))
      ? Number(rawSettings.productsRate)
      : fallbackSettings.productsRate,
    servicesRate: Number.isFinite(Number(rawSettings?.servicesRate))
      ? Number(rawSettings.servicesRate)
      : fallbackSettings.servicesRate,
    salesRate: Number.isFinite(Number(rawSettings?.salesRate))
      ? Number(rawSettings.salesRate)
      : (
          Number.isFinite(Number(rawSettings?.productsRate))
            ? Number(rawSettings.productsRate)
            : fallbackSettings.salesRate
        ),
    categoryVatRates: sanitizeCategoryVatRates(rawSettings?.categoryVatRates ?? fallbackSettings.categoryVatRates),
    vatSettlementOffset: Number.isFinite(Number(rawSettings?.vatSettlementOffset))
      ? Number(rawSettings.vatSettlementOffset)
      : fallbackSettings.vatSettlementOffset,
    vatFiledPeriods: sanitizeVatFiledPeriods(rawSettings?.vatFiledPeriods ?? fallbackSettings.vatFiledPeriods)
  };
};

const DriveDisconnectWarningModal = ({
  isOpen,
  onReconnect,
  onDismiss,
  isReconnecting = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[1100] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900">Google Drive Permission Needed</h3>
        <p className="mt-2 text-sm text-gray-700">
          Google Drive access has not been confirmed. Grant Drive permission before adding transactions, otherwise entries may
          only be stored on this device and can be lost if you log out, clear browser/app storage, or switch devices before syncing.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Keep Working Offline
          </button>
          <button
            type="button"
            onClick={onReconnect}
            disabled={isReconnecting}
            className={`px-3 py-2 text-sm rounded-lg font-semibold ${
              isReconnecting
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95'
            }`}
          >
            {isReconnecting ? 'Reconnecting...' : 'Reconnect Drive'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FuelVatDecisionModal = ({
  isOpen,
  review,
  rememberChoice,
  onRememberChoiceChange,
  onCancel,
  onChooseOtherTravel,
  onChooseDiesel,
  onChoosePetrol
}) => {
  if (!isOpen) return null;

  const allowNonFuelChoice = review?.allowNonFuelChoice === true;
  const title = allowNonFuelChoice ? 'Travel VAT Check' : 'Diesel or Petrol?';
  const secondaryText = allowNonFuelChoice
    ? 'Choose another travel expense to keep the VAT if this is not fuel. If this is always diesel for you, you can stop future prompts below.'
    : 'Choose diesel to keep the VAT, or petrol to remove the VAT before saving. If this is always diesel for you, you can stop future prompts below.';

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[1110] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="mt-2 text-sm text-gray-700">
          {review?.message || 'This expense includes VAT. In Ireland VAT on petrol should not be reclaimed.'}
        </p>
        <p className="mt-2 text-sm text-gray-700">
          {secondaryText}
        </p>
        <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={rememberChoice}
            onChange={(e) => onRememberChoiceChange?.(e.target.checked)}
            className="mt-0.5"
          />
          <span>Understood, don&apos;t ask again when this is always diesel.</span>
        </label>
        <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Keep Editing
          </button>
          {allowNonFuelChoice && (
            <button
              type="button"
              onClick={onChooseOtherTravel}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
            >
              Other Travel - Keep VAT
            </button>
          )}
          <button
            type="button"
            onClick={onChooseDiesel}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
          >
            Diesel - Keep VAT
          </button>
          <button
            type="button"
            onClick={onChoosePetrol}
            className="px-3 py-2 text-sm rounded-lg bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95 font-semibold"
          >
            Petrol - Remove VAT
          </button>
        </div>
      </div>
    </div>
  );
};

const OnboardingGuideModal = ({
  isOpen,
  steps = [],
  currentStepIndex = 0,
  completedStepIds = [],
  onClose,
  onBack,
  onNext,
  onFinish,
  onOpenStep,
  onMarkDone
}) => {
  if (!isOpen) return null;

  const safeSteps = Array.isArray(steps) ? steps : [];
  const completedStepSet = new Set(Array.isArray(completedStepIds) ? completedStepIds : []);
  const totalSteps = safeSteps.length;
  const currentStep = safeSteps[currentStepIndex] || safeSteps[0] || null;
  const isLastStep = totalSteps > 0 && currentStepIndex === totalSteps - 1;
  const completedCount = safeSteps.reduce((count, step) => (
    completedStepSet.has(step.id) ? count + 1 : count
  ), 0);
  const isCurrentStepCompleted = currentStep ? completedStepSet.has(currentStep.id) : false;

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[1120] px-4 py-6">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-accent-gradient">
          <div>
            <p className="text-xs uppercase tracking-wide text-[color:var(--color-text)]/70">Guided App Walkthrough</p>
            <h3 className="text-xl font-semibold text-[color:var(--color-text)]">
              {totalSteps > 0 ? `Step ${currentStepIndex + 1} of ${totalSteps}` : 'Walkthrough'}
            </h3>
            {totalSteps > 0 && (
              <p className="text-xs text-[color:var(--color-text)]/70">{completedCount} completed</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] min-h-[420px]">
          <div className="border-r border-gray-100 max-h-[65vh] overflow-auto bg-gray-50">
            {safeSteps.map((step, idx) => {
              const isActive = idx === currentStepIndex;
              const isCompleted = completedStepSet.has(step.id);
              return (
                <button
                  key={step.id || idx}
                  type="button"
                  onClick={() => onOpenStep?.(idx)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 transition ${
                    isActive ? 'bg-white' : 'hover:bg-white/70'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-500">Step {idx + 1}</div>
                    {isCompleted && (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">Done</span>
                    )}
                  </div>
                  <div className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                    {step.title}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-5 max-h-[65vh] overflow-auto">
            {currentStep ? (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">{currentStep.title}</h4>
                <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-line">
                  {[currentStep.description, currentStep.guidance].filter(Boolean).join('\n\n')}
                </div>
                {currentStep.actionLabel && (
                  <button
                    type="button"
                    onClick={() => onOpenStep?.(currentStepIndex, true)}
                    className="px-3 py-2 text-sm rounded-lg bg-info-gradient text-[color:var(--color-text)] hover:opacity-90"
                  >
                    {currentStep.actionLabel}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onMarkDone?.(currentStepIndex)}
                  disabled={isCurrentStepCompleted}
                  className="px-3 py-2 text-sm rounded-lg border border-green-300 text-green-700 disabled:opacity-60 disabled:cursor-not-allowed hover:bg-green-50"
                >
                  {isCurrentStepCompleted ? 'Completed' : 'Mark Done & Next'}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-600">No guide steps available.</p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              disabled={currentStepIndex === 0}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Continue Later
            </button>
          </div>

          <div className="flex items-center gap-2">
            {isLastStep ? (
              <button
                type="button"
                onClick={onFinish}
                className="px-3 py-2 text-sm rounded-lg bg-warn-gradient text-[color:var(--color-text)] font-semibold hover:brightness-95"
              >
                Finish Guide
              </button>
            ) : (
              <button
                type="button"
                onClick={onNext}
                className="px-3 py-2 text-sm rounded-lg bg-warn-gradient text-[color:var(--color-text)] font-semibold hover:brightness-95"
              >
                Next Step
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function App({ user, setUser, currency }) {
  // Get the current country settings
  const [currentCountry, setCurrentCountry] = useState(() => getCurrentCountry());
  const countryTerminology = useMemo(
    () => getCountryTerminology(currentCountry?.id),
    [currentCountry?.id]
  );
  const dateDisplayLocale = useMemo(
    () => getDateLocaleForCountry(currentCountry),
    [currentCountry]
  );


  // Get formatCurrency from CurrencyContext
  const { formatCurrency } = useCurrency();
  const location = useLocation();
  const isReceiptsRoute = (location?.pathname || '').startsWith('/receipts');

  const resolveCountrySettings = useCallback((countryOrId) => {
    const countryId =
      typeof countryOrId === 'string'
        ? countryOrId
        : (countryOrId?.id || localStorage.getItem('selectedCountry') || 'ie');
    return getCountryById(countryId);
  }, []);

  const sanitizeAmountInput = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).replace(/,/g, '.');
    let cleaned = normalized.replace(/[^0-9.]/g, '');
    const firstDotIndex = cleaned.indexOf('.');
    if (firstDotIndex !== -1) {
      cleaned = `${cleaned.slice(0, firstDotIndex + 1)}${cleaned
        .slice(firstDotIndex + 1)
        .replace(/\./g, '')}`;
    }
    return cleaned;
  };

  const sanitizeSignedAmountInput = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).replace(/,/g, '.').trim();
    let cleaned = normalized.replace(/[^0-9.-]/g, '');

    const hasLeadingNegative = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');

    const firstDotIndex = cleaned.indexOf('.');
    if (firstDotIndex !== -1) {
      cleaned = `${cleaned.slice(0, firstDotIndex + 1)}${cleaned
        .slice(firstDotIndex + 1)
        .replace(/\./g, '')}`;
    }

    if (!cleaned) return hasLeadingNegative ? '-' : '';
    return `${hasLeadingNegative ? '-' : ''}${cleaned}`;
  };

  const createSplitSaleItem = (overrides = {}) => ({
    id: `split-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: '',
    category: '',
    amount: '',
    saveToCost: false,
    ...overrides
  });

  const createSplitPaymentItem = (overrides = {}) => ({
    id: `pay-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method: '',
    amount: '',
    ...overrides
  });

  // State Management
  const [transactions, setTransactionsState] = useState([]);
  const [activeFinancialYear, setActiveFinancialYear] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('active_financial_year') : null;
      return stored ? parseInt(stored, 10) || new Date().getFullYear() : new Date().getFullYear();
    } catch {
      return new Date().getFullYear();
    }
  });
  const [activeTab, setActiveTab] = useState('income');
  const [businessType, setBusinessType] = useState(() => {
    return readLocalBusinessProfileSettings().businessType;
  });
  const [industryId, setIndustryId] = useState(() => {
    return readLocalBusinessProfileSettings().industryId;
  });
  const [businessProfileDetails, setBusinessProfileDetails] = useState(() => (
    normalizeBusinessProfileDetails(readLocalBusinessProfileSettings())
  ));
  const [showBusinessDetailsSettings, setShowBusinessDetailsSettings] = useState(false);
  const isLimitedCompany = businessType === BUSINESS_TYPES.LIMITED_COMPANY;
  const businessTypeLabel = getBusinessTypeLabel(businessType, currentCountry?.id);
  const localizedBusinessTypeOptions = useMemo(
    () => BUSINESS_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: getBusinessTypeLabel(option.id, currentCountry?.id)
    })),
    [currentCountry?.id]
  );
  const industryProfile = useMemo(() => getIndustryProfile(industryId), [industryId]);
  const industryLabel = getIndustryLabel(industryId);
  const industryFeatures = industryProfile.features || {};
  const supportsProjects = industryFeatures.supportsProjects === true;
  const supportsRct = industryFeatures.supportsRct === true;
  const CATEGORIES = useMemo(() => getCategoriesForBusinessProfile(businessType, industryId), [businessType, industryId]);
  const [selectedTimeframe, setSelectedTimeframe] = useState(DEFAULT_TIMEFRAME_ID);
  const [selectedExpenseType, setSelectedExpenseType] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [transactionAmountSearch, setTransactionAmountSearch] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all'); // 'all', 'income', 'expenses'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'amount', 'category', 'description'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [activeFilterType, setActiveFilterType] = useState(''); // '', 'date', 'type', 'category', 'payment'
  const [isAddingTransaction, setIsAddingTransaction] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(createEmptyTransactionDraft());
  const [fuelVatReviewPreference, setFuelVatReviewPreference] = useState(() => readFuelVatReviewPreference());
  const [pendingFuelVatReview, setPendingFuelVatReview] = useState(null);
  const [rememberFuelVatPreference, setRememberFuelVatPreference] = useState(false);
  const [splitSaleMode, setSplitSaleMode] = useState(false);
  const [splitSaleItems, setSplitSaleItems] = useState(() => [createSplitSaleItem()]);
  const [splitPaymentMode, setSplitPaymentMode] = useState(false);
  const [splitPaymentItems, setSplitPaymentItems] = useState(() => [createSplitPaymentItem()]);
  const [showCategoryTotals, setShowCategoryTotals] = useState(false);
  const [showPaymentTotals, setShowPaymentTotals] = useState(false);
  const [showRecentTransactions, setShowRecentTransactions] = useState(false);
  const [selectedRecentTransactionIds, setSelectedRecentTransactionIds] = useState([]);
  const [editingDepreciationId, setEditingDepreciationId] = useState(null);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState({});
  const [isUploadingReceiptAttachment, setIsUploadingReceiptAttachment] = useState(false);
  const [clients, setClients] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [staffHrRecords, setStaffHrRecords] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem(STAFF_HR_RECORDS_STORAGE_KEY) : null;
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [staffHrDraft, setStaffHrDraft] = useState(() => ({
    staffMemberId: '',
    weekStart: getCurrentWeekStartDateString(),
    hoursWorked: '',
    publicHolidayHours: '',
    holidayHoursTaken: '',
    sickHours: '',
    notes: ''
  }));
  const [editingStaffHrRecordId, setEditingStaffHrRecordId] = useState('');
  const [showStaffHrRecords, setShowStaffHrRecords] = useState(false);
  const [staffProfitabilityPeriod, setStaffProfitabilityPeriod] = useState('monthly');
  const [staffHrRecordFilters, setStaffHrRecordFilters] = useState({
    staffMemberId: '',
    weekStart: ''
  });
  const [customDepreciationPeriods, setCustomDepreciationPeriods] = useState({});
  const [showCapitalExpenses, setShowCapitalExpenses] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showAccountantAccessModal, setShowAccountantAccessModal] = useState(false);
  const [showDriveDisconnectWarning, setShowDriveDisconnectWarning] = useState(false);
  const [isReconnectingDriveWarning, setIsReconnectingDriveWarning] = useState(false);
  const driveWarningDismissedRef = useRef(false);
  const fuelVatReviewResolverRef = useRef(null);
  const receiptUploadOrderRef = useRef(Date.now());
  const [collaborationState, setCollaborationState] = useState({
    ownWorkspace: null,
    sentInvites: [],
    pendingInvites: [],
    acceptedWorkspaces: []
  });
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [isCollaborationBusy, setIsCollaborationBusy] = useState(false);
  const [isRefreshingCollaboration, setIsRefreshingCollaboration] = useState(false);
  const [workspaceSubscription, setWorkspaceSubscription] = useState(null);
  const [isWorkspaceSubscriptionLoading, setIsWorkspaceSubscriptionLoading] = useState(false);
  const activeWorkspaceRef = useRef(null);
  const ledgerLoadTrackerRef = useRef(createLedgerLoadTracker());
  const collaborationRequestRef = useRef(0);
  const [ledgerRenderSource, setLedgerRenderSource] = useState(null);
  const userRef = useRef(user || null);
  const pendingReceiptTransactionIdsRef = useRef(new Set());
  const ledgerDataSourceRef = useRef({
    scope: user?.uid ? 'personal' : 'none',
    userId: user?.uid || null,
    workspaceId: null
  });
  const isExternalSharedWorkspace = Boolean(
    activeWorkspace?.id &&
    activeWorkspace?.ownerId &&
    activeWorkspace.ownerId !== user?.uid
  );
  const setTransactions = useCallback((value) => {
    if (canPersistLedgerDataset(
      ledgerRenderSource,
      ledgerDataSourceRef.current,
      activeWorkspaceRef.current?.id,
      auth.currentUser?.uid || userRef.current?.uid || user?.uid
    )) {
      setTransactionsState(value);
    }
  }, [ledgerRenderSource, user]);
  const persistDatasetForCurrentAccess = useCallback(async (datasetKey, value, workspaceOverride = activeWorkspaceRef.current) => {
    const selectedWorkspace = workspaceOverride;
    const loadedSource = ledgerDataSourceRef.current || {};
    const currentUserId = auth.currentUser?.uid || userRef.current?.uid || user?.uid || null;
    if ((selectedWorkspace?.id || null) !== (activeWorkspaceRef.current?.id || null) ||
        !canPersistLedgerDataset(ledgerRenderSource, loadedSource, selectedWorkspace?.id, currentUserId)) {
      console.warn('Skipped ledger save from an outdated or loading workspace.', { datasetKey });
      return;
    }

    if (selectedWorkspace?.id) {
      await saveWorkspaceDataset(selectedWorkspace.id, datasetKey, value, user);
      return;
    }

    switch (datasetKey) {
      case SHARED_WORKSPACE_DATASETS.TRANSACTIONS:
        await saveTransactionsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.DEPRECIATION:
        await saveDepreciationToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.SERVICE_PRICES:
        await saveServicePricesToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.COST_ITEMS:
        await saveCostItemsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.EXTRA_CATEGORIES:
        await saveExtraCategoriesToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.GOALS:
        await saveGoalsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.VAT_SETTINGS:
        await saveVatSettingsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.CLIENTS:
        await saveClientsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.BUSINESS_PROFILE:
        await saveBusinessProfileToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.BANK_ACCOUNTS:
        await saveBankAccountsToHybrid(value);
        return;
      case SHARED_WORKSPACE_DATASETS.JOURNAL_ENTRIES:
        await saveJournalEntriesToHybrid(value);
        return;
      default:
        throw new Error(`Unsupported dataset key: ${datasetKey}`);
    }
  }, [ledgerRenderSource, user]);
  const [authView, setAuthView] = useState(() => {
    if (typeof window === 'undefined') return 'signup';
    return resolveAuthViewFromPath(window.location.pathname, shouldPreferSignIn());
  });
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isDriveInitialized, setIsDriveInitialized] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [showCountrySelection, setShowCountrySelection] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [requiresInitialBusinessSetup, setRequiresInitialBusinessSetup] = useState(() => !isRequiredBusinessSetupComplete());
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = localStorage.getItem('remember_me');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const [showGettingStarted, setShowGettingStarted] = useState(() => {
    if (typeof window === 'undefined') return true;
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    return !onboardingCompleted;
  });
  const [setupProgress, setSetupProgress] = useState(() => computeSetupProgressFromStorage());
  const [showSetupChecklist, setShowSetupChecklist] = useState(false);
  const [showOnboardingGuide, setShowOnboardingGuide] = useState(false);
  const [onboardingGuideStepIndex, setOnboardingGuideStepIndex] = useState(0);
  const [completedOnboardingStepIds, setCompletedOnboardingStepIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(ONBOARDING_GUIDE_PROGRESS_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
    } catch {
      return [];
    }
  });
  const [activeOnboardingStepIndex, setActiveOnboardingStepIndex] = useState(null);
  const [openVatSectionSignal, setOpenVatSectionSignal] = useState(0);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isStandalonePWA, setIsStandalonePWA] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.navigator.standalone ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches;
  });
  const [isIOSDevice, setIsIOSDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  });
  const businessProfileLoadedRef = useRef(false);

  const persistBusinessProfileSettings = useCallback(async (profile, options = {}) => {
    const normalizedProfile = buildBusinessProfileSettings(profile?.businessType, profile?.industryId, profile);
    writeLocalBusinessProfileSettings(normalizedProfile);

    if (options.skipRemote || !businessProfileLoadedRef.current) {
      return false;
    }

    await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.BUSINESS_PROFILE, normalizedProfile);
    return true;
  }, [persistDatasetForCurrentAccess]);

  useEffect(() => {
    persistBusinessProfileSettings({ businessType, industryId, ...businessProfileDetails }).catch((error) => {
      console.warn('Failed to persist business profile settings:', error);
    });
  }, [businessProfileDetails, businessType, industryId, persistBusinessProfileSettings]);

  const toggleSetupStep = (key, value) => {
    if (key === 'all') {
      setSetupProgress({ install: true, drive: true, basics: true });
      localStorage.setItem('setup_step_drive', 'true');
      localStorage.setItem('setup_step_basics', 'true');
      localStorage.setItem('pwa-install-complete', 'true');
      return setShowSetupChecklist(false);
    }
    const next = { ...setupProgress, [key]: value };
    setSetupProgress(next);
    if (key === 'drive') localStorage.setItem('setup_step_drive', value ? 'true' : 'false');
    if (key === 'basics') localStorage.setItem('setup_step_basics', value ? 'true' : 'false');
    if (key === 'install') localStorage.setItem('pwa-install-complete', value ? 'true' : 'false');
  };

  const navigateAuth = useCallback((nextView) => {
    if (typeof window === 'undefined') return;
    const targetPath = nextView === 'signin' ? '/signin' : '/signup';
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
    setAuthView(nextView);
  }, []);

  const handleAuthNavigate = useCallback((view) => {
    if (view === 'signin') {
      navigateAuth('signin');
      return;
    }
    navigateAuth('signup');
  }, [navigateAuth]);

  useEffect(() => {
    const applyPersistence = async () => {
      try {
        await setAuthPersistence(rememberMe);
      } catch (error) {
        console.error('Failed to apply auth persistence preference:', error);
      }
    };
    applyPersistence();
  }, [rememberMe]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePopState = () => {
      setAuthView(resolveAuthViewFromPath(window.location.pathname, shouldPreferSignIn()));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user) {
      setAuthView(resolveAuthViewFromPath(window.location.pathname, shouldPreferSignIn()));
    }
  }, [user]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;
    const normalizedPath = (window.location.pathname || '').toLowerCase();
    if (['/signin', '/login', '/signup', '/register'].includes(normalizedPath)) {
      window.history.replaceState({}, '', '/');
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const allDone = Object.values(setupProgress || {}).every(Boolean);
    const hasData = Array.isArray(transactions) && transactions.length > 0;
    const hidden = localStorage.getItem('setup_checklist_hidden') === 'true';

    if (!allDone && !hidden && !hasData) {
      setShowSetupChecklist(true);
    }
  }, [user, setupProgress, transactions]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;
    const setupComplete = isRequiredBusinessSetupComplete();
    setRequiresInitialBusinessSetup(!setupComplete);
    if (!setupComplete) {
      setShowCountrySelection(true);
      setShowSetupChecklist(false);
    }
  }, [user, setupProgress]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateStandaloneState = () => {
      const standalone = window.navigator.standalone ||
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches;
      setIsStandalonePWA(standalone);
    };

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      updateStandaloneState();
      setDeferredInstallPrompt(null);
      setSetupProgress((prev) => {
        const next = { ...prev, install: true };
        try { localStorage.setItem('pwa-install-complete', 'true'); } catch {}
        return next;
      });
    };

    updateStandaloneState();
    setIsIOSDevice(/iPad|iPhone|iPod/.test(navigator.userAgent));

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);


  const [showSetupChecklistCard, setShowSetupChecklistCard] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('setup_checklist_dismissed') !== 'true';
  });
  const [showDemoDashboard, setShowDemoDashboard] = useState(false);
  const [hideDemoDashboard, setHideDemoDashboard] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('hide_demo_dashboard') === 'true';
    } catch {
      return false;
    }
  });
  const handleHideDemoDashboard = useCallback(() => {
    try { localStorage.setItem('hide_demo_dashboard', 'true'); } catch {}
    setHideDemoDashboard(true);
    setShowDemoDashboard(false);
  }, []);

  const [showBudgetSection, setShowBudgetSection] = useState(false);
  const [showPricingCalculator, setShowPricingCalculator] = useState(false);
  const [servicePrices, setServicePrices] = useState({});
  const [showMakeupTemplates, setShowMakeupTemplates] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('show_makeup_templates') : null;
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const [hiddenServiceCategories, setHiddenServiceCategories] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('hidden_service_categories') : null;
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [hiddenCategories, setHiddenCategories] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('hidden_categories') : null;
      const parsed = stored ? JSON.parse(stored) : {};
      return {
        income: Array.isArray(parsed?.income) ? parsed.income : [],
        expenses: Array.isArray(parsed?.expenses) ? parsed.expenses : []
      };
    } catch {
      return { income: [], expenses: [] };
    }
  });
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [selectedServiceOfferings, setSelectedServiceOfferings] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem(SERVICE_OFFERINGS_STORAGE_KEY) : null;
      return stored ? sanitizeServiceOfferings(JSON.parse(stored)) : [];
    } catch {
      return [];
    }
  });
  const [showServicePreferencesEditor, setShowServicePreferencesEditor] = useState(false);
  const isBeautyServiceCategory = useCallback((cat) => {
    if (!cat) return false;
    const lower = String(cat).toLowerCase();
    const serviceTokens = ['hair', 'nail', 'skin', 'facial', 'massage', 'makeup', 'brow', 'lash', 'service', 'services'];
    return serviceTokens.some((tok) => lower.includes(tok));
  }, []);
  const isProductCategory = useCallback((cat) => {
    if (!cat) return false;
    const lower = String(cat).toLowerCase();
    const productCategoryIds = new Set(['products', 'retail_stock', 'assetsale', 'productsales', 'retail']);
    const productTokens = ['retail', 'product', 'stock', 'goods'];
    return productCategoryIds.has(lower) || productTokens.some((tok) => lower.includes(tok));
  }, []);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [targetIncomeData, setTargetIncomeData] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_TARGET_INCOME_DATA;

    try {
      const savedTargetIncomeData = localStorage.getItem('70livesbeautyledgerTargetIncome');
      if (savedTargetIncomeData) {
        return normalizeTargetIncomeData(JSON.parse(savedTargetIncomeData));
      }
    } catch (error) {
      console.error('Failed to load saved income goal:', error);
    }

    return DEFAULT_TARGET_INCOME_DATA;
  });
  const targetIncomeDataRef = useRef(targetIncomeData);
  useEffect(() => {
    targetIncomeDataRef.current = targetIncomeData;
  }, [targetIncomeData]);
  const [staffPlanDraft, setStaffPlanDraft] = useState(() => buildStaffPlanFromTargetIncome(targetIncomeData));
  const [staffPlanSaveMessage, setStaffPlanSaveMessage] = useState('');
  useEffect(() => {
    setStaffPlanDraft(buildStaffPlanFromTargetIncome(targetIncomeData));
  }, [targetIncomeData]);
  const parsedTargetIncomeAmount = parseFloat(targetIncomeData.amount);
  const hasValidTargetIncomeAmount = Number.isFinite(parsedTargetIncomeAmount) && parsedTargetIncomeAmount > 0;
  const weeklyTargetEquivalent = hasValidTargetIncomeAmount ? (() => {
    switch (targetIncomeData.period) {
      case 'weekly':
        return parsedTargetIncomeAmount;
      case 'monthly':
        return (parsedTargetIncomeAmount * 12) / 52;
      case 'yearly':
        return parsedTargetIncomeAmount / 52;
      default:
        return parsedTargetIncomeAmount;
    }
  })() : 0;
  const normalizedTargetIncome = hasValidTargetIncomeAmount ? {
    weekly: weeklyTargetEquivalent,
    monthly: weeklyTargetEquivalent * 52 / 12,
    yearly: weeklyTargetEquivalent * 52
  } : null;
  const weeklyGoalHours = parseFloat(targetIncomeData.totalWeeklyOpenHours || targetIncomeData.workHoursPerWeek || 0);
  const pricingLaborHourlyCost = parseFloat(targetIncomeData.pricingLaborHourlyCost || 0) > 0
    ? parseFloat(targetIncomeData.pricingLaborHourlyCost)
    : (
        hasValidTargetIncomeAmount && weeklyGoalHours > 0
          ? weeklyTargetEquivalent / weeklyGoalHours
          : (targetIncomeData.goalHourly || null)
      );
  const businessProfitHourlyTarget = parseFloat(targetIncomeData.businessProfitHourlyTarget || 0) > 0
    ? parseFloat(targetIncomeData.businessProfitHourlyTarget)
    : (
        hasValidTargetIncomeAmount && weeklyGoalHours > 0
          ? weeklyTargetEquivalent / weeklyGoalHours
          : 0
      );

  const renderRevenueBar = useCallback((label, value, percent, barClass) => {
    const actualPercent = Math.max(0, Math.min(percent || 0, 100));
    const barWidth = value > 0 ? Math.max(actualPercent, 4) : 0;
    const valueText = formatCurrency(value);

    return (
      <div className="flex items-center gap-3">
        <span className="w-12 text-xs font-medium text-[color:var(--color-text)]">{label}</span>
        <div className="flex-1 relative h-6 rounded-full overflow-hidden bg-gray-200">
          <div
            className={`${barClass} absolute inset-y-0 left-0 rounded-full`}
            style={{ width: `${barWidth}%` }}
          />
          <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold text-[color:var(--color-text)]">
            {valueText}
          </span>
        </div>
      </div>
    );
  }, [formatCurrency]);
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [newService, setNewService] = useState({
  name: '',
  duration: 30,
  cost: 0,
  price: 0
});
  const [showVoiceInput, setShowVoiceInput] = useState(false);
  const [showModalVoiceInput, setShowModalVoiceInput] = useState(false);
  const [showInputMethodSelection, setShowInputMethodSelection] = useState(false);
  const [selectedInputMethod, setSelectedInputMethod] = useState(null); // 'manual', 'voice', 'receipt', 'spreadsheet'
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [isSpreadsheetImporting, setIsSpreadsheetImporting] = useState(false);
  const [showSpreadsheetReviewModal, setShowSpreadsheetReviewModal] = useState(false);
  const [spreadsheetImportDrafts, setSpreadsheetImportDrafts] = useState([]);
  const [spreadsheetImportMeta, setSpreadsheetImportMeta] = useState({
    fileName: '',
    sourceKind: 'sales_report',
    sourceVariant: 'default',
    defaultImportType: 'expenses',
    parserSkippedRows: 0,
    typeFilteredRows: 0,
    autoCalculatedVatRows: 0,
    suggestedCategories: []
  });
  const spreadsheetFileInputRef = useRef(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [hasGoals, setHasGoals] = useState(false);
  const [pendingCategories, setPendingCategories] = useState([]);
  useEffect(() => {
    const normalizedGoals = normalizeTargetIncomeData(targetIncomeData);

    try {
      localStorage.setItem('70livesbeautyledgerTargetIncome', JSON.stringify(normalizedGoals));
    } catch (error) {
      console.warn('Failed to persist income goal:', error);
    }

    const persistGoals = async () => {
      if (!goalsLoadedRef.current) return;
      try {
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.GOALS, normalizedGoals);
      } catch (error) {
        console.warn('Failed to persist income goal via hybrid storage:', error);
      }
    };

    persistGoals();
    const amt = parseFloat(normalizedGoals?.amount || 0);
    setHasGoals(Number.isFinite(amt) && amt > 0);
  }, [persistDatasetForCurrentAccess, targetIncomeData]);
  // Overhead calculator state (for Pricing Calculator)
  const [showOverheadModal, setShowOverheadModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [overheadStart, setOverheadStart] = useState('');
  const [overheadEnd, setOverheadEnd] = useState('');
  const [workDaysPerWeek, setWorkDaysPerWeek] = useState('5');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [premisesType, setPremisesType] = useState('separate'); // 'separate' | 'home'
  const setLast30Days = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29); // inclusive 30-day window
    const toISO = (d) => d.toISOString().slice(0, 10);
    setOverheadEnd(toISO(end));
    setOverheadStart(toISO(start));
  }, []);
  const [overheadHourly, setOverheadHourly] = useState(null);
  const [overheadBreakdown, setOverheadBreakdown] = useState(null);
  // Cost calculator state
  const PRESET_PRODUCTS = [
    // Core disposables
    { id: 'cotton_pads', name: 'Cotton pads', packCost: 2.5, unitsPerPack: 80, usagePerService: 2, notes: '' },
    { id: 'cotton_buds', name: 'Cotton buds', packCost: 1.8, unitsPerPack: 200, usagePerService: 2, notes: '' },
    { id: 'disposable_gloves_pair', name: 'Disposable gloves (pair)', packCost: 6, unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'disposable_towels', name: 'Disposable towels', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'bed_roll', name: 'Bed roll / couch roll', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'facial_tissues', name: 'Facial tissues', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hand_sanitiser', name: 'Hand sanitiser', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'surface_disinfectant', name: 'Surface disinfectant', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'tool_disinfectant', name: 'Tool disinfectant / barbicide', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'alcohol_wipes', name: 'Alcohol wipes', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'antiseptic_solution', name: 'Antiseptic solution', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'ppe_mask', name: 'PPE mask', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'apron', name: 'Apron / disposable apron', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'bin_liners', name: 'Bin liners / clinical waste bags', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    // Skin / facial
    { id: 'cleanser', name: 'Cleanser', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'toner', name: 'Toner', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'exfoliant', name: 'Exfoliant / scrub', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'enzyme_peel', name: 'Enzyme peel', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'chemical_peel', name: 'Chemical peel solution', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'neutralising_solution', name: 'Neutralising solution', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'serum', name: 'Serum', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'ampoule', name: 'Ampoule', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'moisturiser', name: 'Moisturiser', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'spf', name: 'SPF / sunscreen', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'face_mask_cream', name: 'Face mask (cream)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'sheet_mask', name: 'Sheet mask', packCost: '', unitsPerPack: 1, usagePerService: 1, notes: '' },
    { id: 'alginate_mask', name: 'Alginate mask', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'eye_cream', name: 'Eye cream', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lip_balm', name: 'Lip balm', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'facial_oil', name: 'Facial oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'massage_cream_face', name: 'Massage cream (face)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'massage_oil_face', name: 'Massage oil (face)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'steamer_water', name: 'Steamer distilled water', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'spatulas', name: 'Spatulas (disposable)', packCost: '', unitsPerPack: 100, usagePerService: 1, notes: '' },
    // Lash & brow
    { id: 'lash_cleanser', name: 'Lash cleanser / shampoo', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lash_shampoo_pads', name: 'Lash shampoo pads', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'eye_patches', name: 'Eye patches', packCost: '', unitsPerPack: 10, usagePerService: 1, notes: '' },
    { id: 'micro_brushes', name: 'Micro brushes', packCost: '', unitsPerPack: 100, usagePerService: 1, notes: '' },
    { id: 'lash_glue', name: 'Lash glue', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'glue_rings', name: 'Glue rings', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'lash_tape', name: 'Lash tape', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lash_trays', name: 'Lash trays (per strip)', packCost: '', unitsPerPack: 16, usagePerService: 1, notes: '' },
    { id: 'brow_tint', name: 'Brow tint', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lash_tint', name: 'Lash tint', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'tint_developer', name: 'Tint developer', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'brow_mapping_string', name: 'Brow mapping string', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'brow_lamination_step1', name: 'Brow lamination solution (Step 1)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'brow_lamination_step2', name: 'Brow setting solution (Step 2)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'nourishing_oil', name: 'Nourishing oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'brow_wax', name: 'Brow wax', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'brow_thread', name: 'Brow thread', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'disposable_mascara_wands', name: 'Disposable mascara wands', packCost: 3.2, unitsPerPack: 50, usagePerService: 1, notes: '' },
    // Nail
    { id: 'nail_files', name: 'Nail files', packCost: '', unitsPerPack: 20, usagePerService: 1, notes: '' },
    { id: 'buffer_blocks', name: 'Buffer blocks', packCost: '', unitsPerPack: 10, usagePerService: 1, notes: '' },
    { id: 'cuticle_pusher', name: 'Cuticle pusher (disposable)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'cuticle_oil', name: 'Cuticle oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'nail_cleanser', name: 'Nail cleanser', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'dehydrator', name: 'Dehydrator', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'primer', name: 'Primer', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'base_coat', name: 'Base coat', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'gel_polish', name: 'Gel polish (per colour use)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'builder_gel', name: 'Builder gel / BIAB', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'acrylic_powder', name: 'Acrylic powder', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'acrylic_liquid', name: 'Acrylic liquid (monomer)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'top_coat', name: 'Top coat', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'nail_wipes', name: 'Nail wipes', packCost: '', unitsPerPack: 200, usagePerService: 2, notes: '' },
    { id: 'foil_wraps', name: 'Foil wraps', packCost: '', unitsPerPack: 20, usagePerService: 2, notes: '' },
    { id: 'acetone', name: 'Acetone', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'nail_tips', name: 'Nail tips', packCost: '', unitsPerPack: 100, usagePerService: 10, notes: '' },
    { id: 'nail_forms', name: 'Nail forms', packCost: '', unitsPerPack: 100, usagePerService: 2, notes: '' },
    // Hair
    { id: 'shampoo', name: 'Shampoo', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'conditioner', name: 'Conditioner', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'treatment_mask', name: 'Treatment mask', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hair_dye', name: 'Hair dye (per mix)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'developer', name: 'Developer', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hair_toner', name: 'Hair toner', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'bleach_powder', name: 'Bleach powder', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'bond_builder', name: 'Bond builder', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'foils', name: 'Foils', packCost: '', unitsPerPack: 200, usagePerService: 10, notes: '' },
    { id: 'hair_clips', name: 'Hair sectioning clips', packCost: '', unitsPerPack: 12, usagePerService: 2, notes: '' },
    { id: 'neck_strips', name: 'Neck strips', packCost: '', unitsPerPack: 100, usagePerService: 1, notes: '' },
    { id: 'styling_product', name: 'Styling product', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'heat_protectant', name: 'Heat protectant', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hairspray', name: 'Hairspray', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hair_disposable_towels', name: 'Disposable towels (hair)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    // Massage / body
    { id: 'massage_oil_body', name: 'Massage oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'massage_cream_body', name: 'Massage cream', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'massage_wax', name: 'Massage wax', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'body_scrub', name: 'Body scrub', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'body_mask', name: 'Body mask', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hot_stone_oil', name: 'Hot stone oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'hot_stones', name: 'Hot stones (usage cost)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'disposable_underwear', name: 'Disposable underwear', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    // Makeup
    { id: 'foundation', name: 'Foundation', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'concealer', name: 'Concealer', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'powder', name: 'Powder', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'blush', name: 'Blush', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'bronzer', name: 'Bronzer', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'highlighter', name: 'Highlighter', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lipstick', name: 'Lipstick', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lip_gloss', name: 'Lip gloss', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'mascara', name: 'Mascara', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'false_lashes', name: 'False lashes', packCost: '', unitsPerPack: 10, usagePerService: 1, notes: '' },
    { id: 'makeup_lash_glue', name: 'Lash glue (makeup)', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'makeup_remover', name: 'Makeup remover', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'setting_spray', name: 'Setting spray', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'disposable_sponges', name: 'Disposable sponges', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'disposable_applicators', name: 'Disposable applicators', packCost: '', unitsPerPack: 100, usagePerService: 2, notes: '' },
    { id: 'micellar_water', name: 'Micellar water', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    // Clinical / injectables
    { id: 'alcohol_swabs', name: 'Alcohol swabs', packCost: '', unitsPerPack: 100, usagePerService: 1, notes: '' },
    { id: 'chlorhexidine', name: 'Chlorhexidine prep', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'syringes', name: 'Syringes', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'needles', name: 'Needles', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'cannula', name: 'Cannula', packCost: '', unitsPerPack: 20, usagePerService: 1, notes: '' },
    { id: 'dermal_filler', name: 'Dermal filler', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'anti_wrinkle_toxin', name: 'Anti-wrinkle toxin', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'saline_solution', name: 'Saline solution', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'lidocaine', name: 'Lidocaine', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'topical_numbing', name: 'Topical numbing cream', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'gauze_pads', name: 'Gauze pads', packCost: '', unitsPerPack: 50, usagePerService: 2, notes: '' },
    { id: 'ice_packs', name: 'Ice packs', packCost: '', unitsPerPack: 10, usagePerService: 1, notes: '' },
    { id: 'post_treatment_cream', name: 'Post-treatment cream', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    // Laser / device
    { id: 'cooling_gel', name: 'Cooling gel', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'laser_tips', name: 'Laser tips', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'ipl_consumables', name: 'IPL consumables', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'ultrasound_gel', name: 'Ultrasound gel', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'disposable_razors', name: 'Disposable razors', packCost: '', unitsPerPack: 50, usagePerService: 1, notes: '' },
    { id: 'eye_shields', name: 'Protective eye shields', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'device_cartridge', name: 'Device cartridge wear cost', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'filter_replacement', name: 'Filter replacement allocation', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    // Waxing
    { id: 'hard_wax', name: 'Hard wax', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'strip_wax', name: 'Strip wax', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'wax_strips', name: 'Wax strips', packCost: '', unitsPerPack: 100, usagePerService: 4, notes: '' },
    { id: 'pre_wax_cleanser', name: 'Pre-wax cleanser', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'after_wax_oil', name: 'After-wax oil', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
    { id: 'talc_powder', name: 'Talc powder', packCost: '', unitsPerPack: '', usagePerService: 1, notes: '' },
  ];

  const PRESET_PRODUCTS_SORTED = [...PRESET_PRODUCTS].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

  const DEFAULT_COST_ITEMS = [];
  const COST_ITEMS_STORAGE_KEY = '70lives_cost_items';

 const [costItems, setCostItems] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_COST_ITEMS;
    try {
      const saved = localStorage.getItem(COST_ITEMS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (err) {
      console.warn('Failed to load saved cost items:', err);
    }
    return DEFAULT_COST_ITEMS;
  });
  const costItemsLoadedRef = useRef(false);
  const servicePricesLoadedRef = useRef(false);
  const extraCategoriesLoadedRef = useRef(false);
  const goalsLoadedRef = useRef(false);
  const vatSettingsLoadedRef = useRef(false);
  const clientsLoadedRef = useRef(false);
  const bankAccountsLoadedRef = useRef(false);
  const journalEntriesLoadedRef = useRef(false);
  const resetLedgerStateForAccountSwitch = useCallback(() => {
    costItemsLoadedRef.current = false;
    servicePricesLoadedRef.current = false;
    extraCategoriesLoadedRef.current = false;
    goalsLoadedRef.current = false;
    vatSettingsLoadedRef.current = false;
    clientsLoadedRef.current = false;
    bankAccountsLoadedRef.current = false;
    journalEntriesLoadedRef.current = false;
    businessProfileLoadedRef.current = false;

    setTransactionsState([]);
    setEditingTransactionId(null);
    setEditingTransaction({});
    setSelectedRecentTransactionIds([]);
    setCurrentTransaction(createEmptyTransactionDraft());
    setIsAddingTransaction(false);
    setSelectedInputMethod(null);
    setCustomDepreciationPeriods({});
    setServicePrices(DEFAULT_SERVICES);
    setCostItems([]);
    setExtraCategories({ income: [], expenses: [] });
    setTargetIncomeData(DEFAULT_TARGET_INCOME_DATA);
    setVatSettingsState(sanitizeVatSettings({}, getDefaultVatSettingsForCountry(currentCountry)));
    setClients([]);
    setBankAccounts([]);
    setJournalEntries([]);
    const localBusinessProfile = activeWorkspaceRef.current?.ownerId &&
      activeWorkspaceRef.current.ownerId !== (auth.currentUser?.uid || userRef.current?.uid)
      ? buildBusinessProfileSettings(BUSINESS_TYPES.SOLE_TRADER, DEFAULT_INDUSTRY_ID, {})
      : readLocalBusinessProfileSettings();
    setBusinessType(localBusinessProfile.businessType);
    setIndustryId(localBusinessProfile.industryId);
    setBusinessProfileDetails(normalizeBusinessProfileDetails(localBusinessProfile));
  }, [currentCountry]);
  const [autoSaveCostItems, setAutoSaveCostItems] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('auto_save_cost_items') : null;
      return saved ? JSON.parse(saved) : false; // default off
    } catch {
      return false;
    }
  });
  const addTemplateCostItems = useCallback((templates) => {
    setCostItems((prev) => {
      const existingNames = new Set((prev || []).map((c) => (c.name || '').toLowerCase()));
      const additions = [];
      templates.forEach((t) => {
        (t.productItems || []).forEach((p, idx) => {
          const name = p.productName || `Product ${idx + 1}`;
          if (existingNames.has(name.toLowerCase())) return;
          existingNames.add(name.toLowerCase());
          additions.push({
            id: Date.now() + additions.length,
            name,
            unitCost: 0,
            packCost: p.packPrice ?? '',
            packSize: p.packSize ?? '',
            unitsPerPack: p.packSize ?? '',
            usagePerService: p.usagePerService ?? 1,
            notes: p.notes || '',
            unitType: p.unitType || 'piece',
            coveragePreset: p.coveragePreset || ''
          });
        });
      });
      if (!additions.length) return prev;
      // also seed search terms for new items
      setProductSearchTerms((prevTerms) => {
        const next = { ...prevTerms };
        additions.forEach((item) => { next[item.id] = item.name; });
        return next;
      });
      return [...prev, ...additions];
    });
  }, []);
  const seedMakeupServicePresets = useCallback(() => {
    addTemplateCostItems(MAKEUP_TEMPLATE_DEFINITIONS);
    let addedCount = 0;
    setServicePrices((prev) => {
      const currentList = prev?.makeupServices || [];
      const existingNames = new Set(currentList.map((s) => (s.name || '').toLowerCase()));
      const additions = MAKEUP_TEMPLATE_DEFINITIONS.filter((t) => !existingNames.has((t.name || '').toLowerCase()))
        .map((t) => ({
          name: t.name,
          duration: t.durationMinutes,
          category: 'makeupServices',
          cost: 0,
          price: 0,
          defaultHourlyPay: t.defaultHourlyPay,
          defaultProfitMarginPct: t.defaultProfitMarginPct,
          vatMode: t.vatMode || 'exclusive',
          wastageBufferPct: t.wastageBufferPct ?? 10,
          productItems: t.productItems || []
        }));
      addedCount = additions.length;
      const nextList = additions.length ? [...currentList, ...additions] : currentList;
      return {
        ...prev,
        makeupServices: nextList
      };
    });
    setSelectedCategory('makeupServices');
    setShowPricingCalculator(true);
    setSelectedService(null);
    setIsEditingPrices(false);
    setShowMakeupTemplates(true);
    if (addedCount > 0) {
      alert(`Added ${addedCount} makeup templates. You can edit them below.`);
    }
    try { localStorage.setItem('makeup_presets_prompted', 'true'); } catch {}
  }, [addTemplateCostItems]);
  const normalizeExtraCategoriesShape = useCallback((value) => {
    const source = value && typeof value === 'object' ? value : {};
    const normalizeList = (list) => {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      return list
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return null;
          const id = String(entry.id || '').trim();
          const name = String(entry.name || '').trim();
          const icon = String(entry.icon || '').trim();
          if (!id && !name) return null;
          const normalizedId = id || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          if (!normalizedId) return null;
          const dedupeKey = normalizedId.toLowerCase();
          if (seen.has(dedupeKey)) return null;
          seen.add(dedupeKey);
          return {
            id: normalizedId,
            name: name || normalizedId,
            icon
          };
        })
        .filter(Boolean);
    };

    return {
      income: normalizeList(source.income),
      expenses: normalizeList(source.expenses)
    };
  }, []);
  const [extraCategories, setExtraCategories] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('extra_categories') : null;
      return normalizeExtraCategoriesShape(saved ? JSON.parse(saved) : { income: [], expenses: [] });
    } catch {
      return { income: [], expenses: [] };
    }
  });
  const [productSearchTerms, setProductSearchTerms] = useState({});

  useEffect(() => {
    try {
      localStorage.setItem(COST_ITEMS_STORAGE_KEY, JSON.stringify(costItems));
    } catch (err) {
      console.warn('Failed to persist cost items:', err);
    }
  }, [costItems, persistDatasetForCurrentAccess]);

  useEffect(() => {
    const persistCostItems = async () => {
      if (!costItemsLoadedRef.current) return;
      if (!Array.isArray(costItems)) return;
      try {
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.COST_ITEMS, costItems);
      } catch (error) {
        console.warn('Failed to save cost items via hybrid storage:', error);
      }
    };

    persistCostItems();
  }, [costItems, persistDatasetForCurrentAccess]);
  const ensureCoreServiceTemplates = useCallback((prices) => {
    if (!prices || typeof prices !== 'object') return null;
    let changed = false;
    const next = { ...prices };
    Object.entries(DEFAULT_SERVICES).forEach(([key, defaults]) => {
      const current = prices[key];
      if (!Array.isArray(current) || current.length === 0) {
        next[key] = defaults;
        changed = true;
      }
    });
    return changed ? next : null;
  }, []);
  useEffect(() => {
    if (!servicePricesLoadedRef.current) return;
    const patched = ensureCoreServiceTemplates(servicePrices);
    if (patched) {
      setServicePrices(patched);
      persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.SERVICE_PRICES, patched)
        .catch((err) => {
          console.warn('Failed to persist restored service templates:', err);
        });
    }
  }, [ensureCoreServiceTemplates, persistDatasetForCurrentAccess, servicePrices]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('auto_save_cost_items', JSON.stringify(autoSaveCostItems));
      }
    } catch {
      // ignore
    }
  }, [autoSaveCostItems]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('hidden_service_categories', JSON.stringify(hiddenServiceCategories));
      }
    } catch {
      // ignore
    }
  }, [hiddenServiceCategories]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('hidden_categories', JSON.stringify({
          income: Array.isArray(hiddenCategories.income) ? hiddenCategories.income : [],
          expenses: Array.isArray(hiddenCategories.expenses) ? hiddenCategories.expenses : []
        }));
      }
    } catch {
      // ignore
    }
  }, [hiddenCategories]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(SERVICE_OFFERINGS_STORAGE_KEY, JSON.stringify(selectedServiceOfferings));
      }
    } catch {
      // ignore
    }
  }, [selectedServiceOfferings]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('show_makeup_templates', JSON.stringify(showMakeupTemplates));
      }
    } catch {
      // ignore
    }
  }, [showMakeupTemplates]);

  useEffect(() => {
    const persistExtraCategories = async () => {
      if (!extraCategoriesLoadedRef.current) return;

      try {
        await persistDatasetForCurrentAccess(
          SHARED_WORKSPACE_DATASETS.EXTRA_CATEGORIES,
          normalizeExtraCategoriesShape(extraCategories)
        );
      } catch (error) {
        console.warn('Failed to save extra categories via hybrid storage:', error);
      }
    };

    persistExtraCategories();
  }, [extraCategories, normalizeExtraCategoriesShape, persistDatasetForCurrentAccess]);

  const mergedCategories = useMemo(() => {
    const filterForIndustry = (categories = []) => (
      supportsRct ? categories : categories.filter((category) => !CONSTRUCTION_ONLY_CATEGORY_IDS.has(category.id))
    );
    const scopeBeautyCategories = (categories = [], type) => (
      industryId === DEFAULT_INDUSTRY_ID
        ? getBeautyServiceScopedCategories(categories, selectedServiceOfferings, type)
        : categories
    );

    return {
      income: scopeBeautyCategories(
        filterForIndustry(mergeCategoryOptions(CATEGORIES.income || [], extraCategories.income || [])),
        'income'
      ),
      expenses: scopeBeautyCategories(
        filterForIndustry(mergeCategoryOptions(CATEGORIES.expenses || [], extraCategories.expenses || [])),
        'expenses'
      ),
    };
  }, [CATEGORIES, extraCategories, industryId, selectedServiceOfferings, supportsRct]);
  const filteredIncomeCategories = useMemo(
    () => (mergedCategories.income || []).filter((cat) => (
      !hiddenServiceCategories.includes(cat.id) &&
      !(hiddenCategories.income || []).includes(cat.id)
    )),
    [mergedCategories.income, hiddenCategories.income, hiddenServiceCategories]
  );
  const filteredExpenseCategories = useMemo(
    () => (mergedCategories.expenses || []).filter((cat) => !(hiddenCategories.expenses || []).includes(cat.id)),
    [hiddenCategories.expenses, mergedCategories.expenses]
  );
  const activeCategoryOptions = useMemo(
    () => (activeTab === 'income' ? filteredIncomeCategories : filteredExpenseCategories),
    [activeTab, filteredExpenseCategories, filteredIncomeCategories]
  );
  const activeCategoryType = activeTab === 'income' ? 'income' : 'expenses';
  const activeHiddenCategoryOptions = useMemo(
    () => (mergedCategories[activeCategoryType] || []).filter((cat) => (
      (hiddenCategories[activeCategoryType] || []).includes(cat.id)
    )),
    [activeCategoryType, hiddenCategories, mergedCategories]
  );
  const transactionInputCategories = useMemo(
    () => ({
      ...mergedCategories,
      income: filteredIncomeCategories,
      expenses: filteredExpenseCategories
    }),
    [filteredExpenseCategories, filteredIncomeCategories, mergedCategories]
  );
  const selectedServiceOfferingLabels = useMemo(() => {
    const selectedIds = new Set(selectedServiceOfferings);
    return SERVICE_OFFERING_OPTIONS
      .filter((option) => selectedIds.has(option.id))
      .map((option) => option.label);
  }, [selectedServiceOfferings]);
  const toggleServiceOfferingPreference = useCallback((offeringId) => {
    setSelectedServiceOfferings((prev) => {
      const current = sanitizeServiceOfferings(prev);
      if (current.includes(offeringId)) {
        return current.length <= 1 ? current : current.filter((id) => id !== offeringId);
      }
      return sanitizeServiceOfferings([...current, offeringId]);
    });
  }, []);
  const resetServiceOfferingPreferences = useCallback(() => {
    setSelectedServiceOfferings([]);
  }, []);
  const clientOptions = useMemo(
    () => mergeClientLists([], clients),
    [clients]
  );
  const clientById = useMemo(
    () => new Map(clientOptions.map((client) => [client.id, client])),
    [clientOptions]
  );
  const buildClientAllocationsFromIds = useCallback((clientIds = []) => (
    normalizeClientAllocations(
      (Array.isArray(clientIds) ? clientIds : [])
        .map((clientId) => ({ clientId }))
        .filter((entry) => entry.clientId),
      clientById
    )
  ), [clientById]);
  const getExpenseAllocationIds = useCallback((transaction = {}) => (
    getTransactionClientAllocations(transaction, clientById, 0)
      .map((allocation) => allocation.clientId)
      .filter(Boolean)
  ), [clientById]);
  const getExpenseAllocationLabel = useCallback((transaction = {}) => (
    getClientAllocationLabel(transaction, clientById)
  ), [clientById]);
  const [projectPricingMarkup, setProjectPricingMarkup] = useState(() => {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem('project_pricing_markup') || '30' : '30';
    } catch {
      return '30';
    }
  });
  const staffMemberOptions = useMemo(
    () => getStaffMemberOptions(targetIncomeData),
    [targetIncomeData]
  );
  const staffMemberById = useMemo(
    () => new Map(staffMemberOptions.map((staffMember) => [staffMember.id, staffMember])),
    [staffMemberOptions]
  );
  const staffPlanProfiles = useMemo(
    () => buildLabourProfilesForStaffPlan(staffPlanDraft, targetIncomeData),
    [staffPlanDraft, targetIncomeData]
  );
  const staffPlanSummary = useMemo(
    () => buildStaffPlanSummary(staffPlanProfiles),
    [staffPlanProfiles]
  );
  const handleStaffPlanDefaultEmployerChange = useCallback((value) => {
    setStaffPlanDraft((prev) => ({
      ...prev,
      defaultEmployerCostPercent: value,
      staffMembers: (prev.staffMembers || []).map((member) => ({
        ...member,
        employerCostPercent: member.employerCostPercent === '' ? value : member.employerCostPercent
      }))
    }));
  }, []);
  const handleStaffPlanDefaultHolidayChange = useCallback((value) => {
    setStaffPlanDraft((prev) => ({
      ...prev,
      defaultHolidayPayPercent: value,
      staffMembers: (prev.staffMembers || []).map((member) => ({
        ...member,
        holidayPayPercent: member.holidayPayPercent === '' ? value : member.holidayPayPercent
      }))
    }));
  }, []);
  const handleAddStaffPlanMember = useCallback(() => {
    setStaffPlanDraft((prev) => ({
      ...prev,
      staffMembers: [
        ...(Array.isArray(prev.staffMembers) ? prev.staffMembers : []),
        createStaffPlanMember(prev.staffMembers?.length || 0, {
          employerCostPercent: prev.defaultEmployerCostPercent,
          holidayPayPercent: prev.defaultHolidayPayPercent
        })
      ]
    }));
    setStaffPlanSaveMessage('');
  }, []);
  const handleRemoveStaffPlanMember = useCallback((staffId) => {
    setStaffPlanDraft((prev) => ({
      ...prev,
      staffMembers: (Array.isArray(prev.staffMembers) ? prev.staffMembers : []).filter((member) => member.id !== staffId)
    }));
    setStaffPlanSaveMessage('');
  }, []);
  const handleStaffPlanMemberChange = useCallback((staffId, field, value) => {
    setStaffPlanDraft((prev) => ({
      ...prev,
      staffMembers: (Array.isArray(prev.staffMembers) ? prev.staffMembers : []).map((member) => (
        member.id === staffId ? { ...member, [field]: value } : member
      ))
    }));
    setStaffPlanSaveMessage('');
  }, []);
  const handleSaveStaffPlan = useCallback(() => {
    const staffMembers = Array.isArray(staffPlanDraft.staffMembers) ? staffPlanDraft.staffMembers : [];
    const invalidStaffMember = staffMembers.find((member) => (
      String(member.name || '').trim() === '' ||
      toStaffPlanNumber(member.hours) <= 0 ||
      member.hourlyPay === '' ||
      toStaffPlanNumber(member.hourlyPay) < 0 ||
      toStaffPlanNumber(member.employerCostPercent) < 0 ||
      toStaffPlanNumber(member.holidayPayPercent) < 0
    ));

    if (invalidStaffMember) {
      setStaffPlanSaveMessage('Add a name, hours, hourly pay, and non-negative loading/holiday reserve for each staff member.');
      return;
    }

    const normalizedStaffMembers = staffMembers.map((member, index) => ({
      id: member.id || `staff-${index + 1}`,
      name: member.name?.trim() || `Staff ${index + 1}`,
      hoursPeriod: STAFF_HOURS_PERIOD_OPTIONS.some((option) => option.id === member.hoursPeriod) ? member.hoursPeriod : 'weekly',
      hours: member.hours ?? '',
      hourlyPay: member.hourlyPay ?? '',
      employerCostPercent: member.employerCostPercent ?? staffPlanDraft.defaultEmployerCostPercent ?? '',
      holidayPayPercent: member.holidayPayPercent ?? staffPlanDraft.defaultHolidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT
    }));
    const nextStaffPlan = {
      ...staffPlanDraft,
      staffMembers: normalizedStaffMembers
    };
    const staffProfiles = buildLabourProfilesForStaffPlan(nextStaffPlan, targetIncomeData);
    const staffSummary = buildStaffPlanSummary(staffProfiles);
    const ownerProfiles = Array.isArray(targetIncomeData.labourProfiles)
      ? targetIncomeData.labourProfiles.filter((profile) => profile?.type !== 'staff')
      : [];
    const labourProfiles = [...ownerProfiles, ...staffProfiles];
    const allWeeklyHours = labourProfiles.reduce((sum, profile) => sum + Math.max(0, Number(profile.weeklyHours) || 0), 0);
    const pricingLaborHourlyCost = allWeeklyHours > 0
      ? labourProfiles.reduce((sum, profile) => sum + ((Number(profile.loadedHourlyCost) || 0) * (Number(profile.weeklyHours) || 0)), 0) / allWeeklyHours
      : (targetIncomeData.pricingLaborHourlyCost || targetIncomeData.goalHourly || null);

    const nextTargetIncomeData = normalizeTargetIncomeData({
      ...targetIncomeData,
      teamMode: normalizedStaffMembers.length ? 'staff' : 'solo',
      staffCount: normalizedStaffMembers.length,
      defaultEmployerCostPercent: staffPlanDraft.defaultEmployerCostPercent ?? '',
      defaultHolidayPayPercent: staffPlanDraft.defaultHolidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT,
      staffMembers: normalizedStaffMembers,
      labourProfiles,
      pricingLaborHourlyCost,
      goalHourly: pricingLaborHourlyCost,
      staffPlanSummary: {
        ...(targetIncomeData.staffPlanSummary || {}),
        ...staffSummary
      }
    });

    setTargetIncomeData(nextTargetIncomeData);
    setHasGoals(true);
    setHasSavedGoal(true);
    try { localStorage.setItem(GOAL_SAVED_KEY, 'true'); } catch {}
    setStaffPlanSaveMessage('Staff pay plan saved.');
  }, [staffPlanDraft, targetIncomeData]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(
          STAFF_HR_RECORDS_STORAGE_KEY,
          JSON.stringify(Array.isArray(staffHrRecords) ? staffHrRecords : [])
        );
      }
    } catch {
      // ignore
    }
  }, [staffHrRecords]);
  useEffect(() => {
    if (!staffHrDraft.staffMemberId && staffMemberOptions[0]?.id) {
      setStaffHrDraft((prev) => ({ ...prev, staffMemberId: staffMemberOptions[0].id }));
    }
  }, [staffHrDraft.staffMemberId, staffMemberOptions]);
  const staffPlanById = useMemo(() => {
    const staffMembers = Array.isArray(targetIncomeData?.staffMembers) ? targetIncomeData.staffMembers : [];
    return new Map(staffMembers.map((member, index) => {
      const id = normalizeStaffMemberName(member?.id, `staff-${index + 1}`);
      return [id, {
        id,
        name: normalizeStaffMemberName(member?.name, `Staff ${index + 1}`),
        hourlyPay: Number(member?.hourlyPay) || 0,
        plannedWeeklyHours: Number(member?.hours) || 0,
        holidayPayPercent: Number(member?.holidayPayPercent ?? targetIncomeData?.defaultHolidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT) || 0
      }];
    }));
  }, [targetIncomeData]);
  const staffProfitabilityMeta = useMemo(
    () => getStaffPerformancePeriodMeta(staffProfitabilityPeriod),
    [staffProfitabilityPeriod]
  );
  const staffProfitabilityProfiles = useMemo(() => {
    const profiles = Array.isArray(targetIncomeData?.labourProfiles) ? targetIncomeData.labourProfiles : [];
    return profiles
      .filter((profile) => profile?.type === 'staff')
      .map((profile, index) => {
        const weeklyCost = Math.max(0, Number(profile.weeklyCost) || 0);
        const weeklyTarget = Math.max(0, Number(targetIncomeData?.desiredProfitPerStaffMember) || 0) + weeklyCost;
        return {
          ...profile,
          id: profile.id || `staff-${index + 1}`,
          name: profile.name || profile.displayName || `Staff ${index + 1}`,
          displayName: profile.name || profile.displayName || `Staff ${index + 1}`,
          weeklyCost,
          monthlyCost: Math.max(0, Number(profile.monthlyCost) || (weeklyCost * 52) / 12),
          yearlyCost: Math.max(0, Number(profile.yearlyCost) || weeklyCost * 52),
          weeklyIncomeToGenerate: Math.max(0, Number(profile.weeklyIncomeToGenerate) || weeklyTarget),
          monthlyIncomeToGenerate: Math.max(0, Number(profile.monthlyIncomeToGenerate) || (weeklyTarget * 52) / 12),
          yearlyIncomeToGenerate: Math.max(0, Number(profile.yearlyIncomeToGenerate) || weeklyTarget * 52)
        };
      });
  }, [targetIncomeData]);
  const staffProfitabilityRows = useMemo(() => buildStaffPerformanceRows({
    transactions,
    staffProfiles: staffProfitabilityProfiles,
    period: staffProfitabilityPeriod
  }), [staffProfitabilityPeriod, staffProfitabilityProfiles, transactions]);
  const staffHrSummary = useMemo(() => {
    const summaries = staffMemberOptions.map((staffMember, index) => {
      const plan = staffPlanById.get(staffMember.id) || {};
      return {
        id: staffMember.id,
        name: staffMember.name || plan.name || `Staff ${index + 1}`,
        hourlyPay: Number(plan.hourlyPay) || 0,
        plannedWeeklyHours: Number(plan.plannedWeeklyHours) || 0,
        holidayPayPercent: Number(plan.holidayPayPercent) || DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT,
        actualHoursWorked: 0,
        holidayHoursTaken: 0,
        publicHolidayHours: 0,
        sickHours: 0,
        grossWages: 0,
        publicHolidayPay: 0,
        accruedHolidayHours: 0,
        accruedHolidayPay: 0,
        usedHolidayPay: 0,
        remainingHolidayHours: 0,
        remainingHolidayPay: 0
      };
    });
    const summaryById = new Map(summaries.map((summary) => [summary.id, summary]));

    (Array.isArray(staffHrRecords) ? staffHrRecords : []).forEach((record) => {
      const staffId = normalizeStaffMemberName(record?.staffMemberId);
      if (!staffId) return;
      let summary = summaryById.get(staffId);
      if (!summary) {
        const fallbackName = normalizeStaffMemberName(record?.staffMemberName, 'Archived staff');
        summary = {
          id: staffId,
          name: fallbackName,
          hourlyPay: Number(record?.hourlyPay) || 0,
          plannedWeeklyHours: 0,
          holidayPayPercent: Number(record?.holidayPayPercent) || DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT,
          actualHoursWorked: 0,
          holidayHoursTaken: 0,
          publicHolidayHours: 0,
          sickHours: 0,
          grossWages: 0,
          publicHolidayPay: 0,
          accruedHolidayHours: 0,
          accruedHolidayPay: 0,
          usedHolidayPay: 0,
          remainingHolidayHours: 0,
          remainingHolidayPay: 0
        };
        summaries.push(summary);
        summaryById.set(staffId, summary);
      }

      const hourlyPay = Number(record?.hourlyPay) || summary.hourlyPay || 0;
      const holidayPayPercent = Number(record?.holidayPayPercent) || summary.holidayPayPercent || DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT;
      const hoursWorked = Math.max(0, Number(record?.hoursWorked) || 0);
      const publicHolidayHours = Math.max(0, Number(record?.publicHolidayHours) || 0);
      const holidayHoursTaken = Math.max(0, Number(record?.holidayHoursTaken) || 0);
      const sickHours = Math.max(0, Number(record?.sickHours) || 0);
      const workedWages = hoursWorked * hourlyPay;
      const publicHolidayPay = publicHolidayHours * hourlyPay;

      summary.actualHoursWorked += hoursWorked;
      summary.publicHolidayHours += publicHolidayHours;
      summary.holidayHoursTaken += holidayHoursTaken;
      summary.sickHours += sickHours;
      summary.grossWages += workedWages + publicHolidayPay;
      summary.publicHolidayPay += publicHolidayPay;
      summary.accruedHolidayHours += hoursWorked * holidayPayPercent / 100;
      summary.accruedHolidayPay += workedWages * holidayPayPercent / 100;
      summary.usedHolidayPay += holidayHoursTaken * hourlyPay;
    });

    const roundedSummaries = summaries.map((summary) => {
      const remainingHolidayHours = summary.accruedHolidayHours - summary.holidayHoursTaken;
      const remainingHolidayPay = summary.accruedHolidayPay - summary.usedHolidayPay;
      return {
        ...summary,
        actualHoursWorked: Number(summary.actualHoursWorked.toFixed(2)),
        publicHolidayHours: Number(summary.publicHolidayHours.toFixed(2)),
        holidayHoursTaken: Number(summary.holidayHoursTaken.toFixed(2)),
        sickHours: Number(summary.sickHours.toFixed(2)),
        grossWages: Number(summary.grossWages.toFixed(2)),
        publicHolidayPay: Number(summary.publicHolidayPay.toFixed(2)),
        accruedHolidayHours: Number(summary.accruedHolidayHours.toFixed(2)),
        accruedHolidayPay: Number(summary.accruedHolidayPay.toFixed(2)),
        usedHolidayPay: Number(summary.usedHolidayPay.toFixed(2)),
        remainingHolidayHours: Number(remainingHolidayHours.toFixed(2)),
        remainingHolidayPay: Number(remainingHolidayPay.toFixed(2))
      };
    });

    return {
      staff: roundedSummaries,
      totals: roundedSummaries.reduce((acc, summary) => ({
        actualHoursWorked: acc.actualHoursWorked + summary.actualHoursWorked,
        publicHolidayHours: acc.publicHolidayHours + summary.publicHolidayHours,
        holidayHoursTaken: acc.holidayHoursTaken + summary.holidayHoursTaken,
        sickHours: acc.sickHours + summary.sickHours,
        grossWages: acc.grossWages + summary.grossWages,
        publicHolidayPay: acc.publicHolidayPay + summary.publicHolidayPay,
        accruedHolidayPay: acc.accruedHolidayPay + summary.accruedHolidayPay,
        usedHolidayPay: acc.usedHolidayPay + summary.usedHolidayPay,
        remainingHolidayPay: acc.remainingHolidayPay + summary.remainingHolidayPay
      }), {
        actualHoursWorked: 0,
        publicHolidayHours: 0,
        holidayHoursTaken: 0,
        sickHours: 0,
        grossWages: 0,
        publicHolidayPay: 0,
        accruedHolidayPay: 0,
        usedHolidayPay: 0,
        remainingHolidayPay: 0
      })
    };
  }, [staffHrRecords, staffMemberOptions, staffPlanById]);
  const staffHrWeekOptions = useMemo(() => (
    Array.from(new Set((Array.isArray(staffHrRecords) ? staffHrRecords : [])
      .map((record) => record?.weekStart)
      .filter(Boolean)))
      .sort((a, b) => String(b).localeCompare(String(a)))
  ), [staffHrRecords]);
  const filteredStaffHrRecords = useMemo(() => (
    (Array.isArray(staffHrRecords) ? staffHrRecords : [])
      .filter((record) => (
        (!staffHrRecordFilters.staffMemberId || record.staffMemberId === staffHrRecordFilters.staffMemberId) &&
        (!staffHrRecordFilters.weekStart || record.weekStart === staffHrRecordFilters.weekStart)
      ))
      .sort((a, b) => {
        const dateSort = String(b.weekStart || '').localeCompare(String(a.weekStart || ''));
        if (dateSort !== 0) return dateSort;
        return String(a.staffMemberName || '').localeCompare(String(b.staffMemberName || ''));
      })
  ), [staffHrRecordFilters, staffHrRecords]);
  const handleSaveStaffHrRecord = useCallback(() => {
    const staffMember = staffMemberById.get(staffHrDraft.staffMemberId);
    const plan = staffPlanById.get(staffHrDraft.staffMemberId) || {};
    const weekStart = staffHrDraft.weekStart || getCurrentWeekStartDateString();
    if (!staffHrDraft.staffMemberId || !weekStart) {
      alert('Choose a staff member and week first.');
      return;
    }

    const nextRecord = {
      id: `${staffHrDraft.staffMemberId}-${weekStart}`,
      staffMemberId: staffHrDraft.staffMemberId,
      staffMemberName: staffMember?.name || plan.name || 'Staff member',
      weekStart,
      hoursWorked: Math.max(0, Number(staffHrDraft.hoursWorked) || 0),
      publicHolidayHours: Math.max(0, Number(staffHrDraft.publicHolidayHours) || 0),
      holidayHoursTaken: Math.max(0, Number(staffHrDraft.holidayHoursTaken) || 0),
      sickHours: Math.max(0, Number(staffHrDraft.sickHours) || 0),
      hourlyPay: Number(plan.hourlyPay) || 0,
      holidayPayPercent: Number(plan.holidayPayPercent) || DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT,
      notes: String(staffHrDraft.notes || '').trim()
    };

    setStaffHrRecords((prev) => [
      nextRecord,
      ...(Array.isArray(prev)
        ? prev.filter((record) => record.id !== nextRecord.id && record.id !== editingStaffHrRecordId)
        : [])
    ]);
    setStaffHrDraft((prev) => ({
      ...prev,
      hoursWorked: '',
      publicHolidayHours: '',
      holidayHoursTaken: '',
      sickHours: '',
      notes: ''
    }));
    setEditingStaffHrRecordId('');
  }, [editingStaffHrRecordId, staffHrDraft, staffMemberById, staffPlanById]);
  const handleEditStaffHrRecord = useCallback((record) => {
    if (!record?.id) return;
    setEditingStaffHrRecordId(record.id);
    setStaffHrDraft({
      staffMemberId: record.staffMemberId || '',
      weekStart: record.weekStart || getCurrentWeekStartDateString(),
      hoursWorked: record.hoursWorked ?? '',
      publicHolidayHours: record.publicHolidayHours ?? '',
      holidayHoursTaken: record.holidayHoursTaken ?? '',
      sickHours: record.sickHours ?? '',
      notes: record.notes || ''
    });
  }, []);
  const handleCancelStaffHrEdit = useCallback(() => {
    setEditingStaffHrRecordId('');
    setStaffHrDraft((prev) => ({
      ...prev,
      hoursWorked: '',
      publicHolidayHours: '',
      holidayHoursTaken: '',
      sickHours: '',
      notes: ''
    }));
  }, []);
  const handleDeleteStaffHrRecord = useCallback((recordId) => {
    setStaffHrRecords((prev) => (Array.isArray(prev) ? prev.filter((record) => record.id !== recordId) : []));
  }, []);
  const rctSubcontractorOptions = useMemo(
    () => Array.isArray(targetIncomeData.rctSubcontractors) ? targetIncomeData.rctSubcontractors : [],
    [targetIncomeData.rctSubcontractors]
  );
  const rctSubcontractorById = useMemo(
    () => new Map(rctSubcontractorOptions.map((subcontractor) => [subcontractor.id, subcontractor])),
    [rctSubcontractorOptions]
  );
  const persistTargetIncomeDataImmediately = useCallback((nextTargetIncomeData) => {
    const normalizedGoals = normalizeTargetIncomeData(nextTargetIncomeData);
    targetIncomeDataRef.current = normalizedGoals;

    try {
      localStorage.setItem('70livesbeautyledgerTargetIncome', JSON.stringify(normalizedGoals));
    } catch (error) {
      console.warn('Failed to persist income goal locally:', error);
    }

    if (goalsLoadedRef.current) {
      persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.GOALS, normalizedGoals).catch((error) => {
        console.warn('Failed to persist supplier list via cloud storage:', error);
      });
    }
  }, [persistDatasetForCurrentAccess]);
  const upsertRctSubcontractor = useCallback((rawSubcontractor) => {
    const name = String(rawSubcontractor?.name || '').trim();
    if (!name) return null;
    const supplierType = rawSubcontractor?.supplierType === 'rct' ? 'rct' : 'standard';
    const rate = Number(rawSubcontractor?.rctDeductionRate);
    const safeRate = [0, 0.2, 0.35].includes(rate) ? rate : 0.2;
    const id = rawSubcontractor?.id || `rct-subcontractor-${Date.now()}`;
    const currentTargetIncomeData = normalizeTargetIncomeData(targetIncomeDataRef.current);
    const current = Array.isArray(currentTargetIncomeData.rctSubcontractors) ? currentTargetIncomeData.rctSubcontractors : [];
    const nextSubcontractor = { id, name, supplierType, rctDeductionRate: supplierType === 'rct' ? safeRate : 0 };
    const nextTargetIncomeData = normalizeTargetIncomeData({
      ...currentTargetIncomeData,
      rctSubcontractors: [
        ...current.filter((entry) => entry.id !== id),
        nextSubcontractor
      ]
    });
    setTargetIncomeData(nextTargetIncomeData);
    persistTargetIncomeDataImmediately(nextTargetIncomeData);
    return id;
  }, [persistTargetIncomeDataImmediately]);
  const deleteRctSubcontractor = useCallback((subcontractorId) => {
    if (!subcontractorId) return;
    const currentTargetIncomeData = normalizeTargetIncomeData(targetIncomeDataRef.current);
    const nextTargetIncomeData = normalizeTargetIncomeData({
      ...currentTargetIncomeData,
      rctSubcontractors: (Array.isArray(currentTargetIncomeData.rctSubcontractors) ? currentTargetIncomeData.rctSubcontractors : [])
        .filter((entry) => entry.id !== subcontractorId)
    });
    setTargetIncomeData(nextTargetIncomeData);
    persistTargetIncomeDataImmediately(nextTargetIncomeData);
  }, [persistTargetIncomeDataImmediately]);
  const resolveSelectedStaffAssignment = useCallback((staffMemberId, fallbackName = '') => {
    const normalizedId = normalizeStaffMemberName(staffMemberId);
    const selectedStaffMember = normalizedId ? staffMemberById.get(normalizedId) : null;

    return {
      staffMemberId: selectedStaffMember?.id || normalizedId || '',
      staffMemberName: selectedStaffMember?.name || normalizeStaffMemberName(fallbackName)
    };
  }, [staffMemberById]);
  const getClientDedupeKey = useCallback((client) => {
    const normalized = normalizeClientRecord(client);
    if (normalized.email) return `email:${normalized.email}`;
    if (normalized.phone) return `phone:${normalized.phone}`;
    const displayName = getClientDisplayName(normalized).toLowerCase();
    if (displayName) return `name:${displayName}`;
    return `id:${normalized.id}`;
  }, []);
  const handleAddClient = useCallback(async (rawClient) => {
    const normalizedClient = normalizeClientRecord(rawClient);
    setClients((prev) => mergeClientLists(prev, [normalizedClient]));
    return normalizedClient;
  }, []);
  const handleQuickAddManualClient = useCallback(async () => {
    const defaultName = String(
      currentTransaction.clientName ||
      (activeTab === 'income' ? currentTransaction.description : '') ||
      ''
    ).trim();
    const enteredName = window.prompt('Client name', defaultName);
    const name = String(enteredName || '').trim();
    if (!name) return;

    const existingClient = clientOptions.find((client) =>
      getClientDisplayName(client).trim().toLowerCase() === name.toLowerCase()
    );
    const selectedClient = existingClient || await handleAddClient({ name });
    const selectedClientName = selectedClient ? getClientDisplayName(selectedClient) : name;

    setCurrentTransaction((prev) => {
      if (activeTab === 'income') {
        return {
          ...prev,
          clientId: selectedClient?.id || '',
          clientName: selectedClientName,
          clientAllocations: []
        };
      }

      const allocationIds = [
        ...getTransactionClientAllocations(prev, clientById, 0).map((allocation) => allocation.clientId),
        selectedClient?.id
      ].filter(Boolean);
      const clientAllocations = buildClientAllocationsFromIds(allocationIds);
      const primaryAllocation = clientAllocations[0];
      return {
        ...prev,
        clientId: primaryAllocation?.clientId || '',
        clientName: primaryAllocation?.clientName || '',
        clientAllocations,
        allocateToClient: clientAllocations.length > 0
      };
    });
  }, [
    activeTab,
    buildClientAllocationsFromIds,
    clientById,
    clientOptions,
    currentTransaction.clientName,
    currentTransaction.description,
    handleAddClient
  ]);
  const handleQuickAddManualSupplier = useCallback(() => {
    const defaultName = String(currentTransaction.supplierName || currentTransaction.description || '').trim();
    const enteredName = window.prompt('Supplier name', defaultName);
    const name = String(enteredName || '').trim();
    if (!name) return;

    const availableSuppliers = rctSubcontractorOptions.filter((supplier) => supportsRct || supplier.supplierType !== 'rct');
    const existingSupplier = availableSuppliers.find((supplier) =>
      String(supplier?.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    const supplierId = existingSupplier?.id || upsertRctSubcontractor({
      name,
      supplierType: 'standard',
      rctDeductionRate: 0
    });

    setCurrentTransaction((prev) => ({
      ...prev,
      supplierId: supplierId || '',
      supplierName: existingSupplier?.name || name,
      description: prev.description || existingSupplier?.name || name
    }));
  }, [
    currentTransaction.description,
    currentTransaction.supplierName,
    rctSubcontractorOptions,
    supportsRct,
    upsertRctSubcontractor
  ]);
  const handleUpdateClient = useCallback(async (clientId, rawUpdates) => {
    if (!clientId) return null;
    setClients((prev) => {
      const existingClients = Array.isArray(prev) ? prev : [];
      const existingClient = existingClients.find((client) => client.id === clientId);
      if (!existingClient) return existingClients;

      const normalizedClient = normalizeClientRecord({
        ...existingClient,
        ...rawUpdates,
        id: clientId,
        createdAt: existingClient.createdAt,
        updatedAt: new Date().toISOString()
      });

      return mergeClientLists(
        existingClients.filter((client) => client.id !== clientId),
        [normalizedClient]
      );
    });
    return clientId;
  }, []);
  const handleDeleteClient = useCallback(async (clientId) => {
    if (!clientId) return;
    setClients((prev) => (Array.isArray(prev) ? prev.filter((client) => client.id !== clientId) : []));
  }, []);
  const handleImportClients = useCallback(async (incomingClients = []) => {
    const normalizedIncoming = mergeClientLists([], incomingClients);
    const existingClients = Array.isArray(clientOptions) ? clientOptions : [];
    const existingKeys = new Set(existingClients.map((client) => getClientDedupeKey(client)));

    let importedCount = 0;
    let updatedCount = 0;
    normalizedIncoming.forEach((client) => {
      if (existingKeys.has(getClientDedupeKey(client))) {
        updatedCount += 1;
      } else {
        importedCount += 1;
      }
    });

    setClients((prev) => mergeClientLists(prev, normalizedIncoming));

    return {
      importedCount,
      updatedCount
    };
  }, [clientOptions, getClientDedupeKey]);
  const handleStartSaleForClient = (client) => {
    const selectedClientName = getClientDisplayName(client);
    setActiveTab('income');
    setSelectedInputMethod('manual');
    setIsAddingTransaction(true);
    resetSplitSaleState();
    resetSplitPaymentState();
    setCurrentTransaction(createEmptyTransactionDraft('income', {
      category: filteredIncomeCategories[0]?.id || 'nail_services',
      clientId: client?.id || '',
      clientName: selectedClientName,
      paymentMethod: 'cash'
    }));
  };

  const handleStartInvoiceForClient = (client) => {
    const selectedClientName = getClientDisplayName(client);
    setActiveTab('income');
    setSelectedInputMethod('manual');
    setIsAddingTransaction(true);
    resetSplitSaleState();
    resetSplitPaymentState();
    setCurrentTransaction(createEmptyTransactionDraft('income', {
      category: filteredIncomeCategories[0]?.id || 'nails',
      clientId: client?.id || '',
      clientName: selectedClientName,
      description: 'Invoice',
      paymentMethod: 'unpaid',
      paymentStatus: 'unpaid',
      amountPaid: 0,
      outstandingAmount: 0
    }));
  };
  const categoryMetaLookup = useMemo(() => {
    const lookup = {};
    [...(mergedCategories.income || []), ...(mergedCategories.expenses || [])].forEach((category) => {
      if (!category?.id) return;
      lookup[category.id] = {
        name: category.name || category.id,
        icon: category.icon || ''
      };
    });
    return lookup;
  }, [mergedCategories.income, mergedCategories.expenses]);
  const getCategoryDisplayLabel = useCallback((categoryId) => {
    if (!categoryId) return 'Uncategorized';
    if (categoryMetaLookup[categoryId]?.name) {
      return categoryMetaLookup[categoryId].name;
    }
    return String(categoryId)
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (match) => match.toUpperCase());
  }, [categoryMetaLookup]);
  const getCategoryIconForId = useCallback((categoryId) => {
    if (!categoryId) return '';
    return categoryMetaLookup[categoryId]?.icon || '';
  }, [categoryMetaLookup]);

  const normalizePaymentMethodId = useCallback((methodId) => {
    const rawMethod = String(methodId || '').trim().toLowerCase();
    if (!rawMethod) return '';

    const aliases = {
      bank_transfer: 'transfer',
      banktransfer: 'transfer',
      'bank transfer': 'transfer',
      gift_voucher: 'voucher',
      giftvoucher: 'voucher',
      'gift voucher': 'voucher'
    };

    return aliases[rawMethod] || rawMethod;
  }, []);

  const getPaymentMethodLabel = useCallback((methodId) => {
    const normalizedMethod = normalizePaymentMethodId(methodId);
    const paymentMethod = PAYMENT_METHODS.find((entry) => entry.id === normalizedMethod);
    if (paymentMethod?.name) return paymentMethod.name;
    if (!normalizedMethod) return 'Unknown';
    return normalizedMethod
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (match) => match.toUpperCase());
  }, [normalizePaymentMethodId]);

  const getTransactionPaymentEntries = useCallback((transaction) => {
    if (!transaction) return [];

    const groupedEntries = new Map();
    const pushEntry = (methodId, amountValue) => {
      const method = normalizePaymentMethodId(methodId);
      const amount = Number(amountValue);
      if (!method || !Number.isFinite(amount) || amount <= 0) return;
      groupedEntries.set(method, (groupedEntries.get(method) || 0) + amount);
    };

    if (Array.isArray(transaction.paymentBreakdown) && transaction.paymentBreakdown.length > 0) {
      transaction.paymentBreakdown.forEach((entry) => {
        pushEntry(entry?.method, entry?.amount);
      });
    }

    if (groupedEntries.size === 0) {
      const fallbackAmount = Number(transaction.amount) || 0;
      if (fallbackAmount > 0) {
        pushEntry(transaction.paymentMethod || 'cash', fallbackAmount);
      }
    }

    return Array.from(groupedEntries.entries())
      .map(([method, amount]) => ({
        method,
        amount: parseFloat(amount.toFixed(2))
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [normalizePaymentMethodId]);

  const getTransactionPaidAmount = useCallback((transaction) => {
    if (!transaction) return 0;

    const hasExplicitPaidAmount =
      transaction.amountPaid !== undefined &&
      transaction.amountPaid !== null &&
      String(transaction.amountPaid).trim() !== '';
    const explicitPaidAmount = Number(transaction.amountPaid);
    if (hasExplicitPaidAmount && Number.isFinite(explicitPaidAmount) && explicitPaidAmount >= 0) {
      return parseFloat(explicitPaidAmount.toFixed(2));
    }

    if (Array.isArray(transaction.paymentBreakdown) && transaction.paymentBreakdown.length > 0) {
      const breakdownTotal = transaction.paymentBreakdown.reduce((sum, entry) => {
        const amount = Number(entry?.amount);
        return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
      }, 0);
      return parseFloat(breakdownTotal.toFixed(2));
    }

    const fallbackAmount = Number(transaction.amount);
    return Number.isFinite(fallbackAmount) && fallbackAmount > 0
      ? parseFloat(fallbackAmount.toFixed(2))
      : 0;
  }, []);

  const getTransactionSaleTotalAmount = useCallback((transaction) => {
    if (!transaction) return 0;

    const explicitSaleTotal = Number(transaction.saleTotalAmount ?? transaction.originalSaleAmount);
    if (Number.isFinite(explicitSaleTotal) && explicitSaleTotal > 0) {
      return parseFloat(explicitSaleTotal.toFixed(2));
    }

    const grossAmount = Number(transaction.grossAmount);
    if ((transaction.isPartPaymentSale || Number(transaction.outstandingAmount) > 0) && Number.isFinite(grossAmount) && grossAmount > 0) {
      return parseFloat(grossAmount.toFixed(2));
    }

    const fallbackAmount = Number(transaction.amount);
    return Number.isFinite(fallbackAmount) && fallbackAmount > 0
      ? parseFloat(fallbackAmount.toFixed(2))
      : 0;
  }, []);

  const getTransactionOutstandingAmount = useCallback((transaction) => {
    if (!transaction) return 0;

    const explicitOutstandingAmount = Number(transaction.outstandingAmount);
    if (Number.isFinite(explicitOutstandingAmount) && explicitOutstandingAmount > 0) {
      return parseFloat(explicitOutstandingAmount.toFixed(2));
    }

    const totalAmount = getTransactionSaleTotalAmount(transaction);
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) return 0;

    const outstandingAmount = totalAmount - getTransactionPaidAmount(transaction);
    return parseFloat(Math.max(outstandingAmount, 0).toFixed(2));
  }, [getTransactionPaidAmount, getTransactionSaleTotalAmount]);

  const handleDownloadInvoice = useCallback((transaction) => {
    if (!transaction || String(transaction.type || '').toLowerCase() !== 'income') {
      return;
    }

    const linkedClient = transaction.clientId ? clientById.get(transaction.clientId) : null;
    const client = linkedClient
      ? {
          ...linkedClient,
          name: getClientDisplayName(linkedClient)
        }
      : {
          name: transaction.clientName || transaction.customerName || ''
        };

    downloadInvoiceHtml({
      transaction: {
        ...transaction,
        outstandingAmount: getTransactionOutstandingAmount(transaction),
        saleTotalAmount: getTransactionSaleTotalAmount(transaction),
        amountPaid: getTransactionPaidAmount(transaction)
      },
      client,
      businessProfile: buildBusinessProfileSettings(businessType, industryId, businessProfileDetails),
      currencySymbol: currency?.symbol || currentCountry?.currencySymbol || document.documentElement.dataset.currencySymbol || '€',
      taxName: countryTerminology.taxName,
      dateLocale: dateDisplayLocale
    });
  }, [
    businessProfileDetails,
    businessType,
    clientById,
    countryTerminology.taxName,
    currency?.symbol,
    currentCountry?.currencySymbol,
    dateDisplayLocale,
    getTransactionOutstandingAmount,
    getTransactionPaidAmount,
    getTransactionSaleTotalAmount,
    industryId
  ]);

  const getTransactionExpenseTotalAmount = useCallback((transaction) => {
    if (!transaction) return 0;
    const amount = Number(
      transaction.rctGrossAmount ??
      transaction.expenseTotalAmount ??
      transaction.billTotalAmount ??
      transaction.invoiceTotalAmount ??
      transaction.grossAmount ??
      transaction.amount
    );
    return Number.isFinite(amount) && amount > 0 ? parseFloat(amount.toFixed(2)) : 0;
  }, []);

  const getSupplierInvoiceAmounts = useCallback((transaction) => {
    const gross = getTransactionExpenseTotalAmount(transaction);
    const rctDue = Number(transaction?.rctTaxDueAmount ?? transaction?.rctDeductionAmount ?? 0) || 0;
    const rctPaidToRevenue = Number(transaction?.rctPaidToRevenueAmount ?? transaction?.rctTaxPaidAmount ?? 0) || 0;
    const netPayable = Number(transaction?.rctNetPayable ?? 0) > 0
      ? Number(transaction.rctNetPayable)
      : Math.max(gross - rctDue, 0);
    const supplierPaid = Number(transaction?.supplierPaidAmount ?? transaction?.amountPaid ?? transaction?.amount ?? 0) || 0;
    const netOutstanding = Math.max(netPayable - supplierPaid, 0);
    const isRctPrincipal = transaction?.rctRole === 'principal' || transaction?.rctReverseCharge === true;
    const rctOutstanding = isRctPrincipal ? Math.max(rctDue - rctPaidToRevenue, 0) : 0;
    const totalOutstanding = isRctPrincipal
      ? netOutstanding + rctOutstanding
      : Math.max(gross - supplierPaid, 0);

    return {
      gross: parseFloat(gross.toFixed(2)),
      netPayable: parseFloat(netPayable.toFixed(2)),
      supplierPaid: parseFloat(Math.min(supplierPaid, netPayable || gross).toFixed(2)),
      netOutstanding: parseFloat(netOutstanding.toFixed(2)),
      rctDue: parseFloat(rctDue.toFixed(2)),
      rctPaidToRevenue: parseFloat(Math.min(rctPaidToRevenue, rctDue).toFixed(2)),
      rctOutstanding: parseFloat(rctOutstanding.toFixed(2)),
      totalOutstanding: parseFloat(totalOutstanding.toFixed(2)),
      isRctPrincipal
    };
  }, [getTransactionExpenseTotalAmount]);

  const projectSummaries = useMemo(() => {
    const markupRate = Number(projectPricingMarkup);
    const safeMarkupRate = Number.isFinite(markupRate) && markupRate >= 0 ? markupRate / 100 : 0;
    const summariesByKey = new Map();

    const getOrCreateSummary = (transaction, allocation = null) => {
      const jobReference = String(transaction?.jobReference || '').trim() || 'General / no job reference';
      const clientId = allocation?.clientId || transaction?.clientId || '';
      const linkedClient = clientId ? clientById.get(clientId) : null;
      const clientName =
        allocation?.clientName ||
        transaction?.clientName ||
        (linkedClient ? getClientDisplayName(linkedClient) : '') ||
        (clientId ? 'Linked client' : 'No client linked');

      if (!clientId && !allocation?.clientName && !transaction?.clientName && !String(transaction?.jobReference || '').trim()) {
        return null;
      }

      const key = `${clientId || clientName.toLowerCase()}::${jobReference.toLowerCase()}`;
      if (!summariesByKey.has(key)) {
        summariesByKey.set(key, {
          key,
          clientId,
          clientName,
          jobReference,
          transactions: [],
          incomeCount: 0,
          expenseCount: 0,
          invoiceTotal: 0,
          cashReceived: 0,
          outstandingIncome: 0,
          costTotal: 0,
          billableCostTotal: 0,
          cashPaid: 0,
          unpaidCosts: 0,
          rctCostTotal: 0,
          categories: new Map(),
          suppliers: new Set()
        });
      }

      return summariesByKey.get(key);
    };

    (Array.isArray(transactions) ? transactions : []).forEach((transaction) => {
      const type = String(transaction?.type || '').toLowerCase();

      if (type === 'income') {
        const summary = getOrCreateSummary(transaction);
        if (!summary) return;
        summary.transactions.push(transaction);

        const isInstallment = !!transaction.isPartPaymentInstallment;
        const invoiceTotal = isInstallment ? 0 : getTransactionSaleTotalAmount(transaction);
        const paidAmount = getTransactionPaidAmount(transaction);
        const outstandingAmount = isInstallment ? 0 : getTransactionOutstandingAmount(transaction);

        summary.incomeCount += isInstallment ? 0 : 1;
        summary.invoiceTotal += invoiceTotal;
        summary.cashReceived += paidAmount;
        summary.outstandingIncome += outstandingAmount;
      } else if (type === 'expenses' || type === 'expense') {
        const costAmount = getTransactionExpenseTotalAmount(transaction);
        const paidAmount = Number(transaction.amount) || 0;
        const unpaidAmount = Math.max(costAmount - paidAmount, 0);
        const categoryLabel = getCategoryDisplayLabel(transaction.category);
        const supplierName = transaction.supplierName || transaction.vendor || transaction.description || '';
        const allocations = getTransactionClientAllocations(transaction, clientById, costAmount);
        const summaryAllocations = allocations.length > 0
          ? allocations
          : [{ clientId: '', clientName: '', allocatedAmount: costAmount, allocationPercent: 1 }];

        summaryAllocations.forEach((allocation) => {
          const summary = getOrCreateSummary(transaction, allocation);
          if (!summary) return;
          const allocatedCostAmount = Number(allocation.allocatedAmount) || 0;
          const allocatedPaidAmount = parseFloat(((paidAmount > 0 ? paidAmount : 0) * (allocation.allocationPercent || 1)).toFixed(2));
          const allocatedUnpaidAmount = parseFloat((unpaidAmount * (allocation.allocationPercent || 1)).toFixed(2));

          summary.transactions.push(transaction);
          summary.expenseCount += 1;
          summary.costTotal += allocatedCostAmount;
          summary.cashPaid += allocatedPaidAmount;
          summary.unpaidCosts += allocatedUnpaidAmount;
          if (transaction.billableToClient || transaction.allocateToClient) {
            summary.billableCostTotal += allocatedCostAmount;
          }
          if (transaction.rctRole === 'principal' || transaction.rctReverseCharge) {
            summary.rctCostTotal += allocatedCostAmount;
          }
          if (categoryLabel) {
            summary.categories.set(categoryLabel, (summary.categories.get(categoryLabel) || 0) + allocatedCostAmount);
          }
          if (supplierName) {
            summary.suppliers.add(supplierName);
          }
        });
      }
    });

    return Array.from(summariesByKey.values())
      .map((summary) => {
        const suggestedCharge = parseFloat((summary.costTotal * (1 + safeMarkupRate)).toFixed(2));
        const profit = parseFloat((summary.invoiceTotal - summary.costTotal).toFixed(2));
        const margin = summary.invoiceTotal > 0 ? (profit / summary.invoiceTotal) * 100 : null;
        return {
          ...summary,
          invoiceTotal: parseFloat(summary.invoiceTotal.toFixed(2)),
          cashReceived: parseFloat(summary.cashReceived.toFixed(2)),
          outstandingIncome: parseFloat(summary.outstandingIncome.toFixed(2)),
          costTotal: parseFloat(summary.costTotal.toFixed(2)),
          billableCostTotal: parseFloat(summary.billableCostTotal.toFixed(2)),
          cashPaid: parseFloat(summary.cashPaid.toFixed(2)),
          unpaidCosts: parseFloat(summary.unpaidCosts.toFixed(2)),
          rctCostTotal: parseFloat(summary.rctCostTotal.toFixed(2)),
          suggestedCharge,
          profit,
          margin,
          categoryBreakdown: Array.from(summary.categories.entries())
            .map(([label, amount]) => ({ label, amount: parseFloat(amount.toFixed(2)) }))
            .sort((a, b) => b.amount - a.amount),
          supplierNames: Array.from(summary.suppliers).slice(0, 4)
        };
      })
      .sort((a, b) => {
        const outstandingDelta = (b.outstandingIncome + b.unpaidCosts) - (a.outstandingIncome + a.unpaidCosts);
        if (Math.abs(outstandingDelta) > 0.01) return outstandingDelta;
        return (b.costTotal + b.invoiceTotal) - (a.costTotal + a.invoiceTotal);
      });
  }, [
    clientById,
    getCategoryDisplayLabel,
    getTransactionExpenseTotalAmount,
    getTransactionOutstandingAmount,
    getTransactionPaidAmount,
    getTransactionSaleTotalAmount,
    projectPricingMarkup,
    transactions
  ]);

  const handleStartInvoiceForProject = (project) => {
    if (!project) return;
    const amountToCharge = Math.max(project.suggestedCharge - project.invoiceTotal, 0) || project.suggestedCharge || '';
    setActiveTab('income');
    setSelectedInputMethod('manual');
    setIsAddingTransaction(true);
    resetSplitSaleState();
    resetSplitPaymentState();
    setCurrentTransaction(createEmptyTransactionDraft('income', {
      category: filteredIncomeCategories[0]?.id || 'nails',
      clientId: project.clientId || '',
      clientName: project.clientName && project.clientName !== 'No client linked' ? project.clientName : '',
      jobReference: project.jobReference === 'General / no job reference' ? '' : project.jobReference,
      description: project.jobReference === 'General / no job reference' ? 'Project invoice' : `${project.jobReference} invoice`,
      amount: amountToCharge ? String(amountToCharge) : '',
      saleTotalAmount: amountToCharge || null,
      paymentMethod: 'unpaid',
      paymentStatus: 'unpaid',
      amountPaid: 0,
      outstandingAmount: amountToCharge || 0
    }));
  };

  const rctTracker = useMemo(() => {
    const tracker = {
      principalGrossInvoices: 0,
      principalNetPayable: 0,
      principalRctDue: 0,
      rctPaidToRevenue: 0,
      rctBalanceDue: 0,
      subcontractorGrossInvoices: 0,
      subcontractorNetReceived: 0,
      subcontractorRctCredits: 0,
      subcontractorRctCreditsOutstanding: 0,
      principalRows: [],
      subcontractorRows: [],
      paymentRows: [],
      supplierSummaries: []
    };
    const supplierSummaryMap = new Map();
    const linkedRctPaymentsByInvoiceId = new Map();

    (Array.isArray(transactions) ? transactions : []).forEach((transaction) => {
      const categoryId = normalizeCategoryId(transaction?.category || '');
      const type = String(transaction?.type || '').toLowerCase();
      const linkedInvoiceId = transaction?.linkedRctInvoiceId || transaction?.linkedRctSupplierInvoiceId;
      if ((type === 'expenses' || type === 'expense') && categoryId === 'rct_tax' && linkedInvoiceId) {
        const paid = getTransactionPaidAmount(transaction) || Number(transaction.amount) || 0;
        linkedRctPaymentsByInvoiceId.set(
          linkedInvoiceId,
          (linkedRctPaymentsByInvoiceId.get(linkedInvoiceId) || 0) + paid
        );
      }
    });

    const getSupplierSummary = (transaction) => {
      const supplierName = transaction?.rctSubcontractorName || transaction?.supplierName || transaction?.vendor || transaction?.description || 'Unknown supplier';
      const supplierId = transaction?.rctSubcontractorId || transaction?.supplierId || supplierName.toLowerCase();
      if (!supplierSummaryMap.has(supplierId)) {
        supplierSummaryMap.set(supplierId, {
          id: supplierId,
          name: supplierName,
          grossInvoices: 0,
          netPayable: 0,
          paidToSupplier: 0,
          netOutstanding: 0,
          rctDue: 0,
          rctPaidToRevenue: 0,
          rctOutstanding: 0,
          invoiceRows: []
        });
      }
      return supplierSummaryMap.get(supplierId);
    };

    (Array.isArray(transactions) ? transactions : []).forEach((transaction) => {
      const categoryId = normalizeCategoryId(transaction?.category || '');
      const type = String(transaction?.type || '').toLowerCase();

      if (transaction?.rctRole === 'principal' || transaction?.rctReverseCharge) {
        const linkedRctPaid = linkedRctPaymentsByInvoiceId.get(transaction.id) || 0;
        const storedRctPaid = Number(transaction.rctPaidToRevenueAmount ?? transaction.rctTaxPaidAmount ?? 0) || 0;
        const amounts = getSupplierInvoiceAmounts({
          ...transaction,
          rctPaidToRevenueAmount: Math.max(storedRctPaid, linkedRctPaid)
        });
        const gross = amounts.gross;
        const net = amounts.netPayable;
        const due = amounts.rctDue;
        tracker.principalGrossInvoices += gross;
        tracker.principalNetPayable += net;
        tracker.principalRctDue += due;
        const supplierSummary = getSupplierSummary(transaction);
        supplierSummary.grossInvoices += gross;
        supplierSummary.netPayable += net;
        supplierSummary.paidToSupplier += amounts.supplierPaid;
        supplierSummary.netOutstanding += amounts.netOutstanding;
        supplierSummary.rctDue += due;
        supplierSummary.rctPaidToRevenue += amounts.rctPaidToRevenue;
        supplierSummary.rctOutstanding += amounts.rctOutstanding;
        tracker.principalRows.push({
          id: transaction.id,
          date: transaction.date,
          name: transaction.rctSubcontractorName || transaction.supplierName || transaction.vendor || transaction.description || 'RCT subcontractor',
          gross,
          net,
          paid: amounts.supplierPaid,
          netOutstanding: amounts.netOutstanding,
          due,
          rctPaidToRevenue: amounts.rctPaidToRevenue,
          rctOutstanding: amounts.rctOutstanding,
          jobReference: transaction.jobReference || ''
        });
        supplierSummary.invoiceRows.push({
          id: transaction.id,
          date: transaction.date,
          jobReference: transaction.jobReference || '',
          gross,
          net,
          paid: amounts.supplierPaid,
          netOutstanding: amounts.netOutstanding,
          rctDue: amounts.rctDue,
          rctPaidToRevenue: amounts.rctPaidToRevenue,
          rctOutstanding: amounts.rctOutstanding,
          paymentStatus: transaction.paymentStatus || ''
        });
      } else if ((type === 'expenses' || type === 'expense') && categoryId !== 'rct_tax') {
        const amounts = getSupplierInvoiceAmounts(transaction);
        if (amounts.gross > 0 && (transaction.supplierId || transaction.supplierName || transaction.vendor)) {
          const supplierSummary = getSupplierSummary(transaction);
          supplierSummary.grossInvoices += amounts.gross;
          supplierSummary.netPayable += amounts.netPayable;
          supplierSummary.paidToSupplier += amounts.supplierPaid;
          supplierSummary.netOutstanding += amounts.netOutstanding;
          supplierSummary.invoiceRows.push({
            id: transaction.id,
            date: transaction.date,
            jobReference: transaction.jobReference || '',
            gross: amounts.gross,
            net: amounts.netPayable,
            paid: amounts.supplierPaid,
            netOutstanding: amounts.netOutstanding,
            rctDue: 0,
            rctPaidToRevenue: 0,
            rctOutstanding: 0,
            paymentStatus: transaction.paymentStatus || ''
          });
        }
      }

      if (transaction?.rctRole === 'subcontractor' || Number(transaction?.rctCreditAmount) > 0) {
        const gross = Number(transaction.rctGrossAmount ?? transaction.saleTotalAmount ?? transaction.invoiceTotalAmount ?? 0) || 0;
        const expectedCredit = Number(transaction.rctDeductionAmount ?? 0) || 0;
        const credit = Number(transaction.rctCreditAmount ?? 0) || 0;
        const netReceived = Number(transaction.rctNetReceivable ?? transaction.amount ?? 0) || 0;
        const netOutstanding = Math.max(gross - netReceived - credit, 0);
        tracker.subcontractorGrossInvoices += gross;
        tracker.subcontractorNetReceived += netReceived;
        tracker.subcontractorRctCredits += credit;
        tracker.subcontractorRctCreditsOutstanding += Math.max(expectedCredit - credit, 0);
        tracker.subcontractorRows.push({
          id: transaction.id,
          date: transaction.date,
          name: transaction.clientName || transaction.description || 'Principal contractor',
          gross,
          netReceived,
          netOutstanding,
          expectedCredit,
          credit,
          outstandingCredit: Math.max(expectedCredit - credit, 0),
          jobReference: transaction.jobReference || ''
        });
      }

      if ((type === 'expenses' || type === 'expense') && categoryId === 'rct_tax') {
        const paid = getTransactionPaidAmount(transaction) || Number(transaction.amount) || 0;
        tracker.rctPaidToRevenue += paid;
        tracker.paymentRows.push({
          id: transaction.id,
          date: transaction.date,
          description: transaction.description || 'RCT payment to Revenue',
          amount: paid
        });
      }
    });

    tracker.principalGrossInvoices = parseFloat(tracker.principalGrossInvoices.toFixed(2));
    tracker.principalNetPayable = parseFloat(tracker.principalNetPayable.toFixed(2));
    tracker.principalRctDue = parseFloat(tracker.principalRctDue.toFixed(2));
    tracker.rctPaidToRevenue = parseFloat(tracker.rctPaidToRevenue.toFixed(2));
    tracker.subcontractorGrossInvoices = parseFloat(tracker.subcontractorGrossInvoices.toFixed(2));
    tracker.subcontractorNetReceived = parseFloat(tracker.subcontractorNetReceived.toFixed(2));
    tracker.subcontractorRctCredits = parseFloat(tracker.subcontractorRctCredits.toFixed(2));
    tracker.subcontractorRctCreditsOutstanding = parseFloat(tracker.subcontractorRctCreditsOutstanding.toFixed(2));
    tracker.supplierSummaries = Array.from(supplierSummaryMap.values())
      .map((summary) => ({
        ...summary,
        grossInvoices: parseFloat(summary.grossInvoices.toFixed(2)),
        netPayable: parseFloat(summary.netPayable.toFixed(2)),
        paidToSupplier: parseFloat(summary.paidToSupplier.toFixed(2)),
        netOutstanding: parseFloat(summary.netOutstanding.toFixed(2)),
        rctDue: parseFloat(summary.rctDue.toFixed(2)),
        rctPaidToRevenue: parseFloat(summary.rctPaidToRevenue.toFixed(2)),
        rctOutstanding: parseFloat(summary.rctOutstanding.toFixed(2))
      }))
      .sort((a, b) => (b.netOutstanding + b.rctOutstanding) - (a.netOutstanding + a.rctOutstanding));
    tracker.rctBalanceDue = parseFloat(tracker.supplierSummaries
      .reduce((sum, summary) => sum + (Number(summary.rctOutstanding) || 0), 0)
      .toFixed(2));

    return tracker;
  }, [getSupplierInvoiceAmounts, getTransactionPaidAmount, transactions]);

  const handleStartRctRevenuePayment = () => {
    const amountDue = rctTracker.rctBalanceDue || 0;
    setActiveTab('expenses');
    setSelectedInputMethod('manual');
    setIsAddingTransaction(true);
    resetSplitSaleState();
    resetSplitPaymentState();
    setCurrentTransaction(createEmptyTransactionDraft('expenses', {
      category: 'rct_tax',
      description: 'RCT payment to Revenue',
      amount: amountDue ? String(amountDue) : '',
      paymentMethod: 'transfer',
      paymentStatus: 'paid',
      amountPaid: amountDue || null,
      outstandingAmount: 0
    }));
  };

  const handleRecordSupplierPayment = async (transactionId, payment = {}) => {
    const transaction = transactions.find((entry) => entry.id === transactionId);
    if (!transaction) return;

    const amounts = getSupplierInvoiceAmounts(transaction);
    if (amounts.netOutstanding <= 0.01) {
      alert('This supplier invoice has no net supplier balance outstanding.');
      return;
    }

    const paymentAmount = Number(payment.amount);
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      alert('Enter a payment amount greater than 0.');
      return;
    }

    if (paymentAmount - amounts.netOutstanding > 0.01) {
      alert(`Payment cannot be more than the net supplier balance outstanding (${formatCurrency(amounts.netOutstanding)}).`);
      return;
    }

    const method = normalizePaymentMethodId(payment.method || transaction.paymentMethod || 'transfer') || 'transfer';
    const paymentDate = payment.date || new Date().toISOString().split('T')[0];
    const paymentDateValue = new Date(paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      alert('Enter a valid payment date.');
      return;
    }

    try {
      if (!ensureDrivePermissionBeforeTransactionChange('recording a supplier payment')) {
        return;
      }

      const roundedPaymentAmount = parseFloat(paymentAmount.toFixed(2));
      const paidAfter = parseFloat(Math.min(amounts.netPayable, amounts.supplierPaid + roundedPaymentAmount).toFixed(2));
      const nextNetOutstanding = parseFloat(Math.max(amounts.netPayable - paidAfter, 0).toFixed(2));
      const nextTotalOutstanding = parseFloat((nextNetOutstanding + amounts.rctOutstanding).toFixed(2));

      const updatedOriginalTransactions = transactions.map((entry) => {
        if (entry.id !== transactionId) return entry;
        const existingBreakdown = Array.isArray(entry.paymentBreakdown)
          ? entry.paymentBreakdown
              .map((paymentEntry) => ({
                method: normalizePaymentMethodId(paymentEntry?.method || method) || method,
                amount: Number(paymentEntry?.amount) || 0,
                paidAt: paymentEntry?.paidAt || null
              }))
              .filter((paymentEntry) => paymentEntry.method && paymentEntry.amount > 0)
          : [];

        existingBreakdown.push({
          method,
          amount: roundedPaymentAmount,
          paidAt: paymentDate
        });

        return {
          ...entry,
          amount: paidAfter,
          amountPaid: paidAfter,
          supplierPaidAmount: paidAfter,
          paymentMethod: method,
          paymentBreakdown: existingBreakdown,
          outstandingAmount: nextTotalOutstanding,
          paymentStatus: nextTotalOutstanding > 0.01 ? 'part_paid' : 'paid',
          isPartPaymentExpense: nextTotalOutstanding > 0.01,
          updatedAt: new Date().toISOString()
        };
      });

      const paymentRecord = {
        id: Date.now(),
        type: 'supplier_payment',
        date: paymentDate,
        amount: roundedPaymentAmount,
        grossAmount: roundedPaymentAmount,
        category: transaction.category || '',
        description: `Supplier payment for ${transaction.rctSubcontractorName || transaction.supplierName || transaction.vendor || transaction.description || 'invoice'}`,
        paymentMethod: method,
        paymentBreakdown: [{ method, amount: roundedPaymentAmount, paidAt: paymentDate }],
        supplierId: transaction.supplierId || transaction.rctSubcontractorId || '',
        supplierName: transaction.supplierName || transaction.rctSubcontractorName || transaction.vendor || '',
        rctSubcontractorId: transaction.rctSubcontractorId || '',
        rctSubcontractorName: transaction.rctSubcontractorName || '',
        linkedSupplierInvoiceId: transaction.id,
        vatRate: null,
        vatAmount: null,
        vatComponents: [],
        invoiceVatAmount: null,
        invoiceVatComponents: [],
        paymentStatus: 'paid',
        outstandingAmount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const updatedTransactions = sortTransactionsByEntryDateDesc([
        ...updatedOriginalTransactions,
        paymentRecord
      ]);

      setTransactions(updatedTransactions);
      await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);
      setSupplierPaymentDrafts((prev) => {
        const next = { ...prev };
        delete next[transactionId];
        return next;
      });
      setSuccessMessage('Supplier payment recorded.');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error recording supplier payment:', error);
      alert('Failed to record supplier payment. Please try again.');
    }
  };

  const handleRecordRctPayment = async (transactionId, payment = {}) => {
    const transaction = transactions.find((entry) => entry.id === transactionId);
    if (!transaction) return;

    const amounts = getSupplierInvoiceAmounts(transaction);
    if (!amounts.isRctPrincipal || amounts.rctOutstanding <= 0.01) {
      alert('This supplier invoice has no RCT balance outstanding.');
      return;
    }

    const paymentAmount = Number(payment.amount);
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      alert('Enter an RCT payment amount greater than 0.');
      return;
    }

    if (paymentAmount - amounts.rctOutstanding > 0.01) {
      alert(`RCT payment cannot be more than the RCT balance outstanding (${formatCurrency(amounts.rctOutstanding)}).`);
      return;
    }

    const method = normalizePaymentMethodId(payment.method || 'transfer') || 'transfer';
    const paymentDate = payment.date || new Date().toISOString().split('T')[0];
    const paymentDateValue = new Date(paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      alert('Enter a valid RCT payment date.');
      return;
    }

    try {
      if (!ensureDrivePermissionBeforeTransactionChange('recording an RCT payment to Revenue')) {
        return;
      }

      const roundedPaymentAmount = parseFloat(paymentAmount.toFixed(2));
      const rctPaidAfter = parseFloat(Math.min(amounts.rctDue, amounts.rctPaidToRevenue + roundedPaymentAmount).toFixed(2));
      const nextRctOutstanding = parseFloat(Math.max(amounts.rctDue - rctPaidAfter, 0).toFixed(2));
      const nextTotalOutstanding = parseFloat((amounts.netOutstanding + nextRctOutstanding).toFixed(2));

      const updatedOriginalTransactions = transactions.map((entry) => {
        if (entry.id !== transactionId) return entry;
        const existingBreakdown = Array.isArray(entry.rctPaymentBreakdown)
          ? entry.rctPaymentBreakdown
              .map((paymentEntry) => ({
                method: normalizePaymentMethodId(paymentEntry?.method || method) || method,
                amount: Number(paymentEntry?.amount) || 0,
                paidAt: paymentEntry?.paidAt || null
              }))
              .filter((paymentEntry) => paymentEntry.method && paymentEntry.amount > 0)
          : [];

        existingBreakdown.push({
          method,
          amount: roundedPaymentAmount,
          paidAt: paymentDate
        });

        return {
          ...entry,
          rctPaidToRevenueAmount: rctPaidAfter,
          rctTaxPaidAmount: rctPaidAfter,
          rctPaymentBreakdown: existingBreakdown,
          outstandingAmount: nextTotalOutstanding,
          paymentStatus: nextTotalOutstanding > 0.01 ? 'part_paid' : 'paid',
          isPartPaymentExpense: nextTotalOutstanding > 0.01,
          updatedAt: new Date().toISOString()
        };
      });

      const supplierName = transaction.rctSubcontractorName || transaction.supplierName || transaction.vendor || transaction.description || 'subcontractor';
      const paymentRecord = {
        id: Date.now() + 1,
        type: 'expenses',
        date: paymentDate,
        amount: roundedPaymentAmount,
        grossAmount: roundedPaymentAmount,
        category: 'rct_tax',
        description: `RCT payment to Revenue for ${supplierName}`,
        paymentMethod: method,
        paymentBreakdown: [{ method, amount: roundedPaymentAmount, paidAt: paymentDate }],
        supplierId: transaction.supplierId || transaction.rctSubcontractorId || '',
        supplierName: transaction.supplierName || transaction.rctSubcontractorName || transaction.vendor || '',
        rctSubcontractorId: transaction.rctSubcontractorId || '',
        rctSubcontractorName: transaction.rctSubcontractorName || '',
        linkedRctInvoiceId: transaction.id,
        linkedRctSupplierInvoiceId: transaction.id,
        vatRate: null,
        vatAmount: null,
        vatComponents: [],
        invoiceVatAmount: null,
        invoiceVatComponents: [],
        paymentStatus: 'paid',
        outstandingAmount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const updatedTransactions = sortTransactionsByEntryDateDesc([
        ...updatedOriginalTransactions,
        paymentRecord
      ]);

      setTransactions(updatedTransactions);
      await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);
      setRctPaymentDrafts((prev) => {
        const next = { ...prev };
        delete next[transactionId];
        return next;
      });
      setSuccessMessage('RCT payment recorded.');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error recording RCT payment:', error);
      alert('Failed to record RCT payment. Please try again.');
    }
  };

  const getTransactionPaymentLabel = useCallback((transaction) => {
    const entries = getTransactionPaymentEntries(transaction);
    const outstandingAmount = getTransactionOutstandingAmount(transaction);
    if (!entries.length) {
      return getPaymentMethodLabel(transaction?.paymentMethod || 'cash');
    }

    if (entries.length === 1) {
      const methodLabel = getPaymentMethodLabel(entries[0].method);
      return outstandingAmount > 0.01
        ? `${methodLabel} (${formatCurrency(outstandingAmount)} outstanding)`
        : methodLabel;
    }

    const preview = entries
      .slice(0, 2)
      .map((entry) => `${getPaymentMethodLabel(entry.method)} ${formatCurrency(entry.amount)}`)
      .join(' + ');
    const remainingCount = entries.length - 2;
    const splitLabel = remainingCount > 0 ? `Split: ${preview} +${remainingCount} more` : `Split: ${preview}`;
    return outstandingAmount > 0.01
      ? `${splitLabel} (${formatCurrency(outstandingAmount)} outstanding)`
      : splitLabel;
  }, [formatCurrency, getPaymentMethodLabel, getTransactionOutstandingAmount, getTransactionPaymentEntries]);

  const isFoundationProduct = (name = '') => (name || '').toLowerCase().includes('foundation');
  const FOUNDATION_COVERAGE_USAGE = {
    light: 0.45,
    medium: 0.75,
    full: 1.1
  };

  const hasMeaningfulValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';
  const firstMeaningfulValue = (...values) => values.find((value) => hasMeaningfulValue(value));

  // Cost calculator helpers
  const getUnitCost = useCallback((item) => {
    const packCost = parseFloat(item.packCost) || 0;
    const packSize = parseFloat(item.packSize) || 0;
    const unitsPerPack = parseFloat(item.unitsPerPack) || 0;
    const divisor = packSize > 0 ? packSize : unitsPerPack;
    if (packCost > 0 && divisor > 0) {
      return packCost / divisor;
    }
    return parseFloat(item.unitCost) || 0;
  }, []);

  const handleCoveragePresetChange = (id, preset) => {
    updateCostItem(id, 'coveragePreset', preset);
    if (FOUNDATION_COVERAGE_USAGE[preset]) {
      updateCostItem(id, 'usagePerService', FOUNDATION_COVERAGE_USAGE[preset]);
    }
  };

  const addCostItem = () => {
    const newId = Date.now();
    setCostItems((prev) => [
      ...prev,
      { id: newId, name: '', unitCost: 0, packCost: '', packSize: '', unitsPerPack: '', usagePerService: 1, notes: '', unitType: 'piece', coveragePreset: '' }
    ]);
    setProductSearchTerms((prev) => ({ ...prev, [newId]: '' }));
  };

  const updateCostItem = (id, field, value) => {
    setCostItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleProductInput = (id, value) => {
    setProductSearchTerms((prev) => ({ ...prev, [id]: value }));
    updateCostItem(id, 'name', value);
    const matchedPreset = PRESET_PRODUCTS.find(
      (p) => p.name.toLowerCase() === (value || '').trim().toLowerCase()
    );
    const isFoundation = (value || '').toLowerCase().includes('foundation');
    if (isFoundation) {
      updateCostItem(id, 'unitType', 'ml');
      updateCostItem(id, 'coveragePreset', 'medium');
      updateCostItem(id, 'usagePerService', 0.75);
    }
    if (matchedPreset) {
      updateCostItem(id, 'packCost', matchedPreset.packCost || '');
      updateCostItem(id, 'unitsPerPack', matchedPreset.unitsPerPack || '');
      updateCostItem(id, 'usagePerService', matchedPreset.usagePerService || '');
    }
  };

  const removeCostItem = (id) => {
    setCostItems((prev) => prev.filter((item) => item.id !== id));
    setProductSearchTerms((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Keep cost items intact when reopening the modal (no automatic reset to avoid losing presets)
  useEffect(() => {
    if (!showCostModal) return;
  }, [showCostModal]);

  const pushCostItemsFromTransaction = (transaction, options = {}) => {
    const transactionItems = Array.isArray(transaction?.items) ? transaction.items : [];
    const hasExplicitLineItemSelection = transactionItems.some((item) => item?.saveToCost === true);
    const hasExplicitTransactionSelection = transaction?.saveToCost === true;

    if (!autoSaveCostItems && !hasExplicitLineItemSelection && !hasExplicitTransactionSelection) return;
    const transactionType = String(transaction?.type || '').toLowerCase();
    if (transactionType && transactionType !== 'expenses' && transactionType !== 'expense') return;

    const sourceLabel = options.sourceLabel || transaction?.vendor || transaction?.source || 'manual';
    const seenNames = new Set(
      (costItems || []).map((item) => (item?.name || '').trim().toLowerCase()).filter(Boolean)
    );

    const appendCostItems = (newItems) => {
      if (!newItems.length) return;
      setCostItems((prev) => {
        const existingNames = new Set(
          (prev || []).map((item) => (item?.name || '').trim().toLowerCase()).filter(Boolean)
        );
        const deduped = newItems.filter((item) => {
          const nameKey = (item?.name || '').trim().toLowerCase();
          if (!nameKey) return false;
          if (existingNames.has(nameKey)) return false;
          existingNames.add(nameKey);
          return true;
        });
        return deduped.length ? [...prev, ...deduped] : prev;
      });
    };

    if (transactionItems.length > 0) {
      const newCostItems = transactionItems
        .filter((item) => {
          if (!item) return false;
          if (autoSaveCostItems) {
            return item.saveToCost !== false;
          }
          return item.saveToCost === true;
        })
        .map((item, idx) => {
          if (typeof item === 'string') {
            return null;
          }
          const parsedPackDetails = parseLineItemPackDetails(
            String(item.baseDescription || item.description || item.item || item.name || '').trim()
          );
          const quantityRaw = parseFloat(item.quantity);
          const quantity = Number.isFinite(quantityRaw) && quantityRaw > 0 ? quantityRaw : 1;
          const amountRaw = parseFloat(item.amount);
          const amount = Number.isFinite(amountRaw) ? amountRaw : 0;
          const rawPackCost = item.packPrice ?? item.packCost ?? null;
          const explicitUnitsRaw = parseFloat(item.unitsPerPack ?? '');
          const explicitPackSizeRaw = parseFloat(item.packSize ?? '');
          const parsedUnitsRaw = parseFloat(parsedPackDetails.unitsPerPack ?? '');
          const baseUnitsRaw =
            Number.isFinite(explicitUnitsRaw) && explicitUnitsRaw > 0
              ? explicitUnitsRaw
              : (Number.isFinite(explicitPackSizeRaw) && explicitPackSizeRaw > 0
                ? explicitPackSizeRaw
                : parsedUnitsRaw);
          const baseUnits = Number.isFinite(baseUnitsRaw) && baseUnitsRaw > 0 ? baseUnitsRaw : 0;
          const unitsPerPack = baseUnits > 0
            ? (quantity > 1 ? baseUnits * quantity : baseUnits)
            : quantity;
          const packCost = rawPackCost !== null && rawPackCost !== '' && !Number.isNaN(parseFloat(rawPackCost))
            ? rawPackCost
            : (amount > 0 ? amount : '');
          const rawUnitPrice = item.unitPrice ?? item.unitCost ?? null;
          const unitCost = rawUnitPrice !== null && rawUnitPrice !== ''
            ? (parseFloat(rawUnitPrice) || 0)
            : (unitsPerPack > 0 && amount > 0 ? amount / unitsPerPack : 0);
          const resolvedPackType = firstMeaningfulValue(item.packType, parsedPackDetails.packType, '');
          const unitType = firstMeaningfulValue(item.unitType, parsedPackDetails.unitType, 'piece');
          const packNote = unitsPerPack ? `Pack: ${unitsPerPack}${resolvedPackType ? ` per ${resolvedPackType}` : ''}.` : '';
          const baseName = firstMeaningfulValue(
            item.baseDescription,
            parsedPackDetails.baseDescription,
            item.description,
            item.item,
            item.name,
            transaction.vendor,
            'Item'
          );
          const cleanedName = baseName.replace(/\s*\([^)]*\)\s*$/, '').trim() || baseName;
          const nameKey = cleanedName.trim().toLowerCase();
          if (!cleanedName || seenNames.has(nameKey)) {
            return null;
          }
          seenNames.add(nameKey);
          return {
            id: Date.now() + idx,
            name: cleanedName,
            unitCost: packCost && unitsPerPack ? '' : unitCost,
            packCost: packCost || '',
            unitsPerPack: unitsPerPack || '',
            usagePerService: item.usagePerService ?? 1,
            unitType,
            notes: [packNote, quantity > 1 ? `Qty: ${quantity}` : '', `From ${sourceLabel}`].filter(Boolean).join(' ').trim(),
          };
        })
        .filter(Boolean);
      appendCostItems(newCostItems);
      return;
    }

    const fallbackName = (
      transaction?.description ||
      transaction?.supplierName ||
      transaction?.vendor ||
      getCategoryDisplayLabel(transaction?.category || '') ||
      ''
    ).trim();
    const fallbackAmount = parseFloat(
      transaction?.expenseTotalAmount ??
      transaction?.billTotalAmount ??
      transaction?.invoiceTotalAmount ??
      transaction?.amount
    ) || 0;
    if (!fallbackName || fallbackAmount <= 0) return;

    const nameKey = fallbackName.toLowerCase();
    if (seenNames.has(nameKey)) return;

    appendCostItems([{
      id: Date.now(),
      name: fallbackName,
      unitCost: fallbackAmount,
      packCost: '',
      unitsPerPack: '',
      usagePerService: 1,
      unitType: 'piece',
      notes: `From ${sourceLabel}`.trim()
    }]);
  };

  const handleAddCategory = useCallback((type, suggestedName = '') => {
    const promptDefault = String(suggestedName || '').trim();
    const name = (window.prompt('Enter new category name:', promptDefault) || '').trim();
    if (!name) return null;
    const combinedCategories = [
      ...(CATEGORY_OPTIONS[type] || []),
      ...(extraCategories[type] || [])
    ];
    const existingByName = combinedCategories.find(
      (category) => String(category?.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (existingByName?.id) {
      return existingByName.id;
    }

    const baseId =
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || `category_${Date.now()}`;

    let nextId = baseId;
    let suffix = 2;
    const hasId = (candidateId, categories) =>
      (categories || []).some((category) => String(category?.id || '').toLowerCase() === candidateId.toLowerCase());

    while (hasId(nextId, combinedCategories)) {
      nextId = `${baseId}_${suffix}`;
      suffix += 1;
    }

    const newCat = { id: nextId, name, icon: '' };
    setExtraCategories((prev) => {
      const exists = (prev[type] || []).some((c) => c.name.toLowerCase() === name.toLowerCase());
      if (exists) return prev;
      return {
        ...prev,
        [type]: [...(prev[type] || []), newCat],
      };
    });
    return newCat.id;
  }, [extraCategories]);
  const handleDeleteCategory = useCallback((type, categoryId) => {
    const normalizedType = type === 'income' ? 'income' : 'expenses';
    const category = (mergedCategories[normalizedType] || []).find((entry) => entry.id === categoryId);
    if (!category?.id) return;

    const isCustomCategory = (extraCategories[normalizedType] || []).some((entry) => entry.id === categoryId);
    const hasTransactions = (transactions || []).some((transaction) => {
      const transactionType = String(transaction?.type || '').toLowerCase();
      const transactionCategory = normalizeCategoryId(transaction?.category || '');
      const expectedType = normalizedType === 'income' ? 'income' : 'expenses';
      const matchesType = transactionType === expectedType || (expectedType === 'expenses' && transactionType === 'expense');
      return matchesType && transactionCategory === normalizeCategoryId(categoryId);
    });
    const actionLabel = isCustomCategory ? 'delete' : 'hide';
    const historyNote = hasTransactions
      ? ' Existing transactions will keep this category on their records.'
      : '';

    if (!window.confirm(`${actionLabel === 'delete' ? 'Delete' : 'Hide'} "${category.name || category.id}" from ${normalizedType === 'income' ? 'sales' : 'expense'} category choices?${historyNote}`)) {
      return;
    }

    if (isCustomCategory) {
      setExtraCategories((prev) => {
        const normalized = normalizeExtraCategoriesShape(prev);
        return {
          ...normalized,
          [normalizedType]: (normalized[normalizedType] || []).filter((entry) => entry.id !== categoryId)
        };
      });
    } else {
      setHiddenCategories((prev) => {
        const current = Array.isArray(prev?.[normalizedType]) ? prev[normalizedType] : [];
        if (current.includes(categoryId)) return prev;
        return {
          income: Array.isArray(prev?.income) ? prev.income : [],
          expenses: Array.isArray(prev?.expenses) ? prev.expenses : [],
          [normalizedType]: [...current, categoryId]
        };
      });
    }

    if (normalizedType === 'income') {
      setHiddenServiceCategories((prev) => prev.filter((id) => id !== categoryId));
      setServicePrices((prev) => {
        if (!prev[categoryId]) return prev;
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
    }

    setCurrentTransaction((prev) => (
      prev.category === categoryId
        ? { ...prev, category: '', service: '', description: '', amount: '', discountAmount: '' }
        : prev
    ));
    setSelectedService(null);
  }, [
    extraCategories,
    mergedCategories,
    normalizeExtraCategoriesShape,
    transactions
  ]);
  const handleRestoreCategory = useCallback((type, categoryId) => {
    const normalizedType = type === 'income' ? 'income' : 'expenses';
    setHiddenCategories((prev) => ({
      income: Array.isArray(prev?.income) ? prev.income : [],
      expenses: Array.isArray(prev?.expenses) ? prev.expenses : [],
      [normalizedType]: (Array.isArray(prev?.[normalizedType]) ? prev[normalizedType] : [])
        .filter((id) => id !== categoryId)
    }));
  }, []);
  const handleDeleteServiceCategory = useCallback((catId) => {
    if (!catId) return;
    setHiddenServiceCategories((prev) => {
      if (prev.includes(catId)) return prev;
      return [...prev, catId];
    });
    setServicePrices((prev) => {
      const next = { ...prev };
      delete next[catId];
      return next;
    });
  }, []);


  const costSummary = useMemo(() => {
    const perService = costItems.reduce((sum, item) => {
      const unit = getUnitCost(item);
      const usage = parseFloat(item.usagePerService) || 0;
      return sum + unit * usage;
    }, 0);
    return {
      perService: parseFloat(perService.toFixed(2)),
    };
  }, [costItems, getUnitCost]);
  // PWA Enhancement State Variables
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [performanceOptimized, setPerformanceOptimized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Dashboard Layout State - Control which sections are visible in full-width layout
  const [visibleSections, setVisibleSections] = useState({
    taxCalculation: false,
    pricingCalculator: false,
    budget: false,
    aiAssistant: false,
    categoryTotals: false,
    capitalReport: false,
    paymentMethodTotals: false,
    recentTransactions: false,
    staff: false,
    clients: false,
    suppliers: false,
    projects: false,
    accountantWorkspace: false
  });
  const GOAL_SAVED_KEY = '70lives_goal_saved';
  const [showGoalNudge, setShowGoalNudge] = useState(false);
  const [hasSavedGoal, setHasSavedGoal] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(GOAL_SAVED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showTxnNudge, setShowTxnNudge] = useState(false);
  const [budgetPulse, setBudgetPulse] = useState(false);
  const [supplierDraft, setSupplierDraft] = useState({
    id: '',
    name: '',
    supplierType: 'standard',
    rctDeductionRate: 0.2
  });
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [selectedProjectKey, setSelectedProjectKey] = useState('');
  const [supplierPaymentDrafts, setSupplierPaymentDrafts] = useState({});
  const [rctPaymentDrafts, setRctPaymentDrafts] = useState({});
  const supplierMatchesSearch = useCallback((supplier) => {
    const query = supplierSearchTerm.trim().toLowerCase();
    if (!query) return true;
    const matchingSummary = (Array.isArray(rctTracker.supplierSummaries) ? rctTracker.supplierSummaries : []).find((summary) => (
      summary.id === supplier?.id ||
      String(summary.name || '').toLowerCase() === String(supplier?.name || '').toLowerCase()
    ));
    return [
      supplier?.name,
      supplier?.id,
      supportsRct && supplier?.supplierType === 'rct' ? 'rct subcontractor' : 'standard supplier',
      supportsRct ? `${Math.round((Number(supplier?.rctDeductionRate) || 0) * 100)}%` : '',
      ...(Array.isArray(matchingSummary?.invoiceRows)
        ? matchingSummary.invoiceRows.flatMap((row) => [supportsProjects ? row.jobReference : '', row.date])
        : [])
    ].some((value) => String(value || '').toLowerCase().includes(query));
  }, [rctTracker.supplierSummaries, supplierSearchTerm, supportsProjects, supportsRct]);
  const filteredSupplierOptions = useMemo(
    () => rctSubcontractorOptions
      .filter((supplier) => supportsRct || supplier.supplierType !== 'rct')
      .filter(supplierMatchesSearch),
    [rctSubcontractorOptions, supplierMatchesSearch, supportsRct]
  );
  useEffect(() => {
    const query = supplierSearchTerm.trim();
    if (!query) return;
    if (filteredSupplierOptions.length === 1) {
      setSelectedSupplierId(filteredSupplierOptions[0].id);
    } else if (
      selectedSupplierId &&
      !filteredSupplierOptions.some((supplier) => supplier.id === selectedSupplierId)
    ) {
      setSelectedSupplierId('');
    }
  }, [filteredSupplierOptions, selectedSupplierId, supplierSearchTerm]);
  const selectedSupplierForDisplay = useMemo(() => {
    if (!selectedSupplierId || !filteredSupplierOptions.length) return null;
    return filteredSupplierOptions.find((supplier) => supplier.id === selectedSupplierId) || null;
  }, [filteredSupplierOptions, selectedSupplierId]);
  const selectedSupplierSummary = useMemo(() => {
    if (!selectedSupplierForDisplay) return null;
    return (Array.isArray(rctTracker.supplierSummaries) ? rctTracker.supplierSummaries : []).find((supplier) => (
      supplier.id === selectedSupplierForDisplay.id ||
      String(supplier.name || '').toLowerCase() === String(selectedSupplierForDisplay.name || '').toLowerCase()
    )) || null;
  }, [rctTracker.supplierSummaries, selectedSupplierForDisplay]);
  const selectedSupplierPrincipalRows = useMemo(() => {
    if (!selectedSupplierForDisplay) return [];
    return (Array.isArray(rctTracker.principalRows) ? rctTracker.principalRows : []).filter((row) => (
      String(row.name || '').toLowerCase() === String(selectedSupplierForDisplay.name || '').toLowerCase()
    ));
  }, [rctTracker.principalRows, selectedSupplierForDisplay]);
  const filteredProjectSummaries = useMemo(() => {
    if (!supportsProjects) return [];
    const query = projectSearchTerm.trim().toLowerCase();
    if (!query) return projectSummaries;
    return projectSummaries.filter((project) => [
      project.jobReference,
      project.clientName,
      project.key,
      ...(Array.isArray(project.supplierNames) ? project.supplierNames : []),
      ...(Array.isArray(project.categoryBreakdown) ? project.categoryBreakdown.map((entry) => entry.label) : [])
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [projectSearchTerm, projectSummaries, supportsProjects]);
  useEffect(() => {
    const query = projectSearchTerm.trim();
    if (!query) return;
    if (filteredProjectSummaries.length === 1) {
      setSelectedProjectKey(filteredProjectSummaries[0].key);
    } else if (
      selectedProjectKey &&
      !filteredProjectSummaries.some((project) => project.key === selectedProjectKey)
    ) {
      setSelectedProjectKey('');
    }
  }, [filteredProjectSummaries, projectSearchTerm, selectedProjectKey]);
  const selectedProjectForDisplay = useMemo(() => {
    if (!selectedProjectKey || !filteredProjectSummaries.length) return null;
    return filteredProjectSummaries.find((project) => project.key === selectedProjectKey) || null;
  }, [filteredProjectSummaries, selectedProjectKey]);
  const getStoredSubscriptionTier = useCallback(() => {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem('subscription_tier') || '' : '';
    } catch {
      return '';
    }
  }, []);
  useEffect(() => {
    if (!selectedTimeframe) {
      setSelectedTimeframe(ALL_TIMEFRAME_ID);
    }
  }, [selectedTimeframe]);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('project_pricing_markup', String(projectPricingMarkup || ''));
      }
    } catch {
      // ignore
    }
  }, [projectPricingMarkup]);
  const availableFinancialYears = useMemo(() => {
    const years = new Set();
    years.add(new Date().getFullYear());
    if (Array.isArray(transactions)) {
      transactions.forEach((t) => {
        const year = getTransactionYearValue(t);
        if (Number.isFinite(year)) years.add(year);
      });
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions]);
  useEffect(() => {
    try {
      localStorage.setItem('active_financial_year', activeFinancialYear);
    } catch {}
  }, [activeFinancialYear]);

  const persistSubscriptionTier = useCallback((tier) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('subscription_tier', tier || '');
      }
    } catch {}
  }, []);

  const { subscription: liveSubscription } = useSubscription();
  const effectiveSubscription = isExternalSharedWorkspace ? workspaceSubscription : liveSubscription;
  const canManageWorkspaceSubscription = !isExternalSharedWorkspace;

  const normalizedSubscriptionTier = useMemo(() => {
    if (effectiveSubscription?.subscriptionStatus) {
      const plan = effectiveSubscription.subscriptionPlan || '';
      const status = effectiveSubscription.subscriptionStatus || '';
      return `${plan}-${status}`.toLowerCase();
    }
    if (isExternalSharedWorkspace) {
      return '';
    }
    const tier = (
      user?.subscriptionTier ||
      user?.subscriptionStatus ||
      user?.subscriptionPlan ||
      getStoredSubscriptionTier() ||
      ''
    ).toString().toLowerCase();
    return tier;
  }, [effectiveSubscription, getStoredSubscriptionTier, isExternalSharedWorkspace, user]);

  const now = useMemo(() => new Date(), []);
  const trialEndDate = effectiveSubscription?.trialEndDate
    ? (effectiveSubscription.trialEndDate.toDate ? effectiveSubscription.trialEndDate.toDate() : new Date(effectiveSubscription.trialEndDate))
    : null;

  const isTrialActive =
    effectiveSubscription?.subscriptionStatus === 'trial' &&
    trialEndDate &&
    trialEndDate > now;

  const isPremiumActive =
    effectiveSubscription?.subscriptionStatus === 'active' &&
    (effectiveSubscription?.subscriptionPlan === 'premium' || normalizedSubscriptionTier.includes('premium'));

  const hasPremiumAnalyticsAccess = isPremiumActive || isTrialActive;
  const isTrialSubscriber = isTrialActive;
  const isPremiumSubscriber = isPremiumActive;
  const trialUsedFlag = useMemo(() => {
    const expired = effectiveSubscription?.subscriptionStatus === 'expired' || (!isExternalSharedWorkspace && user?.subscriptionStatus === 'expired');
    const usedFlag = (!isExternalSharedWorkspace && user?.trialUsed) || localStorage.getItem('trial_used') === 'true';
    const noTrialStatus = effectiveSubscription?.subscriptionStatus && effectiveSubscription.subscriptionStatus !== 'trial';
    return expired || noTrialStatus || (!isTrialActive && usedFlag);
  }, [effectiveSubscription, isExternalSharedWorkspace, isTrialActive, user]);
  const showTrialCta = canManageWorkspaceSubscription && !trialUsedFlag && !hasPremiumAnalyticsAccess && user && user.email;
  const premiumCtaLabel = showTrialCta ? 'Start Free Trial' : (user && user.email ? 'Upgrade to Premium' : 'Sign Up for Premium');
  const shouldShowAnalyticsUpsell = !hasPremiumAnalyticsAccess && canManageWorkspaceSubscription;
  const workspaceAdvancedReportsAccess = isExternalSharedWorkspace
    ? hasFeatureAccess(effectiveSubscription, PREMIUM_FEATURES.ADVANCED_REPORTS)
    : undefined;
  const workspaceVoiceAccess = isExternalSharedWorkspace
    ? hasFeatureAccess(effectiveSubscription, PREMIUM_FEATURES.VOICE_TO_TEXT)
    : undefined;
  const workspaceReceiptScannerAccess = isExternalSharedWorkspace
    ? hasFeatureAccess(effectiveSubscription, PREMIUM_FEATURES.RECEIPT_SCANNER)
    : undefined;
  const workspacePlanSummary = useMemo(() => {
    if (!isExternalSharedWorkspace) return '';
    const workspaceOwnerLabel = activeWorkspace?.ownerDisplayName || activeWorkspace?.ownerEmail || 'Client';
    if (isWorkspaceSubscriptionLoading) return `${workspaceOwnerLabel} workspace`;
    if (isTrialActive) return `${workspaceOwnerLabel} workspace · Premium trial`;
    if (isPremiumActive) return `${workspaceOwnerLabel} workspace · Premium`;
    return `${workspaceOwnerLabel} workspace · Free`;
  }, [activeWorkspace, isExternalSharedWorkspace, isPremiumActive, isTrialActive, isWorkspaceSubscriptionLoading]);
  const missingIncomeGoal = useMemo(() => {
    const amt = parseFloat(targetIncomeData?.amount || 0);
    return !(amt > 0);
  }, [targetIncomeData]);
  const missingTransactions = useMemo(() => !transactions?.length, [transactions]);
  useEffect(() => {
    const amt = parseFloat(targetIncomeData?.amount || 0);
    if (amt > 0 && !hasSavedGoal) {
      setHasSavedGoal(true);
      try { localStorage.setItem(GOAL_SAVED_KEY, 'true'); } catch {}
    }
  }, [targetIncomeData, hasSavedGoal, GOAL_SAVED_KEY]);

  // Collapse/expand Business Analytics section
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [analyticsPanels, setAnalyticsPanels] = useState({
    revenue: false,
    goal: false,
    popularServices: false,
    biggestExpenses: false,
    adjustedProfit: false,
    estimatedTax: false
  });
  const [taxSummary, setTaxSummary] = useState({
    taxType: 'income_tax',
    taxLabel: 'Income Tax',
    taxYear: null,
    businessStructure: '',
    accountingProfitAfterDepreciation: 0,
    bookDepreciationAddBack: 0,
    capitalAllowances: 0,
    taxAdjustedProfit: 0,
    taxableProfit: 0,
    capitalAllowanceRate: 0,
    capitalAllowanceYears: 0,
    capitalAllowanceRows: [],
    totalTaxLiability: 0,
    effectiveTaxRate: 0,
    incomeTax: 0,
    corporationTax: 0,
    profitAfterTax: 0,
    usc: 0,
    prsi: 0,
    vatDue: 0,
    vatCollected: 0,
    vatPaid: 0,
    capitalGainsTax: 0,
    capitalGains: 0
  });

  const handleBudgetNudge = useCallback(() => {
    setVisibleSections(prev => ({ ...prev, budget: true }));
    setShowBudgetSection(true);
    setBudgetPulse(true);
    setShowGoalNudge(false);
    setTimeout(() => setBudgetPulse(false), 1800);
    setTimeout(() => {
      const el = document.getElementById('budget-section');
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }, []);

  const handleAddIncomeNudge = useCallback(() => {
    setActiveTab('income');
    setShowInputMethodSelection(true);
    setShowTxnNudge(false);
  }, []);

  const handleAddExpenseNudge = useCallback(() => {
    setActiveTab('expenses');
    setShowInputMethodSelection(true);
    setShowTxnNudge(false);
  }, []);

  useEffect(() => {
    setShowGoalNudge(missingIncomeGoal && !hasSavedGoal);
  }, [missingIncomeGoal, hasSavedGoal]);

  useEffect(() => {
    setShowTxnNudge(missingTransactions);
  }, [missingTransactions]);
  const refreshSetupProgress = useCallback(() => {
    setSetupProgress(computeSetupProgressFromStorage());
  }, []);
  const refreshInitialBusinessSetupStatus = useCallback(() => {
    setRequiresInitialBusinessSetup(!isRequiredBusinessSetupComplete());
  }, []);
  const handleBusinessTypeChange = useCallback((nextValue) => {
    const nextBusinessType = normalizeBusinessType(nextValue);
    setBusinessType(nextBusinessType);
    writeLocalBusinessProfileSettings({ businessType: nextBusinessType, industryId, ...businessProfileDetails });
    refreshInitialBusinessSetupStatus();
    refreshSetupProgress();
  }, [businessProfileDetails, industryId, refreshInitialBusinessSetupStatus, refreshSetupProgress]);

  const handleIndustryChange = useCallback((nextValue) => {
    const nextIndustryId = normalizeIndustryId(nextValue);
    setIndustryId(nextIndustryId);
    writeLocalBusinessProfileSettings({ businessType, industryId: nextIndustryId, ...businessProfileDetails });
    refreshInitialBusinessSetupStatus();
    refreshSetupProgress();
  }, [businessProfileDetails, businessType, refreshInitialBusinessSetupStatus, refreshSetupProgress]);

  const handleBusinessProfileDetailChange = useCallback((field, value) => {
    setBusinessProfileDetails((prev) => normalizeBusinessProfileDetails({
      ...prev,
      [field]: value
    }, { preserveTypingWhitespace: true }));
  }, []);

  const handleDirectorDetailChange = useCallback((field, value) => {
    setBusinessProfileDetails((prev) => normalizeBusinessProfileDetails({
      ...prev,
      directorDetails: {
        ...(prev.directorDetails || {}),
        [field]: value
      }
    }, { preserveTypingWhitespace: true }));
  }, []);

  const handleSaveBusinessProfileSettings = useCallback(async () => {
    try {
      businessProfileLoadedRef.current = true;
      await persistBusinessProfileSettings({ businessType, industryId, ...businessProfileDetails });
      setSuccessMessage('Business settings saved');
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to save business settings:', error);
      alert('There was a problem saving your business settings. Please try again.');
    }
  }, [businessProfileDetails, businessType, industryId, persistBusinessProfileSettings]);
  const allSetupStepsDone = useMemo(
    () => Object.values(setupProgress).every(Boolean),
    [setupProgress]
  );
  const shouldShowSetupChecklist = showGettingStarted && showSetupChecklistCard && !allSetupStepsDone;

  const toggleAnalyticsPanel = useCallback((panelKey) => {
    setAnalyticsPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  }, []);

  // Smooth scroll helper
  const scrollToElement = useCallback((elementId) => {
    try {
      setTimeout(() => {
        const el = document.getElementById(elementId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    } catch {}
  }, []);

  // Provide a global hook so firebase.js can notify the app when it
  // restores a persisted auth session (without full Firebase sign-in).
  useEffect(() => {
    window.updateAppAuthState = (restoredUser) => {
      try { window.__authRestored = true; } catch {}
      setUser(restoredUser);
      setShowAuthRequired(false);
      setShowSignUpModal(false);
      setIsCheckingAuth(false);
      localStorage.removeItem('needsDriveAuth');
    };
    return () => { try { delete window.updateAppAuthState; } catch {} };
  }, []);

  // Map section keys to DOM ids for smooth scrolling
  const sectionIds = useMemo(() => ({
    taxCalculation: 'tax-section',
    pricingCalculator: 'pricing-section',
    budget: 'budget-section',
    aiAssistant: 'ai-section',
    categoryTotals: 'category-totals-section',
    capitalReport: 'capital-report-section',
    paymentMethodTotals: 'payment-totals-section',
    recentTransactions: 'recent-transactions-section',
    staff: 'staff-section',
    clients: 'clients-section',
    suppliers: 'suppliers-section',
    projects: 'projects-section',
    accountantWorkspace: 'accountant-workspace-section',
    analytics: 'analytics-section',
    filtersReports: 'filters-reports-section'
  }), []);

  const onboardingGuideSteps = useMemo(() => ([
    {
      id: 'country',
      title: 'Complete Business Setup',
      description: `Choose your country and currency, business structure, and ${countryTerminology.taxRegistrationName} status before entering transactions.`,
      actionLabel: 'Open Setup'
    },
    {
      id: 'vat',
      title: `Set ${countryTerminology.taxName} Registration`,
      description: `Go to Tax Calculation and confirm whether you are registered for ${countryTerminology.taxName}.`,
      guidance: `Set ${countryTerminology.taxName} registration status first so sales and expense ${countryTerminology.taxName} handling is correct from day one.`,
      actionLabel: 'Open Tax Section'
    },
    {
      id: 'goal',
      title: 'Set Budget Plan',
      description: 'Open Budget and set business profit, owner pay, and labour cost assumptions.',
      guidance: 'This gives the Pricing Calculator a clearer labour cost floor before products, overheads, tax, and profit margin.',
      actionLabel: 'Open Budget'
    },
    {
      id: 'sale',
      title: 'Add First Sale',
      description: 'Create your first income transaction.',
      guidance: 'Use manual, voice, receipt, or spreadsheet input to log a sale.',
      actionLabel: 'Add Sale'
    },
    {
      id: 'expense',
      title: 'Add First Expense',
      description: 'Create your first expense transaction.',
      guidance: 'Track real operating costs early so pricing and profit are realistic.',
      actionLabel: 'Add Expense'
    },
    {
      id: 'service',
      title: 'Create a New Service',
      description: 'Open Pricing Calculator and add a service in your selected category.',
      guidance: 'In Pricing Calculator: click Edit Services, then Add New Service.',
      actionLabel: 'Open Pricing Calculator'
    },
    {
      id: 'overhead',
      title: 'Add Overhead Costs',
      description: 'Capture overhead like rent, utilities, insurance, marketing, and software.',
      guidance: 'Use the Overhead Calculator for recurring business costs.',
      actionLabel: 'Open Overhead Calculator'
    },
    {
      id: 'products',
      title: 'Add Products Used Per Service',
      description: 'Add products/materials used in each service (gel, tint, shampoo, serum, wipes, cleanser, etc.).',
      guidance: 'Use the Cost Calculator to assign usage per service and true unit cost.',
      actionLabel: 'Open Cost Calculator'
    },
    {
      id: 'pricing',
      title: 'Calculate Recommended Price',
      description: 'Use the calculator to estimate what you should charge based on costs, time, overhead, and margin.',
      guidance: `Review suggested price, margin, and ${countryTerminology.taxName} impact before publishing.`,
      actionLabel: 'Open Pricing Calculator'
    },
    {
      id: 'savePricing',
      title: 'Edit and Save',
      description: 'Adjust assumptions and save your service price when it reflects your business reality.',
      guidance: 'Save as service/product so it can be reused during transaction entry.',
      actionLabel: 'Open Pricing Calculator'
    },
    {
      id: 'analytics',
      title: 'Review Business Analytics',
      description: isLimitedCompany
        ? 'Open analytics to check profit after tax, trends, costs, and tax estimates.'
        : 'Open analytics to check take-home, trends, costs, and tax estimates.',
      guidance: 'Analytics become more accurate as your transaction data grows.',
      actionLabel: 'Open Analytics'
    },
    {
      id: 'filtersReports',
      title: 'Use Filters and Reports',
      description: 'Filter by date, category, type, and payment method, then export reports.',
      guidance: 'Use filters to isolate problem areas and export clean period reports.',
      actionLabel: 'Open Filters & Reports'
    },
    {
      id: 'capital',
      title: 'Understand the Capital Tab',
      description: 'Capital tracks business assets, depreciation, disposals, and write-offs.',
      guidance: 'Use this for equipment/furniture/tools that are not normal day-to-day expenses.',
      actionLabel: 'Open Capital Tab'
    },
    {
      id: 'receipts',
      title: 'Upload and Manage Receipts',
      description: 'Upload a receipt/invoice and convert it into transactions.',
      guidance: 'After saving receipts, use the Receipts tab to review, edit, and manage them.',
      actionLabel: 'Open Receipt Upload'
    },
    {
      id: 'freshaImport',
      title: 'Import Fresha Sales Report',
      description: 'Export Fresha Sales Log (CSV for a chosen date range) and import it to save manual entry time.',
      guidance: 'Use Upload Spreadsheet from the income flow, then review and save rows.',
      actionLabel: 'Open Sales Import'
    },
    {
      id: 'bankImport',
      title: 'Import Bank Statement',
      description: 'Upload bank CSV to quickly classify income and expenses.',
      guidance: 'Review rows, edit/remove items as needed, then save approved transactions.',
      actionLabel: 'Open Bank Import'
    },
    {
      id: 'recent',
      title: 'Use Recent Transactions',
      description: 'Quickly find, filter, and edit saved transactions from the Recent section.',
      guidance: `This is the fastest place to fix categories, amounts, dates, and ${countryTerminology.taxName} after entry.`,
      actionLabel: 'Open Recent Transactions'
    }
  ]), [countryTerminology.taxName, countryTerminology.taxRegistrationName, isLimitedCompany]);

  // Toggle section; when opening, scroll it into view
  const toggleSection = (sectionKey) => {
    const wasOpen = !!visibleSections[sectionKey];
    setVisibleSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
    if (!wasOpen) {
      const id = sectionIds[sectionKey];
      if (id) scrollToElement(id);
    }
  };

  const openSection = useCallback((sectionKey) => {
    setVisibleSections((prev) => ({
      ...prev,
      [sectionKey]: true
    }));
    const id = sectionIds[sectionKey];
    if (id) scrollToElement(id);
  }, [sectionIds, scrollToElement]);

  const persistCompletedOnboardingSteps = useCallback((stepIds) => {
    const sanitized = Array.from(new Set((stepIds || []).filter((id) => typeof id === 'string')));
    setCompletedOnboardingStepIds(sanitized);
    try {
      localStorage.setItem(ONBOARDING_GUIDE_PROGRESS_KEY, JSON.stringify(sanitized));
    } catch {}
    return sanitized;
  }, []);

  const markGuideStepCompleted = useCallback((index) => {
    const step = onboardingGuideSteps[index];
    if (!step?.id) return;
    setCompletedOnboardingStepIds((prev) => {
      if (prev.includes(step.id)) return prev;
      const next = [...prev, step.id];
      try {
        localStorage.setItem(ONBOARDING_GUIDE_PROGRESS_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [onboardingGuideSteps]);

  const openOnboardingGuide = useCallback((index = null) => {
    const explicitIndex = typeof index === 'number' ? index : onboardingGuideStepIndex;
    const firstIncompleteIndex = onboardingGuideSteps.findIndex((step) => !completedOnboardingStepIds.includes(step.id));
    const fallbackIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0;
    const targetIndex = typeof index === 'number' ? explicitIndex : fallbackIndex;
    const boundedIndex = Math.max(0, Math.min(targetIndex, onboardingGuideSteps.length - 1));
    setOnboardingGuideStepIndex(boundedIndex);
    setShowOnboardingGuide(true);
  }, [completedOnboardingStepIds, onboardingGuideStepIndex, onboardingGuideSteps]);

  const closeOnboardingGuide = useCallback(() => {
    setShowOnboardingGuide(false);
  }, []);

  const completeOnboardingGuide = useCallback(() => {
    const allStepIds = onboardingGuideSteps.map((step) => step.id).filter(Boolean);
    persistCompletedOnboardingSteps(allStepIds);
    localStorage.setItem('setup_step_basics', 'true');
    refreshSetupProgress();
    setActiveOnboardingStepIndex(null);
    setShowOnboardingGuide(false);
    setOnboardingGuideStepIndex(0);
  }, [onboardingGuideSteps, persistCompletedOnboardingSteps, refreshSetupProgress]);

  const completeGuideStepAndAdvance = useCallback((index = onboardingGuideStepIndex) => {
    const boundedIndex = Math.max(0, Math.min(index, onboardingGuideSteps.length - 1));
    markGuideStepCompleted(boundedIndex);
    setActiveOnboardingStepIndex(null);

    if (boundedIndex >= onboardingGuideSteps.length - 1) {
      completeOnboardingGuide();
      return;
    }

    setOnboardingGuideStepIndex(boundedIndex + 1);
    setShowOnboardingGuide(true);
  }, [completeOnboardingGuide, markGuideStepCompleted, onboardingGuideStepIndex, onboardingGuideSteps.length]);

  const goToGuideStep = useCallback((index) => {
    const boundedIndex = Math.max(0, Math.min(index, onboardingGuideSteps.length - 1));
    setOnboardingGuideStepIndex(boundedIndex);
  }, [onboardingGuideSteps.length]);

  const goToNextGuideStep = useCallback(() => {
    setOnboardingGuideStepIndex((prev) => Math.min(prev + 1, onboardingGuideSteps.length - 1));
  }, [onboardingGuideSteps.length]);

  const goToPreviousGuideStep = useCallback(() => {
    setOnboardingGuideStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const runOnboardingGuideAction = useCallback((index = onboardingGuideStepIndex) => {
    const step = onboardingGuideSteps[index];
    if (!step) return;

    setOnboardingGuideStepIndex(index);
    setActiveOnboardingStepIndex(index);
    setShowOnboardingGuide(false);

    switch (step.id) {
      case 'country':
        setShowCountrySelection(true);
        break;
      case 'vat':
        openSection('taxCalculation');
        setOpenVatSectionSignal((prev) => prev + 1);
        break;
      case 'goal':
        openSection('budget');
        setShowBudgetSection(true);
        break;
      case 'sale':
        setActiveTab('income');
        setSelectedInputMethod(null);
        setShowInputMethodSelection(true);
        break;
      case 'expense':
        setActiveTab('expenses');
        setSelectedInputMethod(null);
        setShowInputMethodSelection(true);
        break;
      case 'service':
      case 'pricing':
      case 'savePricing':
        openSection('pricingCalculator');
        setShowPricingCalculator(true);
        break;
      case 'overhead':
        openSection('pricingCalculator');
        setShowPricingCalculator(true);
        setShowOverheadModal(true);
        break;
      case 'products':
        openSection('pricingCalculator');
        setShowPricingCalculator(true);
        setShowCostModal(true);
        break;
      case 'analytics':
        setAnalyticsExpanded(true);
        scrollToElement('analytics-section');
        break;
      case 'filtersReports':
        scrollToElement('filters-reports-section');
        break;
      case 'capital':
        openSection('capitalReport');
        break;
      case 'receipts':
        setActiveTab('expenses');
        setSelectedInputMethod('receipt');
        setShowInputMethodSelection(false);
        setShowReceiptModal(true);
        break;
      case 'freshaImport':
        setActiveTab('income');
        setSelectedInputMethod(null);
        setShowInputMethodSelection(true);
        break;
      case 'bankImport':
        setActiveTab('expenses');
        setSelectedInputMethod(null);
        setShowInputMethodSelection(true);
        break;
      case 'recent':
        openSection('recentTransactions');
        setShowAllTransactions(true);
        break;
      default:
        break;
    }
  }, [onboardingGuideStepIndex, onboardingGuideSteps, openSection, scrollToElement]);

    // Updated categories for services - create a local copy to avoid mutation issues
  const appCategories = {
    income: [
      { id: 'nailServices', name: 'Nail Services', icon: '' },
      { id: 'hairServices', name: 'Hair Services', icon: '' },
      { id: 'skinServices', name: 'Skin Care', icon: '?' },
      { id: 'browServices', name: 'Brow Services', icon: '' },
      { id: 'lashServices', name: 'Lash Services', icon: '' },
      { id: 'makeupServices', name: 'Makeup', icon: '' },
      { id: 'massageServices', name: 'Massage', icon: '' },
      { id: 'otherServices', name: 'Other Services', icon: '?' },
      { id: 'productSales', name: 'Product Sales', icon: '' },
      { id: 'assetSale', name: 'Asset Sale', icon: '' }
    ],
    expenses: CATEGORIES.expenses
  };
  
  // Constants and Helpers
  const isCapitalExpense = useCallback((category) => {
    const CAPITAL_ASSET_CATEGORIES = ['equipment', 'furniture', 'electronics', 'salon_tools', 'vehicle'];
    return CAPITAL_ASSET_CATEGORIES.includes(category);
  }, []);

  const calculateDepreciation = (amount, years) => amount / years;
  const [vatSettingsState, setVatSettingsState] = useState(() => {
    const countryDefaults = getDefaultVatSettingsForCountry(getCurrentCountry());
    if (typeof window === 'undefined') return countryDefaults;
    try {
      const stored = localStorage.getItem('vat_settings');
      return stored ? sanitizeVatSettings(JSON.parse(stored), countryDefaults) : countryDefaults;
    } catch {
      return countryDefaults;
    }
  });
  const vatSettings = vatSettingsState;

  const updateVatSettings = useCallback((updates) => {
    setVatSettingsState((prev) => {
      const countryDefaults = getDefaultVatSettingsForCountry(currentCountry);
      const nextRaw = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      return sanitizeVatSettings(nextRaw, countryDefaults);
    });

    if (typeof updates === 'object' && updates !== null && ('isRegistered' in updates || 'enabled' in updates || 'accountingBasis' in updates)) {
      try {
        localStorage.setItem(VAT_STATUS_CONFIRMED_STORAGE_KEY, 'true');
        const nextVatRegistered = Boolean(
          ('enabled' in updates ? updates.enabled : vatSettingsState.enabled) &&
          ('isRegistered' in updates ? updates.isRegistered : vatSettingsState.isRegistered)
        );
        if (!nextVatRegistered || 'accountingBasis' in updates) {
          localStorage.setItem(VAT_ACCOUNTING_BASIS_CONFIRMED_STORAGE_KEY, 'true');
        }
        refreshInitialBusinessSetupStatus();
        refreshSetupProgress();
      } catch {}
    }
  }, [currentCountry, refreshInitialBusinessSetupStatus, refreshSetupProgress, vatSettingsState.enabled, vatSettingsState.isRegistered]);
  const resolveIncomeTaxJurisdiction = useCallback(() => {
    const countryId = String(currentCountry?.id || '').toLowerCase();
    const countryName = String(currentCountry?.name || '').toLowerCase();

    if (countryId === 'us' || countryName.includes('united states')) {
      return 'US';
    }

    if (countryId === 'uk' || countryId === 'gb' || countryName.includes('united kingdom')) {
      return 'UK';
    }

    return 'Ireland';
  }, [currentCountry]);
  const countryVatRateOptions = useMemo(
    () => (
      Array.isArray(currentCountry?.vatRates)
        ? currentCountry.vatRates
            .map((entry) => ({
              ...entry,
              decimalRate: Number(entry?.rate) / 100
            }))
            .filter((entry) => Number.isFinite(entry.decimalRate))
        : []
    ),
    [currentCountry?.vatRates]
  );
  const getStandardCountryVatRate = useCallback(() => {
    const defaultRate = Number(currentCountry?.defaultVatRate) / 100;
    if (Number.isFinite(defaultRate) && defaultRate >= 0) {
      return defaultRate;
    }
    if (!countryVatRateOptions.length) return 0;
    return Math.max(...countryVatRateOptions.map((entry) => entry.decimalRate));
  }, [countryVatRateOptions, currentCountry?.defaultVatRate]);
  const getReducedCountryVatRate = useCallback(() => {
    const standardRate = getStandardCountryVatRate();
    const reducedRates = countryVatRateOptions
      .map((entry) => entry.decimalRate)
      .filter((rate) => rate > 0 && rate < standardRate)
      .sort((a, b) => b - a);

    if (reducedRates.length) return reducedRates[0];
    return standardRate;
  }, [countryVatRateOptions, getStandardCountryVatRate]);
  const getZeroCountryVatRate = useCallback(() => {
    const zeroRate = countryVatRateOptions.find((entry) => Math.abs(entry.decimalRate) < 0.0001);
    return zeroRate ? zeroRate.decimalRate : 0;
  }, [countryVatRateOptions]);
  const formatTaxRatePercent = useCallback((rate) => {
    const percent = Number(rate) * 100;
    if (!Number.isFinite(percent) || percent <= 0) {
      return '0%';
    }
    return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(1)}%`;
  }, []);
  const getTaxComponentDescription = useCallback((rate) => {
    const normalizedRate = Number(rate);
    if (!Number.isFinite(normalizedRate) || normalizedRate <= 0) {
      return currentCountry.taxSystem === 'vat' ? 'Zero Rate (0%)' : 'No Tax (0%)';
    }

    if (currentCountry.taxSystem === 'vat') {
      const matchedRate = countryVatRateOptions.find((entry) => Math.abs(entry.decimalRate - normalizedRate) < 0.0001);
      if (matchedRate) {
        return `${matchedRate.name} (${formatTaxRatePercent(normalizedRate)})`;
      }
    }

    const percentLabel = formatTaxRatePercent(normalizedRate);
    return `${percentLabel} ${countryTerminology.taxName}`;
  }, [countryTerminology.taxName, countryVatRateOptions, currentCountry.taxSystem, formatTaxRatePercent]);
  const vatRegistrationPeriods = useMemo(
    () => sanitizeVatRegistrationPeriods(vatSettings.registrationPeriods, vatSettings.registrationDate),
    [vatSettings.registrationDate, vatSettings.registrationPeriods]
  );
  const hasOpenVatRegistrationPeriod = useMemo(
    () => vatRegistrationPeriods.some((period) => !period.endDate),
    [vatRegistrationPeriods]
  );
  const isVatRegisteredForTransactionDate = useCallback((transactionDate = null) => {
    if (!vatSettings.enabled) {
      return false;
    }

    if (vatSettings.isRegistered && vatRegistrationPeriods.length === 0) {
      return true;
    }

    const normalizedTransactionDate = normalizeDateString(transactionDate);
    if (!normalizedTransactionDate) {
      return vatSettings.isRegistered || hasOpenVatRegistrationPeriod;
    }

    if (vatRegistrationPeriods.length === 0) {
      return false;
    }

    return vatRegistrationPeriods.some((period) =>
      normalizedTransactionDate >= period.startDate &&
      (!period.endDate || normalizedTransactionDate <= period.endDate)
    );
  }, [
    hasOpenVatRegistrationPeriod,
    vatSettings.enabled,
    vatSettings.isRegistered,
    vatRegistrationPeriods
  ]);
  const getIncomeTaxRateForCategory = useCallback((category, taxJurisdiction, transactionDate = null) => {
    const normalizedCategory = String(category || '').trim().toLowerCase();
    const compactCategoryKey = normalizedCategory.replace(/[\s_-]+/g, '');
    if (compactCategoryKey === 'giftcard' || compactCategoryKey === 'giftvoucher') {
      return 0;
    }

    const normalizedTransactionDate = normalizeDateString(transactionDate);
    if (vatSettings.enabled && normalizedTransactionDate && !isVatRegisteredForTransactionDate(normalizedTransactionDate)) {
      return 0;
    }

    if (taxJurisdiction === 'US') {
      const configuredSalesTaxRate = Number(currentCountry?.defaultVatRate) / 100;
      return Number.isFinite(configuredSalesTaxRate) && configuredSalesTaxRate >= 0
        ? configuredSalesTaxRate
        : 0;
    }

    const countryId = String(currentCountry?.id || '').toLowerCase();
    const countryName = String(currentCountry?.name || '').toLowerCase();
    const shouldUseUkRules =
      taxJurisdiction === 'UK' ||
      countryId === 'uk' ||
      countryId === 'gb' ||
      countryName.includes('united kingdom');
    const configuredSalesRate = Number.isFinite(Number(vatSettings.salesRate))
      ? Number(vatSettings.salesRate)
      : (
          Number.isFinite(Number(vatSettings.productsRate))
            ? Number(vatSettings.productsRate)
            : getStandardCountryVatRate()
        );
    const configuredReducedRate = Number.isFinite(Number(vatSettings.servicesRate))
      ? Number(vatSettings.servicesRate)
      : getReducedCountryVatRate();
    const standardRate = shouldUseUkRules
      ? getStandardCountryVatRate()
      : configuredSalesRate;
    const reducedRate = shouldUseUkRules
      ? getReducedCountryVatRate()
      : configuredReducedRate;
    const categoryVatRateOverride = normalizeVatRateSetting(
      vatSettings.categoryVatRates?.[normalizeCategoryId(category || '')]
    );
    if (categoryVatRateOverride !== null) {
      return categoryVatRateOverride;
    }

    if (
      isIrishTaxJurisdiction(taxJurisdiction, currentCountry) &&
      !shouldUseUkRules
    ) {
      return getDefaultIrishIncomeCategoryVatRate({
        category,
        industryId,
        transactionDate,
        isProductCategory
      });
    }

    if (shouldUseUkRules) {
      if (UK_ZERO_RATED_CATEGORY_IDS.has(normalizedCategory)) {
        return getZeroCountryVatRate();
      }
      if (UK_REDUCED_RATED_CATEGORY_IDS.has(normalizedCategory)) {
        return getReducedCountryVatRate();
      }
      return standardRate;
    }

    return isProductCategory(category)
      ? standardRate
      : reducedRate;
  }, [
    currentCountry,
    getReducedCountryVatRate,
    getStandardCountryVatRate,
    getZeroCountryVatRate,
    industryId,
    isProductCategory,
    vatSettings.categoryVatRates,
    vatSettings.enabled,
    vatSettings.productsRate,
    vatSettings.salesRate,
    vatSettings.servicesRate,
    isVatRegisteredForTransactionDate
  ]);
  const categoryVatRateRows = useMemo(() => {
    const taxJurisdiction = resolveIncomeTaxJurisdiction();
    const referenceDate = new Date().toISOString().slice(0, 10);
    const configuredRates = sanitizeCategoryVatRates(vatSettings.categoryVatRates);

    return (transactionInputCategories.income || [])
      .filter((category) => category?.id)
      .map((category) => {
        const normalizedCategoryId = normalizeCategoryId(category.id);
        const hasOverride = Object.prototype.hasOwnProperty.call(configuredRates, normalizedCategoryId);
        const effectiveRate = getIncomeTaxRateForCategory(normalizedCategoryId, taxJurisdiction, referenceDate);
        const defaultRate = isIrishTaxJurisdiction(taxJurisdiction, currentCountry)
          ? getDefaultIrishIncomeCategoryVatRate({
              category: normalizedCategoryId,
              industryId,
              transactionDate: referenceDate,
              isProductCategory
            })
          : effectiveRate;

        return {
          id: normalizedCategoryId,
          name: category.name || normalizedCategoryId,
          rate: effectiveRate,
          defaultRate,
          hasOverride
        };
      });
  }, [
    currentCountry,
    getIncomeTaxRateForCategory,
    industryId,
    isProductCategory,
    resolveIncomeTaxJurisdiction,
    transactionInputCategories.income,
    vatSettings.categoryVatRates
  ]);
  const calculateTaxAmountFromGross = useCallback((grossAmount, taxRate) => {
    const amount = Number(grossAmount);
    const rate = Number(taxRate);
    if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(rate) || rate <= 0) {
      return 0;
    }
    return amount - (amount / (1 + rate));
  }, []);
  const buildSplitIncomeVatSummary = useCallback((items, taxJurisdiction, transactionDate = null) => {
    const groupedByCategoryAndRate = new Map();
    const lineVatBreakdown = [];

    items.forEach((item, index) => {
      const grossAmount = Number(item.amount) || 0;
      const category = item.category || '';
      const rate = getIncomeTaxRateForCategory(item.category, taxJurisdiction, item.date || transactionDate);
      const vatAmount = calculateTaxAmountFromGross(grossAmount, rate);

      lineVatBreakdown[index] = {
        category,
        rate,
        amount: parseFloat(vatAmount.toFixed(2))
      };

      const key = `${category || '__uncategorized__'}|${rate.toFixed(6)}`;
      const current = groupedByCategoryAndRate.get(key) || {
        category,
        rate,
        grossAmount: 0,
        vatAmount: 0
      };
      current.grossAmount += grossAmount;
      current.vatAmount += vatAmount;
      groupedByCategoryAndRate.set(key, current);
    });

    const rateBreakdown = Array.from(groupedByCategoryAndRate.values())
      .map((entry) => ({
        category: entry.category,
        rate: entry.rate,
        grossAmount: parseFloat(entry.grossAmount.toFixed(2)),
        vatAmount: parseFloat(entry.vatAmount.toFixed(2)),
        description: getTaxComponentDescription(entry.rate)
      }))
      .sort((a, b) => {
        if (b.rate !== a.rate) return b.rate - a.rate;
        return (a.category || '').localeCompare(b.category || '');
      });

    const vatComponents = rateBreakdown.map((entry) => ({
      category: entry.category,
      rate: entry.rate,
      amount: entry.vatAmount,
      description: entry.description
    }));

    const totalVatAmount = parseFloat(
      vatComponents.reduce((sum, component) => sum + (Number(component.amount) || 0), 0).toFixed(2)
    );

    return {
      vatComponents,
      rateBreakdown,
      totalVatAmount,
      effectiveVatRate: vatComponents.length === 1 ? vatComponents[0].rate : null,
      lineVatBreakdown
    };
  }, [calculateTaxAmountFromGross, getIncomeTaxRateForCategory, getTaxComponentDescription]);

  useEffect(() => {
    try {
      localStorage.setItem('vat_settings', JSON.stringify(vatSettings));
    } catch {}

    const persistVatSettings = async () => {
      if (!vatSettingsLoadedRef.current) return;
      try {
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.VAT_SETTINGS, vatSettings);
      } catch (error) {
        console.warn('Failed to persist VAT settings via hybrid storage:', error);
      }
    };

    persistVatSettings();
  }, [persistDatasetForCurrentAccess, vatSettings]);

  const vatContextValue = useMemo(() => ({
    ...vatSettings,
    setVatSettings: updateVatSettings
  }), [vatSettings, updateVatSettings]);

  // Helper: compute overhead figures from expenses in date range
  const computeOverhead = useCallback(() => {
    try {
      if (!overheadStart || !overheadEnd) return;
      const start = new Date(overheadStart);
      const end = new Date(overheadEnd);
      if (isNaN(start) || isNaN(end) || start > end) return;

      // Only Utilities and Rent/Lease (and close variants) are included in overheads
      const UTILITY_KEYS = ['utilities', 'utility', 'electricity', 'water', 'gas', 'internet_telephone', 'internet', 'wifi', 'broadband', 'telephone', 'phone', 'phone bill'];
      const RENT_KEYS = ['rent', 'rent/lease', 'lease'];
      const STAFF_KEYS = ['staff', 'wages', 'payroll', 'salary', 'salaries', 'employee', 'labour', 'labor', 'prsi', 'employer_prsi', 'employee_holiday_pay', 'holiday pay'];
      const INSURANCE_KEYS = ['insurance', 'liability', 'indemnity'];
      const MARKETING_KEYS = ['marketing', 'advertising', 'ads', 'ad spend', 'facebook ads', 'instagram ads', 'google ads'];
      const PROFESSIONAL_KEYS = ['professional fees', 'legal', 'accounting', 'consulting', 'bookkeeping', 'auditor'];
      const inRange = (d) => {
        const dt = new Date(d);
        return !isNaN(dt) && dt >= start && dt <= end;
      };

      let utilitiesExpense = 0;
      let rentExpense = 0;
      let staffExpense = 0;
      let insuranceExpense = 0;
      let marketingExpense = 0;
      let professionalExpense = 0;

      (Array.isArray(transactions) ? transactions : []).forEach((t) => {
        if (!t || t.type !== 'expenses' || !inRange(t.date)) return;
        const amount = Number(t.amount) || 0;
        const cat = (t.category || '').toLowerCase();
        if (UTILITY_KEYS.some((key) => cat.includes(key))) {
          utilitiesExpense += amount;
          return;
        }
        if (RENT_KEYS.some((key) => cat.includes(key))) {
          rentExpense += amount;
          return;
        }
        if (STAFF_KEYS.some((key) => cat.includes(key))) {
          staffExpense += amount;
          return;
        }
        if (INSURANCE_KEYS.some((key) => cat.includes(key))) {
          insuranceExpense += amount;
          return;
        }
        if (MARKETING_KEYS.some((key) => cat.includes(key))) {
          marketingExpense += amount;
          return;
        }
        if (PROFESSIONAL_KEYS.some((key) => cat.includes(key))) {
          professionalExpense += amount;
        }
      });

      const totalExpense = utilitiesExpense + rentExpense + staffExpense + insuranceExpense + marketingExpense + professionalExpense;

      const adjustFactor = premisesType === 'home' ? 0.30 : 1.0;
      const adjusted = totalExpense * adjustFactor;

      const diffMs = Math.max(0, end - start);
      const weeks = diffMs / (1000 * 60 * 60 * 24 * 7);
      const workingDays = weeks * (Number(workDaysPerWeek) || 0);
      const totalWorkHours = workingDays * (Number(hoursPerDay) || 0);
      const hourly = totalWorkHours > 0 ? adjusted / totalWorkHours : 0;

      setOverheadHourly(Number.isFinite(hourly) ? Number(hourly.toFixed(2)) : 0);
      setOverheadBreakdown({
        totalExpense,
        adjusted,
        utilitiesExpense,
        rentExpense,
        staffExpense,
        insuranceExpense,
        marketingExpense,
        professionalExpense,
        weeks: Number(weeks.toFixed(2)),
        workingDays: Number(workingDays.toFixed(1)),
        totalWorkHours: Number(totalWorkHours.toFixed(1)),
        adjustmentLabel: premisesType === 'home' ? '30% utilities (home use)' : '100% utilities (separate premises)'
      });
    } catch (e) {
      console.warn('Overhead compute error', e);
    }
  }, [overheadStart, overheadEnd, transactions, premisesType, workDaysPerWeek, hoursPerDay]);

  // Auto-refresh overhead figures when date range or inputs change
  useEffect(() => {
    if (overheadStart && overheadEnd) {
      computeOverhead();
    }
  }, [overheadStart, overheadEnd, computeOverhead]);

  // Function to check if goals have been set
  const checkIfGoalsSet = useCallback(() => {
    try {
      const savedTargetIncomeData = localStorage.getItem('70livesbeautyledgerTargetIncome');
      if (savedTargetIncomeData) {
        const targetData = JSON.parse(savedTargetIncomeData);
        return targetData.amount && parseFloat(targetData.amount) > 0;
      }
    } catch (e) {
      console.error('Failed to parse saved target income data', e);
    }
    return false;
  }, []);

// Initialize hasGoals state on component mount
useEffect(() => {
  setHasGoals(checkIfGoalsSet());
  
  // Add global function to reset onboarding (for testing)
  window.resetOnboarding = () => {
    localStorage.removeItem('onboarding_completed');
    setShowGettingStarted(true);
    localStorage.removeItem('setup_checklist_dismissed');
    localStorage.removeItem('pwa-install-complete');
    localStorage.removeItem('setup_step_drive');
    localStorage.removeItem('setup_step_basics');
    localStorage.removeItem(BUSINESS_TYPE_CONFIRMED_STORAGE_KEY);
    localStorage.removeItem(INDUSTRY_CONFIRMED_STORAGE_KEY);
    localStorage.removeItem(VAT_STATUS_CONFIRMED_STORAGE_KEY);
    localStorage.removeItem(VAT_ACCOUNTING_BASIS_CONFIRMED_STORAGE_KEY);
    localStorage.removeItem(ONBOARDING_GUIDE_PROGRESS_KEY);
    setCompletedOnboardingStepIds([]);
    setActiveOnboardingStepIndex(null);
    setRequiresInitialBusinessSetup(true);
    refreshSetupProgress();
    setShowSetupChecklistCard(true);
    console.log('? Onboarding reset - will show again');
  };
}, [checkIfGoalsSet, refreshSetupProgress]);

// Check if app is installed (standalone mode) and handle initial auth state
useEffect(() => {
  // Detect if app is installed on mobile
  const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
  
  // If app is installed and user is not logged in, show simple sign-in instead of full onboarding
  if (isStandalone && !user) {
    console.log('App is installed on mobile - showing simple sign-in');
    setShowGettingStarted(false);
    setShowAuthRequired(true);
  }
}, [user]);
// Refresh onboarding and goals state when user logs in
useEffect(() => {
  if (user) {
    // Check if onboarding should be shown
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    setShowGettingStarted(!onboardingCompleted);
    
    // Refresh goals state
    setHasGoals(checkIfGoalsSet());
  }
}, [user, checkIfGoalsSet]);

  useEffect(() => {
    const handleStorageSync = () => {
      refreshSetupProgress();
    };
    const handleAppInstalled = () => {
      localStorage.setItem('pwa-install-complete', 'true');
      refreshSetupProgress();
    };
    window.addEventListener('storage', handleStorageSync);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('storage', handleStorageSync);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [refreshSetupProgress]);

useEffect(() => {
  if (allSetupStepsDone) {
    localStorage.setItem('onboarding_completed', 'true');
    setShowGettingStarted(false);
    setShowSetupChecklistCard(false);
    localStorage.setItem('setup_checklist_dismissed', 'true');
  }
}, [allSetupStepsDone]);
// Auto-detect country from geolocation on first user login (returning users only)
useEffect(() => {
  const autoDetectCountry = async () => {
    if (!user) return;
    if (isNewUser) return; // New users must choose explicitly
    if (!isRequiredBusinessSetupComplete()) {
      setShowCountrySelection(true);
      return;
    }
    
    const savedCountry = localStorage.getItem('selectedCountry');
    if (savedCountry) return; // Country already selected, skip
    
    try {
      console.log('Detecting country from browser/IP hints...');
      const detectedCountry = await detectCountryFromIP();
      
      if (detectedCountry) {
        const countryMapping = {
          'IE': 'ie',
          'UK': 'uk',
          'GB': 'uk',
          'US': 'us'
        };
        
        const normalizedDetectedCountry = String(detectedCountry || '').trim().toUpperCase();
        const detectedCountryId = countryMapping[normalizedDetectedCountry] || null;
        if (!detectedCountryId) {
          console.log('Auto-detect returned unsupported country code, skipping:', detectedCountry);
          return;
        }

        const detectedCountryObj = getCountryById(detectedCountryId);
        if (!detectedCountryObj) {
          console.log('Resolved auto-detect country is unavailable, skipping:', detectedCountryId);
          return;
        }
        
        setCurrentCountry(detectedCountryObj);
        localStorage.setItem('selectedCountry', detectedCountryId);
        
        const countryChangedEvent = new CustomEvent('countryChanged', { 
          detail: { country: detectedCountryObj } 
        });
        window.dispatchEvent(countryChangedEvent);
        
        console.log(`Auto-detected country: ${detectedCountryObj.name} (${detectedCountryId})`);
      }
    } catch (error) {
      console.error('Error auto-detecting country:', error);
    }
  };
  
  autoDetectCountry();
}, [user, isNewUser]);

useEffect(() => {
  userRef.current = user || null;
  if (!user?.uid) {
    ledgerDataSourceRef.current = {
      scope: 'none',
      userId: null,
      workspaceId: null
    };
  }
}, [user]);

useEffect(() => {
  activeWorkspaceRef.current = activeWorkspace;
}, [activeWorkspace]);

  useEffect(() => {
    let isCancelled = false;

    const loadWorkspaceSubscription = async () => {
      if (!isExternalSharedWorkspace || !activeWorkspace?.ownerId) {
        setWorkspaceSubscription(null);
        setIsWorkspaceSubscriptionLoading(false);
        return;
      }

      setIsWorkspaceSubscriptionLoading(true);
      try {
        const subscription = await getUserSubscription(activeWorkspace.ownerId);
        if (!isCancelled) {
          setWorkspaceSubscription(subscription || null);
        }
      } catch (error) {
        console.warn('Unable to load workspace subscription:', error);
        if (!isCancelled) {
          setWorkspaceSubscription(null);
        }
      } finally {
        if (!isCancelled) {
          setIsWorkspaceSubscriptionLoading(false);
        }
      }
    };

    loadWorkspaceSubscription();

    return () => {
      isCancelled = true;
    };
  }, [activeWorkspace?.ownerId, isExternalSharedWorkspace]);

  const getAvailableWorkspaceOptions = useCallback((nextState) => {
    const workspaceMap = new Map();

    if (nextState?.ownWorkspace?.id) {
      workspaceMap.set(nextState.ownWorkspace.id, nextState.ownWorkspace);
    }

    (nextState?.acceptedWorkspaces || []).forEach((workspace) => {
      if (workspace?.id) {
        workspaceMap.set(workspace.id, workspace);
      }
    });

    return [...workspaceMap.values()];
  }, []);

  const resolveWorkspaceSelection = useCallback((nextState, targetUser = user) => {
    const availableWorkspaces = getAvailableWorkspaceOptions(nextState);
    const hasOwnWorkspace = Boolean(nextState?.ownWorkspace?.id);
    const storageKey = targetUser?.uid ? getCollaborationWorkspaceStorageKey(targetUser.uid) : '';

    let storedSelection = '';
    if (storageKey) {
      try {
        storedSelection = localStorage.getItem(storageKey) || '';
      } catch {
        storedSelection = '';
      }
    }

    if (hasOwnWorkspace) {
      if (storedSelection && storedSelection !== PERSONAL_WORKSPACE_SELECTION) {
        const matchedWorkspace = availableWorkspaces.find((workspace) => workspace.id === storedSelection);
        if (matchedWorkspace) {
          return matchedWorkspace;
        }
      }

      return nextState.ownWorkspace;
    }

    if (storedSelection === PERSONAL_WORKSPACE_SELECTION) {
      return null;
    }

    if (storedSelection) {
      const matchedWorkspace = availableWorkspaces.find((workspace) => workspace.id === storedSelection);
      if (matchedWorkspace) {
        return matchedWorkspace;
      }
    }

    return availableWorkspaces[0] || null;
  }, [getAvailableWorkspaceOptions, user]);

  const refreshCollaborationAccess = useCallback(async (targetUser = user) => {
    const requestId = ++collaborationRequestRef.current;
    if (!targetUser?.uid) {
      setCollaborationState({
        ownWorkspace: null,
        sentInvites: [],
        pendingInvites: [],
        acceptedWorkspaces: []
      });
      setActiveWorkspace(null);
      activeWorkspaceRef.current = null;
      return null;
    }

    setIsRefreshingCollaboration(true);
    try {
      try {
        await syncAcceptedInviteMemberships(targetUser);
      } catch (error) {
        console.warn('Unable to sync accepted invite memberships:', error);
      }

      const nextState = await getCollaborationStateForUser(targetUser);
      if (requestId !== collaborationRequestRef.current || auth.currentUser?.uid !== targetUser.uid) {
        return activeWorkspaceRef.current;
      }
      const resolvedWorkspace = resolveWorkspaceSelection(nextState, targetUser);
      const storageKey = getCollaborationWorkspaceStorageKey(targetUser.uid);

      setCollaborationState(nextState);
      setActiveWorkspace(resolvedWorkspace);
      activeWorkspaceRef.current = resolvedWorkspace;

      try {
        if (resolvedWorkspace?.id) {
          localStorage.setItem(storageKey, resolvedWorkspace.id);
        } else if (!nextState.ownWorkspace) {
          localStorage.setItem(storageKey, PERSONAL_WORKSPACE_SELECTION);
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch {}

      return resolvedWorkspace;
    } finally {
      setIsRefreshingCollaboration(false);
    }
  }, [resolveWorkspaceSelection, user]);

  const buildWorkspaceDatasetSnapshot = useCallback(() => ({
    [SHARED_WORKSPACE_DATASETS.TRANSACTIONS]: Array.isArray(transactions) ? transactions : [],
    [SHARED_WORKSPACE_DATASETS.DEPRECIATION]:
      customDepreciationPeriods && typeof customDepreciationPeriods === 'object' ? customDepreciationPeriods : {},
    [SHARED_WORKSPACE_DATASETS.SERVICE_PRICES]:
      servicePrices && typeof servicePrices === 'object' ? servicePrices : DEFAULT_SERVICES,
    [SHARED_WORKSPACE_DATASETS.COST_ITEMS]: Array.isArray(costItems) ? costItems : [],
    [SHARED_WORKSPACE_DATASETS.EXTRA_CATEGORIES]: normalizeExtraCategoriesShape(extraCategories),
    [SHARED_WORKSPACE_DATASETS.GOALS]: normalizeTargetIncomeData(targetIncomeData),
    [SHARED_WORKSPACE_DATASETS.VAT_SETTINGS]:
      vatSettings && typeof vatSettings === 'object' ? vatSettings : {},
    [SHARED_WORKSPACE_DATASETS.CLIENTS]: mergeClientLists([], clients),
    [SHARED_WORKSPACE_DATASETS.BUSINESS_PROFILE]: buildBusinessProfileSettings(businessType, industryId, businessProfileDetails),
    [SHARED_WORKSPACE_DATASETS.BANK_ACCOUNTS]: normalizeBankAccounts(bankAccounts),
    [SHARED_WORKSPACE_DATASETS.JOURNAL_ENTRIES]: normalizeJournalEntries(journalEntries)
  }), [
    bankAccounts,
    businessProfileDetails,
    businessType,
    clients,
    costItems,
    customDepreciationPeriods,
    extraCategories,
    normalizeExtraCategoriesShape,
    industryId,
    journalEntries,
    servicePrices,
    targetIncomeData,
    transactions,
    vatSettings
  ]);

  const normalizeSuspiciousReceiptVehicleCategories = useCallback((sourceTransactions = []) => {
    if (!Array.isArray(sourceTransactions)) return [];

    let changed = false;
    const normalizedTransactions = sourceTransactions.map((transaction) => {
      const transactionType = String(transaction?.type || '').toLowerCase();
      const categoryId = normalizeCategoryId(transaction?.category || '');
      const hasReceiptContext = Boolean(
        transaction?.receiptId ||
        transaction?.receiptImageUrl ||
        transaction?.imageUrl ||
        (Array.isArray(transaction?.items) && transaction.items.length > 0)
      );

      if ((transactionType !== 'expenses' && transactionType !== 'expense') || categoryId !== 'vehicle' || !hasReceiptContext) {
        return transaction;
      }

      const nextCategory = guardAutoDetectedReceiptExpenseCategory({
        category: categoryId,
        amount: transaction?.amount,
        vendor: transaction?.description || transaction?.vendor || '',
        items: Array.isArray(transaction?.items) ? transaction.items : [],
        rawText: transaction?.rawText || transaction?.text || transaction?.fullText || ''
      });

      if (!nextCategory || nextCategory === categoryId) {
        return transaction;
      }

      changed = true;

      const nextTransaction = {
        ...transaction,
        category: nextCategory
      };

      if (Array.isArray(transaction?.items) && transaction.items.length === 1) {
        const onlyItemCategory = normalizeCategoryId(transaction.items[0]?.category || '');
        if (onlyItemCategory === 'vehicle') {
          nextTransaction.items = transaction.items.map((item) => ({
            ...item,
            category: nextCategory
          }));
        }
      }

      if (Array.isArray(transaction?.categoryBreakdown) && transaction.categoryBreakdown.length === 1) {
        const onlyBreakdownCategory = normalizeCategoryId(transaction.categoryBreakdown[0]?.category || '');
        if (onlyBreakdownCategory === 'vehicle') {
          nextTransaction.categoryBreakdown = transaction.categoryBreakdown.map((entry) => ({
            ...entry,
            category: nextCategory
          }));
        }
      }

      return nextTransaction;
    });

    return changed ? normalizedTransactions : sourceTransactions;
  }, []);

  const dedupeReceiptBackedTransactions = useCallback((sourceTransactions = []) => {
    if (!Array.isArray(sourceTransactions)) return [];

    const getReceiptTransactionKey = (transaction) => {
      const receiptId = String(transaction?.receiptId || transaction?.sourceReceiptId || '').trim();
      if (!receiptId) return '';

      const dateKey = normalizeDateKey(transaction?.date || transaction?.invoiceDate || '');
      const amountKey = normalizeAmountKey(transaction?.amount);
      const descriptionKey = normalizeTextKey(transaction?.description || transaction?.vendor || '');

      if (!amountKey) return '';
      return [receiptId, dateKey, amountKey, descriptionKey].join('|');
    };

    const getTransactionUpdatedTime = (transaction) => {
      const dateValue = getTransactionEntryDateValue(transaction);
      return dateValue ? dateValue.getTime() : 0;
    };

    let changed = false;
    const mergedByReceiptKey = new Map();
    const passthroughTransactions = [];

    sourceTransactions.forEach((transaction) => {
      const key = getReceiptTransactionKey(transaction);
      if (!key) {
        passthroughTransactions.push(transaction);
        return;
      }

      const existing = mergedByReceiptKey.get(key);
      if (!existing) {
        mergedByReceiptKey.set(key, transaction);
        return;
      }

      changed = true;
      if (getTransactionUpdatedTime(transaction) >= getTransactionUpdatedTime(existing)) {
        mergedByReceiptKey.set(key, transaction);
      }
    });

    if (!changed) return sourceTransactions;

    return [
      ...passthroughTransactions,
      ...mergedByReceiptKey.values()
    ];
  }, []);

  const normalizeLoadedTransactions = useCallback((sourceTransactions = []) => (
    dedupeReceiptBackedTransactions(
      normalizeSuspiciousReceiptVehicleCategories(sourceTransactions)
    )
  ), [dedupeReceiptBackedTransactions, normalizeSuspiciousReceiptVehicleCategories]);


  const loadUserDataFromDrive = async (workspaceOverride) => {
    // Auth/session callbacks may still hold the selection from before a switch.
    // Ignore those calls before they can cancel the selected client's load.
    if (workspaceOverride !== undefined &&
        (workspaceOverride?.id || null) !== (activeWorkspaceRef.current?.id || null)) return;
    const isLatestRequest = ledgerLoadTrackerRef.current.begin();
    const loadUserId = auth.currentUser?.uid || userRef.current?.uid || user?.uid || null;
    let selectedWorkspace = workspaceOverride;

    if (workspaceOverride === undefined) {
      try {
        selectedWorkspace = await refreshCollaborationAccess(userRef.current || user);
      } catch (error) {
        console.warn('Unable to refresh collaboration access before loading data:', error);
        selectedWorkspace = activeWorkspaceRef.current || null;
      }
    }

    const isCurrentLoad = () => isLatestRequest() &&
      loadUserId === (auth.currentUser?.uid || userRef.current?.uid || user?.uid || null) &&
      (selectedWorkspace?.id || null) === (activeWorkspaceRef.current?.id || null);
    if (!isCurrentLoad()) return;
    const staleLoad = new Error('Ledger selection changed while loading');
    const loadForCurrentSelection = async (promise) => {
      const result = await promise;
      if (!isCurrentLoad()) throw staleLoad;
      return result;
    };

    if (selectedWorkspace?.id) {
      setIsDataLoading(true);
      ledgerDataSourceRef.current = {
        scope: 'loading',
        userId: userRef.current?.uid || user?.uid || null,
        workspaceId: selectedWorkspace.id
      };
      resetLedgerStateForAccountSwitch();
      try {
        const datasets = await loadForCurrentSelection(loadWorkspaceDatasets(selectedWorkspace.id));

        const workspaceTransactions = normalizeLoadedTransactions(
          Array.isArray(datasets?.transactions) ? datasets.transactions : []
        );
        setTransactionsState(workspaceTransactions);

        const yearsWithData = workspaceTransactions
          .map((t) => getTransactionYearValue(t))
          .filter((year) => Number.isFinite(year));
        if (yearsWithData.length > 0 && !yearsWithData.includes(activeFinancialYear)) {
          setActiveFinancialYear(Math.max(...yearsWithData));
        }

        setCustomDepreciationPeriods(
          datasets?.depreciation && typeof datasets.depreciation === 'object'
            ? datasets.depreciation
            : {}
        );

        const workspacePrices =
          datasets?.servicePrices && typeof datasets.servicePrices === 'object'
            ? datasets.servicePrices
            : DEFAULT_SERVICES;
        setServicePrices(workspacePrices);

        setCostItems(Array.isArray(datasets?.costItems) ? datasets.costItems : []);
        setExtraCategories(normalizeExtraCategoriesShape(datasets?.extraCategories));
        setTargetIncomeData(normalizeTargetIncomeData(datasets?.goals));

        const countryVatDefaults = getDefaultVatSettingsForCountry(currentCountry);
        const workspaceVatSettings =
          datasets?.vatSettings && typeof datasets.vatSettings === 'object'
            ? datasets.vatSettings
            : {};
        setVatSettingsState(sanitizeVatSettings(workspaceVatSettings, countryVatDefaults));
        setClients(Array.isArray(datasets?.clients) ? mergeClientLists([], datasets.clients) : []);
        setBankAccounts(normalizeBankAccounts(datasets?.bankAccounts));
        setJournalEntries(normalizeJournalEntries(datasets?.journalEntries));

        const hasWorkspaceBusinessProfile = datasets?.businessProfile && typeof datasets.businessProfile === 'object' && Object.keys(datasets.businessProfile).length > 0;
        if (hasWorkspaceBusinessProfile) {
          const nextBusinessProfile = buildBusinessProfileSettings(
            datasets.businessProfile.businessType,
            datasets.businessProfile.industryId,
            datasets.businessProfile
          );
          setBusinessType(nextBusinessProfile.businessType);
          setIndustryId(nextBusinessProfile.industryId);
          setBusinessProfileDetails(normalizeBusinessProfileDetails(nextBusinessProfile));
          writeLocalBusinessProfileSettings(nextBusinessProfile);
        }

        ledgerDataSourceRef.current = {
          scope: 'workspace',
          userId: loadUserId,
          workspaceId: selectedWorkspace.id
        };
        setLedgerRenderSource(ledgerDataSourceRef.current);
      } catch (error) {
        if (!isCurrentLoad()) return;
        console.error('Error loading shared workspace data:', error);
        ledgerDataSourceRef.current = {
          scope: 'error',
          userId: userRef.current?.uid || user?.uid || null,
          workspaceId: selectedWorkspace.id
        };
        alert('There was a problem loading the shared workspace. Please try again.');
      } finally {
        if (isCurrentLoad()) {
          costItemsLoadedRef.current = true;
          servicePricesLoadedRef.current = true;
          extraCategoriesLoadedRef.current = true;
          goalsLoadedRef.current = true;
          vatSettingsLoadedRef.current = true;
          clientsLoadedRef.current = true;
          bankAccountsLoadedRef.current = true;
          journalEntriesLoadedRef.current = true;
          businessProfileLoadedRef.current = true;
          setIsDataLoading(false);
        }
      }

      return;
    }

    setIsDataLoading(true);
    ledgerDataSourceRef.current = {
      scope: 'loading',
      userId: userRef.current?.uid || user?.uid || null,
      workspaceId: null
    };
    resetLedgerStateForAccountSwitch();

    // Check if we're signed in to Drive first
    if (!isDriveSignedIn()) {
      console.log('Not signed in to Drive, will use localStorage');
      // Set a flag but don't show the login modal here; callers handle UI.
      try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
    }

    try {
      // Load transactions using hybrid storage
      console.log('Attempting to load transactions');
      const transactions = await loadForCurrentSelection(loadTransactionsFromHybrid());
      if (transactions && Array.isArray(transactions)) {
        const normalizedTransactions = normalizeLoadedTransactions(transactions);
        setTransactionsState(normalizedTransactions);
        console.log('Transactions loaded successfully:', normalizedTransactions.length);

        // If the current financial year has no data, fall back to the latest year with transactions
        const yearsWithData = normalizedTransactions
          .map((t) => getTransactionYearValue(t))
          .filter((year) => Number.isFinite(year));
        if (yearsWithData.length > 0 && !yearsWithData.includes(activeFinancialYear)) {
          const latestYear = Math.max(...yearsWithData);
          console.warn(`No transactions found for ${activeFinancialYear}; switching to ${latestYear}.`);
          setActiveFinancialYear(latestYear);
        }
      } else {
        console.log('No transactions found or invalid format');
        setTransactionsState([]);
      }

      // Load depreciation periods using hybrid storage
      console.log('Attempting to load depreciation periods');
      const depreciationPeriods = await loadForCurrentSelection(loadDepreciationFromHybrid());
      if (depreciationPeriods && typeof depreciationPeriods === 'object') {
        setCustomDepreciationPeriods(depreciationPeriods);
        console.log('Depreciation periods loaded successfully');
      } else {
        console.log('No depreciation periods found or invalid format');
        setCustomDepreciationPeriods({});
      }

      // Load service prices using hybrid storage
      console.log('Attempting to load service prices');
      const prices = await loadForCurrentSelection(loadServicePricesFromHybrid());
      if (prices && typeof prices === 'object') {
        setServicePrices(prices);
        console.log('Service prices loaded successfully');
      } else {
        console.log('No service prices found or invalid format, setting defaults');
        // Initialize with defaults if no prices found
        setServicePrices(DEFAULT_SERVICES);
        await saveServicePricesToHybrid(DEFAULT_SERVICES);
      }

      // Load cost items using hybrid storage
      console.log('Attempting to load cost items');
      const loadedCostItems = await loadForCurrentSelection(loadCostItemsFromHybrid());
      if (Array.isArray(loadedCostItems)) {
        setCostItems(loadedCostItems);
        console.log('Cost items loaded successfully:', loadedCostItems.length);
      } else {
        console.log('No cost items found or invalid format');
      }

      // Load custom categories using hybrid storage
      console.log('Attempting to load custom categories');
      const loadedExtraCategories = await loadForCurrentSelection(loadExtraCategoriesFromHybrid());
      if (loadedExtraCategories && typeof loadedExtraCategories === 'object') {
        const normalizedExtraCategories = normalizeExtraCategoriesShape(loadedExtraCategories);
        setExtraCategories(normalizedExtraCategories);
        console.log('Custom categories loaded successfully:', {
          income: normalizedExtraCategories.income.length,
          expenses: normalizedExtraCategories.expenses.length
        });
      } else {
        console.log('No custom categories found or invalid format');
        setExtraCategories({ income: [], expenses: [] });
      }

      // Load income goals using hybrid storage
      console.log('Attempting to load income goals');
      const loadedGoals = await loadForCurrentSelection(loadGoalsFromHybrid());
      if (loadedGoals && typeof loadedGoals === 'object') {
        const normalizedGoals = normalizeTargetIncomeData(loadedGoals);
        setTargetIncomeData(normalizedGoals);
        console.log('Income goals loaded successfully');
      } else {
        console.log('No goals found or invalid format');
      }

      // Load business profile settings using hybrid storage
      console.log('Attempting to load business profile settings');
      const loadedBusinessProfile = await loadForCurrentSelection(loadBusinessProfileFromHybrid());
      const hasLoadedBusinessProfile = loadedBusinessProfile && typeof loadedBusinessProfile === 'object' && Object.keys(loadedBusinessProfile).length > 0;
      if (hasLoadedBusinessProfile) {
        const nextBusinessProfile = buildBusinessProfileSettings(
          loadedBusinessProfile.businessType,
          loadedBusinessProfile.industryId,
          loadedBusinessProfile
        );
        setBusinessType(nextBusinessProfile.businessType);
        setIndustryId(nextBusinessProfile.industryId);
        setBusinessProfileDetails(normalizeBusinessProfileDetails(nextBusinessProfile));
        writeLocalBusinessProfileSettings(nextBusinessProfile);
        console.log('Business profile settings loaded successfully');
      } else {
        console.log('No business profile settings found or invalid format');
      }

      // Load VAT settings using hybrid storage
      console.log('Attempting to load VAT settings');
      const loadedVatSettings = await loadForCurrentSelection(loadVatSettingsFromHybrid());
      const countryVatDefaults = getDefaultVatSettingsForCountry(currentCountry);
      if (loadedVatSettings && typeof loadedVatSettings === 'object' && Object.keys(loadedVatSettings).length > 0) {
        setVatSettingsState(sanitizeVatSettings(loadedVatSettings, countryVatDefaults));
        console.log('VAT settings loaded successfully');
      } else {
        console.log('No VAT settings found or invalid format');
      }

      // Load clients using hybrid storage
      console.log('Attempting to load clients');
      const loadedClients = await loadForCurrentSelection(loadClientsFromHybrid());
      if (Array.isArray(loadedClients)) {
        setClients(mergeClientLists([], loadedClients));
        console.log('Clients loaded successfully:', loadedClients.length);
      } else {
        console.log('No clients found or invalid format');
        setClients([]);
      }

      // Load bank accounts using hybrid storage
      console.log('Attempting to load bank accounts');
      const loadedBankAccounts = await loadForCurrentSelection(loadBankAccountsFromHybrid());
      if (Array.isArray(loadedBankAccounts)) {
        setBankAccounts(normalizeBankAccounts(loadedBankAccounts));
        console.log('Bank accounts loaded successfully:', loadedBankAccounts.length);
      } else {
        console.log('No bank accounts found or invalid format');
        setBankAccounts([]);
      }

      // Load manual journal entries using hybrid storage
      console.log('Attempting to load manual journal entries');
      const loadedJournalEntries = await loadForCurrentSelection(loadJournalEntriesFromHybrid());
      if (Array.isArray(loadedJournalEntries)) {
        setJournalEntries(normalizeJournalEntries(loadedJournalEntries));
        console.log('Manual journal entries loaded successfully:', loadedJournalEntries.length);
      } else {
        console.log('No manual journal entries found or invalid format');
        setJournalEntries([]);
      }

      // Store authentication state in localStorage for persistence
      if (isDriveSignedIn()) {
        localStorage.setItem('driveAuthState', 'authenticated');
      }

      ledgerDataSourceRef.current = {
        scope: 'personal',
        userId: loadUserId,
        workspaceId: null
      };
      setLedgerRenderSource(ledgerDataSourceRef.current);

      if (!hasLoadedBusinessProfile) {
        await saveBusinessProfileToHybrid(readLocalBusinessProfileSettings());
      }

    } catch (error) {
      if (!isCurrentLoad()) return;
      console.error('Comprehensive error loading user data:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      // Provide user feedback
      alert('There was a problem loading your data. Using locally stored data where available.');

      // Set default empty states
      setTransactionsState((prev) => (Array.isArray(prev) ? prev : []));
      setCustomDepreciationPeriods((prev) => (prev && typeof prev === 'object' ? prev : {}));
      setServicePrices((prev) => (prev && typeof prev === 'object' ? prev : DEFAULT_SERVICES));
      setExtraCategories((prev) => normalizeExtraCategoriesShape(prev));
      setTargetIncomeData((prev) => normalizeTargetIncomeData(prev));
      setVatSettingsState((prev) => sanitizeVatSettings(prev, getDefaultVatSettingsForCountry(currentCountry)));
      setClients((prev) => (Array.isArray(prev) ? mergeClientLists([], prev) : []));
      setBankAccounts((prev) => normalizeBankAccounts(prev));
      setJournalEntries((prev) => normalizeJournalEntries(prev));
      const fallbackBusinessProfile = readLocalBusinessProfileSettings();
      setBusinessType(fallbackBusinessProfile.businessType);
      setIndustryId(fallbackBusinessProfile.industryId);
      setBusinessProfileDetails(normalizeBusinessProfileDetails(fallbackBusinessProfile));
      ledgerDataSourceRef.current = {
        scope: 'error',
        userId: userRef.current?.uid || user?.uid || null,
        workspaceId: null
      };
    } finally {
      if (isCurrentLoad()) {
        costItemsLoadedRef.current = true;
        servicePricesLoadedRef.current = true;
        extraCategoriesLoadedRef.current = true;
        goalsLoadedRef.current = true;
        vatSettingsLoadedRef.current = true;
        clientsLoadedRef.current = true;
        bankAccountsLoadedRef.current = true;
        journalEntriesLoadedRef.current = true;
        businessProfileLoadedRef.current = true;
        setIsDataLoading(false);
      }
    }
  };

  const handleSendAccountantInvite = async (accountantEmail) => {
    if (!user?.uid) {
      throw new Error('Sign in before inviting an accountant.');
    }
    if (activeWorkspace?.id && activeWorkspace.ownerId && activeWorkspace.ownerId !== user.uid) {
      throw new Error('Switch back to your own data before sending invitations.');
    }

    setIsCollaborationBusy(true);
    try {
      await createAccountantInvite({
        owner: user,
        accountantEmail,
        datasets: buildWorkspaceDatasetSnapshot()
      });

      const nextWorkspace = await refreshCollaborationAccess(user);
      await loadUserDataFromDrive(nextWorkspace);
    } finally {
      setIsCollaborationBusy(false);
    }
  };

  const handleAcceptAccountantInvite = async (inviteId) => {
    if (!user?.uid) {
      throw new Error('Sign in before accepting an invitation.');
    }

    setIsCollaborationBusy(true);
    try {
      const { workspace } = await acceptAccountantInvite({ inviteId, user });
      const nextWorkspace = await refreshCollaborationAccess(user);
      const acceptedWorkspace = workspace?.id ? workspace : null;
      const resolvedWorkspace = acceptedWorkspace || nextWorkspace || null;

      if (resolvedWorkspace?.id) {
        try {
          localStorage.setItem(getCollaborationWorkspaceStorageKey(user.uid), resolvedWorkspace.id);
        } catch {}
      }

      setActiveWorkspace(resolvedWorkspace);
      activeWorkspaceRef.current = resolvedWorkspace;
      await loadUserDataFromDrive(resolvedWorkspace);
    } finally {
      setIsCollaborationBusy(false);
    }
  };

  const handleRevokeAccountantInvite = async (inviteId) => {
    setIsCollaborationBusy(true);
    try {
      await revokeAccountantInvite(inviteId);
      const nextWorkspace = await refreshCollaborationAccess(user);
      await loadUserDataFromDrive(nextWorkspace);
    } finally {
      setIsCollaborationBusy(false);
    }
  };

  const handleWorkspaceSelectionChange = async (workspaceId) => {
    collaborationRequestRef.current += 1;
    if (!user?.uid) return;

    const availableWorkspaces = getAvailableWorkspaceOptions(collaborationState);
    const nextWorkspace = workspaceId
      ? availableWorkspaces.find((workspace) => workspace.id === workspaceId) || null
      : null;
    const storageKey = getCollaborationWorkspaceStorageKey(user.uid);

    try {
      if (nextWorkspace?.id) {
        localStorage.setItem(storageKey, nextWorkspace.id);
      } else if (!collaborationState.ownWorkspace) {
        localStorage.setItem(storageKey, PERSONAL_WORKSPACE_SELECTION);
      } else {
        localStorage.setItem(storageKey, collaborationState.ownWorkspace.id);
      }
    } catch {}

    const resolvedWorkspace = nextWorkspace || collaborationState.ownWorkspace || null;
    setActiveWorkspace(resolvedWorkspace);
    activeWorkspaceRef.current = resolvedWorkspace;
    await loadUserDataFromDrive(nextWorkspace || resolvedWorkspace);
  };

  const mergeTransactionsForWorkspaceRecovery = useCallback((workspaceTransactions = [], localTransactions = []) => {
    const getRecoveryKey = (transaction) => {
      if (!transaction || typeof transaction !== 'object') return '';
      if (transaction.id !== null && transaction.id !== undefined && transaction.id !== '') {
        return `id:${String(transaction.id)}`;
      }

      return [
        'anon',
        String(transaction.type || ''),
        String(transaction.amount || ''),
        String(transaction.date || ''),
        String(transaction.category || ''),
        String(transaction.description || transaction.vendor || '')
      ].join('|').toLowerCase();
    };

    const getRecoveryTimestamp = (transaction) => {
      const dateValue = getTransactionEntryDateValue(transaction);
      return dateValue ? dateValue.getTime() : 0;
    };

    const mergedByKey = new Map();
    const idlessTransactions = [];

    const addTransaction = (transaction, source) => {
      if (!transaction || typeof transaction !== 'object') return;

      const key = getRecoveryKey(transaction);
      if (!key || key === 'anon|||||') {
        idlessTransactions.push({ ...transaction, recoveredFromLocal: source === 'local' || transaction.recoveredFromLocal });
        return;
      }

      const transactionToStore = source === 'local'
        ? { ...transaction, recoveredFromLocal: true }
        : transaction;
      const existing = mergedByKey.get(key);
      if (!existing) {
        mergedByKey.set(key, transactionToStore);
        return;
      }

      if (getRecoveryTimestamp(transactionToStore) >= getRecoveryTimestamp(existing)) {
        mergedByKey.set(key, transactionToStore);
      }
    };

    (Array.isArray(workspaceTransactions) ? workspaceTransactions : []).forEach((transaction) => {
      addTransaction(transaction, 'workspace');
    });
    (Array.isArray(localTransactions) ? localTransactions : []).forEach((transaction) => {
      addTransaction(transaction, 'local');
    });

    return [...mergedByKey.values(), ...idlessTransactions].sort((left, right) => (
      getRecoveryTimestamp(right) - getRecoveryTimestamp(left)
    ));
  }, []);

  const handleRecoverLocalTransactionsToWorkspace = async () => {
    if (!user?.uid) {
      throw new Error('Sign in before recovering transactions.');
    }

    const targetWorkspace = collaborationState.ownWorkspace;
    if (!targetWorkspace?.id || targetWorkspace.ownerId !== user.uid) {
      throw new Error('Only the workspace owner can recover local transactions into My shared ledger.');
    }

    if (!window.confirm('Recover local transactions into My shared ledger? This will merge saved local records with the shared workspace and keep existing shared transactions.')) {
      return;
    }

    setIsCollaborationBusy(true);
    try {
      const localTransactions = await loadTransactionsFromHybrid();
      if (!Array.isArray(localTransactions) || localTransactions.length === 0) {
        alert('No local transactions were found on this device or Google Drive session.');
        return;
      }

      const datasets = await loadWorkspaceDatasets(targetWorkspace.id);
      const workspaceTransactions = Array.isArray(datasets?.transactions) ? datasets.transactions : [];
      const mergedTransactions = normalizeSuspiciousReceiptVehicleCategories(
        mergeTransactionsForWorkspaceRecovery(workspaceTransactions, localTransactions)
      );

      await saveWorkspaceDataset(
        targetWorkspace.id,
        SHARED_WORKSPACE_DATASETS.TRANSACTIONS,
        mergedTransactions,
        user
      );

      setActiveWorkspace(targetWorkspace);
      activeWorkspaceRef.current = targetWorkspace;
      try {
        localStorage.setItem(getCollaborationWorkspaceStorageKey(user.uid), targetWorkspace.id);
      } catch {}
      await loadUserDataFromDrive(targetWorkspace);

      const addedOrRecoveredCount = Math.max(0, mergedTransactions.length - workspaceTransactions.length);
      alert(
        `Recovered ${addedOrRecoveredCount} transaction${addedOrRecoveredCount === 1 ? '' : 's'} into My shared ledger. ` +
        `Shared ledger now has ${mergedTransactions.length} transaction${mergedTransactions.length === 1 ? '' : 's'}.`
      );
    } finally {
      setIsCollaborationBusy(false);
    }
  };

  const handleReplaceSharedLedgerFromLocalTransactions = async () => {
    if (!user?.uid) {
      throw new Error('Sign in before replacing shared ledger transactions.');
    }

    const targetWorkspace = collaborationState.ownWorkspace;
    if (!targetWorkspace?.id || targetWorkspace.ownerId !== user.uid) {
      throw new Error('Only the workspace owner can replace My shared ledger from local transactions.');
    }

    const confirmed = window.confirm(
      'Replace My shared ledger transactions with the transactions saved on this owner account or Google Drive session? ' +
      'Use this only when the shared ledger is showing the wrong client data. Existing shared ledger transactions will be overwritten.'
    );
    if (!confirmed) {
      return;
    }

    const finalConfirm = window.confirm(
      'Final confirmation: this will overwrite the shared ledger transaction list. Continue only if the owner account currently has the correct local records.'
    );
    if (!finalConfirm) {
      return;
    }

    setIsCollaborationBusy(true);
    try {
      const localTransactions = await loadTransactionsFromHybrid();
      if (!Array.isArray(localTransactions) || localTransactions.length === 0) {
        alert('No local transactions were found on this device or Google Drive session.');
        return;
      }

      const replacementTransactions = normalizeSuspiciousReceiptVehicleCategories(
        localTransactions.map((transaction) => ({
          ...transaction,
          recoveredFromLocal: true,
          replacedSharedLedgerAt: new Date().toISOString()
        }))
      );

      await saveWorkspaceDataset(
        targetWorkspace.id,
        SHARED_WORKSPACE_DATASETS.TRANSACTIONS,
        replacementTransactions,
        user
      );

      setActiveWorkspace(targetWorkspace);
      activeWorkspaceRef.current = targetWorkspace;
      try {
        localStorage.setItem(getCollaborationWorkspaceStorageKey(user.uid), targetWorkspace.id);
      } catch {}
      await loadUserDataFromDrive(targetWorkspace);

      alert(
        `Replaced My shared ledger with ${replacementTransactions.length} local transaction${replacementTransactions.length === 1 ? '' : 's'}.`
      );
    } finally {
      setIsCollaborationBusy(false);
    }
  };

  const dismissDriveDisconnectWarning = () => {
    driveWarningDismissedRef.current = true;
    setShowDriveDisconnectWarning(false);
  };

  const handleReconnectDriveWarning = async () => {
    if (typeof window !== 'undefined' && !window.navigator.onLine) {
      alert('You are offline. Reconnect to the internet, then reconnect Google Drive to sync your saved data.');
      return;
    }

    setIsReconnectingDriveWarning(true);
    try {
      await signInWithGoogle();
      const driveReady = await ensureDriveSession();
      if (!driveReady) {
        throw new Error('Drive session not available');
      }

      await loadUserDataFromDrive();
      localStorage.removeItem('needsDriveAuth');
      localStorage.setItem('driveAuthState', 'authenticated');
      driveWarningDismissedRef.current = false;
      setShowDriveDisconnectWarning(false);
      window.dispatchEvent(new CustomEvent('drive-reconnected'));
    } catch (error) {
      console.error('Drive reconnect from warning modal failed:', error);
      alert('Could not reconnect Google Drive. You can continue offline, but reconnect before clearing browser/app storage.');
    } finally {
      setIsReconnectingDriveWarning(false);
    }
  };

  const showDrivePermissionRequiredWarning = useCallback((actionLabel = 'adding transactions') => {
    try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
    driveWarningDismissedRef.current = false;
    setShowDriveDisconnectWarning(true);
    alert(
      `Google Drive permission is required before ${actionLabel}. Please choose Reconnect Drive and tick/allow the Drive permission. ` +
      'If you continue without syncing, transactions may only exist on this device and can be lost if you log out, clear browser/app storage, or switch devices.'
    );
  }, []);

  const ensureDrivePermissionBeforeTransactionChange = useCallback((actionLabel = 'changing transactions') => {
    if (!user) {
      setShowAuthRequired(true);
      return false;
    }

    if (activeWorkspaceRef.current?.id || isDriveSignedIn()) {
      return canPersistLedgerDataset(
        ledgerRenderSource,
        ledgerDataSourceRef.current,
        activeWorkspaceRef.current?.id,
        auth.currentUser?.uid || user?.uid
      );
    }

    showDrivePermissionRequiredWarning(actionLabel);
    return false;
  }, [ledgerRenderSource, showDrivePermissionRequiredWarning, user]);

  const syncTransactionsBeforeLeaving = useCallback(async ({ showErrors = false } = {}) => {
    if (!user || activeWorkspaceRef.current?.id) return true;
    if (!Array.isArray(transactions) || transactions.length === 0) return true;

    if (!isDriveSignedIn()) {
      try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
      driveWarningDismissedRef.current = false;
      setShowDriveDisconnectWarning(true);
      if (showErrors) {
        alert('Google Drive permission is not confirmed, so transactions cannot be safely synced before leaving. Reconnect Drive before logging out or clearing storage.');
      }
      return false;
    }

    try {
      await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, transactions);
      try { localStorage.setItem('lastSyncTime', new Date().toISOString()); } catch {}
      return true;
    } catch (error) {
      console.error('Failed to sync transactions before leaving:', error);
      if (showErrors) {
        alert('Transactions could not be synced to Google Drive. Stay signed in and try reconnecting Drive before leaving.');
      }
      return false;
    }
  }, [persistDatasetForCurrentAccess, transactions, user]);

  useEffect(() => {
    if (!user) {
      driveWarningDismissedRef.current = false;
      setShowDriveDisconnectWarning(false);
      return;
    }

    const evaluateDriveWarningState = () => {
      const needsDriveAuth = (() => {
        try {
          return localStorage.getItem('needsDriveAuth') === 'true';
        } catch {
          return false;
        }
      })();

      const driveConnected = isDriveSignedIn();
      const shouldWarn = needsDriveAuth || (isDriveInitialized && !driveConnected);

      if (shouldWarn) {
        if (!driveWarningDismissedRef.current) {
          setShowDriveDisconnectWarning(true);
        }
      } else {
        driveWarningDismissedRef.current = false;
        setShowDriveDisconnectWarning(false);
      }
    };

    evaluateDriveWarningState();

    const warningInterval = window.setInterval(evaluateDriveWarningState, 5000);
    window.addEventListener('drive-reconnected', evaluateDriveWarningState);
    window.addEventListener('online', evaluateDriveWarningState);
    window.addEventListener('offline', evaluateDriveWarningState);

    return () => {
      window.clearInterval(warningInterval);
      window.removeEventListener('drive-reconnected', evaluateDriveWarningState);
      window.removeEventListener('online', evaluateDriveWarningState);
      window.removeEventListener('offline', evaluateDriveWarningState);
    };
  }, [user, isDriveInitialized]);

  useEffect(() => {
    if (!user) return undefined;

    const handleFinalSync = () => {
      syncTransactionsBeforeLeaving();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleFinalSync();
      }
    };

    const handleBeforeUnload = (event) => {
      if (
        !activeWorkspaceRef.current?.id &&
        Array.isArray(transactions) &&
        transactions.length > 0 &&
        !isDriveSignedIn()
      ) {
        const message = 'Google Drive permission is not confirmed. Reconnect Drive before leaving, or unsynced transactions may be lost.';
        try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
        event.preventDefault();
        event.returnValue = message;
        return message;
      }

      handleFinalSync();
      return undefined;
    };

    window.addEventListener('pagehide', handleFinalSync);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handleFinalSync);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncTransactionsBeforeLeaving, transactions, user]);

  // Attempt to restore previously authenticated sessions so users stay signed in.
  useEffect(() => {
    if (user) return;

    let cancelled = false;

    const bootstrapPersistedSession = async () => {
      try {
        const restoredUser = await restoreFirebaseAuthFromGoogle();

        if (restoredUser && !cancelled) {
          try { window.__authRestored = true; } catch {}
          setUser(restoredUser);
          setShowAuthRequired(false);
          setShowSignUpModal(false);
          setIsCheckingAuth(false);
          localStorage.removeItem('needsDriveAuth');

          try {
            const driveReady = await ensureDriveSession();
            if (driveReady) {
              const workspaceSelection = await refreshCollaborationAccess(restoredUser).catch((error) => {
                console.warn('Unable to refresh collaboration access after session restore:', error);
                return null;
              });
              await loadUserDataFromDrive(workspaceSelection);
            }
          } catch (driveError) {
            console.warn('Drive session restore failed after auth restore:', driveError);
          }
        }
      } catch (restoreError) {
        console.warn('Automatic auth restore was unsuccessful:', restoreError);
      }
    };

    bootstrapPersistedSession();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Country selection handlers
  const handleCountrySelect = (country, setupProfile = null) => {
    const resolvedCountry = resolveCountrySettings(country);
    console.log('Country selected:', resolvedCountry);
    setCurrentCountry(resolvedCountry);
    localStorage.setItem('selectedCountry', resolvedCountry.id);
    window.dispatchEvent(new CustomEvent('countryChanged', { detail: { country: resolvedCountry } }));

    if (setupProfile?.businessType) {
      const nextBusinessType = normalizeBusinessType(setupProfile.businessType);
      setBusinessType(nextBusinessType);
      writeLocalBusinessProfileSettings({ businessType: nextBusinessType, industryId, ...businessProfileDetails });
    }

    if (setupProfile?.industryId) {
      const nextIndustryId = normalizeIndustryId(setupProfile.industryId);
      setIndustryId(nextIndustryId);
      writeLocalBusinessProfileSettings({
        businessType: setupProfile?.businessType || businessType,
        industryId: nextIndustryId,
        ...businessProfileDetails
      });
    }

    if (Array.isArray(setupProfile?.serviceOfferings)) {
      const nextServiceOfferings = sanitizeServiceOfferings(setupProfile.serviceOfferings);
      setSelectedServiceOfferings(nextServiceOfferings);
      localStorage.setItem(SERVICE_OFFERINGS_STORAGE_KEY, JSON.stringify(nextServiceOfferings));
    }

    if (typeof setupProfile?.vatRegistered === 'boolean') {
      const vatRegistered = setupProfile.vatRegistered;
      const countryDefaults = getDefaultVatSettingsForCountry(resolvedCountry);
      setVatSettingsState((prev) => sanitizeVatSettings({
        ...prev,
        enabled: vatRegistered,
        isRegistered: vatRegistered,
        accountingBasis: vatRegistered && setupProfile?.vatAccountingBasis === 'invoice' ? 'invoice' : 'cash'
      }, countryDefaults));
      localStorage.setItem(VAT_STATUS_CONFIRMED_STORAGE_KEY, 'true');
      localStorage.setItem(VAT_ACCOUNTING_BASIS_CONFIRMED_STORAGE_KEY, 'true');
    }

    if (setupProfile?.businessType && setupProfile?.industryId && typeof setupProfile?.vatRegistered === 'boolean') {
      localStorage.setItem('setup_step_basics', 'true');
      setRequiresInitialBusinessSetup(false);
      refreshSetupProgress();
    }

    setIsNewUser(false);
    setShowCountrySelection(false);

    if (activeOnboardingStepIndex !== null) {
      const activeStep = onboardingGuideSteps[activeOnboardingStepIndex];
      if (activeStep?.id === 'country') {
        completeGuideStepAndAdvance(activeOnboardingStepIndex);
      }
    }
  };

  const handleCloseCountrySelection = (force = false) => {
    if ((isNewUser || requiresInitialBusinessSetup) && !force) return; // Initial setup must be completed.
    setShowCountrySelection(false);
    if (force) {
      setIsNewUser(false);
      refreshInitialBusinessSetupStatus();
    }

    if (!force && activeOnboardingStepIndex !== null) {
      const activeStep = onboardingGuideSteps[activeOnboardingStepIndex];
      if (activeStep?.id === 'country') {
        setTimeout(() => openOnboardingGuide(activeOnboardingStepIndex), 0);
      }
    }
  };

  // Onboarding handler for new users
  const handleOnboardingAction = (actionType) => {
    if (actionType === 'income') {
      setActiveTab('income');
      setSelectedInputMethod('manual');
      setIsAddingTransaction(true);
      resetSplitSaleState();
      resetSplitPaymentState();
      setCurrentTransaction(createEmptyTransactionDraft('income', {
        category: 'nail_services',
        receiptUrl: ''
      }));
    } else if (actionType === 'expense') {
      setActiveTab('expenses');
      setSelectedInputMethod('manual');
      setIsAddingTransaction(true);
      resetSplitSaleState();
      resetSplitPaymentState();
      setCurrentTransaction(createEmptyTransactionDraft('expenses', {
        category: 'supplies',
        receiptUrl: ''
      }));
    }
  };

  // Upgrade handler with trial abuse prevention
  const handleUpgradeClick = async () => {
    if (isExternalSharedWorkspace) {
      alert('This shared workspace subscription is managed by the client account, not the accountant login.');
      return;
    }

    let subscriptionSnapshot = liveSubscription;
    // Get latest subscription if we don't have one yet
    if (!subscriptionSnapshot && user?.uid) {
      try {
        subscriptionSnapshot = await getUserSubscription(user.uid);
      } catch (e) {
        console.warn('Could not fetch subscription, continuing with existing flags', e);
      }
    }

    const nowSafe = new Date();
    const trialEndSnapshot = subscriptionSnapshot?.trialEndDate
      ? (subscriptionSnapshot.trialEndDate.toDate
          ? subscriptionSnapshot.trialEndDate.toDate()
          : new Date(subscriptionSnapshot.trialEndDate))
      : null;

    const trialExpired = subscriptionSnapshot?.subscriptionStatus === 'trial' && trialEndSnapshot && trialEndSnapshot <= nowSafe;
    const currentStatus = (subscriptionSnapshot?.subscriptionStatus || '').toString().toLowerCase();
    const needsBillingAction = ['past_due', 'unpaid', 'incomplete'].includes(currentStatus);

    // If the user already has a Stripe subscription that needs attention, go to the billing portal
    // instead of creating a new checkout session (avoids duplicate subscriptions / double charges).
    if ((needsBillingAction || currentStatus === 'active') && user?.uid && user?.email) {
      try {
        await ensureStripeCustomer(user.uid, user.email, user.displayName || user.name);
        await createCustomerPortalSession(user.uid);
        return;
      } catch (portalError) {
        console.error('Error opening billing portal:', portalError);
        // Continue through normal upgrade flow as a fallback.
      }
    }

    const trialUsed =
      trialExpired ||
      subscriptionSnapshot?.subscriptionStatus === 'expired' ||
      subscriptionSnapshot?.trialUsed ||
      user?.subscriptionStatus === 'expired' ||
      user?.trialUsed ||
      localStorage.getItem('trial_used') === 'true';

    // If trial is used, go straight to checkout
    if (trialUsed) {
      if (user && user.email) {
        try {
          await ensureStripeCustomer(user.uid, user.email, user.displayName || user.name);
          await createCheckoutSession(user.uid, 'premium', user.email);
        } catch (error) {
          console.error('Error starting checkout after trial used:', error);
          alert('Unable to open checkout. Please try again or contact support.');
        }
      } else {
        setShowSignUpModal(true);
      }
      return;
    }

    // Trial not yet used: start trial flow
    if (user && user.email) {
      try {
        await createTrialUser(user.uid, user.email);

        // Refresh user data
        const updatedSubscriptionData = await getUserSubscription(user.uid);
        const updatedUserData = {
          ...user,
          subscriptionTier: updatedSubscriptionData?.subscriptionStatus || 'trial',
          subscriptionPlan: updatedSubscriptionData?.subscriptionPlan || 'premium',
          subscriptionStatus: updatedSubscriptionData?.subscriptionStatus || 'trial',
          lastUpdated: Date.now()
        };
        setUser(updatedUserData);
        persistSubscriptionTier(updatedUserData.subscriptionTier);

        setIsDataLoading(true);
        setTimeout(() => setIsDataLoading(false), 100);

        try { localStorage.setItem('trial_used', 'true'); } catch {}

        alert('Successfully upgraded to Premium Trial! Enjoy 14 days of full access.');
      } catch (error) {
        console.error('Error upgrading user:', error);

        if (error.message.includes('already used a free trial')) {
          // If backend signals trial already used, bounce to checkout
        try {
          await ensureStripeCustomer(user.uid, user.email, user.displayName || user.name);
          await createCheckoutSession(user.uid, 'premium', user.email);
          return;
        } catch (checkoutError) {
          console.error('Checkout fallback failed:', checkoutError);
          alert('Unable to open checkout. Please try again or contact support.');
        }
        } else {
          alert('Failed to start trial. Please try again.');
        }
      }
    } else {
      // User not signed in - show sign up modal
      setShowSignUpModal(true);
    }
  };

  const handlePWAInstall = async () => {
    if (setupProgress?.install) return;

    try {
      if (deferredInstallPrompt && typeof deferredInstallPrompt.prompt === 'function') {
        await deferredInstallPrompt.prompt();
        if (deferredInstallPrompt.userChoice) {
          await deferredInstallPrompt.userChoice;
        }
        setDeferredInstallPrompt(null);
      } else {
        alert('To install 70Lives, use your browser menu and choose "Add to Home Screen". You can use Safari or Chrome.');
      }
    } catch (error) {
      console.warn('Install prompt failed:', error);
    }

    toggleSetupStep('install', true);
    refreshSetupProgress();
  };

  const handleInstallStep = () => {
    handlePWAInstall();
  };

  const handleDriveConnectStep = async () => {
    try {
      let driveReady = await ensureDriveSession();
      if (!driveReady) {
        await signInWithGoogle();
        driveReady = await ensureDriveSession();
      }

      if (driveReady) {
        localStorage.setItem('setup_step_drive', 'true');
        localStorage.removeItem('needsDriveAuth');
        refreshSetupProgress();
      } else {
        showDrivePermissionRequiredWarning('adding transactions');
      }
    } catch (error) {
      console.error('Drive connection failed:', error);
      showDrivePermissionRequiredWarning('adding transactions');
    }
  };

  const handleBasicsStep = () => {
    setShowSetupChecklist(false);
    if (!isRequiredBusinessSetupComplete()) {
      setRequiresInitialBusinessSetup(true);
      setShowCountrySelection(true);
      return;
    }
    openOnboardingGuide(0);
  };

  const handleChecklistDismiss = () => {
    setShowSetupChecklistCard(false);
    localStorage.setItem('setup_checklist_dismissed', 'true');
  };

  // Authentication Handlers
  const handleGoogleSignIn = async (planType = 'trial') => {
    try {
      console.log('Starting Google Sign In process');
      try {
        await setAuthPersistence(rememberMe);
      } catch (persistenceError) {
        console.warn('Could not apply remember-me preference before sign-in:', persistenceError);
      }

      // Detailed initialization logging
      if (!isDriveInitialized) {
        console.log('Drive not initialized, attempting initialization');
        await initDriveService();
        setIsDriveInitialized(true);
      }

      console.log('Calling signInWithGoogle()');
      const googleUser = await signInWithGoogle();
      if (googleUser && googleUser.email) {
        try { localStorage.setItem('has_signed_in_before', 'true'); } catch {}
      }

      if (googleUser && googleUser.driveAccessGranted === false) {
        showDrivePermissionRequiredWarning('adding transactions');
      } else if (googleUser && googleUser.driveAccessGranted === true) {
        try { localStorage.removeItem('needsDriveAuth'); } catch {}
      }

      console.log('Detailed Google User Info:', {
        id: googleUser.id,
        email: googleUser.email,
        name: googleUser.name
      });

      if (googleUser && googleUser.email) {
        const firebaseUid = auth.currentUser?.uid || googleUser.firebaseUid || googleUser.id;
        const legacyUid = googleUser.id;
        const effectiveUserId = firebaseUid || legacyUid;

        console.log('Checking user access for:', googleUser.email);

        try {
          if (firebaseUid && legacyUid && firebaseUid !== legacyUid) {
            try {
              await migrateLegacyUserDoc(firebaseUid, legacyUid, googleUser.email);
            } catch (migrationError) {
              console.warn('Legacy user migration skipped:', migrationError);
            }
          }

          // Check if user is a legacy authorized user
          console.log('Calling isLegacyAuthorizedUser for:', googleUser.email);
          const isLegacyUser = await isLegacyAuthorizedUser(googleUser.email);
          console.log('isLegacyUser result:', isLegacyUser);

          let desiredPlanType = planType;

          // If user already consumed a trial, flip to free before attempting creation
          if (planType === 'trial') {
            try {
              const emailUsedTrial = await hasEmailUsedTrial(googleUser.email);
              if (emailUsedTrial) {
                console.log('Trial already used for email, switching to free plan');
                desiredPlanType = 'free';
                try { localStorage.setItem('trial_used', 'true'); } catch {}
              }
            } catch (trialCheckError) {
              console.warn('Trial check failed, defaulting to requested plan:', trialCheckError);
            }
          }

          let existingSubscriptionData = null;
          try {
            existingSubscriptionData = await getUserSubscription(effectiveUserId);
          } catch (subscriptionLookupError) {
            console.warn('Unable to check existing subscription before user creation:', subscriptionLookupError);
          }

          const existingStatus = (existingSubscriptionData?.subscriptionStatus || '').toString().toLowerCase();
          const existingPlan = (existingSubscriptionData?.subscriptionPlan || '').toString().toLowerCase();
          const premiumLikeStatuses = new Set(['trial', 'active', 'past_due', 'unpaid', 'incomplete', 'paused']);
          const existingPremiumLike = existingPlan === 'premium' || premiumLikeStatuses.has(existingStatus);

          if (isLegacyUser) {
            console.log('? Legacy authorized user detected:', googleUser.email);
          } else {
            const hasExistingSubscription = !!existingSubscriptionData;
            if (!hasExistingSubscription) {
              console.log(`New user - creating with ${desiredPlanType} plan:`, googleUser.email);
            }
            setIsNewUser(!hasExistingSubscription);

            // Skip creation if trial was requested but already used (we'll use existing record instead)
            const shouldCreateUser = !hasExistingSubscription && !(planType === 'trial' && desiredPlanType === 'free');
            const shouldUpgradeToTrial = desiredPlanType === 'trial' && !existingPremiumLike && existingStatus !== 'trial';

            if (shouldCreateUser || shouldUpgradeToTrial) {
              try {
                if (desiredPlanType === 'free') {
                  console.log('Creating free user...');
                  await createFreeUser(effectiveUserId, googleUser.email);
                  console.log('? Free user created successfully');
                } else {
                  console.log('Creating trial user...');
                  await createTrialUser(effectiveUserId, googleUser.email);
                  console.log('? Trial user created successfully');
                }
              } catch (createUserError) {
                console.error('Error creating user:', createUserError);
                // Continue with sign-in even if user creation fails
                console.log('Continuing with sign-in despite user creation error');
              }
            }
          }

          // Ensure a Stripe customer exists for this user
          try {
            await ensureStripeCustomer(effectiveUserId, googleUser.email, googleUser.name);
            console.log('Stripe customer ensured for user:', effectiveUserId);
          } catch (stripeError) {
            console.error('Failed to ensure Stripe customer:', stripeError);
          }

          // Fetch actual subscription data from database
          console.log('Calling getUserSubscription for user ID:', effectiveUserId);
          let subscriptionData = null;
          try {
            subscriptionData = await getUserSubscription(effectiveUserId);
            console.log('Subscription data received:', subscriptionData);
          } catch (subscriptionError) {
            console.error('Error fetching subscription data:', subscriptionError);
            console.log('Continuing with default subscription data');
          }

          // Set user data with subscription info
          // If no subscription data found, default to free user
          const finalSubscriptionData = subscriptionData || {
            subscriptionStatus: 'free',
            subscriptionPlan: 'free'
          };

          const userData = {
            uid: effectiveUserId,
            displayName: googleUser.name,
            email: googleUser.email,
            accessToken: googleUser.accessToken,
            subscriptionTier: finalSubscriptionData.subscriptionStatus,
            subscriptionPlan: finalSubscriptionData.subscriptionPlan,
            subscriptionStatus: finalSubscriptionData.subscriptionStatus
          };

          console.log('Final user data:', userData);
          persistSubscriptionTier(userData.subscriptionTier);
          setUser(userData);
          await prepareBrowserLedgerCacheForUser(userData.uid, resetLedgerStateForAccountSwitch);

          // Close modals immediately after setting user data
          setShowAuthRequired(false);
          setShowSignUpModal(false);

          // Show country selection for new users
          if (isNewUser) {
            // Check if user has already selected a country
            const hasSelectedCountry = localStorage.getItem('selectedCountry');
            if (!hasSelectedCountry) {
              setTimeout(() => {
                setShowCountrySelection(true);
              }, 1000); // Small delay to let the UI settle
            }
          }

          // User data is already set above with setUser(userData)

          const workspaceSelection = await refreshCollaborationAccess(userData).catch((error) => {
            console.warn('Unable to refresh collaboration access after sign-in:', error);
            return null;
          });
          await loadUserDataFromDrive(workspaceSelection);

        } catch (authCheckError) {
          console.error('Detailed Authorization Check Error:', {
            name: authCheckError.name,
            message: authCheckError.message,
            code: authCheckError.code,
            stack: authCheckError.stack
          });

          alert('Authorization check failed. Please try again.');
          throw authCheckError;
        }
      } else {
        console.error('No valid user information received');
        alert('Failed to retrieve user information.');
      }
    } catch (error) {
      console.error('Comprehensive Sign-In Error:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });

      alert('Sign-in failed. Please try again.');

      // Clean up
      try {
        await signOutFromDrive();
      } catch (signOutError) {
        console.error('Error during sign-out:', signOutError);
      }

      setUser(null);
    }
  };

  const handleLogout = async () => {
    try {
      const syncedBeforeLogout = await syncTransactionsBeforeLeaving({ showErrors: true });
      if (!syncedBeforeLogout) {
        return;
      }
      ledgerLoadTrackerRef.current.invalidate();
      collaborationRequestRef.current += 1;

      // Sign out from Drive
      await signOutFromDrive();

      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('firebase_auth_user');
      localStorage.removeItem('firebase_auth_timestamp');
      localStorage.removeItem('needsDriveAuth');
      localStorage.removeItem('hasDriveConsent');
      localStorage.removeItem('driveAuthState');
      localStorage.removeItem('setup_step_drive');
      localStorage.removeItem(LEDGER_CACHE_OWNER_KEY);

      // Clear state
      setUser(null);
      resetLedgerStateForAccountSwitch();
      await clearBrowserLedgerCache();
      setCollaborationState({
        ownWorkspace: null,
        sentInvites: [],
        pendingInvites: [],
        acceptedWorkspaces: []
      });
      setActiveWorkspace(null);
      activeWorkspaceRef.current = null;

      // Sign out from Firebase
      await signOut(auth);    
      // Show pricing modal again after logout
      setShowSignUpModal(true);
      try { window.__authRestored = false; } catch {}
 
    } catch (error) {
      console.error("Logout Error:", error);
      alert("There was a problem logging out. Please try again.");
    }
  };


  // Initialize Google Drive API
  useEffect(() => {
    const checkGapiAndInitialize = () => {
      // Check if the script has loaded properly
      if (!window.gapi) {
        console.log('GAPI not available yet, waiting...');
        setTimeout(checkGapiAndInitialize, 1000); // Check again in 1 second
        return;
      }

      // Once GAPI is available, initialize the drive service
      const initializeDrive = async () => {
        try {
          console.log('GAPI loaded, starting drive initialization');
          const success = await initDriveService();
          console.log('Drive initialization result:', success);
          setIsDriveInitialized(success);
        } catch (error) {
          console.error('Failed to initialize Google Drive:', error);
        }
      };

      initializeDrive();
    };

    // Start checking right away
    checkGapiAndInitialize();
  }, []);

  // Auth State Observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed:", currentUser);

      if (currentUser) {
        await prepareBrowserLedgerCacheForUser(currentUser.uid, resetLedgerStateForAccountSwitch);
        const storedTier = getStoredSubscriptionTier();
        const normalizedFirebaseUser = {
          uid: currentUser.uid,
          displayName: currentUser.displayName,
          email: currentUser.email,
          photoURL: currentUser.photoURL,
          subscriptionTier: currentUser.subscriptionTier || storedTier || ''
        };
        setUser(normalizedFirebaseUser);
        const workspaceSelection = await refreshCollaborationAccess(normalizedFirebaseUser).catch((error) => {
          console.warn('Unable to refresh collaboration access on auth state change:', error);
          return null;
        });

        const providerGoogleUid = currentUser.providerData?.find(
          (provider) => provider.providerId === 'google.com'
        )?.uid;
        if (providerGoogleUid && providerGoogleUid !== currentUser.uid && currentUser.email) {
          migrateLegacyUserDoc(currentUser.uid, providerGoogleUid, currentUser.email).catch((error) => {
            console.warn('Legacy migration skipped on auth state change:', error);
          });
        }

        // Ensure & sync Stripe customer/subscription in the background so returning
        // Premium users don't get stuck showing "Free" due to stale Firestore data.
        if (currentUser.uid && currentUser.email) {
          ensureStripeCustomer(
            currentUser.uid,
            currentUser.email,
            currentUser.displayName || currentUser.name
          ).catch((stripeError) => {
            console.warn('Stripe customer sync failed:', stripeError);
          });
        }

        // Check if Drive is initialized
        if (!isDriveInitialized) {
          try {
            console.log('Attempting to initialize Drive service');
            const success = await initDriveService();
            setIsDriveInitialized(success);

            if (success) {
              // Check if we're signed in to Drive
              if (isDriveSignedIn()) {
                console.log('Drive session active, loading user data');
                await loadUserDataFromDrive(workspaceSelection);
                setShowSignUpModal(false); // Close sign-up modal on successful load
              } else {
                console.log('Drive not signed in; attempting silent session restore');
                try {
                  const ensured = await ensureDriveSession();
                  if (ensured && isDriveSignedIn()) {
                    console.log('Silent Drive restore succeeded, loading user data');
                    await loadUserDataFromDrive(workspaceSelection);
                    setShowAuthRequired(false);
                    setIsCheckingAuth(false);
                    localStorage.removeItem('needsDriveAuth');
                    setShowSignUpModal(false);
                  } else {
                    console.log('Silent restore failed; continuing with local data');
                    localStorage.setItem('needsDriveAuth', 'true');
                    await loadUserDataFromDrive(workspaceSelection);
                    setShowAuthRequired(false);
                    setShowSignUpModal(false); // Close sign-up modal if it's open
                    setIsCheckingAuth(false);
                  }
                } catch (e) {
                  console.warn('Silent Drive restore error; continuing with local data', e);
                  localStorage.setItem('needsDriveAuth', 'true');
                  await loadUserDataFromDrive(workspaceSelection);
                  setShowAuthRequired(false);
                  setShowSignUpModal(false);
                  setIsCheckingAuth(false);
                }
              }
            } else {
              console.warn('Drive initialization failed; loading local/hybrid data fallback.');
              try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
              await loadUserDataFromDrive(workspaceSelection);
              setShowAuthRequired(false);
              setShowSignUpModal(false);
              setIsCheckingAuth(false);
            }
          } catch (error) {
            console.error('Failed to initialize Drive:', error);
            try { localStorage.setItem('needsDriveAuth', 'true'); } catch {}
            await loadUserDataFromDrive(workspaceSelection);
            setShowAuthRequired(false);
            setShowSignUpModal(false);
            setIsCheckingAuth(false);
          }
        } else if (isDriveSignedIn()) {
          // Drive is already initialized and signed in
          console.log('Firebase and Drive authenticated, loading data');
          await loadUserDataFromDrive(workspaceSelection);
          setIsCheckingAuth(false);
          localStorage.removeItem('needsDriveAuth');
          setShowSignUpModal(false); 
          // Close sign-up modal on successful load
               } else {
          // Firebase authenticated but Drive not signed in
          console.log('Firebase authenticated but Drive not signed in; attempting silent restore');
          try {
            const ensured = await ensureDriveSession();
            if (ensured && isDriveSignedIn()) {
              console.log('Silent Drive restore succeeded, loading user data');
              await loadUserDataFromDrive(workspaceSelection);
              setIsCheckingAuth(false);
              localStorage.removeItem('needsDriveAuth');
              setShowSignUpModal(false);
              setShowAuthRequired(false);
            } else {
              console.log('Silent restore failed; continuing with local data');
              localStorage.setItem('needsDriveAuth', 'true');
              await loadUserDataFromDrive(workspaceSelection);
              setShowAuthRequired(false);
              setShowSignUpModal(false); // Close sign-up modal if it's open
              setIsCheckingAuth(false);
            }
          } catch (e) {
            console.warn('Silent Drive restore error; continuing with local data', e);
            localStorage.setItem('needsDriveAuth', 'true');
            await loadUserDataFromDrive(workspaceSelection);
            setShowAuthRequired(false);
            setShowSignUpModal(false);
            setIsCheckingAuth(false);
          }
        }
      } else {
        // No Firebase user, clearing state
        console.log('No Firebase user, clearing state');
        ledgerLoadTrackerRef.current.invalidate();
        collaborationRequestRef.current += 1;

        setUser(null);
        resetLedgerStateForAccountSwitch();
        setCollaborationState({
          ownWorkspace: null,
          sentInvites: [],
          pendingInvites: [],
          acceptedWorkspaces: []
        });
        setActiveWorkspace(null);
        activeWorkspaceRef.current = null;
        localStorage.removeItem('needsDriveAuth');
        // Preserve browser cache here so silent auth/session restores can recover
        // locally saved data that has not reached Drive yet. Explicit logout and
        // account switches still clear cache via their dedicated paths.
        
        // Detect if this is PWA/mobile app vs web browser
        const isPWA = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;

        // If we appear to have a previous Drive/Firebase-like session, attempt a
        // silent restore before showing any auth prompts to avoid nagging.
        const maybeHasSession = localStorage.getItem('google_access_token') || localStorage.getItem('firebase_auth_user');

        if (isFirstLoad) {
          if (maybeHasSession) {
            try {
              // Attempt Drive session silently; firebase.js will also try to restore
              await ensureDriveSession();
            } catch {}
            // Give a brief window for the custom 'firebase-auth-restored' flow
            // to update state via window.updateAppAuthState
            setTimeout(() => {
              const restored = !!window.__authRestored || !!localStorage.getItem('firebase_auth_user');
              if (!restored) {
                if (isPWA) {
                  setShowAuthRequired(true);
                } else {
                  setShowSignUpModal(true);
                }
                setIsCheckingAuth(false);
              }
            }, 800);
          } else {
            // No prior session indicators; show appropriate prompt immediately
            if (isPWA) {
              setShowAuthRequired(true);
            } else {
              setShowSignUpModal(true);
            }
            setIsCheckingAuth(false);
          }
          setIsFirstLoad(false);
        } else {
          setIsCheckingAuth(false);
        }
      }

    
    });

    return () => unsubscribe();
  }, [isDriveInitialized, getStoredSubscriptionTier]); // Add dependencies

  // REPLACE the useEffect for saving transactions with this:
// Save transactions when they change
useEffect(() => {
  const saveData = async () => {
    if (Array.isArray(transactions) && transactions.length > 0) {
      try {
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, transactions);
      } catch (error) {
        console.error('Error saving transactions:', error);
      }
    }
  };

  saveData();
}, [persistDatasetForCurrentAccess, transactions]);

useEffect(() => {
  const handleManualTransactionSync = (event) => {
    if (activeWorkspaceRef.current?.id) return;

    const syncedTransactions = event?.detail?.transactions;
    if (!Array.isArray(syncedTransactions)) return;

    const normalizedTransactions = normalizeSuspiciousReceiptVehicleCategories(syncedTransactions);
    setTransactions(normalizedTransactions);

    const yearsWithData = normalizedTransactions
      .map((transaction) => getTransactionYearValue(transaction))
      .filter((year) => Number.isFinite(year));
    if (yearsWithData.length > 0 && !yearsWithData.includes(activeFinancialYear)) {
      setActiveFinancialYear(Math.max(...yearsWithData));
    }
  };

  window.addEventListener('ledger-transactions-synced', handleManualTransactionSync);
  return () => {
    window.removeEventListener('ledger-transactions-synced', handleManualTransactionSync);
  };
}, [activeFinancialYear, normalizeSuspiciousReceiptVehicleCategories, setTransactions]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // REPLACE the useEffect for saving depreciation periods with this:
// Save depreciation periods when they change
useEffect(() => {
  const saveDepreciationData = async () => {
    if (Object.keys(customDepreciationPeriods).length > 0) {
      try {
        await persistDatasetForCurrentAccess(
          SHARED_WORKSPACE_DATASETS.DEPRECIATION,
          customDepreciationPeriods
        );
      } catch (error) {
        console.error('Error saving depreciation periods:', error);
      }
    }
  };

  saveDepreciationData();
}, [customDepreciationPeriods, persistDatasetForCurrentAccess]);
 // Add this useEffect near your other useEffect hooks
useEffect(() => {
  // Initialize showRecentTransactions to false (closed by default)
  setShowRecentTransactions(false);
}, []); // Empty dependency array means this runs once on mount
// PWA Enhancement Initialization
  useEffect(() => {
    const initializePWAEnhancements = async () => {
      try {
        // Initialize performance optimizations
        initializePerformanceOptimizations();
        setPerformanceOptimized(true);
        
        // Initialize encryption
        const encryptionInitialized = await initializeEncryption();
        setEncryptionEnabled(encryptionInitialized);
        
        // Check if onboarding should be shown
        console.log('PWA enhancements initialized:', {
          performance: true,
          encryption: encryptionInitialized,
          onboarding: false
        });
      } catch (error) {
        console.error('Failed to initialize PWA enhancements:', error);
      }
    };

    initializePWAEnhancements();
  }, []);

  // Service Worker message handling
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerMessage = (event) => {
        if (event.data?.type === 'BACKGROUND_SYNC') {
          console.log('Background sync completed, refreshing data...');
          loadUserDataFromDrive();
        }
      };

      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, []);

  useEffect(() => {
    const handleDriveReconnected = async () => {
      if (!user) return;
      try {
        await loadUserDataFromDrive();
        setShowAuthRequired(false);
        localStorage.removeItem('needsDriveAuth');
      } catch (error) {
        console.error('Failed to reload data after Drive reconnect:', error);
      }
    };

    window.addEventListener('drive-reconnected', handleDriveReconnected);
    return () => {
      window.removeEventListener('drive-reconnected', handleDriveReconnected);
    };
  }, [user, loadUserDataFromDrive]);
  // REPLACE the useEffect for saving service prices with this:
// Save service prices when they change
useEffect(() => {
  const savePricesData = async () => {
    if (!servicePricesLoadedRef.current) return;
    if (Object.keys(servicePrices).length > 0) {
      try {
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.SERVICE_PRICES, servicePrices);
      } catch (error) {
        console.error('Error saving service prices:', error);
      }
    }
  };

  savePricesData();
}, [persistDatasetForCurrentAccess, servicePrices]);

useEffect(() => {
  const persistClients = async () => {
    if (!clientsLoadedRef.current) return;
    try {
      await persistDatasetForCurrentAccess(
        SHARED_WORKSPACE_DATASETS.CLIENTS,
        mergeClientLists([], clients)
      );
    } catch (error) {
      console.error('Error saving clients:', error);
    }
  };

  persistClients();
}, [clients, persistDatasetForCurrentAccess]);

useEffect(() => {
  const persistBankAccounts = async () => {
    if (!bankAccountsLoadedRef.current) return;
    try {
      await persistDatasetForCurrentAccess(
        SHARED_WORKSPACE_DATASETS.BANK_ACCOUNTS,
        normalizeBankAccounts(bankAccounts)
      );
    } catch (error) {
      console.error('Error saving bank accounts:', error);
    }
  };

  persistBankAccounts();
}, [bankAccounts, persistDatasetForCurrentAccess]);

useEffect(() => {
  const persistJournalEntries = async () => {
    if (!journalEntriesLoadedRef.current) return;
    try {
      await persistDatasetForCurrentAccess(
        SHARED_WORKSPACE_DATASETS.JOURNAL_ENTRIES,
        normalizeJournalEntries(journalEntries)
      );
    } catch (error) {
      console.error('Error saving manual journal entries:', error);
    }
  };

  persistJournalEntries();
}, [journalEntries, persistDatasetForCurrentAccess]);

const isSplitIncomeTransaction = useCallback((transaction) => {
  if (!transaction) return false;

  if (transaction.isSplitTransaction) return true;

  const breakdownCategoryCount = Array.isArray(transaction.categoryBreakdown)
    ? new Set(
        transaction.categoryBreakdown
          .map((entry) => entry?.category)
          .filter(Boolean)
      ).size
    : 0;

  if (breakdownCategoryCount > 1) return true;

  const itemCategoryCount = Array.isArray(transaction.items)
    ? new Set(
        transaction.items
          .map((item) => item?.category)
          .filter(Boolean)
      ).size
    : 0;

  return itemCategoryCount > 1;
}, []);

const getTransactionCategoryEntries = useCallback((transaction) => {
  if (!transaction) return [];

  const fallbackCategory = transaction.category || '';
  const fallbackAmount = Number(transaction.amount) || 0;

  if (Array.isArray(transaction.categoryBreakdown) && transaction.categoryBreakdown.length > 0) {
    const breakdownEntries = transaction.categoryBreakdown
      .map((entry) => ({
        category: entry?.category || '',
        amount: Number(entry?.amount) || 0
      }))
      .filter((entry) => entry.category && entry.amount > 0);
    if (breakdownEntries.length > 0) {
      return breakdownEntries;
    }
  }

  if (Array.isArray(transaction.items) && transaction.items.length > 0) {
    const splitEntries = transaction.items
      .map((item) => ({
        category: item?.category || '',
        amount: Number(item?.amount ?? item?.unitPrice ?? 0) || 0
      }))
      .filter((entry) => entry.category && entry.amount > 0);

    if (splitEntries.length > 0) {
      return splitEntries;
    }
  }

  if (!fallbackCategory || fallbackAmount <= 0) {
    return [];
  }

  return [{
    category: fallbackCategory,
    amount: fallbackAmount
  }];
}, []);

const resolveFallbackVatRateForReporting = useCallback((transaction, category) => {
  if (!isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) return null;
  if (String(transaction?.type || '').toLowerCase() !== 'income') return null;
  return getIncomeTaxRateForCategory(category || transaction?.category || '', resolveIncomeTaxJurisdiction(), transaction?.date || transaction?.invoiceDate);
}, [
  getIncomeTaxRateForCategory,
  isVatRegisteredForTransactionDate,
  resolveIncomeTaxJurisdiction
]);

const sanitizeStoredVatRateForReporting = useCallback((transaction, category, storedRate) => {
  if (!isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) return storedRate;
  if (String(transaction?.type || '').toLowerCase() !== 'income') return storedRate;

  const countryId = String(currentCountry?.id || '').toLowerCase();
  const countryName = String(currentCountry?.name || '').toLowerCase();
  const isIreland = countryId === 'ie' || countryName.includes('ireland');
  const normalizedStoredRate = Number(storedRate);
  if (!isIreland || Math.abs(normalizedStoredRate - 0.06) > 0.0001) {
    return storedRate;
  }

  return getIncomeTaxRateForCategory(category || transaction?.category || '', 'Ireland', transaction?.date || transaction?.invoiceDate);
}, [
  currentCountry?.id,
  currentCountry?.name,
  getIncomeTaxRateForCategory,
  isVatRegisteredForTransactionDate
]);

const buildTransactionVatEntries = useCallback((transaction, options = {}) => {
  if (!isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) {
    return [];
  }

  return buildTransactionVatEntriesForReporting(transaction, {
    includeZeroVatEntries: options.includeZeroVatEntries === true,
    vatAccountingBasis: vatSettings.accountingBasis,
    resolveFallbackVatRate: resolveFallbackVatRateForReporting,
    resolveTaxComponentDescription: getTaxComponentDescription,
    sanitizeStoredVatRate: sanitizeStoredVatRateForReporting
  });
}, [getTaxComponentDescription, isVatRegisteredForTransactionDate, resolveFallbackVatRateForReporting, sanitizeStoredVatRateForReporting, vatSettings.accountingBasis]);

  const getTransactionVatAmount = useCallback((transaction) => (
  (() => {
    if (!isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) {
      return 0;
    }

    const entries = buildTransactionVatEntries(transaction);
    if (entries.some((entry) => entry?.vatBox)) {
      return parseFloat(Math.max(...entries.map((entry) => Number(entry?.vatAmount) || 0), 0).toFixed(2));
    }
    return getTransactionVatAmountForReporting(transaction, {
      vatAccountingBasis: vatSettings.accountingBasis,
      resolveFallbackVatRate: resolveFallbackVatRateForReporting,
      resolveTaxComponentDescription: getTaxComponentDescription,
      sanitizeStoredVatRate: sanitizeStoredVatRateForReporting
    });
  })()
), [buildTransactionVatEntries, getTaxComponentDescription, isVatRegisteredForTransactionDate, resolveFallbackVatRateForReporting, sanitizeStoredVatRateForReporting, vatSettings.accountingBasis]);

const getTransactionDisplayVatAmount = useCallback((transaction) => {
  const reportingVatAmount = Number(getTransactionVatAmount(transaction));
  if (Number.isFinite(reportingVatAmount) && reportingVatAmount > 0) {
    return parseFloat(reportingVatAmount.toFixed(2));
  }

  const transactionType = String(transaction?.type || '').trim().toLowerCase();
  if (!['expense', 'expenses'].includes(transactionType)) {
    return 0;
  }

  const explicitVatAmount = Number(transaction?.vatAmount);
  if (Number.isFinite(explicitVatAmount) && explicitVatAmount > 0) {
    return parseFloat(explicitVatAmount.toFixed(2));
  }

  if (Array.isArray(transaction?.vatComponents)) {
    const componentTotal = transaction.vatComponents.reduce((sum, component) => {
      const amount = Number(component?.amount);
      return Number.isFinite(amount) && amount > 0 ? sum + amount : sum;
    }, 0);
    if (componentTotal > 0) {
      return parseFloat(componentTotal.toFixed(2));
    }
  }

  return 0;
}, [getTransactionVatAmount]);

const getVatTransactionKey = useCallback((transaction) => {
  if (!transaction || typeof transaction !== 'object') return '';
  if (transaction.id !== undefined && transaction.id !== null && String(transaction.id).trim() !== '') {
    return `id:${String(transaction.id).trim()}`;
  }

  const txDate = getTransactionDateValue(transaction);
  const txDateKey = txDate ? txDate.toISOString() : String(transaction.date || '');
  const txType = String(transaction.type || '').toLowerCase();
  const txAmount = Number(transaction.amount || 0).toFixed(2);
  const txCategory = String(transaction.category || '');
  const txDescription = String(transaction.description || '');
  const txCreatedAt = formatDateForInput(transaction.createdAt) || '';
  const txUpdatedAt = formatDateForInput(transaction.updatedAt) || '';

  return `anon:${txType}|${txAmount}|${txDateKey}|${txCategory}|${txDescription}|${txCreatedAt}|${txUpdatedAt}`;
}, []);

const getSignedTransactionVatAmount = useCallback((transaction) => {
  const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
  if (!Number.isFinite(vatAmount) || vatAmount <= 0) return 0;

  const txType = String(transaction?.type || '').toLowerCase();
  if (txType === 'income') return vatAmount;
  if (txType === 'expense' || txType === 'expenses') return -vatAmount;
  return 0;
}, [getTransactionVatAmount]);

const getTransactionIncomeNetAmount = useCallback((transaction) => {
  if (!transaction) return 0;

  const grossAmount = getTransactionSaleTotalAmount(transaction);
  if (!Number.isFinite(grossAmount) || grossAmount <= 0) {
    return 0;
  }

  const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
  const netAmount = grossAmount - (Number.isFinite(vatAmount) && vatAmount > 0 ? vatAmount : 0);
  return parseFloat(Math.max(netAmount, 0).toFixed(2));
}, [getTransactionSaleTotalAmount, getTransactionVatAmount]);

const getTransactionExpenseNetAmount = useCallback((transaction) => {
  if (!transaction) return 0;

  const grossAmount = getTransactionExpenseTotalAmount(transaction);
  if (!Number.isFinite(grossAmount) || grossAmount <= 0) {
    return 0;
  }

  const reclaimableVatAmount = transaction?.vatReclaimable === false
    ? 0
    : Number(getTransactionVatAmount(transaction) || 0);
  const netAmount = grossAmount - (Number.isFinite(reclaimableVatAmount) && reclaimableVatAmount > 0 ? reclaimableVatAmount : 0);
  return parseFloat(Math.max(netAmount, 0).toFixed(2));
}, [getTransactionExpenseTotalAmount, getTransactionVatAmount]);

const getFilteredTransactions = useMemo(() => {
    // Safety check - ensure transactions is an array
    if (!Array.isArray(transactions)) {
      return [];
    }

    const normalizedKeywordSearch = String(transactionSearchTerm || '').trim().toLowerCase();
    const normalizedAmountSearch = String(transactionAmountSearch || '').trim().replace(/[€£$,\s]/g, '');
    const amountSearchValue = normalizedAmountSearch === '' ? NaN : Number(normalizedAmountSearch);
    const hasAmountSearch = normalizedAmountSearch !== '';

    // First filter the transactions
    let filteredTransactions = transactions.filter(transaction => {
      const transactionDate = getTransactionDateValue(transaction);
      const now = new Date();

      // Timeframe Filtering
      const matchesTimeframe = (() => {
        if (!transactionDate) {
          return false;
        }
        switch (selectedTimeframe) {
          case 'financialYear': {
            return getTransactionYearValue(transaction) === activeFinancialYear;
          }
          case 'today':
            return transactionDate.toDateString() === now.toDateString();
          case 'yesterday':
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);
            return transactionDate.toDateString() === yesterday.toDateString();
          case 'thisWeek': {
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            startOfWeek.setHours(0, 0, 0, 0);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            endOfWeek.setHours(23, 59, 59, 999);
            return transactionDate >= startOfWeek && transactionDate <= endOfWeek;
          }
          case 'lastWeek':
            const startOfLastWeek = new Date(now);
            startOfLastWeek.setDate(now.getDate() - now.getDay() - 7);
            const endOfLastWeek = new Date(startOfLastWeek);
            endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
            return transactionDate >= startOfLastWeek && transactionDate <= endOfLastWeek;
          case 'thisMonth':
            return (
              transactionDate.getMonth() === now.getMonth() &&
              transactionDate.getFullYear() === now.getFullYear()
            );
          case 'lastMonth':
            const lastMonth = new Date(now);
            lastMonth.setMonth(now.getMonth() - 1);
            return transactionDate.getMonth() === lastMonth.getMonth() && transactionDate.getFullYear() === lastMonth.getFullYear();
          case 'thisYear': {
            const currentYear = now.getFullYear();
            return transactionDate.getFullYear() === currentYear;
          }
          default:
            return true; // No timeframe filter applied
        }
      })();

      // Custom Date Filtering
      const matchesCustomDate = (() => {
        if (selectedTimeframe === "custom" && startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return transactionDate >= start && transactionDate <= end;
        }
        return true;
      })();

      // Transaction Type Filtering (All, Income, Expenses)
      const matchesTransactionType = (() => {
        const normalizedType = String(transaction.type || '').toLowerCase();
        if (transactionTypeFilter === 'income') return normalizedType === 'income';
        if (transactionTypeFilter === 'expenses') return normalizedType === 'expenses' || normalizedType === 'expense';
        return true; // 'all' shows everything
      })();

      // Category Filtering
      const matchesCategory = (() => {
        if (!Array.isArray(selectedCategories) || selectedCategories.length === 0) return true;
        const primaryCategory = transaction.category || '';
        const isSplitCategoryTransaction = transaction.isSplitTransaction || isSplitIncomeTransaction(transaction);

        if (primaryCategory && !isSplitCategoryTransaction) {
          return selectedCategories.includes(primaryCategory);
        }

        const categoryEntries = getTransactionCategoryEntries(transaction);
        if (!categoryEntries.length) {
          return selectedCategories.includes(primaryCategory);
        }
        return categoryEntries.some((entry) => selectedCategories.includes(entry.category));
      })();

      // Payment Method Filtering
      const matchesPaymentMethod = (() => {
        if (!Array.isArray(selectedPaymentMethods) || selectedPaymentMethods.length === 0) return true;
        const paymentEntries = getTransactionPaymentEntries(transaction);
        if (!paymentEntries.length) {
          return selectedPaymentMethods.includes(normalizePaymentMethodId(transaction.paymentMethod || 'cash'));
        }
        return paymentEntries.some((entry) => selectedPaymentMethods.includes(entry.method));
      })();

      // Capital vs Revenue Expense Filtering (only for expenses)
      const matchesExpenseType = (() => {
        if (transaction.type !== 'expense' && transaction.type !== 'expenses') return true;
        if (selectedExpenseType === 'capital' && !isCapitalExpense(transaction.category)) return false;
        if (selectedExpenseType === 'revenue' && isCapitalExpense(transaction.category)) return false;
        return true;
      })();

      const matchesKeywordSearch = (() => {
        if (!normalizedKeywordSearch) return true;
        const categoryLabel = getCategoryDisplayLabel(transaction.category || '');
        const paymentLabel = getPaymentMethodLabel(transaction.paymentMethod || '');
        const searchableValues = [
          transaction.description,
          transaction.supplierName,
          transaction.vendor,
          transaction.vendorName,
          transaction.clientName,
          transaction.notes,
          transaction.category,
          categoryLabel,
          transaction.paymentMethod,
          paymentLabel,
          transaction.receiptFileName,
          transaction.receiptFilename,
          transaction.receiptName
        ];

        if (Array.isArray(transaction.items)) {
          transaction.items.forEach((item) => {
            searchableValues.push(item?.description, item?.name, item?.category);
          });
        }

        if (Array.isArray(transaction.splitItems)) {
          transaction.splitItems.forEach((item) => {
            searchableValues.push(item?.description, item?.name, item?.category);
          });
        }

        return searchableValues
          .filter((value) => value !== undefined && value !== null)
          .some((value) => String(value).toLowerCase().includes(normalizedKeywordSearch));
      })();

      const matchesAmountSearch = (() => {
        if (!hasAmountSearch) return true;
        const amount = Number(transaction.amount);
        if (!Number.isFinite(amount)) return false;

        const absoluteAmount = Math.abs(amount);
        const formattedAmount = absoluteAmount.toFixed(2);
        const compactAmount = formattedAmount.replace(/\.00$/, '');

        if (Number.isFinite(amountSearchValue)) {
          return Math.abs(absoluteAmount - Math.abs(amountSearchValue)) < 0.005 ||
            formattedAmount.includes(normalizedAmountSearch) ||
            compactAmount.includes(normalizedAmountSearch);
        }

        return formattedAmount.includes(normalizedAmountSearch) || compactAmount.includes(normalizedAmountSearch);
      })();

      return matchesTimeframe && matchesCustomDate && matchesTransactionType &&
             matchesCategory && matchesPaymentMethod && matchesExpenseType &&
             matchesKeywordSearch && matchesAmountSearch;
    });

    // Apply sorting
    filteredTransactions.sort((a, b) => {
      let comparison = 0;
      const aDate = getTransactionDateValue(a);
      const bDate = getTransactionDateValue(b);
      const aTime = aDate ? aDate.getTime() : 0;
      const bTime = bDate ? bDate.getTime() : 0;

      switch (sortBy) {
        case 'date':
          comparison = aTime - bTime;
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'category':
          const aCategoryName = CATEGORIES[a.type]?.find(c => c.id === a.category)?.name || a.category;
          const bCategoryName = CATEGORIES[b.type]?.find(c => c.id === b.category)?.name || b.category;
          comparison = aCategoryName.localeCompare(bCategoryName);
          break;
        case 'description':
          comparison = (a.description || '').localeCompare(b.description || '');
          break;
        default:
          comparison = aTime - bTime;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return filteredTransactions;
}, [transactions, selectedTimeframe, startDate, endDate, transactionTypeFilter,
    selectedCategories, selectedPaymentMethods, selectedExpenseType, transactionSearchTerm,
    transactionAmountSearch, isCapitalExpense, isSplitIncomeTransaction, sortBy, sortOrder,
    activeFinancialYear, getCategoryDisplayLabel, getPaymentMethodLabel, getTransactionCategoryEntries,
    getTransactionPaymentEntries, normalizePaymentMethodId, CATEGORIES]);

const recentTransactions = useMemo(() => {
  if (!Array.isArray(getFilteredTransactions)) return [];
  const linkedPartPaymentSaleIds = new Set(
    (Array.isArray(transactions) ? transactions : [])
      .map((transaction) => transaction?.linkedPartPaymentSaleId)
      .filter((id) => id !== undefined && id !== null && id !== '')
  );
  const visibleTransactions = getFilteredTransactions.filter((transaction) => {
    const amount = Number(transaction?.amount);
    const saleTotalAmount = getTransactionSaleTotalAmount(transaction);
    const paidAmount = getTransactionPaidAmount(transaction);
    const outstandingAmount = getTransactionOutstandingAmount(transaction);
    const isFullySettledZeroValueInvoice =
      transaction?.type === 'income' &&
      linkedPartPaymentSaleIds.has(transaction?.id) &&
      Number.isFinite(amount) &&
      Math.abs(amount) <= 0.01 &&
      saleTotalAmount > 0.01 &&
      paidAmount > 0.01 &&
      outstandingAmount <= 0.01 &&
      String(transaction?.paymentStatus || '').toLowerCase() === 'paid';

    return !isFullySettledZeroValueInvoice;
  });
  const sorted = [...visibleTransactions];
  sorted.sort(compareTransactionsByEntryDateDesc);
  return sorted;
}, [
  getFilteredTransactions,
  getTransactionOutstandingAmount,
  getTransactionPaidAmount,
  getTransactionSaleTotalAmount,
  transactions
]);

const displayedRecentTransactions = useMemo(() => {
  if (!Array.isArray(recentTransactions)) return [];
  return showAllTransactions ? recentTransactions : recentTransactions.slice(0, 10);
}, [recentTransactions, showAllTransactions]);

const selectedRecentTransactionIdSet = useMemo(
  () => new Set(selectedRecentTransactionIds),
  [selectedRecentTransactionIds]
);

const selectableDisplayedRecentTransactions = useMemo(
  () => displayedRecentTransactions.filter((transaction) => transaction?.id !== undefined && transaction?.id !== null),
  [displayedRecentTransactions]
);

const selectedDisplayedRecentTransactionCount = useMemo(
  () => selectableDisplayedRecentTransactions.filter((transaction) => selectedRecentTransactionIdSet.has(String(transaction.id))).length,
  [selectableDisplayedRecentTransactions, selectedRecentTransactionIdSet]
);

const allDisplayedRecentTransactionsSelected =
  selectableDisplayedRecentTransactions.length > 0 &&
  selectedDisplayedRecentTransactionCount === selectableDisplayedRecentTransactions.length;

useEffect(() => {
  if (!Array.isArray(transactions) || selectedRecentTransactionIds.length === 0) return;

  const existingIds = new Set(
    transactions
      .filter((transaction) => transaction?.id !== undefined && transaction?.id !== null)
      .map((transaction) => String(transaction.id))
  );
  const nextSelectedIds = selectedRecentTransactionIds.filter((id) => existingIds.has(id));

  if (nextSelectedIds.length !== selectedRecentTransactionIds.length) {
    setSelectedRecentTransactionIds(nextSelectedIds);
  }
}, [selectedRecentTransactionIds, transactions]);

const getTransactionCategoryLabel = useCallback((transaction) => {
  if (!transaction) return 'Uncategorized';

  const categoryEntries = getTransactionCategoryEntries(transaction)
    .map((entry) => entry.category)
    .filter(Boolean);
  const uniqueCategoryIds = [...new Set(categoryEntries)];

  if (uniqueCategoryIds.length > 1) {
    const names = uniqueCategoryIds.map((categoryId) => getCategoryDisplayLabel(categoryId));
    const preview = names.slice(0, 2).join(' + ');
    const remainingCount = names.length - 2;
    return remainingCount > 0 ? `Split: ${preview} +${remainingCount} more` : `Split: ${preview}`;
  }

  if (transaction.category) {
    return getCategoryDisplayLabel(transaction.category);
  }

  if (uniqueCategoryIds.length > 0) {
    return getCategoryDisplayLabel(uniqueCategoryIds[0]);
  }

  return 'Uncategorized';
}, [getCategoryDisplayLabel, getTransactionCategoryEntries]);

const getTransactionCategoryBreakdownPreview = useCallback((transaction) => {
  const entries = getTransactionCategoryEntries(transaction);
  if (!entries.length) return [];

  const grouped = Object.values(
    entries.reduce((acc, entry) => {
      const categoryId = entry?.category || '';
      const amount = Number(entry?.amount) || 0;
      if (!categoryId || amount <= 0) return acc;
      if (!acc[categoryId]) {
        acc[categoryId] = { category: categoryId, amount: 0 };
      }
      acc[categoryId].amount += amount;
      return acc;
    }, {})
  );

  return grouped
    .map((entry) => ({
      ...entry,
      amount: parseFloat(entry.amount.toFixed(2))
    }))
    .sort((a, b) => b.amount - a.amount);
}, [getTransactionCategoryEntries]);

const takeHomeSnapshots = useMemo(() => {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return { today: 0, week: 0, month: 0 };
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);

  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);

  const startOfMonth = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1);
  const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 1);

  const totals = { today: 0, week: 0, month: 0 };

  transactions.forEach((transaction) => {
    if (!transaction) return;

    const amount = Number(transaction.amount);
    const txDate = getTransactionDateValue(transaction);
    const txType = (transaction.type || '').toLowerCase();
    if (!Number.isFinite(amount) || !txDate) return;

    let signedAmount = 0;
    if (txType === 'income') signedAmount = amount;
    if (txType === 'expense' || txType === 'expenses') signedAmount = -amount;
    if (!signedAmount) return;

    if (txDate >= startOfToday && txDate < endOfToday) {
      totals.today += signedAmount;
    }

    if (txDate >= startOfWeek && txDate < endOfWeek) {
      totals.week += signedAmount;
    }

    if (txDate >= startOfMonth && txDate < endOfMonth) {
      totals.month += signedAmount;
    }
  });

  return {
    today: Number(totals.today.toFixed(2)),
    week: Number(totals.week.toFixed(2)),
    month: Number(totals.month.toFixed(2))
  };
}, [transactions]);

// Calculate Totals
const totals = useMemo(() => {
  if (!Array.isArray(getFilteredTransactions)) {
    return {
      income: 0,
      taxableIncome: 0,
      expenses: 0,
      deductibleExpenses: 0,
      cashReceived: 0,
      outstandingIncome: 0,
      cashPaid: 0,
      unpaidExpenses: 0
    };
  }
  const nextTotals = getFilteredTransactions.reduce((acc, t) => {
    const txType = (t?.type || '').toLowerCase();

    if (txType === 'income') {
      if ((t?.category || '').toLowerCase() !== 'assetsale') {
        const isInstallment = t?.isPartPaymentInstallment === true;
        if (!isInstallment) {
          acc.income += getTransactionSaleTotalAmount(t);
          acc.taxableIncome += getTransactionIncomeNetAmount(t);
          acc.outstandingIncome += getTransactionOutstandingAmount(t);
        }
        acc.cashReceived += Number(t.amount) || 0;
      }
    } else if (txType === 'expense' || txType === 'expenses') {
      // Only include non-capital expenses in the total expenses
      if (!isCapitalExpense(t?.category)) {
        const expenseTotal = getTransactionExpenseTotalAmount(t);
        const paymentStatus = String(t?.paymentStatus || '').toLowerCase();
        const isMarkedPaid = paymentStatus === 'paid';
        const cashPaid = isMarkedPaid
          ? expenseTotal
          : Math.max(getTransactionPaidAmount(t), 0);
        acc.expenses += expenseTotal;
        acc.deductibleExpenses += getTransactionExpenseNetAmount(t);
        acc.cashPaid += cashPaid;
        acc.unpaidExpenses += isMarkedPaid ? 0 : Math.max(expenseTotal - cashPaid, 0);
      }
    }
    return acc;
  }, {
    income: 0,
    taxableIncome: 0,
    expenses: 0,
    deductibleExpenses: 0,
    cashReceived: 0,
    outstandingIncome: 0,
    cashPaid: 0,
    unpaidExpenses: 0
  });

  return {
    income: parseFloat(nextTotals.income.toFixed(2)),
    taxableIncome: parseFloat(nextTotals.taxableIncome.toFixed(2)),
    expenses: parseFloat(nextTotals.expenses.toFixed(2)),
    deductibleExpenses: parseFloat(nextTotals.deductibleExpenses.toFixed(2)),
    cashReceived: parseFloat(nextTotals.cashReceived.toFixed(2)),
    outstandingIncome: parseFloat(nextTotals.outstandingIncome.toFixed(2)),
    cashPaid: parseFloat(nextTotals.cashPaid.toFixed(2)),
    unpaidExpenses: parseFloat(nextTotals.unpaidExpenses.toFixed(2))
  };
}, [
  getFilteredTransactions,
  getTransactionExpenseTotalAmount,
  getTransactionExpenseNetAmount,
  getTransactionIncomeNetAmount,
  getTransactionOutstandingAmount,
  getTransactionPaidAmount,
  getTransactionSaleTotalAmount,
  isCapitalExpense
]);

  const computeVatPositionForTransactions = useCallback((transactionList) => {
    if (!Array.isArray(transactionList)) {
      return { vatCollected: 0, vatPaid: 0, vatDue: 0 };
    }

    let vatCollected = 0;
    let vatPaid = 0;

    transactionList.forEach((transaction) => {
      const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
      if (!Number.isFinite(vatAmount) || vatAmount <= 0) return;

      const txType = String(transaction?.type || '').toLowerCase();
      if (txType === 'income') {
        vatCollected += vatAmount;
      } else if (txType === 'expense' || txType === 'expenses') {
        vatPaid += vatAmount;
      }
    });

    vatCollected = parseFloat(vatCollected.toFixed(2));
    vatPaid = parseFloat(vatPaid.toFixed(2));
    const vatDue = parseFloat((vatCollected - vatPaid).toFixed(2));

    return { vatCollected, vatPaid, vatDue };
  }, [getTransactionVatAmount]);

  const vatPositionForSelectedTransactions = useMemo(() =>
    computeVatPositionForTransactions(getFilteredTransactions),
    [computeVatPositionForTransactions, getFilteredTransactions]);

  const vatFiledPeriods = useMemo(() =>
    sanitizeVatFiledPeriods(vatSettings.vatFiledPeriods),
    [vatSettings.vatFiledPeriods]);

  const vatFiledSnapshotMap = useMemo(() => {
    return buildLatestVatFiledSnapshotMap(vatFiledPeriods);
  }, [vatFiledPeriods]);

  const vatFiledNetForSelectedTransactions = useMemo(() => {
    return calculateVatFiledNetForTransactions({
      transactions: getFilteredTransactions,
      vatFiledPeriods,
      vatFiledSnapshotMap,
      getVatTransactionKey,
      getSignedTransactionVatAmount
    });
  }, [
    getFilteredTransactions,
    getSignedTransactionVatAmount,
    getVatTransactionKey,
    vatFiledPeriods,
    vatFiledSnapshotMap
  ]);

  const vatOutstandingPositionForSelectedTransactions = useMemo(() => {
    const vatCollected = Number(vatPositionForSelectedTransactions.vatCollected || 0);
    const vatPaid = Number(vatPositionForSelectedTransactions.vatPaid || 0);
    const rawNetVat = Number(vatPositionForSelectedTransactions.vatDue || 0);
    const filedNet = Number(vatFiledNetForSelectedTransactions || 0);
    const legacySettlementOffset = Number(vatSettings.vatSettlementOffset || 0);
    const netOutstanding = parseFloat((rawNetVat - filedNet - legacySettlementOffset).toFixed(2));

    return {
      vatCollected,
      vatPaid,
      rawNetVat: parseFloat(rawNetVat.toFixed(2)),
      filedNet: parseFloat(filedNet.toFixed(2)),
      settlementOffset: parseFloat(legacySettlementOffset.toFixed(2)),
      netOutstanding,
      vatDue: parseFloat(Math.max(0, netOutstanding).toFixed(2)),
      vatReclaim: parseFloat(Math.max(0, -netOutstanding).toFixed(2))
    };
  }, [vatFiledNetForSelectedTransactions, vatPositionForSelectedTransactions, vatSettings.vatSettlementOffset]);

  useEffect(() => {
    setTaxSummary((prev) => {
      const nextVatCollected = vatPositionForSelectedTransactions.vatCollected;
      const nextVatPaid = vatPositionForSelectedTransactions.vatPaid;
      const nextVatDue = vatPositionForSelectedTransactions.vatDue;

      if (
        (prev?.vatCollected || 0) === nextVatCollected &&
        (prev?.vatPaid || 0) === nextVatPaid &&
        (prev?.vatDue || 0) === nextVatDue
      ) {
        return prev;
      }

      return {
        ...(prev || {}),
        vatCollected: nextVatCollected,
        vatPaid: nextVatPaid,
        vatDue: nextVatDue
      };
    });
  }, [vatPositionForSelectedTransactions]);

  // Updated Transactions with Depreciation
  const updatedTransactions = useMemo(() => {
    const currentYear = Number(activeFinancialYear) || new Date().getFullYear();

    return transactions.map(transaction => {
      if (isCapitalExpense(transaction.category)) {
        // Get depreciation period (default 8 years if not specified)
        const depreciationYears = customDepreciationPeriods[transaction.id] || 8;

        // Get asset status (active, sold, written off)
        const assetStatus = transaction.assetStatus || 'active';

        // Calculate purchase year from transaction date
        const purchaseDate = getTransactionFinancialDateValue(transaction) || new Date(transaction.date);
        const purchaseYear = purchaseDate.getFullYear();

        // Calculate years elapsed since purchase
        const yearsElapsed = currentYear - purchaseYear;

        // Calculate remaining depreciation years
        const remainingYears = Math.max(0, depreciationYears - yearsElapsed);

        // Calculate if the asset is still depreciating (only if active)
        const isStillDepreciating = assetStatus === 'active' && remainingYears > 0;

        // Calculate annual depreciation (only if still depreciating)
        const annualDepreciation = isStillDepreciating
          ? calculateDepreciation(transaction.amount, depreciationYears)
          : 0;

        // Calculate accumulated depreciation
        const accumulatedDepreciation = Math.min(
          transaction.amount,
          annualDepreciation * (assetStatus === 'active' ? yearsElapsed : transaction.yearsAtDisposal || yearsElapsed)
        );

        // Calculate current value
        const currentValue = Math.max(0, transaction.amount - accumulatedDepreciation);

        return {
          ...transaction,
          depreciationYears,
          purchaseYear,
          yearsElapsed,
          remainingYears,
          assetStatus,
          isStillDepreciating,
          annualDepreciation,
          accumulatedDepreciation,
          currentValue,
          disposalDate: transaction.disposalDate || null,
          disposalAmount: transaction.disposalAmount || 0,
          yearsAtDisposal: transaction.yearsAtDisposal || null,
          disposalReason: transaction.disposalReason || null
        };
      }
      return transaction;
    });
  }, [activeFinancialYear, transactions, customDepreciationPeriods, isCapitalExpense]);

  // Calculate total annual depreciation (only for assets still depreciating)
  const totalAnnualDepreciation = useMemo(() => {
    return updatedTransactions.reduce((acc, t) =>
      acc + (isCapitalExpense(t.category) && t.isStillDepreciating ? t.annualDepreciation || 0 : 0), 0
    );
  }, [updatedTransactions, isCapitalExpense]);

  // Calculate net profit: income - regular expenses (excluding depreciation)
  const netProfit = useMemo(() =>
    totals.taxableIncome - totals.deductibleExpenses,
    [totals]);

  // Accounting profit for tax/P&L uses net sales turnover, excluding VAT collected.
  const taxableAdjustedNetProfit = useMemo(() =>
    parseFloat((totals.taxableIncome - (totals.deductibleExpenses + totalAnnualDepreciation)).toFixed(2)),
    [totals, totalAnnualDepreciation]);

  const estimatedDirectTaxTotal = useMemo(() => {
    if (isLimitedCompany) {
      return Math.max(0, taxSummary.corporationTax || taxSummary.totalTaxLiability || 0);
    }

    return Math.max(
      0,
      (taxSummary.incomeTax || 0) + (taxSummary.usc || 0) + (taxSummary.prsi || 0)
    );
  }, [
    isLimitedCompany,
    taxSummary.corporationTax,
    taxSummary.incomeTax,
    taxSummary.prsi,
    taxSummary.totalTaxLiability,
    taxSummary.usc
  ]);

  const directTaxLabel = useMemo(() => {
    if (isLimitedCompany) return 'Corporation Tax';
    if (currentCountry?.id === 'uk') return 'Income Tax + National Insurance';
    if (currentCountry?.id === 'us') return 'Income Tax';
    return 'Income Tax + USC + PRSI';
  }, [currentCountry?.id, isLimitedCompany]);

  const incomeTaxSetAsideRate = useMemo(() => {
    const incomeTaxTotal = estimatedDirectTaxTotal;
    const taxableProfitBase = Math.max(0, Number(taxableAdjustedNetProfit) || 0);
    if (incomeTaxTotal <= 0 || taxableProfitBase <= 0) {
      return 0;
    }
    return Math.min(incomeTaxTotal / taxableProfitBase, 1);
  }, [estimatedDirectTaxTotal, taxableAdjustedNetProfit]);

  const taxSetAsideSnapshots = useMemo(() => {
    const emptySnapshot = { incomeTax: 0, vatDue: 0, vatReclaim: 0, totalSetAside: 0 };

    if (!Array.isArray(transactions) || transactions.length === 0) {
      return {
        today: emptySnapshot,
        week: emptySnapshot,
        month: emptySnapshot
      };
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);

    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const startOfMonth = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 1);

    const vatNetByPeriod = { today: 0, week: 0, month: 0 };

    transactions.forEach((transaction) => {
      if (!transaction) return;

      const txDate = getTransactionDateValue(transaction);
      const txType = (transaction.type || '').toLowerCase();
      if (!txDate) return;

      const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
      if (!Number.isFinite(vatAmount) || vatAmount <= 0) return;

      let signedVatAmount = 0;
      if (txType === 'income') signedVatAmount = vatAmount;
      if (txType === 'expense' || txType === 'expenses') signedVatAmount = -vatAmount;
      if (!signedVatAmount) return;

      if (txDate >= startOfToday && txDate < endOfToday) {
        vatNetByPeriod.today += signedVatAmount;
      }
      if (txDate >= startOfWeek && txDate < endOfWeek) {
        vatNetByPeriod.week += signedVatAmount;
      }
      if (txDate >= startOfMonth && txDate < endOfMonth) {
        vatNetByPeriod.month += signedVatAmount;
      }
    });

    const buildSnapshot = (periodKey) => {
      const takeHome = Math.max(0, Number(takeHomeSnapshots[periodKey] || 0));
      const estimatedIncomeTax = parseFloat((takeHome * incomeTaxSetAsideRate).toFixed(2));

      const vatNet = Number(vatNetByPeriod[periodKey] || 0);
      const vatDue = parseFloat(Math.max(0, vatNet).toFixed(2));
      const vatReclaim = parseFloat(Math.max(0, -vatNet).toFixed(2));
      const totalSetAside = parseFloat((estimatedIncomeTax + vatDue).toFixed(2));

      return {
        incomeTax: estimatedIncomeTax,
        vatDue,
        vatReclaim,
        totalSetAside
      };
    };

    return {
      today: buildSnapshot('today'),
      week: buildSnapshot('week'),
      month: buildSnapshot('month')
    };
  }, [transactions, getTransactionVatAmount, takeHomeSnapshots, incomeTaxSetAsideRate]);

  const payrollSetAsideSnapshots = useMemo(() => {
    const emptySnapshot = { wages: 0, holidayPay: 0, totalSetAside: 0 };
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);

    const startOfWeek = new Date(startOfToday);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() + (day === 0 ? -6 : 1 - day));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const startOfMonth = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 1);

    const actualSnapshots = {
      today: { ...emptySnapshot, records: 0 },
      week: { ...emptySnapshot, records: 0 },
      month: { ...emptySnapshot, records: 0 }
    };

    (Array.isArray(staffHrRecords) ? staffHrRecords : []).forEach((record) => {
      const recordDate = record?.weekStart ? new Date(record.weekStart) : null;
      if (!recordDate || Number.isNaN(recordDate.getTime())) return;

      const hourlyPay = Math.max(0, Number(record?.hourlyPay) || 0);
      const holidayPayPercent = Math.max(0, Number(record?.holidayPayPercent) || DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT);
      const hoursWorked = Math.max(0, Number(record?.hoursWorked) || 0);
      const publicHolidayHours = Math.max(0, Number(record?.publicHolidayHours) || 0);
      const workedWages = hoursWorked * hourlyPay;
      const wages = workedWages + (publicHolidayHours * hourlyPay);
      const holidayPay = workedWages * holidayPayPercent / 100;

      const apply = (periodKey) => {
        actualSnapshots[periodKey].wages += wages;
        actualSnapshots[periodKey].holidayPay += holidayPay;
        actualSnapshots[periodKey].records += 1;
      };

      if (recordDate >= startOfToday && recordDate < endOfToday) apply('today');
      if (recordDate >= startOfWeek && recordDate < endOfWeek) apply('week');
      if (recordDate >= startOfMonth && recordDate < endOfMonth) apply('month');
    });

    const finalizeActual = (snapshot) => ({
      wages: parseFloat(snapshot.wages.toFixed(2)),
      holidayPay: parseFloat(snapshot.holidayPay.toFixed(2)),
      totalSetAside: parseFloat((snapshot.wages + snapshot.holidayPay).toFixed(2)),
      source: 'actual'
    });

    const labourProfiles = Array.isArray(targetIncomeData?.labourProfiles)
      ? targetIncomeData.labourProfiles
      : [];

    const staffProfiles = labourProfiles.filter((profile) => profile?.type === 'staff');
    if (!staffProfiles.length) {
      return {
        today: actualSnapshots.today.records > 0 ? finalizeActual(actualSnapshots.today) : emptySnapshot,
        week: actualSnapshots.week.records > 0 ? finalizeActual(actualSnapshots.week) : emptySnapshot,
        month: actualSnapshots.month.records > 0 ? finalizeActual(actualSnapshots.month) : emptySnapshot
      };
    }

    const weeklyHolidayPay = staffProfiles.reduce(
      (sum, profile) => sum + Math.max(0, Number(profile?.weeklyHolidayPayReserve) || 0),
      0
    );
    const weeklyStaffCost = staffProfiles.reduce(
      (sum, profile) => sum + Math.max(0, Number(profile?.weeklyCost) || 0),
      0
    );
    const weeklyWages = Math.max(0, weeklyStaffCost - weeklyHolidayPay);
    const openDays = Math.max(1, Math.min(7, Number(targetIncomeData?.openDaysPerWeek || targetIncomeData?.workDaysPerWeek || 5) || 5));

    const buildSnapshot = (multiplier) => {
      const wages = parseFloat((weeklyWages * multiplier).toFixed(2));
      const holidayPay = parseFloat((weeklyHolidayPay * multiplier).toFixed(2));
      return {
        wages,
        holidayPay,
        totalSetAside: parseFloat((wages + holidayPay).toFixed(2))
      };
    };

    return {
      today: actualSnapshots.today.records > 0 ? finalizeActual(actualSnapshots.today) : buildSnapshot(1 / openDays),
      week: actualSnapshots.week.records > 0 ? finalizeActual(actualSnapshots.week) : buildSnapshot(1),
      month: actualSnapshots.month.records > 0 ? finalizeActual(actualSnapshots.month) : buildSnapshot(52 / 12)
    };
  }, [staffHrRecords, targetIncomeData]);

  const selectedPeriodAnalyticsSnapshot = useMemo(() => {
    const income = parseFloat((Number(totals.taxableIncome) || 0).toFixed(2));
    const expenses = parseFloat((Number(totals.deductibleExpenses) || 0).toFixed(2));
    const netProfit = parseFloat((income - expenses).toFixed(2));
    const adjustedNetProfit = parseFloat((Number(taxableAdjustedNetProfit) || 0).toFixed(2));
    const incomeTax = parseFloat((Number(estimatedDirectTaxTotal) || 0).toFixed(2));
    const vatDue = Number(vatOutstandingPositionForSelectedTransactions.vatDue || 0);
    const vatReclaim = Number(vatOutstandingPositionForSelectedTransactions.vatReclaim || 0);
    const totalSetAside = parseFloat((incomeTax + vatDue).toFixed(2));
    const availableTakeHome = parseFloat((adjustedNetProfit - incomeTax).toFixed(2));

    let label = 'Selected View';
    if (selectedTimeframe === 'custom' && startDate && endDate) {
      label = 'Custom Period';
    } else if (selectedTimeframe && selectedTimeframe !== ALL_TIMEFRAME_ID) {
      label = TIMEFRAMES.find((timeframe) => timeframe.id === selectedTimeframe)?.name || 'Selected Period';
    } else if (Array.isArray(getFilteredTransactions) && getFilteredTransactions.length !== transactions.length) {
      label = 'Filtered View';
    }

    return {
      label,
      income,
      expenses,
      netProfit,
      adjustedNetProfit,
      incomeTax,
      vatDue,
      vatReclaim,
      totalSetAside,
      availableTakeHome,
      transactionCount: Array.isArray(getFilteredTransactions) ? getFilteredTransactions.length : 0
    };
  }, [
    endDate,
    estimatedDirectTaxTotal,
    getFilteredTransactions,
    selectedTimeframe,
    startDate,
    taxableAdjustedNetProfit,
    totals.deductibleExpenses,
    totals.taxableIncome,
    transactions.length,
    vatOutstandingPositionForSelectedTransactions.vatDue,
    vatOutstandingPositionForSelectedTransactions.vatReclaim
  ]);

  // Category Totals
  const getCategoryTotals = useMemo(() => {
    if (!Array.isArray(getFilteredTransactions)) {
      return {};
    }
    return getFilteredTransactions.reduce((acc, t) => {
      const categoryEntries = getTransactionCategoryEntries(t);

      if (!categoryEntries.length) {
        return acc;
      }

      categoryEntries.forEach((entry) => {
        if (!acc[entry.category]) {
          acc[entry.category] = { income: 0, expenses: 0 };
        }

        if (t.type === 'income') {
          acc[entry.category].income += Number(entry.amount) || 0;
        } else {
          acc[entry.category].expenses += Number(entry.amount) || 0;
        }
      });

      return acc;
    }, {});
  }, [getFilteredTransactions, getTransactionCategoryEntries]);

  // Payment Method Totals
  const getPaymentMethodTotals = useMemo(() => {
    if (!Array.isArray(getFilteredTransactions)) {
      return {};
    }
    return getFilteredTransactions.reduce((acc, t) => {
      const paymentEntries = getTransactionPaymentEntries(t);
      paymentEntries.forEach((entry) => {
        if (!acc[entry.method]) {
          acc[entry.method] = { income: 0, expenses: 0 };
        }

        if (t.type === 'income') {
          acc[entry.method].income += Number(entry.amount) || 0;
        } else {
          acc[entry.method].expenses += Number(entry.amount) || 0;
        }
      });

      return acc;
    }, {});
  }, [getFilteredTransactions, getTransactionPaymentEntries]);

  // Handle service selection - now using selectedCategory instead of selectedService
  const handleServiceSelect = useCallback((service) => {
    setCurrentTransaction(prev => ({
      ...prev,
      description: service.name,
      amount: service.price
    }));
  }, []);

  const resetSplitSaleState = () => {
    setSplitSaleMode(false);
    setSplitSaleItems([createSplitSaleItem()]);
  };

  const resetSplitPaymentState = () => {
    setSplitPaymentMode(false);
    setSplitPaymentItems([createSplitPaymentItem()]);
  };

  const applyDiscountToSplitItems = useCallback((items = [], discountAmount = 0) => {
    const normalizedItems = (Array.isArray(items) ? items : [])
      .map((item) => ({
        ...item,
        amount: parseFloat((Number(item?.amount) || 0).toFixed(2))
      }))
      .filter((item) => item.amount > 0);

    if (normalizedItems.length === 0) return [];

    const totalCents = normalizedItems.reduce((sum, item) => sum + Math.max(0, Math.round(item.amount * 100)), 0);
    if (totalCents <= 0) return normalizedItems;

    const requestedDiscountCents = Math.max(0, Math.round((Number(discountAmount) || 0) * 100));
    const discountCents = Math.min(requestedDiscountCents, totalCents);
    if (discountCents <= 0) return normalizedItems;

    let allocatedCents = 0;
    const adjustedItems = normalizedItems.map((item, index) => {
      const itemCents = Math.max(0, Math.round(item.amount * 100));
      let shareCents =
        index === normalizedItems.length - 1
          ? discountCents - allocatedCents
          : Math.floor((itemCents * discountCents) / totalCents);

      shareCents = Math.min(shareCents, itemCents);
      allocatedCents += shareCents;

      return {
        ...item,
        amount: parseFloat(((itemCents - shareCents) / 100).toFixed(2))
      };
    });

    let remainderCents = discountCents - allocatedCents;
    if (remainderCents > 0) {
      for (let index = adjustedItems.length - 1; index >= 0 && remainderCents > 0; index -= 1) {
        const itemCents = Math.max(0, Math.round((adjustedItems[index].amount || 0) * 100));
        if (itemCents <= 0) continue;

        const extraShare = Math.min(itemCents, remainderCents);
        adjustedItems[index] = {
          ...adjustedItems[index],
          amount: parseFloat(((itemCents - extraShare) / 100).toFixed(2))
        };
        remainderCents -= extraShare;
      }
    }

    return adjustedItems;
  }, []);

  const allocatePaidAmountToSplitItems = useCallback((items = [], paidAmount = 0) => {
    const normalizedItems = (Array.isArray(items) ? items : [])
      .map((item) => ({
        ...item,
        amount: parseFloat((Number(item?.amount) || 0).toFixed(2))
      }))
      .filter((item) => item.amount > 0);

    if (normalizedItems.length === 0) return [];

    const totalCents = normalizedItems.reduce((sum, item) => sum + Math.max(0, Math.round(item.amount * 100)), 0);
    const paidCents = Math.min(
      Math.max(0, Math.round((Number(paidAmount) || 0) * 100)),
      totalCents
    );

    if (totalCents <= 0 || paidCents >= totalCents) return normalizedItems;

    let allocatedCents = 0;
    return normalizedItems.map((item, index) => {
      const itemCents = Math.max(0, Math.round(item.amount * 100));
      const shareCents = index === normalizedItems.length - 1
        ? paidCents - allocatedCents
        : Math.floor((itemCents * paidCents) / totalCents);
      allocatedCents += shareCents;

      return {
        ...item,
        amount: parseFloat((Math.max(shareCents, 0) / 100).toFixed(2)),
        saleTotalAmount: parseFloat((itemCents / 100).toFixed(2))
      };
    }).filter((item) => item.amount > 0);
  }, []);

  const updateSplitSaleItemField = (itemId, field, value) => {
    setSplitSaleItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        if (field === 'amount') {
          return { ...item, amount: sanitizeAmountInput(value) };
        }
        if (field === 'category') {
          return { ...item, category: value };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const addSplitSaleItem = () => {
    setSplitSaleItems((prev) => [...prev, createSplitSaleItem()]);
  };

  const removeSplitSaleItem = (itemId) => {
    setSplitSaleItems((prev) => {
      if (prev.length <= 1) {
        return [createSplitSaleItem()];
      }
      return prev.filter((item) => item.id !== itemId);
    });
  };

  const updateSplitPaymentItemField = (itemId, field, value) => {
    setSplitPaymentItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        if (field === 'amount') {
          return { ...item, amount: sanitizeAmountInput(value) };
        }
        if (field === 'method') {
          return { ...item, method: normalizePaymentMethodId(value) };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const addSplitPaymentItem = () => {
    setSplitPaymentItems((prev) => [...prev, createSplitPaymentItem()]);
  };

  const removeSplitPaymentItem = (itemId) => {
    setSplitPaymentItems((prev) => {
      if (prev.length <= 1) {
        return [createSplitPaymentItem()];
      }
      return prev.filter((item) => item.id !== itemId);
    });
  };

  const normalizedSplitSaleItems = useMemo(() => {
    return splitSaleItems.map((item) => {
      const normalizedAmount = String(item.amount ?? '')
        .replace(/,/g, '.')
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*?)\./g, '$1');
      const amountValue = parseFloat(normalizedAmount);
      return {
        id: item.id,
        description: (item.description || '').trim(),
        category: item.category || '',
        amount: Number.isFinite(amountValue) ? amountValue : 0
      };
    });
  }, [splitSaleItems]);

  const validSplitSaleItems = useMemo(() => {
    return normalizedSplitSaleItems.filter((item) => item.category && item.amount > 0);
  }, [normalizedSplitSaleItems]);

  const normalizedSplitPaymentItems = useMemo(() => {
    return splitPaymentItems
      .map((item) => {
        const amount = parseFloat(sanitizeAmountInput(item.amount || ''));
        return {
          id: item.id,
          method: normalizePaymentMethodId(item.method || ''),
          amount: Number.isFinite(amount) ? amount : 0
        };
      })
      .filter((item) => item.method && item.amount > 0);
  }, [normalizePaymentMethodId, splitPaymentItems]);

  const splitPaymentTotal = useMemo(() => {
    return normalizedSplitPaymentItems.reduce((sum, item) => sum + item.amount, 0);
  }, [normalizedSplitPaymentItems]);

  const splitSaleTotal = useMemo(() => {
    return validSplitSaleItems.reduce((sum, item) => sum + item.amount, 0);
  }, [validSplitSaleItems]);

  const manualDiscountAmount = useMemo(() => {
    const parsedDiscount = parseFloat(sanitizeAmountInput(currentTransaction.discountAmount || ''));
    return Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
  }, [currentTransaction.discountAmount]);

  const manualGrossAmount = useMemo(() => {
    if (splitSaleMode) {
      return parseFloat(splitSaleTotal.toFixed(2));
    }
    const parsedAmount = parseFloat(sanitizeAmountInput(currentTransaction.amount || ''));
    return Number.isFinite(parsedAmount) ? parsedAmount : 0;
  }, [currentTransaction.amount, splitSaleMode, splitSaleTotal]);

  const manualPaymentTargetAmount = useMemo(() => {
    const netAmount = manualGrossAmount - manualDiscountAmount;
    return parseFloat(Math.max(netAmount, 0).toFixed(2));
  }, [manualDiscountAmount, manualGrossAmount]);

  const selectedPrimaryExpenseVatRate = useMemo(() => {
    if (activeTab === 'income') return null;
    const parsedRate = Number(currentTransaction.vatRate);
    return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : null;
  }, [activeTab, currentTransaction.vatRate]);

  const primaryExpenseVatPreviewAmount = useMemo(() => {
    if (
      activeTab === 'income' ||
      !vatSettings.enabled ||
      !vatSettings.isRegistered ||
      !selectedPrimaryExpenseVatRate ||
      manualPaymentTargetAmount <= 0
    ) {
      return 0;
    }

    return parseFloat(
      calculateTaxAmountFromGross(manualPaymentTargetAmount, selectedPrimaryExpenseVatRate).toFixed(2)
    );
  }, [
    activeTab,
    calculateTaxAmountFromGross,
    manualPaymentTargetAmount,
    selectedPrimaryExpenseVatRate,
    vatSettings.enabled,
    vatSettings.isRegistered
  ]);

  const dominantSplitSaleCategory = useMemo(() => {
    const totalsByCategory = validSplitSaleItems.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + item.amount;
      return acc;
    }, {});

    return Object.entries(totalsByCategory).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  }, [validSplitSaleItems]);

  const canSaveSplitSale = splitSaleMode && splitSaleTotal > 0 && validSplitSaleItems.length > 0;
  const splitSaleVatPreview = useMemo(() => {
    if (activeTab !== 'income' || !splitSaleMode || validSplitSaleItems.length === 0) {
      return null;
    }
    const taxJurisdiction = resolveIncomeTaxJurisdiction();
    return buildSplitIncomeVatSummary(validSplitSaleItems, taxJurisdiction, currentTransaction.date);
  }, [
    activeTab,
    currentTransaction.date,
    splitSaleMode,
    validSplitSaleItems,
    resolveIncomeTaxJurisdiction,
    buildSplitIncomeVatSummary
  ]);

  const manualIncomeVatPreview = useMemo(() => {
    if (activeTab !== 'income') return null;

    if (splitSaleMode) {
      const discountAmount = Number(manualDiscountAmount) || 0;
      const discountedSplitItems = applyDiscountToSplitItems(validSplitSaleItems, discountAmount);
      const taxJurisdiction = resolveIncomeTaxJurisdiction();
      const discountedSplitVatPreview = buildSplitIncomeVatSummary(
        discountedSplitItems,
        taxJurisdiction,
        currentTransaction.date
      );
      const payableGross = discountedSplitItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      const vatAmount = Number(discountedSplitVatPreview.totalVatAmount) || 0;
      return {
        amountBasis: 'gross',
        rate: Number(discountedSplitVatPreview.effectiveVatRate) || 0,
        grossAmount: parseFloat(payableGross.toFixed(2)),
        netAmount: parseFloat(Math.max(payableGross - vatAmount, 0).toFixed(2)),
        vatAmount: parseFloat(vatAmount.toFixed(2)),
        isVatRegistered: isVatRegisteredForTransactionDate(currentTransaction.date),
        rateLabel: discountedSplitVatPreview.vatComponents?.length > 1 ? 'mixed rates' : null
      };
    }

    const enteredAmount = parseFloat(sanitizeAmountInput(currentTransaction.amount || ''));
    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) return null;

    const enteredDiscount = Number(manualDiscountAmount) || 0;
    const amountBasis = currentTransaction.amountBasis === 'net' ? 'net' : 'gross';
    const isVatRegistered = isVatRegisteredForTransactionDate(currentTransaction.date);
    const taxJurisdiction = resolveIncomeTaxJurisdiction();
    const vatRate = isVatRegistered
      ? getIncomeTaxRateForCategory(currentTransaction.category, taxJurisdiction, currentTransaction.date)
      : 0;

    const netBeforeVat = amountBasis === 'net'
      ? Math.max(enteredAmount - enteredDiscount, 0)
      : Math.max((enteredAmount - enteredDiscount) - calculateTaxAmountFromGross(enteredAmount - enteredDiscount, vatRate), 0);
    const vatAmount = isVatRegistered && vatRate > 0
      ? (amountBasis === 'net'
          ? netBeforeVat * vatRate
          : calculateTaxAmountFromGross(enteredAmount - enteredDiscount, vatRate))
      : 0;
    const grossAmount = amountBasis === 'net'
      ? netBeforeVat + vatAmount
      : Math.max(enteredAmount - enteredDiscount, 0);

    return {
      amountBasis,
      rate: vatRate,
      grossAmount: parseFloat(grossAmount.toFixed(2)),
      netAmount: parseFloat(netBeforeVat.toFixed(2)),
      vatAmount: parseFloat(vatAmount.toFixed(2)),
      isVatRegistered,
      rateLabel: null
    };
  }, [
    activeTab,
    applyDiscountToSplitItems,
    buildSplitIncomeVatSummary,
    calculateTaxAmountFromGross,
    currentTransaction.amount,
    currentTransaction.amountBasis,
    currentTransaction.category,
    currentTransaction.date,
    getIncomeTaxRateForCategory,
    isVatRegisteredForTransactionDate,
    manualDiscountAmount,
    resolveIncomeTaxJurisdiction,
    splitSaleMode,
    validSplitSaleItems
  ]);

  useEffect(() => {
    if (!splitSaleMode) return;

    const nextAmount = splitSaleTotal > 0 ? splitSaleTotal.toFixed(2) : '';

    setCurrentTransaction((prev) => {
      const nextCategory = dominantSplitSaleCategory || prev.category;
      const shouldUpdate =
        prev.amount !== nextAmount ||
        (dominantSplitSaleCategory && prev.category !== nextCategory) ||
        (prev.service && prev.service !== '');

      if (!shouldUpdate) return prev;

      return {
        ...prev,
        amount: nextAmount,
        category: nextCategory,
        service: ''
      };
    });
  }, [splitSaleMode, splitSaleTotal, dominantSplitSaleCategory]);

  const normalizeDateKey = (value) => {
    if (!value) return '';
    const parsed = parseFlexibleDateValue(value);
    if (!parsed) return '';
    return parsed.toISOString().slice(0, 10);
  };

  const normalizeAmountKey = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(parsed)) return '';
    return parsed.toFixed(2);
  };

  const normalizeTextKey = (value) => {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  };

  const extractReferenceTokens = (transaction = {}) => {
    const sources = [
      transaction.invoiceNumber,
      transaction.referenceNumber,
      transaction.receiptId,
      transaction.description,
      transaction.notes
    ]
      .filter(Boolean)
      .map((value) => String(value))
      .join(' ');

    if (!sources) return [];

    const explicitMatches = Array.from(
      sources.matchAll(/(?:invoice|inv|ref(?:erence)?|order|receipt)\s*[:#-]?\s*([a-z0-9-]{3,})/gi)
    ).map((match) => match[1].toUpperCase());

    const hashMatches = Array.from(sources.matchAll(/#([a-z0-9-]{3,})/gi)).map((match) => match[1].toUpperCase());

    return Array.from(new Set([...explicitMatches, ...hashMatches]));
  };

  const findPotentialDuplicateTransactions = (candidateTransaction, existingTransactions = []) => {
    const candidateDateKey = normalizeDateKey(candidateTransaction?.date);
    const candidateAmountKey = normalizeAmountKey(candidateTransaction?.amount);
    const candidateDescriptionKey = normalizeTextKey(candidateTransaction?.description);
    const candidateReferenceTokens = extractReferenceTokens(candidateTransaction);
    const candidateReceiptId = candidateTransaction?.receiptId ? String(candidateTransaction.receiptId) : '';

    return existingTransactions.filter((existingTransaction) => {
      if (!existingTransaction) return false;
      if (candidateTransaction?.id !== undefined && existingTransaction.id === candidateTransaction.id) return false;

      const existingDateKey = normalizeDateKey(existingTransaction.date);
      const existingAmountKey = normalizeAmountKey(existingTransaction.amount);
      const existingDescriptionKey = normalizeTextKey(existingTransaction.description);
      const existingReferenceTokens = extractReferenceTokens(existingTransaction);
      const existingReceiptId = existingTransaction?.receiptId ? String(existingTransaction.receiptId) : '';

      const sameReceiptReference = candidateReceiptId && existingReceiptId && candidateReceiptId === existingReceiptId;
      const sameDateAndAmount =
        candidateDateKey &&
        existingDateKey &&
        candidateAmountKey &&
        existingAmountKey &&
        candidateDateKey === existingDateKey &&
        candidateAmountKey === existingAmountKey;
      const sameReference =
        candidateReferenceTokens.length > 0 &&
        existingReferenceTokens.length > 0 &&
        candidateReferenceTokens.some((token) => existingReferenceTokens.includes(token));
      const sameDescription =
        candidateDescriptionKey &&
        existingDescriptionKey &&
        candidateDescriptionKey.length >= 6 &&
        candidateDescriptionKey === existingDescriptionKey;
      const sameAmountAndReference =
        candidateAmountKey &&
        existingAmountKey &&
        candidateAmountKey === existingAmountKey &&
        sameReference;
      const sameReceiptTransaction =
        sameReceiptReference &&
        candidateAmountKey &&
        existingAmountKey &&
        candidateAmountKey === existingAmountKey &&
        (
          !candidateDateKey ||
          !existingDateKey ||
          candidateDateKey === existingDateKey
        ) &&
        (
          !candidateDescriptionKey ||
          !existingDescriptionKey ||
          candidateDescriptionKey === existingDescriptionKey
        );

      return sameReceiptTransaction || sameAmountAndReference || (sameDateAndAmount && (sameReference || sameDescription));
    });
  };

  const resolveDuplicateTransactionChoice = (candidateTransaction, existingTransactions = [], options = {}) => {
    const duplicates = findPotentialDuplicateTransactions(candidateTransaction, existingTransactions);

    if (!duplicates.length) {
      return {
        shouldSave: true,
        baseTransactions: existingTransactions,
        removedCount: 0
      };
    }

    if (options.skipPrompt) {
      return {
        shouldSave: false,
        baseTransactions: existingTransactions,
        removedCount: 0,
        duplicateCount: duplicates.length
      };
    }

    const summary = duplicates
      .slice(0, 5)
      .map((transaction, index) => {
        const amountValue = parseFloat(transaction.amount) || 0;
        return `${index + 1}. ${transaction.date || 'No date'} | ${formatCurrency(amountValue)} | ${transaction.description || 'No description'}`;
      })
      .join('\n');

    const choice = window.prompt(
      `Potential duplicate transaction(s) found (${duplicates.length}).\n\n${summary}\n\nChoose an option:\n1 = Save anyway\n2 = Cancel save\n3 = Delete existing duplicate(s) and save this one`,
      '2'
    );

    if (choice === null || choice.trim() === '2') {
      return {
        shouldSave: false,
        baseTransactions: existingTransactions,
        removedCount: 0
      };
    }

    if (choice.trim() === '3') {
      const duplicateIds = new Set(duplicates.map((transaction) => transaction.id));
      return {
        shouldSave: true,
        baseTransactions: existingTransactions.filter((transaction) => !duplicateIds.has(transaction.id)),
        removedCount: duplicates.length
      };
    }

    return {
      shouldSave: true,
      baseTransactions: existingTransactions,
      removedCount: 0
    };
  };

  const normalizeImportType = (value) => (String(value || '').toLowerCase() === 'income' ? 'income' : 'expenses');

  const getDefaultCategoryForType = useCallback((transactionType) => {
    const normalizedType = normalizeImportType(transactionType);
    const categoryOptions = transactionInputCategories[normalizedType] || [];
    if (categoryOptions.length > 0 && categoryOptions[0]?.id) {
      return categoryOptions[0].id;
    }
    return normalizedType === 'income' ? 'nails' : 'supplies';
  }, [transactionInputCategories]);

  const findImportedCategoryMatch = useCallback((rawCategory, transactionType) => {
    const normalizedType = normalizeImportType(transactionType);
    const categoryOptions = transactionInputCategories[normalizedType] || [];
    if (categoryOptions.length === 0) return '';

    const rawValue = String(rawCategory || '').trim();
    if (!rawValue) return '';

    const rawKey = normalizeTextKey(rawValue);
    if (!rawKey) return '';

    const exactIdMatch = categoryOptions.find((category) => normalizeTextKey(category?.id) === rawKey);
    if (exactIdMatch?.id) {
      return exactIdMatch.id;
    }

    const exactNameMatch = categoryOptions.find((category) => normalizeTextKey(category?.name) === rawKey);
    if (exactNameMatch?.id) {
      return exactNameMatch.id;
    }

    const partialMatch = categoryOptions.find((category) => {
      const optionIdKey = normalizeTextKey(category?.id);
      const optionNameKey = normalizeTextKey(category?.name);
      if (!optionIdKey && !optionNameKey) return false;
      return (
        (optionIdKey && (optionIdKey.includes(rawKey) || rawKey.includes(optionIdKey))) ||
        (optionNameKey && (optionNameKey.includes(rawKey) || rawKey.includes(optionNameKey)))
      );
    });
    if (partialMatch?.id) {
      return partialMatch.id;
    }

    const aliasMap = SPREADSHEET_IMPORT_CATEGORY_ALIASES[normalizedType] || {};
    const aliasId = Object.entries(aliasMap).find(([alias]) => {
      const aliasKey = normalizeTextKey(alias);
      return aliasKey && (rawKey === aliasKey || rawKey.includes(aliasKey) || aliasKey.includes(rawKey));
    })?.[1];
    if (aliasId) {
      const aliasMatch = categoryOptions.find((category) => category.id === aliasId);
      if (aliasMatch?.id) {
        return aliasMatch.id;
      }
    }

    return '';
  }, [transactionInputCategories]);

  const inferIncomeCategoryForSpreadsheetRow = useCallback((row = {}) => {
    const descriptionValue = String(row?.serviceName || row?.description || '').trim();
    const rawCategoryValue = String(row?.category || '').trim();
    const sourceTypeValue = String(row?.sourceType || '').trim();
    const sourceTypeKey = normalizeTextKey(sourceTypeValue);
    const combinedKey = normalizeTextKey(`${descriptionValue} ${rawCategoryValue}`);

    if (!combinedKey && !rawCategoryValue && !sourceTypeKey) {
      return { categoryCandidate: '', suggestedCategory: null };
    }

    const hasKeyword = (keywords = []) =>
      keywords.some((keyword) => {
        const keywordKey = normalizeTextKey(keyword);
        return keywordKey && combinedKey.includes(keywordKey);
      });

    const findExistingIncomeCategoryByTerms = (terms = []) => {
      const categoryOptions = transactionInputCategories.income || [];
      const termKeys = terms.map((term) => normalizeTextKey(term)).filter(Boolean);
      if (!categoryOptions.length || !termKeys.length) return '';
      const match = categoryOptions.find((category) => {
        const idKey = normalizeTextKey(category?.id);
        const nameKey = normalizeTextKey(category?.name);
        return termKeys.some((termKey) => (
          (idKey && (idKey === termKey || idKey.includes(termKey) || termKey.includes(idKey))) ||
          (nameKey && (nameKey === termKey || nameKey.includes(termKey) || termKey.includes(nameKey)))
        ));
      });
      return match?.id || '';
    };

    if (hasKeyword(['gov funding', 'government funding', 'government grant', 'grant funding', 'grant'])) {
      const govFundingId =
        findImportedCategoryMatch('gov_funding', 'income') ||
        findExistingIncomeCategoryByTerms(['gov funding', 'government funding', 'government grant']);
      return { categoryCandidate: govFundingId || 'gov_funding', suggestedCategory: null };
    }

    if (hasKeyword(['loan received', 'loan income', 'business loan', 'loan drawdown', 'loan advance'])) {
      const loanId =
        findImportedCategoryMatch('loan', 'income') ||
        findExistingIncomeCategoryByTerms(['loan', 'business loan']);
      return { categoryCandidate: loanId || 'loan', suggestedCategory: null };
    }

    if (hasKeyword(['gift card', 'giftcard', 'gift voucher', 'giftvoucher', 'voucher'])) {
      const giftCardId =
        findImportedCategoryMatch('gift_card', 'income') ||
        findExistingIncomeCategoryByTerms(['gift_card', 'gift card', 'gift voucher']);
      return { categoryCandidate: giftCardId || 'gift_card', suggestedCategory: null };
    }

    if (sourceTypeKey === 'product') {
      const specificProductCategoryId = findImportedCategoryMatch(rawCategoryValue, 'income');
      if (specificProductCategoryId) {
        return { categoryCandidate: specificProductCategoryId, suggestedCategory: null };
      }

      const retailCategoryId =
        findExistingIncomeCategoryByTerms(['retail products', 'retail', 'products', 'product', 'stock']) ||
        'retail';
      return { categoryCandidate: retailCategoryId, suggestedCategory: null };
    }

    if (hasKeyword(['brow', 'brows', 'eyebrow', 'eyebrows'])) {
      const browsCategoryId = findImportedCategoryMatch('brows', 'income') || 'brows';
      return { categoryCandidate: browsCategoryId, suggestedCategory: null };
    }

    if (hasKeyword([
      'injectable',
      'injectables',
      'botox',
      'filler',
      'fillers',
      'dermal filler',
      'anti wrinkle',
      'anti-wrinkle',
      'skin booster',
      'profhilo'
    ])) {
      const injectablesCategoryId =
        findExistingIncomeCategoryByTerms(['injectables', 'injectable']) ||
        findImportedCategoryMatch('injectables', 'income');
      return { categoryCandidate: injectablesCategoryId || 'injectables', suggestedCategory: null };
    }

    if (hasKeyword([
      'body treatment',
      'body treatments',
      'body contouring',
      'body sculpting',
      'body wrap',
      'body scrub',
      'lymphatic drainage',
      'cellulite treatment'
    ])) {
      const bodyTreatmentsCategoryId =
        findExistingIncomeCategoryByTerms(['body treatments', 'body treatment']) ||
        findImportedCategoryMatch('body_treatments', 'income');
      return { categoryCandidate: bodyTreatmentsCategoryId || 'body_treatments', suggestedCategory: null };
    }

    if (hasKeyword(['lash', 'lashes', 'eyelash', 'eyelashes', 'lash lift', 'lashlift'])) {
      const lashCategoryId = findImportedCategoryMatch('lashes', 'income') || 'lashes';
      return { categoryCandidate: lashCategoryId, suggestedCategory: null };
    }

    if (hasKeyword([
      'shellac',
      'shelac',
      'gel',
      'polish',
      'nail',
      'nails',
      'acrylic',
      'acrylics',
      'manicure',
      'pedicure',
      'file buff',
      'file'
    ])) {
      const nailCategoryId = findImportedCategoryMatch('nails', 'income') || 'nails';
      return { categoryCandidate: nailCategoryId, suggestedCategory: null };
    }

    if (hasKeyword([
      'wax',
      'waxing',
      'threading',
      'full leg',
      'half leg',
      'bikini',
      'hollywood',
      'brazilian',
      'underarm',
      'lip',
      'chin'
    ])) {
      const waxingCategoryId =
        findExistingIncomeCategoryByTerms(['waxing', 'wax', 'threading']) ||
        findImportedCategoryMatch('waxing_services', 'income');
      if (waxingCategoryId) {
        return { categoryCandidate: waxingCategoryId, suggestedCategory: null };
      }
      return {
        categoryCandidate: '',
        suggestedCategory: IMPORT_SUGGESTED_INCOME_CATEGORIES.waxing_services
      };
    }

    if (hasKeyword([
      'spray tan',
      'spraytan',
      'self tan',
      'tan',
      'tanning',
      'full body spray tan',
      'half body spray tan'
    ])) {
      const sprayTanCategoryId =
        findExistingIncomeCategoryByTerms(['spray tan', 'spraytan', 'tanning']) ||
        findImportedCategoryMatch('spray_tan', 'income');
      if (sprayTanCategoryId) {
        return { categoryCandidate: sprayTanCategoryId, suggestedCategory: null };
      }
      return {
        categoryCandidate: '',
        suggestedCategory: IMPORT_SUGGESTED_INCOME_CATEGORIES.spray_tan
      };
    }

    if (hasKeyword([
      'candle',
      'cream',
      'serum',
      'cleanser',
      'shampoo',
      'conditioner',
      'mask',
      'moisturiser',
      'moisturizer',
      'lotion',
      'scrub',
      'toner',
      'product'
    ])) {
      const retailCategoryId =
        findExistingIncomeCategoryByTerms(['retail products', 'retail', 'products', 'product']) ||
        'retail';
      return { categoryCandidate: retailCategoryId, suggestedCategory: null };
    }

    return { categoryCandidate: '', suggestedCategory: null };
  }, [findImportedCategoryMatch, transactionInputCategories.income]);

  const resolveImportedCategory = useCallback((rawCategory, transactionType) => {
    const normalizedType = normalizeImportType(transactionType);
    const fallbackCategoryId = getDefaultCategoryForType(normalizedType);
    const matchedCategoryId = findImportedCategoryMatch(rawCategory, normalizedType);
    if (matchedCategoryId) {
      return { id: matchedCategoryId, usedDefault: false };
    }

    return { id: fallbackCategoryId, usedDefault: true };
  }, [findImportedCategoryMatch, getDefaultCategoryForType]);

  const inferExpenseCategoryFromBankDescription = (descriptionValue) => {
    const normalizedDescription = normalizeTextKey(descriptionValue);
    if (!normalizedDescription) return '';

    const categoryId = Object.entries(BANK_STATEMENT_CATEGORY_HINTS).find(([, keywords]) => {
      return (keywords || []).some((keyword) => {
        const normalizedKeyword = normalizeTextKey(keyword);
        return normalizedKeyword && normalizedDescription.includes(normalizedKeyword);
      });
    })?.[0];

    return categoryId || '';
  };

  const buildImportedVatData = useCallback((amountValue, importedVatAmount, categoryId, transactionType, options = {}) => {
    const normalizedAmount = Number(amountValue);
    const vatAmountCandidate = Number(importedVatAmount);
    const normalizedType = normalizeImportType(transactionType);
    const skipAutoIncomeTaxCalculation = options?.skipAutoIncomeTaxCalculation === true;
    const normalizedCategoryKey = normalizeTextKey(categoryId);
    const normalizedSourceType = normalizeTextKey(options?.sourceType || '');
    const normalizedDescription = normalizeTextKey(options?.description || '');
    const isGiftCardSale =
      normalizedType === 'income' &&
      (
        normalizedCategoryKey === 'giftcard' ||
        normalizedDescription.includes('giftcard') ||
        normalizedDescription.includes('giftvoucher')
      );

    if (isGiftCardSale) {
      return {
        vatRate: null,
        vatAmount: null,
        vatComponents: []
      };
    }

    if (!isVatRegisteredForTransactionDate(options?.date)) {
      return {
        vatRate: null,
        vatAmount: null,
        vatComponents: []
      };
    }

    if (Number.isFinite(vatAmountCandidate) && vatAmountCandidate > 0) {
      const safeVatAmount = parseFloat(vatAmountCandidate.toFixed(2));
      const netAmount = normalizedAmount - safeVatAmount;
      const inferredVatRate = netAmount > 0 ? safeVatAmount / netAmount : null;
      const normalizedVatRate =
        Number.isFinite(inferredVatRate) && inferredVatRate > 0
          ? parseFloat(inferredVatRate.toFixed(6))
          : null;

      return {
        vatRate: normalizedVatRate,
        vatAmount: safeVatAmount,
        vatComponents: [{
          category: categoryId,
          rate: normalizedVatRate || 0,
          amount: safeVatAmount,
          description: normalizedVatRate
            ? getTaxComponentDescription(normalizedVatRate)
            : (currentCountry.taxSystem === 'vat' ? 'Imported VAT' : 'Imported Tax')
        }]
      };
    }

    if (!skipAutoIncomeTaxCalculation && normalizedType === 'income' && isVatRegisteredForTransactionDate(options?.date)) {
      const taxJurisdiction = resolveIncomeTaxJurisdiction();
      const standardVatRate = taxJurisdiction === 'UK'
        ? getStandardCountryVatRate()
        : (
          Number.isFinite(Number(vatSettings.salesRate))
            ? Number(vatSettings.salesRate)
            : getStandardCountryVatRate()
        );
      const sourceTypeVatRate =
        normalizedSourceType === 'product'
          ? standardVatRate
          : normalizedSourceType === 'service'
            ? getIncomeTaxRateForCategory(categoryId, taxJurisdiction, options?.date)
            : null;
      const vatRate =
        Number.isFinite(sourceTypeVatRate) && sourceTypeVatRate > 0
          ? sourceTypeVatRate
          : getIncomeTaxRateForCategory(categoryId, taxJurisdiction, options?.date);
      const calculatedVatAmount = calculateTaxAmountFromGross(normalizedAmount, vatRate);

      if (calculatedVatAmount > 0) {
        const roundedVatAmount = parseFloat(calculatedVatAmount.toFixed(2));
        return {
          vatRate,
          vatAmount: roundedVatAmount,
          vatComponents: [{
            category: categoryId,
            rate: vatRate,
            amount: roundedVatAmount,
            description: getTaxComponentDescription(vatRate)
          }]
        };
      }
    }

    return {
      vatRate: null,
      vatAmount: null,
      vatComponents: []
    };
  }, [
    calculateTaxAmountFromGross,
    currentCountry.taxSystem,
    getIncomeTaxRateForCategory,
    getStandardCountryVatRate,
    getTaxComponentDescription,
    isVatRegisteredForTransactionDate,
    resolveIncomeTaxJurisdiction,
    vatSettings.salesRate
  ]);

  const sortTransactionsByEntryDateDesc = useCallback((items = []) => {
    return [...items].sort(compareTransactionsByEntryDateDesc);
  }, []);

  const resetSpreadsheetImportState = () => {
    setShowSpreadsheetReviewModal(false);
    setSpreadsheetImportDrafts([]);
    setSpreadsheetImportMeta({
      fileName: '',
      sourceKind: 'sales_report',
      sourceVariant: 'default',
      defaultImportType: 'expenses',
      parserSkippedRows: 0,
      typeFilteredRows: 0,
      autoCalculatedVatRows: 0,
      suggestedCategories: []
    });
  };

  const handleApplySuggestedImportCategory = useCallback((suggestedCategory) => {
    if (!suggestedCategory?.id || !suggestedCategory?.name) return;

    const normalizedType = normalizeImportType(suggestedCategory.type || spreadsheetImportMeta.defaultImportType);
    const suggestionName = String(suggestedCategory.name || '').trim();
    const baseSuggestionId =
      String(suggestedCategory.id || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || `category_${Date.now()}`;

    const categoryOptions = mergedCategories[normalizedType] || [];
    const existingCategory = categoryOptions.find((category) => {
      const existingIdKey = normalizeTextKey(category?.id);
      const existingNameKey = normalizeTextKey(category?.name);
      const suggestionIdKey = normalizeTextKey(baseSuggestionId);
      const suggestionNameKey = normalizeTextKey(suggestionName);
      return (
        (existingIdKey && existingIdKey === suggestionIdKey) ||
        (existingNameKey && existingNameKey === suggestionNameKey)
      );
    });

    const resolvedCategoryId = existingCategory?.id || baseSuggestionId;

    if (!existingCategory) {
      setExtraCategories((prev) => {
        const normalized = normalizeExtraCategoriesShape(prev);
        const currentList = normalized[normalizedType] || [];
        const existsAlready = currentList.some((category) => {
          const existingIdKey = normalizeTextKey(category?.id);
          const existingNameKey = normalizeTextKey(category?.name);
          return (
            existingIdKey === normalizeTextKey(resolvedCategoryId) ||
            existingNameKey === normalizeTextKey(suggestionName)
          );
        });

        if (existsAlready) return normalized;

        return {
          ...normalized,
          [normalizedType]: [
            ...currentList,
            {
              id: resolvedCategoryId,
              name: suggestionName || resolvedCategoryId,
              icon: ''
            }
          ]
        };
      });
    }

    setSpreadsheetImportDrafts((prev) =>
      prev.map((row) => {
        if (row.pendingSuggestedCategoryId !== suggestedCategory.id) return row;
        return {
          ...row,
          category: resolvedCategoryId,
          usedDefaultCategory: false,
          pendingSuggestedCategoryId: ''
        };
      })
    );

    setSpreadsheetImportMeta((prev) => ({
      ...prev,
      suggestedCategories: (prev.suggestedCategories || []).filter((entry) => entry.id !== suggestedCategory.id)
    }));
  }, [
    mergedCategories,
    normalizeExtraCategoriesShape,
    spreadsheetImportMeta.defaultImportType
  ]);

  const handleApplyAllSuggestedImportCategories = useCallback(() => {
    const suggestions = Array.isArray(spreadsheetImportMeta.suggestedCategories)
      ? spreadsheetImportMeta.suggestedCategories
      : [];
    suggestions.forEach((suggestedCategory) => {
      handleApplySuggestedImportCategory(suggestedCategory);
    });
  }, [handleApplySuggestedImportCategory, spreadsheetImportMeta.suggestedCategories]);

  const handleAddSpreadsheetDraftCategory = useCallback((draftId, type) => {
    const normalizedType = normalizeImportType(type || spreadsheetImportMeta.defaultImportType);
    const categoryId = handleAddCategory(normalizedType);
    if (!categoryId) return;

    setSpreadsheetImportDrafts((prev) =>
      prev.map((row) => (
        row.draftId === draftId
          ? {
              ...row,
              category: categoryId,
              usedDefaultCategory: false,
              pendingSuggestedCategoryId: ''
            }
          : row
      ))
    );
  }, [handleAddCategory, spreadsheetImportMeta.defaultImportType]);

  const handleSpreadsheetImportClick = () => {
    if (isSpreadsheetImporting) return;
    setSelectedInputMethod('spreadsheet');
    if (spreadsheetFileInputRef.current) {
      spreadsheetFileInputRef.current.click();
    }
  };

  const handleSpreadsheetFileSelected = async (event) => {
    const inputElement = event?.target;
    const selectedFile = inputElement?.files?.[0];

    if (inputElement) {
      inputElement.value = '';
    }

    if (!selectedFile) {
      setSelectedInputMethod(null);
      return;
    }

    if (!user) {
      setShowAuthRequired(true);
      setSelectedInputMethod(null);
      return;
    }

    setIsSpreadsheetImporting(true);

    try {
      const parsedImport = await parseSpreadsheetTransactions(selectedFile);
      const parsedRows = Array.isArray(parsedImport?.parsedRows) ? parsedImport.parsedRows : [];
      const skippedRows = Array.isArray(parsedImport?.skippedRows) ? parsedImport.skippedRows : [];

      if (parsedRows.length === 0) {
        alert('No valid rows were found. Make sure the file includes statement rows with dates and amounts.');
        return;
      }

      const defaultImportType = normalizeImportType(activeTab);
      const isBankStatementImport = parsedImport?.sourceKind === 'bank_statement';
      let typeFilteredRows = 0;
      let autoCalculatedVatRows = 0;
      const suggestedCategoriesById = new Map();

      const draftRows = parsedRows.reduce((acc, row, index) => {
        const amount = Number(row?.amount);
        if (!Number.isFinite(amount) || amount <= 0) return acc;

        const rowType = normalizeImportType(row?.transactionType || defaultImportType);
        if (isBankStatementImport && rowType !== defaultImportType) {
          typeFilteredRows += 1;
          return acc;
        }

        const description = String(row?.serviceName || row?.description || '').trim() ||
          `Imported ${rowType === 'income' ? 'sale' : 'transaction'}`;
        const inferredExpenseCategory = isBankStatementImport && rowType === 'expenses'
          ? inferExpenseCategoryFromBankDescription(row?.serviceName || row?.description || '')
          : '';
        const smartIncomeCategory =
          rowType === 'income' && !isBankStatementImport
            ? inferIncomeCategoryForSpreadsheetRow({
                ...row,
                description
              })
            : { categoryCandidate: '', suggestedCategory: null };
        const categoryCandidate =
          smartIncomeCategory?.categoryCandidate ||
          row?.category ||
          inferredExpenseCategory ||
          (isBankStatementImport ? 'misc' : '');
        const { id: categoryId, usedDefault } = resolveImportedCategory(categoryCandidate, rowType);
        let pendingSuggestedCategoryId = '';
        if (smartIncomeCategory?.suggestedCategory && usedDefault) {
          const suggestedCategory = smartIncomeCategory.suggestedCategory;
          if (suggestedCategory?.id && suggestedCategory?.name) {
            suggestedCategoriesById.set(suggestedCategory.id, suggestedCategory);
            pendingSuggestedCategoryId = suggestedCategory.id;
          }
        }
        const importedVatAmount = Number(row?.vatAmount);
        let vatAmountForDraft = Number.isFinite(importedVatAmount) ? importedVatAmount : null;

        const shouldAutoCalculateSalesVat =
          !isBankStatementImport &&
          rowType === 'income' &&
          isVatRegisteredForTransactionDate(row?.date || new Date().toISOString().split('T')[0]) &&
          (!Number.isFinite(importedVatAmount) || importedVatAmount <= 0);

        if (shouldAutoCalculateSalesVat) {
          const draftDate = row?.date || new Date().toISOString().split('T')[0];
          const vatPreview = buildImportedVatData(amount, null, categoryId, rowType, {
            sourceType: row?.sourceType || '',
            description,
            date: draftDate
          });
          const previewVatAmount = Number(vatPreview?.vatAmount);
          if (Number.isFinite(previewVatAmount) && previewVatAmount > 0) {
            vatAmountForDraft = parseFloat(previewVatAmount.toFixed(2));
            autoCalculatedVatRows += 1;
          } else {
            vatAmountForDraft = null;
          }
        }

        acc.push({
          draftId: `draft-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
          rowNumber: row?.rowNumber || index + 1,
          type: rowType,
          sourceType: row?.sourceType || '',
          date: row?.date || new Date().toISOString().split('T')[0],
          amount: amount.toFixed(2),
          vatAmount: Number.isFinite(vatAmountForDraft) ? vatAmountForDraft.toFixed(2) : '',
          category: categoryId,
          description,
          clientAllocations: [],
          clientId: '',
          clientName: '',
          allocateToClient: false,
          usedDefaultCategory: usedDefault,
          pendingSuggestedCategoryId
        });

        return acc;
      }, []);

      if (draftRows.length === 0) {
        const missingTypeLabel = defaultImportType === 'income' ? 'credit/sales' : 'debit/expense';
        alert(`No ${missingTypeLabel} rows were found for this tab.`);
        return;
      }

      setSpreadsheetImportDrafts(draftRows);
      setSpreadsheetImportMeta({
        fileName: selectedFile.name,
        sourceKind: parsedImport?.sourceKind || 'sales_report',
        sourceVariant: parsedImport?.sourceVariant || 'default',
        defaultImportType,
        parserSkippedRows: skippedRows.length,
        typeFilteredRows,
        autoCalculatedVatRows,
        suggestedCategories: Array.from(suggestedCategoriesById.values())
      });
      setShowInputMethodSelection(false);
      setShowSpreadsheetReviewModal(true);
    } catch (error) {
      console.error('File import failed:', error);
      alert(`Failed to import file: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSpreadsheetImporting(false);
    }
  };

  const handleSpreadsheetDraftFieldChange = (draftId, field, value) => {
    setSpreadsheetImportDrafts((prev) =>
      prev.map((row) => {
        if (row.draftId !== draftId) return row;
        if (field === 'amount' || field === 'vatAmount') {
          return {
            ...row,
            [field]: sanitizeAmountInput(value)
          };
        }
        return {
          ...row,
          [field]: value
        };
      })
    );
  };

  const handleSpreadsheetDraftDelete = (draftId) => {
    setSpreadsheetImportDrafts((prev) => prev.filter((row) => row.draftId !== draftId));
  };

  const handleCancelSpreadsheetReview = () => {
    resetSpreadsheetImportState();
    setSelectedInputMethod(null);
  };

  const handleConfirmSpreadsheetImport = async () => {
    if (!Array.isArray(spreadsheetImportDrafts) || spreadsheetImportDrafts.length === 0) {
      alert('No rows left to import.');
      return;
    }

    if (!ensureDrivePermissionBeforeTransactionChange('importing transactions')) {
      return;
    }

    const nowIso = new Date().toISOString();
    let duplicateCount = 0;
    let importedCount = 0;
    let invalidCount = 0;
    let defaultCategoryCount = 0;
    const nextTransactions = Array.isArray(transactions) ? [...transactions] : [];

    spreadsheetImportDrafts.forEach((draft, index) => {
      const amount = parseFloat(sanitizeAmountInput(draft?.amount || ''));
      const vatAmountRaw = draft?.vatAmount === '' ? null : parseFloat(sanitizeAmountInput(draft?.vatAmount || ''));
      const rawDraftDate = String(draft?.date || '').trim();
      const normalizedDraftDate = /^\d{4}-\d{2}-\d{2}$/.test(rawDraftDate)
        ? rawDraftDate
        : (() => {
            const parsed = parseFlexibleDateValue(rawDraftDate);
            if (!parsed) return '';
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })();

      if (!normalizedDraftDate || !Number.isFinite(amount) || amount <= 0) {
        invalidCount += 1;
        return;
      }

      const rowType = normalizeImportType(draft?.type || spreadsheetImportMeta.defaultImportType);
      const { id: categoryId, usedDefault } = resolveImportedCategory(draft?.category, rowType);
      if (usedDefault) {
        defaultCategoryCount += 1;
      }

      const description = String(draft?.description || '').trim() ||
        `Imported ${rowType === 'income' ? 'sale' : 'transaction'}`;
      const draftClientAllocations = rowType === 'income'
        ? []
        : normalizeClientAllocations(draft?.clientAllocations, clientById);
      const primaryDraftAllocation = draftClientAllocations[0];
      const vatData = buildImportedVatData(amount, vatAmountRaw, categoryId, rowType, {
        skipAutoIncomeTaxCalculation: spreadsheetImportMeta.sourceKind === 'bank_statement',
        sourceType: draft?.sourceType || '',
        description,
        date: normalizedDraftDate
      });

      const candidateTransaction = {
        id: Date.now() + index + Math.random(),
        type: rowType,
        date: normalizedDraftDate,
        amount: parseFloat(amount.toFixed(2)),
        grossAmount: parseFloat(amount.toFixed(2)),
        discountAmount: 0,
        category: categoryId,
        description,
        service: rowType === 'income' ? description : '',
        paymentMethod: normalizePaymentMethodId('card') || 'card',
        paymentBreakdown: [],
        clientId: rowType === 'income' ? '' : (primaryDraftAllocation?.clientId || ''),
        clientName: rowType === 'income' ? '' : (primaryDraftAllocation?.clientName || ''),
        clientAllocations: draftClientAllocations,
        allocateToClient: rowType !== 'income' && draftClientAllocations.length > 0,
        billableToClient: false,
        isSplitTransaction: false,
        isSplitPayment: false,
        source: 'spreadsheet_import',
        importSource: spreadsheetImportMeta.fileName || '',
        importSheet: '',
        importRowNumber: draft?.rowNumber || null,
        createdAt: nowIso,
        updatedAt: nowIso,
        ...vatData
      };

      const duplicates = findPotentialDuplicateTransactions(candidateTransaction, nextTransactions);
      if (duplicates.length > 0) {
        duplicateCount += 1;
        return;
      }

      nextTransactions.push(candidateTransaction);
      importedCount += 1;
    });

    if (importedCount === 0) {
      alert('No new transactions were imported. Review the rows and try again.');
      return;
    }

    const sortedTransactions = sortTransactionsByEntryDateDesc(nextTransactions);
    setTransactions(sortedTransactions);
    await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, sortedTransactions);

    const summaryParts = [
      `Imported ${importedCount} ${spreadsheetImportMeta.defaultImportType === 'income' ? 'sale' : 'expense'}${importedCount === 1 ? '' : 's'} from ${spreadsheetImportMeta.fileName || 'spreadsheet'}.`
    ];
    if (duplicateCount > 0) {
      summaryParts.push(`${duplicateCount} duplicate row(s) skipped.`);
    }
    if (invalidCount > 0) {
      summaryParts.push(`${invalidCount} invalid row(s) skipped.`);
    }
    if (spreadsheetImportMeta.parserSkippedRows > 0) {
      summaryParts.push(`${spreadsheetImportMeta.parserSkippedRows} source row(s) skipped during parsing.`);
    }
    if (spreadsheetImportMeta.typeFilteredRows > 0) {
      summaryParts.push(`${spreadsheetImportMeta.typeFilteredRows} row(s) ignored because they belong to the other tab.`);
    }
    if (spreadsheetImportMeta.autoCalculatedVatRows > 0) {
      summaryParts.push(`${spreadsheetImportMeta.autoCalculatedVatRows} row(s) had ${countryTerminology.taxName} auto-calculated from gross totals.`);
    }
    if (defaultCategoryCount > 0) {
      summaryParts.push(`${defaultCategoryCount} row(s) used a default category.`);
    }

    setSuccessMessage(summaryParts.join(' '));
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 5000);

    resetSpreadsheetImportState();
    setSelectedInputMethod(null);
  };

  const handleTargetIncomeSave = useCallback((updatedGoal) => {
    setTargetIncomeData(updatedGoal);
    setHasGoals(true);
    setHasSavedGoal(true);
    try { localStorage.setItem(GOAL_SAVED_KEY, 'true'); } catch {}
    setVisibleSections(prev => ({ ...prev, budget: false }));
  }, []);

  const persistFuelVatReviewPreference = useCallback((nextPreference) => {
    const normalizedPreference = nextPreference === 'diesel' ? 'diesel' : '';
    setFuelVatReviewPreference(normalizedPreference);
    try {
      if (normalizedPreference) {
        localStorage.setItem(FUEL_VAT_REVIEW_PREFERENCE_STORAGE_KEY, normalizedPreference);
      } else {
        localStorage.removeItem(FUEL_VAT_REVIEW_PREFERENCE_STORAGE_KEY);
      }
    } catch {}
  }, []);

  const resolvePendingFuelVatReview = useCallback((decision) => {
    if (rememberFuelVatPreference && decision === 'diesel') {
      persistFuelVatReviewPreference('diesel');
    }
    if (typeof fuelVatReviewResolverRef.current === 'function') {
      fuelVatReviewResolverRef.current(decision);
      fuelVatReviewResolverRef.current = null;
    }
    setRememberFuelVatPreference(false);
    setPendingFuelVatReview(null);
  }, [persistFuelVatReviewPreference, rememberFuelVatPreference]);

  const requestFuelVatDecision = useCallback((vatReview) => new Promise((resolve) => {
    if (typeof fuelVatReviewResolverRef.current === 'function') {
      fuelVatReviewResolverRef.current('cancel');
    }
    setRememberFuelVatPreference(false);
    fuelVatReviewResolverRef.current = resolve;
    setPendingFuelVatReview(vatReview);
  }), []);

  const materializeExpenseVatSelection = useCallback((transaction) => {
    if (!isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) {
      return clearVatFromTransaction(transaction);
    }

    return materializeExpenseVatFromRate(transaction, {
      resolveTaxComponentDescription: getTaxComponentDescription
    });
  }, [getTaxComponentDescription, isVatRegisteredForTransactionDate]);

  const materializeManualEditedVatSelection = useCallback((transaction) => {
    const transactionType = String(transaction?.type || '').trim().toLowerCase();
    if (['expense', 'expenses'].includes(transactionType)) {
      return materializeExpenseVatFromRate(transaction, {
        resolveTaxComponentDescription: getTaxComponentDescription
      });
    }

    return materializeExpenseVatSelection(transaction);
  }, [getTaxComponentDescription, materializeExpenseVatSelection]);

  const clearVatBeforeRegistrationDate = useCallback((transaction) => {
    if (isVatRegisteredForTransactionDate(transaction?.date || transaction?.invoiceDate)) {
      return transaction;
    }

    const clearedTransaction = clearVatFromTransaction(transaction);
    return {
      ...clearedTransaction,
      invoiceVatAmount: 0,
      invoiceVatRate: 0,
      invoiceVatComponents: [],
      categoryBreakdown: Array.isArray(clearedTransaction.categoryBreakdown)
        ? clearedTransaction.categoryBreakdown.map((entry) => ({
            ...entry,
            vatAmount: 0,
            vatRate: 0
          }))
        : clearedTransaction.categoryBreakdown,
      splitItems: Array.isArray(clearedTransaction.splitItems)
        ? clearedTransaction.splitItems.map((item) => ({
            ...item,
            vatAmount: 0,
            vatRate: 0
          }))
        : clearedTransaction.splitItems
    };
  }, [isVatRegisteredForTransactionDate]);

  const applyIrishExpenseVatGuard = useCallback(async (transaction, { mode = 'manual' } = {}) => {
    const vatReview = getIrishBlockedExpenseVatWarning({
      transaction,
      vatSettings,
      currentCountry
    });

    if (!vatReview) {
      return {
        shouldContinue: true,
        transaction,
        vatReview: null,
        wasVatCleared: false
      };
    }

    if (vatReview.action === 'ask_fuel_type') {
      if (fuelVatReviewPreference === 'diesel') {
        return {
          shouldContinue: true,
          transaction,
          vatReview: { ...vatReview, fuelType: 'diesel', usedSavedPreference: true },
          wasVatCleared: false
        };
      }

      const decision = await requestFuelVatDecision(vatReview);

      if (decision === 'petrol') {
        return {
          shouldContinue: true,
          transaction: clearVatFromTransaction(transaction),
          vatReview: { ...vatReview, fuelType: 'petrol' },
          wasVatCleared: true
        };
      }

      if (decision === 'diesel') {
        return {
          shouldContinue: true,
          transaction,
          vatReview: { ...vatReview, fuelType: 'diesel' },
          wasVatCleared: false
        };
      }

      if (decision === 'other_travel') {
        return {
          shouldContinue: true,
          transaction,
          vatReview: { ...vatReview, fuelType: 'other_travel' },
          wasVatCleared: false
        };
      }

      return {
        shouldContinue: false,
        transaction,
        vatReview,
        wasVatCleared: false
      };
    }

    if (mode === 'import') {
      return {
        shouldContinue: true,
        transaction: clearVatFromTransaction(transaction),
        vatReview,
        wasVatCleared: true
      };
    }

    const shouldClearBlockedVat = window.confirm(vatReview.message);
    if (!shouldClearBlockedVat) {
      return {
        shouldContinue: false,
        transaction,
        vatReview,
        wasVatCleared: false
      };
    }

    return {
      shouldContinue: true,
      transaction: clearVatFromTransaction(transaction),
      vatReview,
      wasVatCleared: true
    };
  }, [currentCountry, fuelVatReviewPreference, requestFuelVatDecision, vatSettings]);

  const buildReceiptVatSyncPayload = useCallback((transaction = {}) => {
    if (!transaction?.receiptId) return null;

    const transactionAmount = Number(transaction.amount) || 0;
    const vatAmount = Number(getTransactionVatAmount(transaction) || transaction.vatAmount || 0);
    const normalizedVatAmount = Number.isFinite(vatAmount) && vatAmount > 0
      ? parseFloat(vatAmount.toFixed(2))
      : 0;
    const rawRate = Number(transaction.vatRate);
    const normalizedVatRate = Number.isFinite(rawRate)
      ? (rawRate > 1 ? rawRate / 100 : rawRate)
      : (normalizedVatAmount > 0 && transactionAmount > normalizedVatAmount
          ? parseFloat((normalizedVatAmount / (transactionAmount - normalizedVatAmount)).toFixed(6))
          : 0);
    const vatComponents = normalizedVatAmount > 0
      ? [{
          category: transaction.category || '',
          rate: normalizedVatRate,
          amount: normalizedVatAmount,
          grossAmount: transactionAmount,
          description: getTaxComponentDescription(normalizedVatRate)
        }]
      : [];
    const syncedItems = Array.isArray(transaction.items) && transaction.items.length > 0
      ? transaction.items.map((item, index) => ({
          ...item,
          category: item?.category || transaction.category || '',
          vatAmount: index === 0 ? normalizedVatAmount : 0,
          vatRate: index === 0 ? normalizedVatRate : 0,
          vatComponents: index === 0 ? vatComponents : []
        }))
      : [{
          description: transaction.description || 'Receipt item',
          quantity: 1,
          unitPrice: transactionAmount,
          amount: transactionAmount,
          category: transaction.category || '',
          vatAmount: normalizedVatAmount,
          vatRate: normalizedVatRate,
          vatComponents
        }];

    return {
      amount: transactionAmount,
      vendor: transaction.description || transaction.supplierName || transaction.clientName || 'Receipt',
      category: transaction.category || '',
      vatAmount: normalizedVatAmount,
      vatRate: normalizedVatRate,
      vatComponents,
      'extractedData.date': transaction.date || '',
      'extractedData.amount': transactionAmount,
      'extractedData.vendor': transaction.description || transaction.supplierName || transaction.clientName || 'Receipt',
      'extractedData.description': transaction.description || '',
      'extractedData.category': transaction.category || '',
      'extractedData.paymentMethod': transaction.paymentMethod || '',
      'extractedData.vatAmount': normalizedVatAmount,
      'extractedData.vatRate': normalizedVatRate,
      'extractedData.vatComponents': vatComponents,
      'extractedData.items': syncedItems
    };
  }, [getTaxComponentDescription, getTransactionVatAmount]);

  const syncLinkedReceiptFromTransaction = useCallback(async (transaction) => {
    if (!canPersistLedgerDataset(
      ledgerRenderSource,
      ledgerDataSourceRef.current,
      activeWorkspaceRef.current?.id,
      auth.currentUser?.uid || userRef.current?.uid
    )) return;
    const payload = buildReceiptVatSyncPayload(transaction);
    if (!payload || !transaction?.receiptId) return;

    try {
      await updateReceipt(transaction.receiptId, payload, { workspace: activeWorkspaceRef.current });
    } catch (error) {
      console.warn('Failed to sync linked receipt VAT data after transaction edit:', error);
    }
  }, [buildReceiptVatSyncPayload, ledgerRenderSource]);

  const uploadTransactionReceiptAttachment = useCallback(async (file, transaction = {}) => {
    if (!file) return null;
    if (!userRef.current?.uid && !user?.uid) {
      throw new Error('Sign in before attaching receipt files.');
    }
    if (!isAllowedReceiptAttachmentFile(file)) {
      throw new Error('Attach a PDF or image file for the receipt.');
    }
    if (file.size > MAX_RECEIPT_ATTACHMENT_SIZE_BYTES) {
      throw new Error('Receipt files must be 10 MB or smaller.');
    }

    const currentUserId = userRef.current?.uid || user?.uid;
    const workspace = activeWorkspaceRef.current;
    const transactionId = String(transaction?.id || editingTransactionId || Date.now());
    const timestamp = Date.now();
    const safeFileName = sanitizeStorageFileName(file.name || 'receipt');
    const basePath = workspace?.id
      ? `workspaceReceipts/${workspace.id}/transactions/${transactionId}`
      : `receipts/${currentUserId}/transactions/${transactionId}`;
    const fullPath = `${basePath}/${timestamp}-${safeFileName}`;
    const storage = getStorage(app);
    const fileRef = storageRef(storage, fullPath);

    await uploadBytes(fileRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        transactionId,
        uploadedByUid: currentUserId,
        ...(workspace?.id ? { workspaceId: workspace.id } : {})
      }
    });

    const downloadUrl = await getDownloadURL(fileRef);
    const uploadedAt = new Date().toISOString();
    return {
      receiptImageUrl: downloadUrl,
      receiptUrl: downloadUrl,
      receiptFileName: file.name || safeFileName,
      receiptFileType: file.type || '',
      receiptStoragePath: fullPath,
      receiptAttachedAt: uploadedAt,
      receiptAttachmentSource: 'manual_transaction_edit',
      receiptAttachmentWorkspaceId: workspace?.id || '',
      updatedAt: uploadedAt
    };
  }, [editingTransactionId, user]);

  const deleteTransactionReceiptAttachmentFile = useCallback(async (transaction = {}) => {
    const path = transaction?.receiptStoragePath || transaction?.storagePath || '';
    if (!path) return;

    try {
      const storage = getStorage(app);
      await deleteObject(storageRef(storage, path));
    } catch (error) {
      console.warn('Failed to delete receipt attachment file from Storage:', error);
    }
  }, []);

  useEffect(() => () => {
    if (typeof fuelVatReviewResolverRef.current === 'function') {
      fuelVatReviewResolverRef.current('cancel');
      fuelVatReviewResolverRef.current = null;
    }
  }, []);

  // Transaction Handlers
const handleSave = async () => {
  if (!user) {
    setShowAuthRequired(true);
    return;
  }

  if (!ensureDrivePermissionBeforeTransactionChange('adding transactions')) {
    return;
  }

  const splitItemsForSave =
    splitSaleMode
      ? splitSaleItems
          .map((item) => {
            const parsedAmount = parseFloat(sanitizeAmountInput(item.amount));
            return {
              description: (item.description || '').trim(),
              category: item.category || '',
              amount: Number.isFinite(parsedAmount) ? parsedAmount : 0
            };
          })
          .filter((item) => item.category && item.amount > 0)
      : [];

  const splitPaymentsForSave = splitPaymentMode
    ? splitPaymentItems
        .map((entry) => ({
          method: normalizePaymentMethodId(entry?.method || ''),
          amount: parseFloat(sanitizeAmountInput(entry?.amount || ''))
        }))
        .filter((entry) => entry.method && Number.isFinite(entry.amount) && entry.amount > 0)
    : [];

  if (splitSaleMode && splitItemsForSave.length === 0) {
    alert(`Add at least one split ${activeTab === 'income' ? 'sale' : 'expense'} item with a category and amount before saving.`);
    return;
  }

  const splitSaleAmount = splitItemsForSave.reduce((sum, item) => sum + item.amount, 0);
  const rawBaseAmount = splitSaleMode
    ? splitSaleAmount
    : parseFloat(sanitizeAmountInput(currentTransaction.amount || ''));
  const enteredBaseAmount = Number.isFinite(rawBaseAmount) ? rawBaseAmount : 0;

  const rawDiscountAmount = parseFloat(sanitizeAmountInput(currentTransaction.discountAmount || ''));
  const enteredDiscountAmount = Number.isFinite(rawDiscountAmount) ? rawDiscountAmount : 0;

  if (enteredDiscountAmount > enteredBaseAmount) {
    alert(`Discount (${formatCurrency(enteredDiscountAmount)}) cannot be greater than amount (${formatCurrency(enteredBaseAmount)}).`);
    return;
  }

  const incomeAmountBasis = activeTab === 'income' && !splitSaleMode && currentTransaction.amountBasis === 'net'
    ? 'net'
    : 'gross';
  const incomeAmountBasisVatRate =
    activeTab === 'income' &&
    !splitSaleMode &&
    incomeAmountBasis === 'net' &&
    isVatRegisteredForTransactionDate(currentTransaction.date)
      ? getIncomeTaxRateForCategory(currentTransaction.category, resolveIncomeTaxJurisdiction(), currentTransaction.date)
      : 0;
  const amountBasisMultiplier = incomeAmountBasis === 'net' && incomeAmountBasisVatRate > 0
    ? 1 + incomeAmountBasisVatRate
    : 1;
  const baseAmountBeforeDiscount = parseFloat((enteredBaseAmount * amountBasisMultiplier).toFixed(2));
  const discountAmount = parseFloat((enteredDiscountAmount * amountBasisMultiplier).toFixed(2));
  const netAmountToSave = parseFloat((baseAmountBeforeDiscount - discountAmount).toFixed(2));
  if (netAmountToSave <= 0) {
    alert('Final amount after discount must be greater than 0.');
    return;
  }

  const effectiveSplitItemsForSave = splitSaleMode
    ? applyDiscountToSplitItems(splitItemsForSave, discountAmount)
    : [];

  // Create a copy of the current transaction
  let transactionToSave = {
    ...currentTransaction,
    id: Date.now(),
    type: activeTab,
    amount: netAmountToSave,
    grossAmount: parseFloat(baseAmountBeforeDiscount.toFixed(2)),
    discountAmount: parseFloat(discountAmount.toFixed(2)),
    amountBasis: activeTab === 'income' ? incomeAmountBasis : 'gross',
    enteredAmount: parseFloat(enteredBaseAmount.toFixed(2)),
    enteredDiscountAmount: parseFloat(enteredDiscountAmount.toFixed(2))
  };

  if (activeTab === 'income') {
    const selectedClient = transactionToSave.clientId
      ? clientById.get(transactionToSave.clientId)
      : null;
    transactionToSave.clientId = selectedClient?.id || '';
    transactionToSave.clientName = selectedClient
      ? getClientDisplayName(selectedClient)
      : (transactionToSave.clientName || '').trim();
    transactionToSave.clientAllocations = [];
  } else {
    const clientAllocations = normalizeClientAllocations(transactionToSave.clientAllocations, clientById);
    const primaryAllocation = clientAllocations[0];
    transactionToSave.clientAllocations = clientAllocations;
    transactionToSave.clientId = primaryAllocation?.clientId || '';
    transactionToSave.clientName = primaryAllocation?.clientName || '';
    transactionToSave.allocateToClient = clientAllocations.length > 0;
    transactionToSave.billableToClient = Boolean(transactionToSave.billableToClient);
    transactionToSave.jobReference = (transactionToSave.jobReference || '').trim();
    const selectedSupplier = transactionToSave.supplierId
      ? rctSubcontractorById.get(transactionToSave.supplierId)
      : null;
    transactionToSave.supplierId = selectedSupplier?.id || transactionToSave.supplierId || '';
    transactionToSave.supplierName = selectedSupplier?.name || (transactionToSave.supplierName || '').trim();
    if (selectedSupplier?.supplierType === 'rct') {
      transactionToSave.rctSubcontractorId = selectedSupplier.id;
      transactionToSave.rctSubcontractorName = selectedSupplier.name;
      transactionToSave.rctDeductionRate = selectedSupplier.rctDeductionRate ?? transactionToSave.rctDeductionRate ?? 0.2;
    }
  }
  const selectedStaffAssignment = resolveSelectedStaffAssignment(
    transactionToSave.staffMemberId,
    transactionToSave.staffMemberName
  );
  transactionToSave.staffMemberId = selectedStaffAssignment.staffMemberId;
  transactionToSave.staffMemberName = selectedStaffAssignment.staffMemberName;

  if (splitSaleMode) {
    const splitCategoryBreakdown = Object.values(
      effectiveSplitItemsForSave.reduce((acc, item) => {
        if (!item.category || !item.amount) return acc;
        if (!acc[item.category]) {
          acc[item.category] = { category: item.category, amount: 0 };
        }
        acc[item.category].amount += Number(item.amount) || 0;
        return acc;
      }, {})
    ).map((entry) => ({
      ...entry,
      amount: parseFloat(entry.amount.toFixed(2))
    }));

    const dominantSplitCategory = [...splitCategoryBreakdown]
      .sort((a, b) => b.amount - a.amount)[0]?.category;

    transactionToSave = {
      ...transactionToSave,
      amount: netAmountToSave,
      category: dominantSplitCategory || dominantSplitSaleCategory || transactionToSave.category || effectiveSplitItemsForSave[0]?.category || '',
      description:
        transactionToSave.description ||
        `Split ${activeTab === 'income' ? 'sale' : 'expense'} (${effectiveSplitItemsForSave.length} items)`,
      items: effectiveSplitItemsForSave.map((item) => ({
        description: item.description || (activeTab === 'income' ? 'Sale item' : 'Expense item'),
        category: item.category,
        quantity: 1,
        unitPrice: item.amount,
        amount: item.amount,
        saveToCost: activeTab !== 'income' ? transactionToSave.saveToCost === true : item.saveToCost
      })),
      categoryBreakdown: splitCategoryBreakdown,
      isSplitTransaction: true,
      service: ''
    };
  } else if (activeTab === 'income') {
    transactionToSave = {
      ...transactionToSave,
      items: [],
      categoryBreakdown: [],
      isSplitTransaction: false
    };
  }

  if (splitPaymentMode) {
    if (splitPaymentsForSave.length === 0) {
      alert('Add at least one split payment line with a payment method and amount.');
      return;
    }

    const splitPaymentTotalAmount = splitPaymentsForSave.reduce((sum, entry) => sum + entry.amount, 0);
    const transactionAmount = Number(transactionToSave.amount) || 0;

    if (splitPaymentTotalAmount - transactionAmount > 0.01) {
      alert(`Split payment total (${formatCurrency(splitPaymentTotalAmount)}) cannot be more than transaction amount (${formatCurrency(transactionAmount)}).`);
      return;
    }

    const outstandingAmount = parseFloat(Math.max(transactionAmount - splitPaymentTotalAmount, 0).toFixed(2));
    if (outstandingAmount > 0.01 && activeTab !== 'income') {
      alert('Part payments can only be recorded for income sales.');
      return;
    }

    if (outstandingAmount > 0.01 && !transactionToSave.clientId) {
      alert('Select a client before saving a part payment, so the outstanding balance can be tracked.');
      return;
    }

    const dominantSplitPaymentMethod = [...splitPaymentsForSave]
      .sort((a, b) => b.amount - a.amount)[0]?.method || 'cash';

    transactionToSave.paymentMethod = dominantSplitPaymentMethod;
    transactionToSave.paymentBreakdown = splitPaymentsForSave.map((entry) => ({
      method: entry.method,
      amount: parseFloat(entry.amount.toFixed(2)),
      paidAt: transactionToSave.date || new Date().toISOString().split('T')[0]
    }));
    transactionToSave.amountPaid = parseFloat(splitPaymentTotalAmount.toFixed(2));
    transactionToSave.outstandingAmount = outstandingAmount;
    transactionToSave.paymentStatus = outstandingAmount > 0.01 ? 'part_paid' : 'paid';
    transactionToSave.isSplitPayment = transactionToSave.paymentBreakdown.length > 1 || outstandingAmount > 0.01;
    transactionToSave.saleTotalAmount = parseFloat(transactionAmount.toFixed(2));
    transactionToSave.isPartPaymentSale = outstandingAmount > 0.01;

    if (outstandingAmount > 0.01) {
      transactionToSave.amount = parseFloat(splitPaymentTotalAmount.toFixed(2));

      if (splitSaleMode && effectiveSplitItemsForSave.length > 0) {
        const paidSplitItems = allocatePaidAmountToSplitItems(effectiveSplitItemsForSave, splitPaymentTotalAmount);
        const paidSplitCategoryBreakdown = Object.values(
          paidSplitItems.reduce((acc, item) => {
            if (!item.category || !item.amount) return acc;
            if (!acc[item.category]) {
              acc[item.category] = { category: item.category, amount: 0 };
            }
            acc[item.category].amount += Number(item.amount) || 0;
            return acc;
          }, {})
        ).map((entry) => ({
          ...entry,
          amount: parseFloat(entry.amount.toFixed(2))
        }));

        transactionToSave.items = paidSplitItems.map((item) => ({
          description: item.description || (activeTab === 'income' ? 'Sale item' : 'Expense item'),
          category: item.category,
          quantity: 1,
          unitPrice: item.amount,
          amount: item.amount,
          saleTotalAmount: item.saleTotalAmount
        }));
        transactionToSave.categoryBreakdown = paidSplitCategoryBreakdown;
      }
    }
  } else {
    const normalizedPaymentMethod = normalizePaymentMethodId(transactionToSave.paymentMethod || 'cash') || 'cash';
    const isUnpaidTransaction = normalizedPaymentMethod === 'unpaid';
    transactionToSave.paymentMethod = normalizedPaymentMethod;
    transactionToSave.paymentBreakdown = [];
    const invoiceTotalAmount = parseFloat((Number(transactionToSave.amount) || 0).toFixed(2));

    if (isUnpaidTransaction) {
      if (!transactionToSave.clientId && activeTab === 'income') {
        alert('Select a client before saving an unpaid sale, so the outstanding balance can be tracked.');
        return;
      }

      transactionToSave.invoiceDate = transactionToSave.invoiceDate || transactionToSave.date;
      transactionToSave.paymentDate = '';
      transactionToSave.amount = 0;
      transactionToSave.grossAmount = 0;
      transactionToSave.amountPaid = 0;
      transactionToSave.outstandingAmount = invoiceTotalAmount;
      transactionToSave.paymentStatus = 'unpaid';
      transactionToSave.saleTotalAmount = activeTab === 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.expenseTotalAmount = activeTab !== 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.billTotalAmount = activeTab !== 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.isPartPaymentSale = activeTab === 'income';
      transactionToSave.isPartPaymentExpense = activeTab !== 'income';

      if (Array.isArray(transactionToSave.items) && transactionToSave.items.length > 0) {
        transactionToSave.items = transactionToSave.items.map((item) => {
          const originalAmount = Number(item.amount ?? item.unitPrice ?? 0) || 0;
          return {
            ...item,
            invoiceAmount: parseFloat(originalAmount.toFixed(2)),
            saleTotalAmount: activeTab === 'income' ? parseFloat(originalAmount.toFixed(2)) : item.saleTotalAmount,
            expenseTotalAmount: activeTab !== 'income' ? parseFloat(originalAmount.toFixed(2)) : item.expenseTotalAmount,
            unitPrice: 0,
            amount: 0
          };
        });
      }
      if (Array.isArray(transactionToSave.categoryBreakdown) && transactionToSave.categoryBreakdown.length > 0) {
        transactionToSave.invoiceCategoryBreakdown = transactionToSave.categoryBreakdown.map((entry) => ({
          ...entry,
          amount: parseFloat((Number(entry.amount) || 0).toFixed(2))
        }));
        transactionToSave.categoryBreakdown = transactionToSave.categoryBreakdown.map((entry) => ({
          ...entry,
          amount: 0
        }));
      }
    } else {
      transactionToSave.amountPaid = invoiceTotalAmount;
      transactionToSave.outstandingAmount = 0;
      transactionToSave.paymentStatus = 'paid';
      transactionToSave.saleTotalAmount = activeTab === 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.expenseTotalAmount = activeTab !== 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.billTotalAmount = activeTab !== 'income' ? invoiceTotalAmount : undefined;
      transactionToSave.isPartPaymentSale = false;
      transactionToSave.isPartPaymentExpense = false;
    }
    transactionToSave.isSplitPayment = false;
  }

  if (activeTab !== 'income') {
    const rctCategorySelected = normalizeCategoryId(transactionToSave.category) === 'subcontractor_rct';
    const selectedRctSubcontractor = transactionToSave.rctSubcontractorId
      ? rctSubcontractorById.get(transactionToSave.rctSubcontractorId)
      : null;
    if (supportsRct && (rctCategorySelected || transactionToSave.rctReverseCharge || selectedRctSubcontractor)) {
      const invoiceAmount = Number(
        transactionToSave.billTotalAmount ??
        transactionToSave.expenseTotalAmount ??
        transactionToSave.invoiceTotalAmount ??
        transactionToSave.amount
      ) || 0;
      const deductionRate = [0, 0.2, 0.35].includes(Number(transactionToSave.rctDeductionRate))
        ? Number(transactionToSave.rctDeductionRate)
        : Number(selectedRctSubcontractor?.rctDeductionRate ?? 0.2);
      const rctDeductionAmount = parseFloat((invoiceAmount * deductionRate).toFixed(2));
      const rctNetPayable = parseFloat(Math.max(invoiceAmount - rctDeductionAmount, 0).toFixed(2));
      const rctVatAmount = parseFloat((invoiceAmount * DEFAULT_RCT_VAT_RATE).toFixed(2));

      transactionToSave = {
        ...transactionToSave,
        category: 'subcontractor_rct',
        rctRole: 'principal',
        rctReverseCharge: true,
        rctSubcontractorId: selectedRctSubcontractor?.id || transactionToSave.rctSubcontractorId || '',
        rctSubcontractorName: selectedRctSubcontractor?.name || transactionToSave.rctSubcontractorName || '',
        rctDeductionRate: deductionRate,
        rctGrossAmount: parseFloat(invoiceAmount.toFixed(2)),
        rctDeductionAmount,
        rctNetPayable,
        rctTaxDueAmount: rctDeductionAmount,
        rctReverseChargeVatRate: DEFAULT_RCT_VAT_RATE,
        rctReverseChargeVatAmount: rctVatAmount,
        rctVatOutputAmount: rctVatAmount,
        rctVatInputAmount: rctVatAmount,
        expenseTotalAmount: parseFloat(invoiceAmount.toFixed(2)),
        billTotalAmount: parseFloat(invoiceAmount.toFixed(2)),
        invoiceTotalAmount: parseFloat(invoiceAmount.toFixed(2)),
        amount: transactionToSave.paymentStatus === 'unpaid' ? 0 : rctNetPayable,
        amountPaid: transactionToSave.paymentStatus === 'unpaid' ? 0 : rctNetPayable,
        outstandingAmount: transactionToSave.paymentStatus === 'unpaid'
          ? parseFloat((rctNetPayable + rctDeductionAmount).toFixed(2))
          : 0,
        notes: [
          transactionToSave.notes,
          `RCT split: ${formatCurrency(rctNetPayable)} net payable to subcontractor, ${formatCurrency(rctDeductionAmount)} RCT due to Revenue. Reverse charge VAT ${formatCurrency(rctVatAmount)} at 13.5%.`
        ].filter(Boolean).join('\n')
      };
    }

    transactionToSave = materializeExpenseVatSelection(transactionToSave);
  }

  if (supportsRct && activeTab === 'income' && normalizeCategoryId(transactionToSave.category) === 'rct_contract_income') {
    const invoiceAmount = Number(transactionToSave.saleTotalAmount ?? transactionToSave.invoiceTotalAmount ?? netAmountToSave) || 0;
    const deductionRate = [0, 0.2, 0.35].includes(Number(transactionToSave.rctDeductionRate))
      ? Number(transactionToSave.rctDeductionRate)
      : 0.2;
    const rctDeductionAmount = parseFloat((invoiceAmount * deductionRate).toFixed(2));
    const netReceivable = parseFloat(Math.max(invoiceAmount - rctDeductionAmount, 0).toFixed(2));
    const isUnpaidRctInvoice = transactionToSave.paymentStatus === 'unpaid' || transactionToSave.paymentMethod === 'unpaid';

    transactionToSave = {
      ...transactionToSave,
      rctRole: 'subcontractor',
      rctDeductionRate: deductionRate,
      rctGrossAmount: parseFloat(invoiceAmount.toFixed(2)),
      rctDeductionAmount,
      rctCreditAmount: isUnpaidRctInvoice ? 0 : rctDeductionAmount,
      rctNetReceivable: netReceivable,
      saleTotalAmount: parseFloat(invoiceAmount.toFixed(2)),
      invoiceTotalAmount: parseFloat(invoiceAmount.toFixed(2)),
      amount: isUnpaidRctInvoice ? 0 : netReceivable,
      amountPaid: isUnpaidRctInvoice ? 0 : parseFloat(invoiceAmount.toFixed(2)),
      outstandingAmount: isUnpaidRctInvoice ? parseFloat(invoiceAmount.toFixed(2)) : 0,
      paymentStatus: isUnpaidRctInvoice ? 'unpaid' : 'paid',
      notes: [
        transactionToSave.notes,
        `RCT income split: ${formatCurrency(netReceivable)} net paid by principal, ${formatCurrency(rctDeductionAmount)} RCT credit paid to Revenue.`
      ].filter(Boolean).join('\n')
    };
  }

  const expenseVatGuardResult = await applyIrishExpenseVatGuard(transactionToSave, { mode: 'manual' });
  if (!expenseVatGuardResult.shouldContinue) {
    return;
  }
  transactionToSave = expenseVatGuardResult.transaction;

  // For income transactions, automatically calculate VAT if enabled and registered
  if (activeTab === 'income' && isVatRegisteredForTransactionDate(transactionToSave.date)) {
    const taxJurisdiction = resolveIncomeTaxJurisdiction();

    if (splitSaleMode && effectiveSplitItemsForSave.length > 0) {
      const fullInvoiceSplitVatSummary = buildSplitIncomeVatSummary(effectiveSplitItemsForSave, taxJurisdiction, transactionToSave.date);
      const vatSplitItemsForSave =
        transactionToSave.isPartPaymentSale && Array.isArray(transactionToSave.items) && transactionToSave.items.length > 0
          ? transactionToSave.items
          : effectiveSplitItemsForSave;
      const splitVatSummary = buildSplitIncomeVatSummary(vatSplitItemsForSave, taxJurisdiction, transactionToSave.date);

      transactionToSave.vatRate = splitVatSummary.effectiveVatRate;
      transactionToSave.vatAmount = splitVatSummary.totalVatAmount;
      transactionToSave.vatComponents = splitVatSummary.vatComponents;
      transactionToSave.invoiceVatRate = fullInvoiceSplitVatSummary.effectiveVatRate;
      transactionToSave.invoiceVatAmount = fullInvoiceSplitVatSummary.totalVatAmount;
      transactionToSave.invoiceVatComponents = fullInvoiceSplitVatSummary.vatComponents;

      if (Array.isArray(transactionToSave.items)) {
        transactionToSave.items = transactionToSave.items.map((item, index) => {
          const lineVat = splitVatSummary.lineVatBreakdown[index];
          const invoiceLineVat = fullInvoiceSplitVatSummary.lineVatBreakdown[index];
          if (!lineVat && !invoiceLineVat) return item;
          return {
            ...item,
            vatRate: lineVat?.rate ?? invoiceLineVat?.rate,
            vatAmount: lineVat?.amount ?? 0,
            invoiceVatRate: invoiceLineVat?.rate ?? lineVat?.rate,
            invoiceVatAmount: invoiceLineVat?.amount ?? lineVat?.amount ?? 0
          };
        });
      }
    } else {
      const vatRate = getIncomeTaxRateForCategory(transactionToSave.category, taxJurisdiction, transactionToSave.date);
      const invoiceGrossAmount = parseFloat(transactionToSave.saleTotalAmount ?? transactionToSave.invoiceTotalAmount ?? transactionToSave.grossAmount ?? transactionToSave.amount) || 0;
      const grossAmount = transactionToSave.paymentStatus === 'unpaid' || transactionToSave.paymentMethod === 'unpaid'
        ? invoiceGrossAmount
        : (parseFloat(transactionToSave.amount) || invoiceGrossAmount);
      const vatAmount = calculateTaxAmountFromGross(grossAmount, vatRate);
      const invoiceVatAmount = calculateTaxAmountFromGross(invoiceGrossAmount, vatRate);

      transactionToSave.vatRate = vatRate;
      transactionToSave.vatAmount = parseFloat(vatAmount.toFixed(2));
      transactionToSave.vatComponents = [{
        rate: vatRate,
        amount: parseFloat(vatAmount.toFixed(2)),
        description: getTaxComponentDescription(vatRate)
      }];
      transactionToSave.invoiceVatRate = vatRate;
      transactionToSave.invoiceVatAmount = parseFloat(invoiceVatAmount.toFixed(2));
      transactionToSave.invoiceVatComponents = [{
        rate: vatRate,
        amount: parseFloat(invoiceVatAmount.toFixed(2)),
        grossAmount: parseFloat(invoiceGrossAmount.toFixed(2)),
        description: getTaxComponentDescription(vatRate)
      }];
    }
  }

  if (activeTab === 'income' && !isVatRegisteredForTransactionDate(transactionToSave.date)) {
    transactionToSave = clearVatBeforeRegistrationDate(transactionToSave);
  }

  if (!transactionToSave.createdAt) {
    transactionToSave.createdAt = new Date().toISOString();
  }
  if (!transactionToSave.updatedAt) {
    transactionToSave.updatedAt = transactionToSave.createdAt;
  }

  const newTransaction = transactionToSave;

  const duplicateResolution = resolveDuplicateTransactionChoice(newTransaction, transactions);
  if (!duplicateResolution.shouldSave) {
    return;
  }

  const transactionsAfterDedup = duplicateResolution.baseTransactions;

  // Optionally save this expense to the Cost Calculator
  pushCostItemsFromTransaction(newTransaction, { sourceLabel: 'manual' });

  // Add the new transaction to the array and sort by date (newest first)
  const updatedTransactions = sortTransactionsByEntryDateDesc([...transactionsAfterDedup, newTransaction]);

  // Save using the hybrid storage system
  persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions)
    .then(() => {
      console.log('Transaction saved successfully');
    })
    .catch(error => {
      console.error('Error saving transaction:', error);
      // We still update the state even if saving fails
      // because the hybrid storage system will have saved to localStorage
    });

  // Update state
  setTransactions(updatedTransactions);
  setIsAddingTransaction(false);
  setSelectedInputMethod(null);
  setCurrentTransaction(createEmptyTransactionDraft(activeTab));
  resetSplitSaleState();
  resetSplitPaymentState();
  // Make sure to show the success message
  if (duplicateResolution.removedCount > 0) {
    setSuccessMessage(`Removed ${duplicateResolution.removedCount} duplicate transaction(s) and saved the new entry.`);
  } else {
    setSuccessMessage('Transaction saved successfully.');
  }
  setShowSuccess(true);
  setTimeout(() => setShowSuccess(false), 3000);

  // Don't automatically show recent transactions
  // setShowRecentTransactions(true);
};

const confirmDelete = (id) => {
  if (window.confirm("Are you sure you want to permanently delete this transaction?")) {
    handleDelete(id);
  }
};

// REPLACE the handleDelete function with this:
const handleDelete = (id) => {
  if (!ensureDrivePermissionBeforeTransactionChange('deleting transactions')) {
    return;
  }

  const updatedTransactions = transactions.filter(transaction => transaction.id !== id);
  setTransactions(updatedTransactions);

  // Save the updated transactions
  persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions)
    .then(() => {
      console.log('Transactions updated after deletion');
    })
    .catch(error => {
      console.error('Error saving transactions after deletion:', error);
    });
};

const handleUpdateDepreciation = (id, years) => {
  const updatedDepreciationPeriods = { ...customDepreciationPeriods, [id]: years };
  setCustomDepreciationPeriods(updatedDepreciationPeriods);
  setEditingDepreciationId(null);

  // Save the updated depreciation periods
  persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.DEPRECIATION, updatedDepreciationPeriods)
    .catch(error => {
      console.error('Error saving depreciation period update:', error);
    });
};

const handleAccountantAssetUpdate = useCallback((assetId, updates = {}) => {
  if (!assetId || !updates || typeof updates !== 'object') return;
  if (!ensureDrivePermissionBeforeTransactionChange('updating asset register')) {
    return;
  }

  const normalizedAssetId = String(assetId);
  const nowIso = new Date().toISOString();
  let nextDepreciationPeriods = customDepreciationPeriods;

  if (Object.prototype.hasOwnProperty.call(updates, 'depreciationYears')) {
    const depreciationYears = Math.max(1, Number(updates.depreciationYears) || 1);
    nextDepreciationPeriods = {
      ...customDepreciationPeriods,
      [assetId]: depreciationYears
    };
    setCustomDepreciationPeriods(nextDepreciationPeriods);
    persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.DEPRECIATION, nextDepreciationPeriods)
      .catch((error) => {
        console.error('Error saving accountant asset depreciation update:', error);
      });
  }

  const updatedTransactions = transactions.map((transaction) => {
    if (String(transaction.id) !== normalizedAssetId) return transaction;

    const nextTransaction = {
      ...transaction,
      updatedAt: nowIso
    };

    if (Object.prototype.hasOwnProperty.call(updates, 'description')) {
      nextTransaction.description = updates.description;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'date')) {
      nextTransaction.date = updates.date;
      nextTransaction.purchaseDate = updates.date;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'amount')) {
      const amount = Number(updates.amount);
      nextTransaction.amount = Number.isFinite(amount) ? amount : 0;
      delete nextTransaction.currentValue;
      delete nextTransaction.annualDepreciation;
      delete nextTransaction.accumulatedDepreciation;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'depreciationYears')) {
      nextTransaction.depreciationYears = Math.max(1, Number(updates.depreciationYears) || 1);
      delete nextTransaction.currentValue;
      delete nextTransaction.annualDepreciation;
      delete nextTransaction.accumulatedDepreciation;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'assetStatus')) {
      nextTransaction.assetStatus = updates.assetStatus || 'active';
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'disposalDate')) {
      nextTransaction.disposalDate = updates.disposalDate || '';
      const purchaseDate = getTransactionFinancialDateValue(nextTransaction) || new Date(nextTransaction.date);
      const disposalDate = updates.disposalDate ? new Date(updates.disposalDate) : null;
      if (disposalDate && !Number.isNaN(disposalDate.getTime()) && purchaseDate && !Number.isNaN(purchaseDate.getTime())) {
        nextTransaction.yearsAtDisposal = Math.max(0, disposalDate.getFullYear() - purchaseDate.getFullYear());
      }
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'disposalAmount')) {
      const disposalAmount = Number(updates.disposalAmount);
      nextTransaction.disposalAmount = Number.isFinite(disposalAmount) ? disposalAmount : 0;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'disposalReason')) {
      nextTransaction.disposalReason = updates.disposalReason || '';
    }

    return nextTransaction;
  });

  setTransactions(updatedTransactions);
  persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions)
    .catch((error) => {
      console.error('Error saving accountant asset register update:', error);
    });
}, [
  customDepreciationPeriods,
  ensureDrivePermissionBeforeTransactionChange,
  persistDatasetForCurrentAccess,
  setTransactions,
  transactions
]);

  // State for asset disposal modal
  const [disposingAssetId, setDisposingAssetId] = useState(null);
  const [disposalData, setDisposalData] = useState({
    disposalType: 'sold', // 'sold' or 'written-off'
    disposalDate: new Date().toISOString().split('T')[0],
    disposalAmount: 0,
    disposalReason: ''
  });

  // Handle asset disposal (selling or writing off)
  // REPLACE the handleAssetDisposal function with this:
const handleAssetDisposal = () => {
  if (!disposingAssetId) return;
  if (!ensureDrivePermissionBeforeTransactionChange('updating transactions')) {
    return;
  }

  const assetToDispose = transactions.find(t => t.id === disposingAssetId);
  if (!assetToDispose) return;

  // Calculate years at disposal
  const purchaseDate = getTransactionFinancialDateValue(assetToDispose) || new Date(assetToDispose.date);
  const disposalDate = new Date(disposalData.disposalDate);
  const yearsAtDisposal = disposalDate.getFullYear() - purchaseDate.getFullYear();

  // Update the transaction with disposal information
  const updatedTransaction = {
    ...assetToDispose,
    assetStatus: disposalData.disposalType === 'sold' ? 'sold' : 'written-off',
    disposalDate: disposalData.disposalDate,
    disposalAmount: parseFloat(disposalData.disposalAmount) || 0,
    yearsAtDisposal,
    disposalReason: disposalData.disposalReason
  };

  // Create a new transactions array with the updated asset
  let updatedTransactions = transactions.map(t =>
    t.id === disposingAssetId ? updatedTransaction : t
  );

  // Create a sale transaction if the asset was sold
  if (disposalData.disposalType === 'sold' && disposalData.disposalAmount > 0) {
    const nowIso = new Date().toISOString();
    const saleTransaction = {
      id: Date.now(),
      type: 'income',
      category: 'assetSale',
      description: `Sale of ${assetToDispose.description || CATEGORIES.expenses.find(c => c.id === assetToDispose.category)?.name || 'Asset'}`,
      amount: parseFloat(disposalData.disposalAmount),
      date: disposalData.disposalDate,
      paymentMethod: 'cash', // Default payment method
      relatedAssetId: disposingAssetId, // This links the sale to the original asset
      createdAt: nowIso,
      updatedAt: nowIso
    };

    updatedTransactions = [...updatedTransactions, saleTransaction];
  }

  // Sort transactions by date (newest first)
  updatedTransactions = sortTransactionsByEntryDateDesc(updatedTransactions);

  // Update state
  setTransactions(updatedTransactions);

  // Save the updated transactions
  persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions)
    .then(() => {
      console.log('Asset disposal saved successfully');
    })
    .catch(error => {
      console.error('Error saving asset disposal:', error);
    });

  // Reset state
  setDisposingAssetId(null);
  setDisposalData({
    disposalType: 'sold',
    disposalDate: new Date().toISOString().split('T')[0],
    disposalAmount: 0,
    disposalReason: ''
  });
};


  const handleSaveService = (category) => {
    setServicePrices(prev => {
      const currentServices = prev[category] || [];
      return {
        ...prev,
        [category]: [...currentServices, newService]
      };
    });

    setNewService({ name: '', duration: 30, cost: 0, price: 0 });
  };

  const handleUpdateService = (category, index, updatedService) => {
    setServicePrices(prev => {
      const services = [...(prev[category] || [])];
      services[index] = updatedService;
      return {
        ...prev,
        [category]: services
      };
    });
  };

  const handleDeleteService = (category, index) => {
    setServicePrices(prev => {
      const services = [...(prev[category] || [])];
      services.splice(index, 1);
      return {
        ...prev,
        [category]: services
      };
    });
  };

  // Voice input handler - automatically save transactions without modal
  const handleVoiceTransactionParsed = async (parsedTransaction, options = {}) => {
    if (!ensureDrivePermissionBeforeTransactionChange('adding transactions')) {
      return false;
    }

    // Map the parsed transaction to our transaction format
    // Also capture any line items to cost calculator
    const sourceLabel = options.sourceLabel || 'voice';
    const receiptImageUrl = parsedTransaction.receiptImageUrl || parsedTransaction.imageUrl || parsedTransaction.receiptUrl || null;
    const receiptId = parsedTransaction.receiptId || parsedTransaction.sourceReceiptId || null;
    const normalizedReceiptId = receiptId ? String(receiptId).trim() : '';
    const receiptTransactionPendingKey = normalizedReceiptId
      ? [
          normalizedReceiptId,
          normalizeDateKey(parsedTransaction.date || parsedTransaction.invoiceDate || ''),
          normalizeAmountKey(parsedTransaction.amount),
          normalizeTextKey(parsedTransaction.description || parsedTransaction.vendor || '')
        ].join('|')
      : '';
    if (sourceLabel === 'receipt' && receiptTransactionPendingKey) {
      const alreadyQueued = pendingReceiptTransactionIdsRef.current.has(receiptTransactionPendingKey);
      const alreadySaved = (Array.isArray(transactions) ? transactions : []).some((transaction) => (
        [
          String(transaction?.receiptId || transaction?.sourceReceiptId || '').trim(),
          normalizeDateKey(transaction?.date || transaction?.invoiceDate || ''),
          normalizeAmountKey(transaction?.amount),
          normalizeTextKey(transaction?.description || transaction?.vendor || '')
        ].join('|') === receiptTransactionPendingKey
      ));

      if (alreadyQueued || alreadySaved) {
        return false;
      }

      pendingReceiptTransactionIdsRef.current.add(receiptTransactionPendingKey);
    }
    const nowIso = new Date().toISOString();
    const parsedAmountValue = parseFloat(sanitizeSignedAmountInput(parsedTransaction.amount ?? '0')) || 0;
    const parsedDiscountValue = parseFloat(sanitizeAmountInput(parsedTransaction.discountAmount ?? '0')) || 0;
    const parsedGrossValue = parseFloat(sanitizeSignedAmountInput(parsedTransaction.grossAmount ?? '0')) || 0;
    const parsedNetValue = parseFloat(sanitizeSignedAmountInput(parsedTransaction.netAmount ?? '0')) || 0;
    const normalizedItems = Array.isArray(parsedTransaction.items)
      ? parsedTransaction.items
          .map((item, index) => {
            const itemAmount = parseFloat(sanitizeSignedAmountInput(item?.amount ?? item?.unitPrice ?? '0')) || 0;
            return {
              ...item,
              description: item?.description || `Item ${index + 1}`,
              category: item?.category || parsedTransaction.category || '',
              quantity: Number.isFinite(Number(item?.quantity)) && Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
              unitPrice: item?.unitPrice ?? itemAmount,
              amount: itemAmount
            };
          })
          .filter((item) => item.category || Math.abs(item.amount) > 0)
      : [];
    const normalizedCategoryBreakdown = Array.isArray(parsedTransaction.categoryBreakdown)
      ? parsedTransaction.categoryBreakdown
          .map((entry) => ({
            category: entry?.category || '',
            amount: parseFloat(sanitizeSignedAmountInput(entry?.amount ?? '0')) || 0
          }))
          .filter((entry) => entry.category && Math.abs(entry.amount) > 0)
      : [];
    const itemCategoryCount = new Set(normalizedItems.map((item) => item.category).filter(Boolean)).size;
    const breakdownCategoryCount = new Set(normalizedCategoryBreakdown.map((entry) => entry.category).filter(Boolean)).size;
    const inferredSplitTransaction =
      Boolean(parsedTransaction.isSplitTransaction) ||
      breakdownCategoryCount > 1 ||
      itemCategoryCount > 1;
    const shouldKeepClientLink =
      parsedTransaction.type === 'income' ||
      parsedTransaction.billableToClient === true ||
      Boolean(parsedTransaction.jobReference) ||
      Boolean(parsedTransaction.allocateToClient) ||
      (Array.isArray(parsedTransaction.clientAllocations) && parsedTransaction.clientAllocations.length > 0);
    const parsedClient = parsedTransaction.clientId
      ? clientById.get(parsedTransaction.clientId)
      : null;
    const parsedClientAllocations = parsedTransaction.type === 'income'
      ? []
      : normalizeClientAllocations(
          Array.isArray(parsedTransaction.clientAllocations) && parsedTransaction.clientAllocations.length > 0
            ? parsedTransaction.clientAllocations
            : (parsedTransaction.clientId ? [{ clientId: parsedTransaction.clientId, clientName: parsedTransaction.clientName }] : []),
          clientById
        );
    const primaryParsedAllocation = parsedClientAllocations[0];
    const parsedClientName = shouldKeepClientLink
      ? (parsedTransaction.type === 'income'
          ? (parsedClient ? getClientDisplayName(parsedClient) : String(parsedTransaction.clientName || '').trim())
          : (primaryParsedAllocation?.clientName || ''))
      : '';
    const parsedStaffAssignment = parsedTransaction.type === 'income'
      ? resolveSelectedStaffAssignment(parsedTransaction.staffMemberId, parsedTransaction.staffMemberName)
      : { staffMemberId: '', staffMemberName: '' };

    const mappedTransaction = {
      id: Date.now() + Math.random(), // Generate unique ID
      date: parsedTransaction.date || new Date().toISOString().split('T')[0],
      amount: parsedAmountValue,
      grossAmount: parsedGrossValue > 0 ? parsedGrossValue : parsedAmountValue,
      discountAmount: parsedDiscountValue > 0 ? parsedDiscountValue : 0,
      category: parsedTransaction.category,
      description: parsedTransaction.description,
      paymentMethod: parsedTransaction.paymentMethod,
      paymentStatus: parsedTransaction.paymentStatus || 'paid',
      paymentBreakdown: Array.isArray(parsedTransaction.paymentBreakdown) ? parsedTransaction.paymentBreakdown : [],
      amountPaid: parsedTransaction.amountPaid ?? null,
      outstandingAmount: Number.isFinite(Number(parsedTransaction.outstandingAmount))
        ? parseFloat(Number(parsedTransaction.outstandingAmount).toFixed(2))
        : 0,
      saleTotalAmount: Number.isFinite(Number(parsedTransaction.saleTotalAmount))
        ? parseFloat(Number(parsedTransaction.saleTotalAmount).toFixed(2))
        : undefined,
      expenseTotalAmount: Number.isFinite(Number(parsedTransaction.expenseTotalAmount))
        ? parseFloat(Number(parsedTransaction.expenseTotalAmount).toFixed(2))
        : undefined,
      billTotalAmount: Number.isFinite(Number(parsedTransaction.billTotalAmount))
        ? parseFloat(Number(parsedTransaction.billTotalAmount).toFixed(2))
        : undefined,
      invoiceTotalAmount: Number.isFinite(Number(parsedTransaction.invoiceTotalAmount))
        ? parseFloat(Number(parsedTransaction.invoiceTotalAmount).toFixed(2))
        : undefined,
      invoiceDate: parsedTransaction.invoiceDate || '',
      paymentDate: parsedTransaction.paymentDate || '',
      isPartPaymentSale: parsedTransaction.isPartPaymentSale === true,
      isPartPaymentInstallment: parsedTransaction.isPartPaymentInstallment === true,
      linkedPartPaymentSaleId: parsedTransaction.linkedPartPaymentSaleId || '',
      jobReference: parsedTransaction.jobReference || '',
      allocateToClient: parsedTransaction.allocateToClient === true || parsedClientAllocations.length > 0,
      billableToClient: parsedTransaction.billableToClient === true,
      supplierId: parsedTransaction.supplierId || '',
      supplierName: parsedTransaction.supplierName || '',
      service: '',
      clientId: shouldKeepClientLink
        ? (parsedTransaction.type === 'income'
            ? (parsedClient?.id || parsedTransaction.clientId || '')
            : (primaryParsedAllocation?.clientId || ''))
        : '',
      clientName: parsedClientName,
      clientAllocations: shouldKeepClientLink ? parsedClientAllocations : [],
      staffMemberId: parsedStaffAssignment.staffMemberId,
      staffMemberName: parsedStaffAssignment.staffMemberName,
      vatRate: parsedTransaction.vatRate || null,
      vatAmount: parsedTransaction.vatAmount || null,
      invoiceVatAmount: parsedTransaction.invoiceVatAmount ?? null,
      invoiceVatRate: parsedTransaction.invoiceVatRate ?? null,
      invoiceVatComponents: Array.isArray(parsedTransaction.invoiceVatComponents) ? parsedTransaction.invoiceVatComponents : [],
      netAmount: parsedNetValue > 0 ? parsedNetValue : null,
      vatComponents: Array.isArray(parsedTransaction.vatComponents) ? parsedTransaction.vatComponents : [],
      type: parsedTransaction.type,
      rctRole: parsedTransaction.rctRole || '',
      rctReverseCharge: parsedTransaction.rctReverseCharge === true,
      rctSubcontractorId: parsedTransaction.rctSubcontractorId || '',
      rctSubcontractorName: parsedTransaction.rctSubcontractorName || '',
      rctDeductionRate: parsedTransaction.rctDeductionRate ?? null,
      rctGrossAmount: parsedTransaction.rctGrossAmount ?? null,
      rctDeductionAmount: parsedTransaction.rctDeductionAmount ?? null,
      rctNetPayable: parsedTransaction.rctNetPayable ?? null,
      rctTaxDueAmount: parsedTransaction.rctTaxDueAmount ?? null,
      rctReverseChargeVatRate: parsedTransaction.rctReverseChargeVatRate ?? null,
      rctReverseChargeVatAmount: parsedTransaction.rctReverseChargeVatAmount ?? null,
      rctVatOutputAmount: parsedTransaction.rctVatOutputAmount ?? null,
      rctVatInputAmount: parsedTransaction.rctVatInputAmount ?? null,
      receiptImageUrl: receiptImageUrl || undefined,
      receiptId: receiptId || undefined,
      items: normalizedItems,
      categoryBreakdown: normalizedCategoryBreakdown,
      isSplitTransaction: inferredSplitTransaction,
      createdAt: parsedTransaction.createdAt || nowIso,
      updatedAt: parsedTransaction.updatedAt || nowIso
    };

    const mappedCategoryId = normalizeCategoryId(mappedTransaction.category || '');
    const isExpenseLike = mappedTransaction.type === 'expenses' || mappedTransaction.type === 'expense';
    const isFoodOrDrinkExpense = isExpenseLike && ['meals', 'food', 'food_and_drink'].includes(mappedCategoryId);
    if (isFoodOrDrinkExpense) {
      mappedTransaction.category = 'meals';
      mappedTransaction.items = mappedTransaction.items.map((item) => ({
        ...item,
        category: item.category || 'meals',
        vatReclaimable: false
      }));
    }

    // Handle VAT calculation
    if (!isFoodOrDrinkExpense && !mappedTransaction.vatComponents.length && parsedTransaction.vatAmount && parsedTransaction.vatRate) {
      // Use VAT information from voice input (for expenses)
      const description = getTaxComponentDescription(parsedTransaction.vatRate);

      mappedTransaction.vatComponents = [{
        rate: parsedTransaction.vatRate,
        amount: parseFloat(parsedTransaction.vatAmount),
        description: description
      }];
    } else if (!mappedTransaction.vatComponents.length && parsedTransaction.type === 'income' && isVatRegisteredForTransactionDate(mappedTransaction.date || parsedTransaction.date)) {
      // Automatic VAT calculation for income transactions
      const taxJurisdiction = resolveIncomeTaxJurisdiction();
      const vatRate = getIncomeTaxRateForCategory(parsedTransaction.category, taxJurisdiction, mappedTransaction.date || parsedTransaction.date);

      if (vatRate > 0) {
        // Calculate VAT amount from gross amount
        const grossAmount = parseFloat(parsedTransaction.amount) || 0;
        const vatAmount = grossAmount - (grossAmount / (1 + vatRate));

        mappedTransaction.vatRate = vatRate;
        mappedTransaction.vatAmount = parseFloat(vatAmount.toFixed(2));

        mappedTransaction.vatComponents = [{
          rate: vatRate,
          amount: parseFloat(vatAmount.toFixed(2)),
          description: getTaxComponentDescription(vatRate)
        }];
      }
    }

    if (!isVatRegisteredForTransactionDate(mappedTransaction.date || parsedTransaction.date)) {
      Object.assign(mappedTransaction, clearVatBeforeRegistrationDate(mappedTransaction));
    }

    if (isExpenseLike) {
      Object.assign(mappedTransaction, materializeExpenseVatSelection(mappedTransaction));
    }

    const importedExpenseVatGuard = await applyIrishExpenseVatGuard(mappedTransaction, { mode: 'import' });
    if (!importedExpenseVatGuard.shouldContinue) {
      return false;
    }
    const blockedVatWasAutoRemoved = importedExpenseVatGuard.wasVatCleared;
    Object.assign(mappedTransaction, importedExpenseVatGuard.transaction);

    const duplicateResolution = resolveDuplicateTransactionChoice(
      mappedTransaction,
      transactions,
      { skipPrompt: sourceLabel === 'receipt' }
    );
    if (!duplicateResolution.shouldSave) {
      if (receiptTransactionPendingKey) {
        pendingReceiptTransactionIdsRef.current.delete(receiptTransactionPendingKey);
      }
      return false;
    }

    // Add the transaction directly to the list. Use a functional update so
    // rapid batch receipt saves append to the latest transaction state.
    setTransactions((prevTransactions) => {
      const latestTransactions = Array.isArray(prevTransactions) ? prevTransactions : [];
      const latestDuplicateResolution = resolveDuplicateTransactionChoice(
        mappedTransaction,
        latestTransactions,
        { skipPrompt: sourceLabel === 'receipt' }
      );

      if (!latestDuplicateResolution.shouldSave) {
        return latestTransactions;
      }

      if (latestDuplicateResolution.removedCount > 0) {
        const duplicateIds = new Set(
          findPotentialDuplicateTransactions(mappedTransaction, latestTransactions)
            .map((transaction) => transaction.id)
        );
        return sortTransactionsByEntryDateDesc([
          ...latestTransactions.filter((transaction) => !duplicateIds.has(transaction.id)),
          mappedTransaction
        ]);
      }

      return sortTransactionsByEntryDateDesc([...latestTransactions, mappedTransaction]);
    });
    pushCostItemsFromTransaction(mappedTransaction, { sourceLabel });

    // Show success notification
    const transactionType = parsedTransaction.type === 'income' ? 'Sale' : 'Expense';
    const parsedAmount = parseFloat(parsedTransaction.amount);
    const displayAmount = Number.isFinite(parsedAmount) ? parsedAmount.toFixed(2) : '0.00';
    if (duplicateResolution.removedCount > 0) {
      setSuccessMessage(`Removed ${duplicateResolution.removedCount} duplicate transaction(s). ${transactionType} added: €${displayAmount} - ${parsedTransaction.description || 'No description'}${blockedVatWasAutoRemoved ? ' VAT was set to €0 because it is not reclaimable for this Irish expense category.' : ''}`);
    } else {
      setSuccessMessage(`${transactionType} transaction added: €${displayAmount} - ${parsedTransaction.description || 'No description'}${blockedVatWasAutoRemoved ? ' VAT was set to €0 because it is not reclaimable for this Irish expense category.' : ''}`);
    }
    setShowSuccess(true);

    // Hide success message after 4 seconds
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 4000);

    console.log('Transaction added automatically:', mappedTransaction);
    return true;
  };

  const handleReceiptTransactionAdd = async (receiptData = {}) => {
    const extracted = receiptData.extractedData || {};
    const receiptUploadedAt = new Date().toISOString();
    receiptUploadOrderRef.current = Math.max(receiptUploadOrderRef.current + 1, Date.now());
    const receiptUploadOrder = receiptUploadOrderRef.current;
    const amountValue = extracted.amount ?? receiptData.amount;
    const extractedItems = Array.isArray(extracted.items) ? extracted.items : [];
    const receiptCategoryBreakdown = Array.isArray(receiptData.categoryBreakdown)
      ? receiptData.categoryBreakdown
      : [];
    const extractedCategoryBreakdown = Array.isArray(extracted.categoryBreakdown)
      ? extracted.categoryBreakdown
      : [];
    const mergedCategoryBreakdown =
      extractedCategoryBreakdown.length > 0
        ? extractedCategoryBreakdown
        : receiptCategoryBreakdown;

    const parsedTransaction = {
      type: receiptData.type || extracted.type || 'expenses',
      date: extracted.date || receiptData.date || new Date().toISOString().split('T')[0],
      amount: amountValue,
      category: extracted.category || receiptData.category,
      description: extracted.vendor || receiptData.vendor || extracted.description || receiptData.description || 'Receipt',
      paymentMethod: extracted.paymentMethod || receiptData.paymentMethod || 'card',
      clientId: extracted.clientId || receiptData.clientId || '',
      clientName: extracted.clientName || receiptData.clientName || '',
      clientAllocations: Array.isArray(extracted.clientAllocations)
        ? extracted.clientAllocations
        : (Array.isArray(receiptData.clientAllocations) ? receiptData.clientAllocations : []),
      vatRate: extracted.vatRate ?? receiptData.vatRate ?? null,
      vatAmount: extracted.vatAmount ?? receiptData.vatAmount ?? null,
      vatComponents: extracted.vatComponents ?? receiptData.vatComponents ?? [],
      items: extractedItems.length > 0 ? extractedItems : (receiptData.items || []),
      categoryBreakdown: mergedCategoryBreakdown,
      isSplitTransaction:
        receiptData.isSplitTransaction ??
        extracted.isSplitTransaction ??
        (new Set(
          (mergedCategoryBreakdown || [])
            .map((entry) => entry?.category)
            .filter(Boolean)
        ).size > 1),
      receiptImageUrl: extracted.receiptImageUrl || receiptData.receiptImageUrl || receiptData.imageUrl || null,
      receiptId: receiptData.id || receiptData.receiptId || null,
      receiptUploadedAt,
      receiptUploadOrder,
      createdAt: receiptData.createdAt || extracted.createdAt || receiptUploadedAt,
      updatedAt: receiptData.updatedAt || extracted.updatedAt || receiptUploadedAt
    };

    return handleVoiceTransactionParsed(parsedTransaction, { sourceLabel: 'receipt' });
  };

  const buildEditingSplitItems = (transaction) => {
    if (!transaction) return [createSplitSaleItem()];

    const fromItems = Array.isArray(transaction.items)
      ? transaction.items
          .map((item) => {
            const description = item?.description || '';
            let amount = parseFloat(item?.amount ?? item?.unitPrice ?? 0);
            if (Number.isFinite(amount) && amount > 0 && /discount|saving|dec\/?/i.test(description)) {
              amount = -amount;
            }
            return {
              id: item?.id || createSplitSaleItem().id,
              description,
              category: item?.category || '',
              amount: Number.isFinite(amount) && Math.abs(amount) > 0 ? sanitizeSignedAmountInput(amount.toString()) : '',
              saveToCost: item?.saveToCost === true
            };
          })
          .filter((item) => item.category)
      : [];

    const fromBreakdown = Array.isArray(transaction.categoryBreakdown)
      ? transaction.categoryBreakdown
          .map((entry) => {
            const amount = parseFloat(entry?.amount);
            return {
              id: createSplitSaleItem().id,
              description: '',
              category: entry?.category || '',
              amount: Number.isFinite(amount) && Math.abs(amount) > 0 ? sanitizeSignedAmountInput(amount.toString()) : '',
              saveToCost: false
            };
          })
          .filter((item) => item.category)
      : [];

    if (fromBreakdown.length > fromItems.length) {
      return fromBreakdown;
    }

    if (fromItems.length > 0) {
      return fromItems;
    }

    if (fromBreakdown.length > 0) {
      return fromBreakdown;
    }

    return [
      createSplitSaleItem({
        description: transaction.description || (transaction.type === 'income' ? 'Sale item' : 'Expense item'),
        category: transaction.category || '',
        amount: sanitizeSignedAmountInput((parseFloat(transaction.amount) || 0).toString()),
        saveToCost: false
      })
    ];
  };

  const buildEditingPaymentBreakdown = (transaction) => {
    if (!transaction) return [createSplitPaymentItem()];

    const fromBreakdown = Array.isArray(transaction.paymentBreakdown)
      ? transaction.paymentBreakdown
          .map((entry) => {
            const amount = parseFloat(entry?.amount);
            return {
              id: createSplitPaymentItem().id,
              method: normalizePaymentMethodId(entry?.method || ''),
              amount: Number.isFinite(amount) && amount > 0 ? sanitizeAmountInput(amount.toString()) : ''
            };
          })
          .filter((entry) => entry.method)
      : [];

    if (fromBreakdown.length > 0) {
      return fromBreakdown;
    }

    const fallbackAmount = parseFloat(transaction.amount);
    const fallbackMethod = normalizePaymentMethodId(transaction.paymentMethod || 'cash') || 'cash';
    return [
      createSplitPaymentItem({
        method: fallbackMethod,
        amount: Number.isFinite(fallbackAmount) && fallbackAmount > 0
          ? sanitizeAmountInput(fallbackAmount.toString())
          : ''
      })
    ];
  };

  // Transaction management functions
  const handleEditTransaction = (transaction) => {
    const splitIncome = isSplitIncomeTransaction(transaction);
    const splitPayment =
      transaction.isSplitPayment ||
      (Array.isArray(transaction.paymentBreakdown) && transaction.paymentBreakdown.length > 1);
    const transactionGrossAmount = Number(transaction.grossAmount);
    const transactionDiscountAmount = Number(transaction.discountAmount);
    const transactionVatAmount = Number(getTransactionVatAmount(transaction) || transaction.vatAmount);
    const transactionVatRate = Number(transaction.vatRate);
    const resolvedGrossAmount = Number.isFinite(transactionGrossAmount) && transactionGrossAmount > 0
      ? transactionGrossAmount
      : Number(transaction.amount) || 0;
    const normalizedTransactionStaffName = normalizeStaffMemberName(transaction.staffMemberName);
    const matchedStaffMember = staffMemberOptions.find((staffMember) => (
          staffMember.id === normalizeStaffMemberName(transaction.staffMemberId) ||
          (normalizedTransactionStaffName && staffMember.name.toLowerCase() === normalizedTransactionStaffName.toLowerCase())
        ));

    setEditingTransactionId(transaction.id);
    setEditingTransaction({
      date: formatDateForInput(transaction.date) || '',
      amount: sanitizeAmountInput(resolvedGrossAmount.toString()),
      discountAmount: Number.isFinite(transactionDiscountAmount) && transactionDiscountAmount > 0
        ? sanitizeAmountInput(transactionDiscountAmount.toString())
        : '',
      vatAmount: Number.isFinite(transactionVatAmount)
        ? sanitizeSignedAmountInput(transactionVatAmount.toString())
        : '',
      vatRate: Number.isFinite(transactionVatRate)
        ? sanitizeAmountInput((transactionVatRate <= 1 ? transactionVatRate * 100 : transactionVatRate).toString())
        : '',
      category: transaction.category,
      description: transaction.description,
      paymentMethod: transaction.paymentMethod || 'cash',
      paymentBreakdown: splitPayment ? buildEditingPaymentBreakdown(transaction) : [],
      service: transaction.service || '',
      clientId: transaction.clientId || '',
      clientName: transaction.clientName || '',
      clientAllocations: normalizeClientAllocations(
        Array.isArray(transaction.clientAllocations) && transaction.clientAllocations.length > 0
          ? transaction.clientAllocations
          : (transaction.clientId ? [{ clientId: transaction.clientId, clientName: transaction.clientName }] : []),
        clientById
      ),
      staffMemberId: matchedStaffMember?.id || transaction.staffMemberId || '',
      staffMemberName: matchedStaffMember?.name || transaction.staffMemberName || '',
      type: transaction.type,
      isSplitTransaction: splitIncome,
      isSplitPayment: !!splitPayment,
      splitItems: splitIncome ? buildEditingSplitItems(transaction) : [],
      receiptImageUrl: transaction.receiptImageUrl || transaction.receiptUrl || transaction.imageUrl || '',
      receiptUrl: transaction.receiptUrl || transaction.receiptImageUrl || transaction.imageUrl || '',
      receiptFileName: transaction.receiptFileName || transaction.receiptFilename || transaction.receiptName || '',
      receiptFileType: transaction.receiptFileType || '',
      receiptStoragePath: transaction.receiptStoragePath || transaction.storagePath || '',
      receiptAttachedAt: transaction.receiptAttachedAt || '',
      receiptAttachmentFile: null,
      receiptAttachmentPreviewUrl: '',
      removeReceiptAttachment: false
    });
  };

  const handleEditTransactionFromClient = useCallback((transaction) => {
    if (!transaction) return;
    setShowAllTransactions(true);
    openSection('recentTransactions');
    handleEditTransaction(transaction);
  }, [openSection]);

  const getEditingVatBaseAmount = (draft) => {
    if (draft?.isSplitTransaction && Array.isArray(draft.splitItems)) {
      const splitTotal = draft.splitItems.reduce((sum, item) => {
        const amount = parseFloat(sanitizeSignedAmountInput(item?.amount || ''));
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);
      return Math.max(0, splitTotal);
    }

    const grossAmount = parseFloat(sanitizeAmountInput(draft?.amount || ''));
    const discountAmount = parseFloat(sanitizeAmountInput(draft?.discountAmount || ''));
    return Math.max(
      0,
      (Number.isFinite(grossAmount) ? grossAmount : 0) -
      (Number.isFinite(discountAmount) ? discountAmount : 0)
    );
  };

  const handleEditingFieldChange = (field, value) => {
    setEditingTransaction((prev) => {
      let nextValue = value;
      if (field === 'amount' || field === 'discountAmount') {
        nextValue = sanitizeAmountInput(value);
      } else if (field === 'vatAmount') {
        nextValue = sanitizeSignedAmountInput(value);
      } else if (field === 'vatRate') {
        nextValue = sanitizeAmountInput(value);
      }

      const next = {
        ...prev,
        [field]: nextValue
      };

      const vatBaseAmount = getEditingVatBaseAmount(next);
      if (vatBaseAmount <= 0) {
        return next;
      }

      if (field === 'vatRate') {
        const rateValue = parseFloat(sanitizeAmountInput(nextValue || ''));
        if (Number.isFinite(rateValue) && rateValue >= 0) {
          const normalizedRate = rateValue > 1 ? rateValue / 100 : rateValue;
          const vatAmount = calculateTaxAmountFromGross(vatBaseAmount, normalizedRate);
          next.vatAmount = sanitizeAmountInput(vatAmount.toFixed(2));
        }
      } else if (field === 'vatAmount') {
        const vatAmount = parseFloat(sanitizeSignedAmountInput(nextValue || ''));
        const netAmount = vatBaseAmount - vatAmount;
        if (Number.isFinite(vatAmount) && vatAmount >= 0 && netAmount > 0) {
          next.vatRate = sanitizeAmountInput(((vatAmount / netAmount) * 100).toFixed(2));
        }
      } else if ((field === 'amount' || field === 'discountAmount') && next.vatRate !== '' && next.vatRate !== null && next.vatRate !== undefined) {
        const rateValue = parseFloat(sanitizeAmountInput(next.vatRate || ''));
        if (Number.isFinite(rateValue) && rateValue >= 0) {
          const normalizedRate = rateValue > 1 ? rateValue / 100 : rateValue;
          const vatAmount = calculateTaxAmountFromGross(vatBaseAmount, normalizedRate);
          next.vatAmount = sanitizeAmountInput(vatAmount.toFixed(2));
        }
      }

      return next;
    });
  };

  const handleEditingReceiptAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!isAllowedReceiptAttachmentFile(file)) {
      alert('Please choose a receipt PDF or image file.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_RECEIPT_ATTACHMENT_SIZE_BYTES) {
      alert('Receipt files must be 10 MB or smaller.');
      event.target.value = '';
      return;
    }

    const previewUrl = file.type?.startsWith('image/') ? URL.createObjectURL(file) : '';
    setEditingTransaction((prev) => ({
      ...prev,
      receiptAttachmentFile: file,
      receiptAttachmentPreviewUrl: previewUrl,
      receiptFileName: file.name,
      receiptFileType: file.type || '',
      removeReceiptAttachment: false
    }));
  };

  const handleClearPendingReceiptAttachment = () => {
    setEditingTransaction((prev) => ({
      ...prev,
      receiptAttachmentFile: null,
      receiptAttachmentPreviewUrl: '',
      receiptFileName: prev.receiptImageUrl || prev.receiptUrl ? prev.receiptFileName : '',
      receiptFileType: prev.receiptImageUrl || prev.receiptUrl ? prev.receiptFileType : ''
    }));
  };

  const handleRemoveEditingReceiptAttachment = () => {
    setEditingTransaction((prev) => {
      const hasSavedAttachment = Boolean(prev.receiptImageUrl || prev.receiptUrl || prev.receiptStoragePath);
      return {
        ...prev,
        ...buildClearedReceiptAttachmentPatch(),
        receiptAttachmentFile: null,
        receiptAttachmentPreviewUrl: '',
        removeReceiptAttachment: hasSavedAttachment
      };
    });
  };

  const handleEditingSplitItemChange = (itemId, field, value) => {
    setEditingTransaction((prev) => {
      const currentItems = Array.isArray(prev.splitItems) ? prev.splitItems : [];
      return {
        ...prev,
        splitItems: currentItems.map((item) => {
          if (item.id !== itemId) return item;
          if (field === 'amount') {
            return { ...item, amount: sanitizeSignedAmountInput(value) };
          }
          return { ...item, [field]: value };
        })
      };
    });
  };

  const handleAddEditingSplitItem = () => {
    setEditingTransaction((prev) => ({
      ...prev,
      splitItems: [...(Array.isArray(prev.splitItems) ? prev.splitItems : []), createSplitSaleItem()]
    }));
  };

  const handleRemoveEditingSplitItem = (itemId) => {
    setEditingTransaction((prev) => {
      const currentItems = Array.isArray(prev.splitItems) ? prev.splitItems : [];
      if (currentItems.length <= 1) {
        return {
          ...prev,
          splitItems: [createSplitSaleItem()]
        };
      }
      return {
        ...prev,
        splitItems: currentItems.filter((item) => item.id !== itemId)
      };
    });
  };

  const handleEditingPaymentSplitChange = (itemId, field, value) => {
    setEditingTransaction((prev) => {
      const currentItems = Array.isArray(prev.paymentBreakdown) ? prev.paymentBreakdown : [];
      return {
        ...prev,
        paymentBreakdown: currentItems.map((item) => {
          if (item.id !== itemId) return item;
          if (field === 'amount') {
            return { ...item, amount: sanitizeAmountInput(value) };
          }
          if (field === 'method') {
            return { ...item, method: normalizePaymentMethodId(value) };
          }
          return { ...item, [field]: value };
        })
      };
    });
  };

  const handleAddEditingPaymentSplitItem = () => {
    setEditingTransaction((prev) => ({
      ...prev,
      paymentBreakdown: [...(Array.isArray(prev.paymentBreakdown) ? prev.paymentBreakdown : []), createSplitPaymentItem()]
    }));
  };

  const handleRemoveEditingPaymentSplitItem = (itemId) => {
    setEditingTransaction((prev) => {
      const currentItems = Array.isArray(prev.paymentBreakdown) ? prev.paymentBreakdown : [];
      if (currentItems.length <= 1) {
        return {
          ...prev,
          paymentBreakdown: [createSplitPaymentItem()]
        };
      }
      return {
        ...prev,
        paymentBreakdown: currentItems.filter((item) => item.id !== itemId)
      };
    });
  };

  const handleSaveTransaction = async () => {
    try {
      if (!ensureDrivePermissionBeforeTransactionChange('editing transactions')) {
        return;
      }

      const originalTransaction = transactions.find((transaction) => transaction.id === editingTransactionId);
      const isEditingSplitTransaction =
        editingTransaction.isSplitTransaction || isSplitIncomeTransaction(originalTransaction);
      const isEditingSplitPayment = !!editingTransaction.isSplitPayment;
      const normalizedEditingSplitItems = isEditingSplitTransaction
        ? (Array.isArray(editingTransaction.splitItems) ? editingTransaction.splitItems : [])
            .map((item) => {
              const amount = parseFloat(sanitizeSignedAmountInput(item?.amount || ''));
              return {
                description: (item?.description || '').trim(),
                category: item?.category || '',
                amount: Number.isFinite(amount) ? amount : 0,
                saveToCost: item?.saveToCost === true
              };
            })
            .filter((item) => item.category && Math.abs(item.amount) > 0)
        : [];
      const normalizedEditingPaymentItems = isEditingSplitPayment
        ? (Array.isArray(editingTransaction.paymentBreakdown) ? editingTransaction.paymentBreakdown : [])
            .map((entry) => {
              const amount = parseFloat(sanitizeAmountInput(entry?.amount || ''));
              return {
                method: normalizePaymentMethodId(entry?.method || ''),
                amount: Number.isFinite(amount) ? amount : 0
              };
            })
            .filter((entry) => entry.method && entry.amount > 0)
        : [];

      if (isEditingSplitTransaction && normalizedEditingSplitItems.length === 0) {
        const splitType = editingTransaction.type === 'income' ? 'sale' : 'expense';
        alert(`Add at least one split ${splitType} item with a category and amount before saving.`);
        return;
      }

      const editingBaseAmountForValidation = isEditingSplitTransaction
        ? normalizedEditingSplitItems.reduce((sum, item) => sum + item.amount, 0)
        : (() => {
            const parsedAmount = parseFloat(sanitizeAmountInput(editingTransaction.amount || ''));
            if (Number.isFinite(parsedAmount)) return parsedAmount;
            const originalGrossAmount = parseFloat(originalTransaction?.grossAmount);
            if (Number.isFinite(originalGrossAmount)) return originalGrossAmount;
            const originalAmount = parseFloat(originalTransaction?.amount);
            return Number.isFinite(originalAmount) ? originalAmount : 0;
          })();

      const editingDiscountAmount = isEditingSplitTransaction
        ? 0
        : (() => {
            const parsedDiscountAmount = parseFloat(sanitizeAmountInput(editingTransaction.discountAmount || ''));
            return Number.isFinite(parsedDiscountAmount) ? parsedDiscountAmount : 0;
          })();

      if (!isEditingSplitTransaction && editingDiscountAmount > editingBaseAmountForValidation) {
        alert(`Discount (${formatCurrency(editingDiscountAmount)}) cannot be greater than amount (${formatCurrency(editingBaseAmountForValidation)}).`);
        return;
      }

      const editingAmountForValidation = parseFloat(
        Math.max(editingBaseAmountForValidation - editingDiscountAmount, 0).toFixed(2)
      );

      if (editingAmountForValidation <= 0) {
        alert('Final amount after discount must be greater than 0.');
        return;
      }

      if (isEditingSplitPayment) {
        if (normalizedEditingPaymentItems.length === 0) {
          alert('Add at least one split payment line with a payment method and amount before saving.');
          return;
        }

        const splitPaymentTotalAmount = normalizedEditingPaymentItems.reduce((sum, entry) => sum + entry.amount, 0);
        if (splitPaymentTotalAmount - editingAmountForValidation > 0.01) {
          alert(`Split payment total (${formatCurrency(splitPaymentTotalAmount)}) cannot be more than transaction amount (${formatCurrency(editingAmountForValidation)}).`);
          return;
        }

        const editingOutstandingAmount = parseFloat(Math.max(editingAmountForValidation - splitPaymentTotalAmount, 0).toFixed(2));
        if (editingOutstandingAmount > 0.01 && editingTransaction.type !== 'income') {
          alert('Part payments can only be recorded for income sales.');
          return;
        }

        if (editingOutstandingAmount > 0.01 && !editingTransaction.clientId) {
          alert('Select a client before saving a part payment, so the outstanding balance can be tracked.');
          return;
        }
      }

      const hasVatAmountInput = editingTransaction.vatAmount !== '' && editingTransaction.vatAmount !== undefined && editingTransaction.vatAmount !== null;
      const hasVatRateInput = editingTransaction.vatRate !== '' && editingTransaction.vatRate !== undefined && editingTransaction.vatRate !== null;
      const parsedEditingVatAmount = hasVatAmountInput
        ? parseFloat(sanitizeSignedAmountInput(editingTransaction.vatAmount))
        : null;
      const parsedEditingVatRateRaw = hasVatRateInput
        ? parseFloat(sanitizeAmountInput(editingTransaction.vatRate))
        : null;
      const parsedEditingVatRate = Number.isFinite(parsedEditingVatRateRaw)
        ? (parsedEditingVatRateRaw > 1 ? parsedEditingVatRateRaw / 100 : parsedEditingVatRateRaw)
        : null;
      const applyEditedVatData = (candidateTransaction) => {
        const next = { ...candidateTransaction };

        if (
          next.type === 'income' &&
          !isVatRegisteredForTransactionDate(next.date || next.invoiceDate)
        ) {
          return clearVatBeforeRegistrationDate(next);
        }

        if (!hasVatAmountInput && !hasVatRateInput) {
          return materializeExpenseVatSelection({
            ...next,
            vatAmount: null,
            vatRate: null,
            vatComponents: [],
            invoiceVatAmount: null,
            invoiceVatRate: null,
            invoiceVatComponents: []
          });
        }

        next.vatAmount = Number.isFinite(parsedEditingVatAmount)
          ? parseFloat(parsedEditingVatAmount.toFixed(2))
          : null;
        next.vatRate = Number.isFinite(parsedEditingVatRate)
          ? parsedEditingVatRate
          : null;

        if (next.vatAmount !== null && next.vatRate !== null) {
          next.vatComponents = [{
            rate: next.vatRate,
            amount: next.vatAmount,
            description: `${(next.vatRate * 100).toFixed(1)}% ${countryTerminology.taxName}`
          }];
        } else if (next.vatAmount === null) {
          next.vatComponents = [];
        }

        return materializeManualEditedVatSelection(next);
      };
      const selectedEditingClient = editingTransaction.clientId
        ? clientById.get(editingTransaction.clientId)
        : null;
      const resolvedEditingClientName = editingTransaction.type === 'income'
        ? (selectedEditingClient ? getClientDisplayName(selectedEditingClient) : String(editingTransaction.clientName || '').trim())
        : '';
      const editingClientAllocations = editingTransaction.type === 'income'
        ? []
        : normalizeClientAllocations(editingTransaction.clientAllocations, clientById);
      const primaryEditingAllocation = editingClientAllocations[0];
      const resolvedEditingStaffAssignment = resolveSelectedStaffAssignment(
        editingTransaction.staffMemberId,
        editingTransaction.staffMemberName
      );
      let receiptAttachmentPatch = null;
      if (editingTransaction.receiptAttachmentFile) {
        try {
          setIsUploadingReceiptAttachment(true);
          receiptAttachmentPatch = await uploadTransactionReceiptAttachment(
            editingTransaction.receiptAttachmentFile,
            originalTransaction || { id: editingTransactionId }
          );
        } catch (attachmentError) {
          console.error('Error attaching receipt file:', attachmentError);
          alert(attachmentError?.message || 'Failed to attach the receipt file. Please try again.');
          return;
        } finally {
          setIsUploadingReceiptAttachment(false);
        }
      } else if (editingTransaction.removeReceiptAttachment) {
        await deleteTransactionReceiptAttachmentFile(originalTransaction);
        receiptAttachmentPatch = {
          ...buildClearedReceiptAttachmentPatch(),
          updatedAt: new Date().toISOString()
        };
      }

      let updatedTransactions = transactions.map(t =>
        t.id === editingTransactionId
          ? (() => {
              const dominantSplitPaymentMethod = isEditingSplitPayment
                ? [...normalizedEditingPaymentItems].sort((a, b) => b.amount - a.amount)[0]?.method
                : null;
              const baseTransaction = {
                ...t,
                date: editingTransaction.date,
                description: editingTransaction.description,
                paymentMethod: normalizePaymentMethodId(editingTransaction.paymentMethod || t.paymentMethod || 'cash') || 'cash',
                service: editingTransaction.service,
                clientId: editingTransaction.type === 'income'
                  ? (selectedEditingClient?.id || '')
                  : (primaryEditingAllocation?.clientId || ''),
                clientName: editingTransaction.type === 'income'
                  ? resolvedEditingClientName
                  : (primaryEditingAllocation?.clientName || ''),
                clientAllocations: editingClientAllocations,
                allocateToClient: editingTransaction.type === 'income' ? false : editingClientAllocations.length > 0,
                staffMemberId: resolvedEditingStaffAssignment.staffMemberId,
                staffMemberName: resolvedEditingStaffAssignment.staffMemberName,
                updatedAt: new Date().toISOString(),
                ...(receiptAttachmentPatch || {})
              };

              if (isEditingSplitTransaction) {
                const splitTotal = normalizedEditingSplitItems.reduce((sum, item) => sum + item.amount, 0);
                const splitCategoryBreakdown = Object.values(
                  normalizedEditingSplitItems.reduce((acc, item) => {
                    if (!acc[item.category]) {
                      acc[item.category] = { category: item.category, amount: 0 };
                    }
                    acc[item.category].amount += item.amount;
                    return acc;
                  }, {})
                ).map((entry) => ({
                  ...entry,
                  amount: parseFloat(entry.amount.toFixed(2))
                }));

                const dominantCategory = [...splitCategoryBreakdown]
                  .sort((a, b) => b.amount - a.amount)[0]?.category || baseTransaction.category || '';

                let splitItems = normalizedEditingSplitItems.map((item) => ({
                  description: item.description || (baseTransaction.type === 'income' ? 'Sale item' : 'Expense item'),
                  category: item.category,
                  quantity: 1,
                  unitPrice: parseFloat(item.amount.toFixed(2)),
                  amount: parseFloat(item.amount.toFixed(2)),
                  saveToCost: item.saveToCost === true
                }));

                const nextTransaction = {
                  ...baseTransaction,
                  amount: parseFloat(splitTotal.toFixed(2)),
                  grossAmount: parseFloat(splitTotal.toFixed(2)),
                  discountAmount: 0,
                  category: dominantCategory,
                  items: splitItems,
                  categoryBreakdown: splitCategoryBreakdown,
                  isSplitTransaction: true,
                  service: ''
                };

                if (isVatRegisteredForTransactionDate(nextTransaction.date) && nextTransaction.type === 'income' && !hasVatAmountInput && !hasVatRateInput) {
                  const taxJurisdiction = resolveIncomeTaxJurisdiction();
                  const splitVatSummary = buildSplitIncomeVatSummary(normalizedEditingSplitItems, taxJurisdiction, nextTransaction.date);

                  nextTransaction.vatRate = splitVatSummary.effectiveVatRate;
                  nextTransaction.vatAmount = splitVatSummary.totalVatAmount;
                  nextTransaction.vatComponents = splitVatSummary.vatComponents;
                  nextTransaction.items = splitItems.map((item, index) => {
                    const lineVat = splitVatSummary.lineVatBreakdown[index];
                    if (!lineVat) return item;
                    return {
                      ...item,
                      vatRate: lineVat.rate,
                      vatAmount: lineVat.amount
                    };
                  });
                }

                if (isEditingSplitPayment) {
                  const paidAmount = normalizedEditingPaymentItems.reduce((sum, entry) => sum + entry.amount, 0);
                  const outstandingAmount = parseFloat(Math.max(nextTransaction.amount - paidAmount, 0).toFixed(2));
                  nextTransaction.paymentMethod = dominantSplitPaymentMethod || baseTransaction.paymentMethod || 'cash';
                  nextTransaction.paymentBreakdown = normalizedEditingPaymentItems.map((entry) => ({
                    method: entry.method,
                    amount: parseFloat(entry.amount.toFixed(2))
                  }));
                  nextTransaction.amountPaid = parseFloat(paidAmount.toFixed(2));
                  nextTransaction.outstandingAmount = outstandingAmount;
                  nextTransaction.paymentStatus = outstandingAmount > 0.01 ? 'part_paid' : 'paid';
                  nextTransaction.isSplitPayment = nextTransaction.paymentBreakdown.length > 1 || outstandingAmount > 0.01;
                } else {
                  nextTransaction.paymentMethod = baseTransaction.paymentMethod || 'cash';
                  nextTransaction.paymentBreakdown = [];
                  nextTransaction.amountPaid = parseFloat((Number(nextTransaction.amount) || 0).toFixed(2));
                  nextTransaction.outstandingAmount = 0;
                  nextTransaction.paymentStatus = 'paid';
                  nextTransaction.isSplitPayment = false;
                }

                return applyEditedVatData(nextTransaction);
              }

              const parsedAmount = parseFloat(sanitizeAmountInput(editingTransaction.amount || ''));
              const parsedDiscount = parseFloat(sanitizeAmountInput(editingTransaction.discountAmount || ''));
              const resolvedGrossAmount = Number.isFinite(parsedAmount)
                ? parsedAmount
                : Number(parseFloat(t.grossAmount)) || Number(parseFloat(t.amount)) || 0;
              const resolvedDiscountAmount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
              const resolvedNetAmount = parseFloat(
                Math.max(resolvedGrossAmount - resolvedDiscountAmount, 0).toFixed(2)
              );

              const nextTransaction = {
                ...baseTransaction,
                amount: resolvedNetAmount,
                grossAmount: parseFloat(resolvedGrossAmount.toFixed(2)),
                discountAmount: parseFloat(resolvedDiscountAmount.toFixed(2)),
                category: editingTransaction.category,
                ...(baseTransaction.type === 'income'
                  ? {
                      items: [],
                      categoryBreakdown: [],
                      isSplitTransaction: false
                    }
                  : {
                      items: [],
                      splitItems: [],
                      categoryBreakdown: [],
                      isSplitTransaction: false
                    })
              };

              if (isVatRegisteredForTransactionDate(nextTransaction.date) && nextTransaction.type === 'income' && !hasVatAmountInput && !hasVatRateInput) {
                const taxJurisdiction = resolveIncomeTaxJurisdiction();
                const vatRate = getIncomeTaxRateForCategory(nextTransaction.category, taxJurisdiction, nextTransaction.date);
                const vatAmount = calculateTaxAmountFromGross(nextTransaction.amount, vatRate);
                nextTransaction.vatRate = vatRate;
                nextTransaction.vatAmount = parseFloat(vatAmount.toFixed(2));
                nextTransaction.vatComponents = [{
                  rate: vatRate,
                  amount: parseFloat(vatAmount.toFixed(2)),
                  description: getTaxComponentDescription(vatRate)
                }];
              }

              if (isEditingSplitPayment) {
                const paidAmount = normalizedEditingPaymentItems.reduce((sum, entry) => sum + entry.amount, 0);
                const outstandingAmount = parseFloat(Math.max(nextTransaction.amount - paidAmount, 0).toFixed(2));
                nextTransaction.paymentMethod = dominantSplitPaymentMethod || baseTransaction.paymentMethod || 'cash';
                nextTransaction.paymentBreakdown = normalizedEditingPaymentItems.map((entry) => ({
                  method: entry.method,
                  amount: parseFloat(entry.amount.toFixed(2))
                }));
                nextTransaction.amountPaid = parseFloat(paidAmount.toFixed(2));
                nextTransaction.outstandingAmount = outstandingAmount;
                nextTransaction.paymentStatus = outstandingAmount > 0.01 ? 'part_paid' : 'paid';
                nextTransaction.isSplitPayment = nextTransaction.paymentBreakdown.length > 1 || outstandingAmount > 0.01;
              } else {
                nextTransaction.paymentMethod = baseTransaction.paymentMethod || 'cash';
                nextTransaction.paymentBreakdown = [];
                nextTransaction.amountPaid = parseFloat((Number(nextTransaction.amount) || 0).toFixed(2));
                nextTransaction.outstandingAmount = 0;
                nextTransaction.paymentStatus = 'paid';
                nextTransaction.isSplitPayment = false;
              }

              return applyEditedVatData(nextTransaction);
            })()
          : t
      );

      const editedTransactionToSave = updatedTransactions.find(
        (transaction) => transaction.id === editingTransactionId
      );
      const editedExpenseVatGuard = await applyIrishExpenseVatGuard(editedTransactionToSave, { mode: 'manual' });
      if (!editedExpenseVatGuard.shouldContinue) {
        return;
      }
      updatedTransactions = updatedTransactions.map((transaction) => (
        transaction.id === editingTransactionId
          ? editedExpenseVatGuard.transaction
          : transaction
      ));

      setTransactions(updatedTransactions);
      const savedTransaction = updatedTransactions.find((transaction) => transaction.id === editingTransactionId);
      pushCostItemsFromTransaction(savedTransaction, { sourceLabel: 'recent transaction edit' });
      await syncLinkedReceiptFromTransaction(savedTransaction);
      await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);

      setEditingTransactionId(null);
      setEditingTransaction({});

      setSuccessMessage('? Transaction updated successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingTransactionId(null);
    setEditingTransaction({});
  };

  const handleToggleRecentTransactionSelection = useCallback((transactionId, checked) => {
    if (transactionId === undefined || transactionId === null) return;
    const key = String(transactionId);
    setSelectedRecentTransactionIds((prev) => {
      const existing = new Set(prev);
      if (checked) {
        existing.add(key);
      } else {
        existing.delete(key);
      }
      return Array.from(existing);
    });
  }, []);

  const handleToggleAllDisplayedRecentTransactions = useCallback((checked) => {
    const displayedIds = selectableDisplayedRecentTransactions.map((transaction) => String(transaction.id));
    setSelectedRecentTransactionIds((prev) => {
      const next = new Set(prev);
      displayedIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return Array.from(next);
    });
  }, [selectableDisplayedRecentTransactions]);

  const handleSelectAllFilteredRecentTransactions = useCallback(() => {
    const filteredIds = (Array.isArray(recentTransactions) ? recentTransactions : [])
      .filter((transaction) => transaction?.id !== undefined && transaction?.id !== null)
      .map((transaction) => String(transaction.id));
    setSelectedRecentTransactionIds(filteredIds);
  }, [recentTransactions]);

  const handleDeleteTransaction = async (transactionId) => {
    const transaction = transactions.find(t => t.id === transactionId);
    const confirmMessage = `Are you sure you want to delete this transaction?\n\n${transaction?.description || 'No description'}\nAmount: ${formatCurrency(transaction?.amount || 0)}\nDate: ${formatDisplayDate(transaction?.date, dateDisplayLocale)}\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        if (!ensureDrivePermissionBeforeTransactionChange('deleting transactions')) {
          return;
        }

        const updatedTransactions = transactions.filter(t => t.id !== transactionId);
        setTransactions(updatedTransactions);
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);

        setSuccessMessage('Transaction deleted successfully!');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        console.error('Error deleting transaction:', error);
        alert('? Failed to delete transaction. Please try again.');
      }
    }
  };

  const handleDeleteSelectedTransactions = async () => {
    if (!Array.isArray(selectedRecentTransactionIds) || selectedRecentTransactionIds.length === 0) {
      return;
    }

    const selectedIds = new Set(selectedRecentTransactionIds);
    const selectedTransactions = transactions.filter((transaction) => selectedIds.has(String(transaction.id)));
    if (selectedTransactions.length === 0) {
      setSelectedRecentTransactionIds([]);
      return;
    }

    const confirmMessage = `Delete ${selectedTransactions.length} selected transaction${selectedTransactions.length === 1 ? '' : 's'}?\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        if (!ensureDrivePermissionBeforeTransactionChange('deleting transactions')) {
          return;
        }

        const updatedTransactions = transactions.filter((transaction) => !selectedIds.has(String(transaction.id)));
        setTransactions(updatedTransactions);
        setSelectedRecentTransactionIds([]);
        await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);

        setSuccessMessage(`${selectedTransactions.length} transaction${selectedTransactions.length === 1 ? '' : 's'} deleted successfully!`);
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        console.error('Error deleting selected transactions:', error);
        alert('Failed to delete selected transactions. Please try again.');
      }
    }
  };

  const handleRecordPartPayment = async (transactionId, payment = {}) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    if (!transaction) return;

    const outstandingAmount = getTransactionOutstandingAmount(transaction);
    if (outstandingAmount <= 0.01) return;

    const paymentAmount = Number(payment.amount);
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      alert('Enter a payment amount greater than 0.');
      return;
    }

    const rctCreditAmount = Number(payment.rctCreditAmount || 0);
    if (!Number.isFinite(rctCreditAmount) || rctCreditAmount < 0) {
      alert('Enter a valid RCT credit amount.');
      return;
    }
    const settlementAmount = paymentAmount + rctCreditAmount;

    if (settlementAmount - outstandingAmount > 0.01) {
      alert(`Payment plus RCT credit (${formatCurrency(settlementAmount)}) cannot be more than the outstanding balance (${formatCurrency(outstandingAmount)}).`);
      return;
    }

    const method = normalizePaymentMethodId(payment.method || transaction.paymentMethod || 'cash') || 'cash';
    const paymentDate = payment.date || new Date().toISOString().split('T')[0];
    const paymentDateValue = new Date(paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      alert('Enter a valid payment date.');
      return;
    }

    try {
      if (!ensureDrivePermissionBeforeTransactionChange('recording a part payment')) {
        return;
      }

      const roundedPaymentAmount = parseFloat(paymentAmount.toFixed(2));
      const roundedRctCreditAmount = parseFloat(rctCreditAmount.toFixed(2));
      const roundedSettlementAmount = parseFloat((roundedPaymentAmount + roundedRctCreditAmount).toFixed(2));
      const saleTotalAmount = getTransactionSaleTotalAmount(transaction);
      const paidBefore = getTransactionPaidAmount(transaction);
      const paidAfter = parseFloat(Math.min(saleTotalAmount, paidBefore + roundedSettlementAmount).toFixed(2));
      const nextOutstandingAmount = parseFloat(Math.max(saleTotalAmount - paidAfter, 0).toFixed(2));
      const paidAt = paymentDate;

      const updatedOriginalTransactions = transactions.map((t) => {
        if (t.id !== transactionId) return t;

        const existingBreakdown = Array.isArray(t.paymentBreakdown)
          ? t.paymentBreakdown
              .map((entry) => ({
                method: normalizePaymentMethodId(entry?.method || method) || method,
                amount: Number(entry?.amount) || 0,
                paidAt: entry?.paidAt || null
              }))
              .filter((entry) => entry.method && entry.amount > 0)
          : [];

        const paidAmount = getTransactionPaidAmount(t);
        const paymentBreakdown = existingBreakdown.length > 0
          ? existingBreakdown
          : paidAmount > 0
            ? [{ method, amount: parseFloat(paidAmount.toFixed(2)), paidAt: t.date || null }]
            : [];

        paymentBreakdown.push({
          method,
          amount: roundedPaymentAmount,
          paidAt,
          ...(roundedRctCreditAmount > 0 ? { rctCreditAmount: roundedRctCreditAmount } : {})
        });

        return {
          ...t,
          paymentMethod: t.paymentMethod || method,
          paymentBreakdown,
          saleTotalAmount,
          amountPaid: paidAfter,
          outstandingAmount: nextOutstandingAmount,
          paymentStatus: nextOutstandingAmount > 0.01 ? 'part_paid' : 'paid',
          isPartPaymentSale: nextOutstandingAmount > 0.01 || paymentBreakdown.length > 1,
          isSplitPayment: paymentBreakdown.length > 1 || nextOutstandingAmount > 0.01,
          rctRole: roundedRctCreditAmount > 0 ? 'subcontractor' : t.rctRole,
          rctCreditAmount: parseFloat(((Number(t.rctCreditAmount) || 0) + roundedRctCreditAmount).toFixed(2)),
          updatedAt: new Date().toISOString()
        };
      });

      const paymentTransaction = {
        ...transaction,
        id: Date.now(),
        date: paymentDate,
        amount: roundedPaymentAmount,
        grossAmount: roundedPaymentAmount,
        discountAmount: 0,
        description: `Payment for ${transaction.description || 'sale'}`,
        paymentMethod: method,
        paymentBreakdown: [{
          method,
          amount: roundedPaymentAmount,
          paidAt
        }],
        amountPaid: roundedPaymentAmount,
        rctRole: roundedRctCreditAmount > 0 ? 'subcontractor' : transaction.rctRole,
        rctCreditAmount: roundedRctCreditAmount,
        rctSettlementAmount: roundedSettlementAmount,
        outstandingAmount: 0,
        paymentStatus: 'paid',
        saleTotalAmount: roundedPaymentAmount,
        isPartPaymentSale: false,
        isPartPaymentInstallment: true,
        linkedPartPaymentSaleId: transaction.id,
        isSplitPayment: false,
        isSplitTransaction: false,
        items: [],
        categoryBreakdown: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (isVatRegisteredForTransactionDate(transaction.date || paymentTransaction.date || paymentDate) && paymentTransaction.type === 'income') {
        const taxJurisdiction = resolveIncomeTaxJurisdiction();
        const vatRate = Number.isFinite(Number(transaction.vatRate))
          ? Number(transaction.vatRate)
          : getIncomeTaxRateForCategory(paymentTransaction.category, taxJurisdiction, paymentTransaction.date);
        const vatAmount = calculateTaxAmountFromGross(paymentTransaction.amount, vatRate);
        paymentTransaction.vatRate = vatRate;
        paymentTransaction.vatAmount = parseFloat(vatAmount.toFixed(2));
        paymentTransaction.vatComponents = [{
          rate: vatRate,
          amount: parseFloat(vatAmount.toFixed(2)),
          description: getTaxComponentDescription(vatRate)
        }];
      }

      const updatedTransactions = sortTransactionsByEntryDateDesc([
        ...updatedOriginalTransactions,
        paymentTransaction
      ]);

      setTransactions(updatedTransactions);
      await persistDatasetForCurrentAccess(SHARED_WORKSPACE_DATASETS.TRANSACTIONS, updatedTransactions);

      setSuccessMessage('Part payment recorded.');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error recording part payment:', error);
      alert('Failed to record payment. Please try again.');
    }
  };

  // Responsive design helpers
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const isManualSaveDisabled = splitSaleMode
    ? !canSaveSplitSale || !currentTransaction.date
    : (!currentTransaction.category || !currentTransaction.amount || !currentTransaction.date);
  const pendingInviteCount = collaborationState.pendingInvites.length;
  const ownerInviteStatuses = (collaborationState.sentInvites || []).map((invite) => invite?.status);
  const hasAcceptedAccountantInvite = ownerInviteStatuses.includes('accepted');
  const accountantAccessLabel = !isExternalSharedWorkspace && hasAcceptedAccountantInvite
    ? 'Accountant connected'
    : 'Invite Accountant';

  return (
    <VatContext.Provider value={vatContextValue}>
      {isReceiptsRoute ? (
        <div className="min-h-screen bg-white p-4 md:p-6">
          {user ? (
            <ReceiptScannerProvider>
              <ReceiptManagement
                onAddTransaction={handleReceiptTransactionAdd}
                activeWorkspace={activeWorkspace}
                transactions={transactions}
              />
            </ReceiptScannerProvider>
          ) : (
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Sign in to view receipts</h1>
              <p className="text-sm text-gray-600 mb-4">
                Receipt images are tied to your account. Please sign in to access your saved receipts.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/signin"
                  className="px-4 py-2 rounded-lg bg-info-gradient text-[color:var(--color-text)] hover:opacity-90 transition text-sm font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Create Account
                </Link>
              </div>
            </div>
          )}
        </div>
      ) : (
  <div className="min-h-screen bg-white p-4 md:p-6">
      {/* Loading indicator */}
      {isDataLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
            <p className="text-gray-700">Loading your financial data...</p>
          </div>
        </div>
      )}
      {showSetupChecklist && (
        <SetupChecklistModal
          isOpen={showSetupChecklist}
          onClose={() => {
            localStorage.setItem('setup_checklist_hidden', 'true');
            setShowSetupChecklist(false);
          }}
          progress={setupProgress}
          onToggleStep={toggleSetupStep}
          onInstall={handlePWAInstall}
          onBasics={handleBasicsStep}
        />
      )}
      <OnboardingGuideModal
        isOpen={showOnboardingGuide}
        steps={onboardingGuideSteps}
        currentStepIndex={onboardingGuideStepIndex}
        completedStepIds={completedOnboardingStepIds}
        onClose={closeOnboardingGuide}
        onBack={goToPreviousGuideStep}
        onNext={goToNextGuideStep}
        onFinish={completeOnboardingGuide}
        onMarkDone={completeGuideStepAndAdvance}
        onOpenStep={(index, runAction = false) => {
          if (runAction) {
            runOnboardingGuideAction(index);
            return;
          }
          goToGuideStep(index);
        }}
      />

      {activeOnboardingStepIndex !== null &&
        !showOnboardingGuide &&
        !showCountrySelection &&
        !showInputMethodSelection &&
        !showReceiptModal &&
        !showSpreadsheetReviewModal &&
        onboardingGuideSteps[activeOnboardingStepIndex] && (
        <div className="fixed bottom-4 right-4 z-[1115] bg-white border border-gray-200 rounded-xl shadow-lg p-3 max-w-sm">
          <p className="text-xs text-gray-500">Guide in progress</p>
          <p className="text-sm font-semibold text-gray-900">{onboardingGuideSteps[activeOnboardingStepIndex].title}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openOnboardingGuide(activeOnboardingStepIndex)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Back to guide
            </button>
            <button
              type="button"
              onClick={() => completeGuideStepAndAdvance(activeOnboardingStepIndex)}
              className="px-3 py-1.5 text-xs rounded-lg bg-warn-gradient text-[color:var(--color-text)] font-semibold hover:brightness-95"
            >
              Done & Next
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg z-40 max-w-sm">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-[color:var(--color-text)] flex-shrink-0" />
            <span className="text-sm">
              {successMessage || 'Transaction saved successfully!'}
            </span>
          </div>
        </div>
      )}

      <div className="main-content">
              {isCheckingAuth && !user && (
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Checking your account...</p>
          </div>
        </div>
      )}

        {user ? (
          <div className="max-w-6xl mx-auto elevate-all">
           {/* Mobile-Optimized Header */}
<div className="mb-6" style={{ paddingTop: 'var(--safe-top, 0px)' }}>

  {/* Top Row - Logo and User Info */}
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    {/* Logo - Left aligned */}
    <div className="flex items-center">
      <img src={logo} alt="70Lives Business Finance Tracker Logo" className="h-10 sm:h-11 w-auto" />
    </div>

    {/* User Info - Right aligned, compact on mobile */}
    <div className="flex flex-wrap items-center gap-2">
      {/* Country Selector */}
      <button
        onClick={() => setShowCountrySelection(true)}
        className="hidden"
        title="Change Country & Currency"
      >
        <span className="text-base sm:text-lg">
          {({ ie:'IE', uk:'UK', us:'US', de:'DE', fr:'FR', es:'ES', it:'IT' }[currentCountry.id] || (currentCountry.id || '').toUpperCase())}
        </span>
        <span className="font-medium">
          {currency?.symbol || ({ EUR:'€', USD:'$', GBP:'£' }[currentCountry.currency] || currentCountry.currencySymbol)}
        </span>
        <span className="hidden sm:inline">{currentCountry.name}</span>
      </button>
      <button
        onClick={() => setShowBusinessDetailsSettings((prev) => !prev)}
        className="inline-flex h-10 flex-shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        title="Business settings"
      >
        <FileText className="h-4 w-4" />
        <span>Business settings</span>
      </button>
      {/* Sync Status */}
      <div className="flex flex-shrink-0 items-center space-x-2">
        {!activeWorkspace?.id && <SyncStatus />}
      </div>

      <button
        onClick={() => setShowAccountantAccessModal(true)}
        className="relative inline-flex h-10 flex-shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 transition hover:bg-gray-50"
        title={accountantAccessLabel}
      >
        <Users className="h-4 w-4" />
        <span className="hidden sm:inline">{accountantAccessLabel}</span>
        {pendingInviteCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
            {pendingInviteCount}
          </span>
        )}
      </button>

      {/* Welcome text - responsive */}
      <span className="hidden text-sm font-medium text-gray-800 sm:inline">Welcome, {user.displayName}</span>
      <span className="block basis-full truncate text-sm font-medium text-gray-800 sm:hidden">Hi, {user.displayName?.split(' ')[0]}</span>

      <button
        onClick={handleLogout}
        className="ml-auto inline-flex h-10 flex-shrink-0 items-center justify-center rounded-lg bg-info-gradient px-3 text-sm text-[color:var(--color-text)] transition hover:opacity-90"
      >
        Logout
      </button>
    </div>
  </div>

  {showBusinessDetailsSettings && (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="min-w-[180px] flex-1">
          <p className="text-xs font-medium text-gray-600">Country & currency</p>
          <button
            type="button"
            onClick={() => setShowCountrySelection(true)}
            className="mt-1 inline-flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 transition hover:bg-gray-50 sm:w-auto"
            title="Change Country & Currency"
          >
            <span>
              {({ ie:'IE', uk:'UK', us:'US', de:'DE', fr:'FR', es:'ES', it:'IT' }[currentCountry.id] || (currentCountry.id || '').toUpperCase())}
              {' '}
              {currency?.symbol || ({ EUR:'EUR', USD:'$', GBP:'GBP' }[currentCountry.currency] || currentCountry.currencySymbol)}
              {' '}
              {currentCountry.name}
            </span>
          </button>
        </div>
        <label className="min-w-[180px] flex-1 text-xs font-medium text-gray-600" htmlFor="business-settings-type-select">
          Business type
          <select
            id="business-settings-type-select"
            value={businessType}
            onChange={(event) => handleBusinessTypeChange(event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700"
            title="Business type"
          >
            {localizedBusinessTypeOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[200px] flex-1 text-xs font-medium text-gray-600" htmlFor="business-settings-industry-select">
          Industry
          <select
            id="business-settings-industry-select"
            value={industryId}
            onChange={(event) => handleIndustryChange(event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700"
            title={`Industry: ${industryLabel}`}
          >
            {INDUSTRY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={handleSaveBusinessProfileSettings}
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          title="Save business settings"
        >
          <Save className="h-4 w-4" />
          Save settings
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <label className="text-xs font-medium text-gray-600">
          Business / legal name
          <input
            type="text"
            value={businessProfileDetails.businessName}
            onChange={(event) => handleBusinessProfileDetailChange('businessName', event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
            placeholder="Company or self-employed name"
          />
        </label>
        <label className="text-xs font-medium text-gray-600">
          Trading name
          <input
            type="text"
            value={businessProfileDetails.tradingName}
            onChange={(event) => handleBusinessProfileDetailChange('tradingName', event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
            placeholder="Trading as"
          />
        </label>
        <label className="text-xs font-medium text-gray-600">
          Tax reference
          <input
            type="text"
            value={businessProfileDetails.taxReference}
            onChange={(event) => handleBusinessProfileDetailChange('taxReference', event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
            placeholder="Tax registration / PPSN / VAT ref"
          />
        </label>
        <label className="text-xs font-medium text-gray-600 lg:col-span-2">
          Business address
          <textarea
            value={businessProfileDetails.businessAddress}
            onChange={(event) => handleBusinessProfileDetailChange('businessAddress', event.target.value)}
            className="mt-1 min-h-[72px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Registered or business address"
          />
        </label>
        {isLimitedCompany && (
          <>
            <label className="text-xs font-medium text-gray-600">
              Company registration number
              <input
                type="text"
                value={businessProfileDetails.companyRegistrationNumber}
                onChange={(event) => handleBusinessProfileDetailChange('companyRegistrationNumber', event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
                placeholder="CRO / Companies House number"
              />
            </label>
            <label className="text-xs font-medium text-gray-600">
              Incorporation date
              <input
                type="date"
                value={businessProfileDetails.incorporationDate}
                onChange={(event) => handleBusinessProfileDetailChange('incorporationDate', event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
              />
            </label>
            <label className="text-xs font-medium text-gray-600">
              Director name
              <input
                type="text"
                value={businessProfileDetails.directorDetails.name}
                onChange={(event) => handleDirectorDetailChange('name', event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
                placeholder="Director / proprietor"
              />
            </label>
            <label className="text-xs font-medium text-gray-600">
              Director role
              <input
                type="text"
                value={businessProfileDetails.directorDetails.role}
                onChange={(event) => handleDirectorDetailChange('role', event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
                placeholder="Director"
              />
            </label>
            <label className="text-xs font-medium text-gray-600">
              Appointed date
              <input
                type="date"
                value={businessProfileDetails.directorDetails.appointedDate}
                onChange={(event) => handleDirectorDetailChange('appointedDate', event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
              />
            </label>
            <label className="text-xs font-medium text-gray-600 lg:col-span-2">
              Director address
              <textarea
                value={businessProfileDetails.directorDetails.address}
                onChange={(event) => handleDirectorDetailChange('address', event.target.value)}
                className="mt-1 min-h-[72px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Optional director address"
              />
            </label>
          </>
        )}
      </div>
    </div>
  )}

  {/* Subscription Status - Centered */}
  <div className="flex justify-center mb-4">
    {isExternalSharedWorkspace ? (
      <div className="rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800">
        {workspacePlanSummary}
      </div>
    ) : (
      <SubscriptionStatus
        currentUser={user}
        onUpgrade={() => {
          console.log('Upgrade clicked, user state:', user);
          if (user && user.email) {
            console.log('User is signed in, SubscriptionStatus will handle upgrade');
          } else {
            console.log('User not found, showing sign up modal');
            setShowSignUpModal(true);
          }
        }}
      />
    )}
  </div>
</div>

{/* Action Buttons - Mobile Grid Layout */}
<div className="grid grid-cols-2 gap-3 justify-items-center max-w-xs mx-auto sm:max-w-none sm:flex sm:flex-wrap sm:justify-center sm:gap-4 mb-6 w-full order-1">
  <button
    onClick={() => {
      setActiveTab("income");
      setShowInputMethodSelection(true);
    }}
    className={`w-full max-w-[190px] px-6 py-4 bg-[color:var(--color-accent)] text-[color:var(--color-text)] rounded-xl border-2 border-black shadow-md hover:brightness-95 transition text-base font-semibold text-center sm:w-40 ${showTxnNudge ? 'animate-pulse' : ''}`}
  >
    Add Sale
  </button>

  <button
    onClick={() => {
      setActiveTab("expenses");
      setShowInputMethodSelection(true);
    }}
    className={`w-full max-w-[190px] px-6 py-4 bg-primary-gradient text-[color:var(--color-text)] rounded-xl border-2 border-black shadow-md hover:brightness-95 transition text-base font-semibold text-center sm:w-40 ${showTxnNudge ? 'animate-pulse' : ''}`}
  >
    Add Expense
  </button>

  <Link
    to="/receipts"
    className="col-span-2 justify-self-center w-full max-w-[190px] px-6 py-4 bg-[color:var(--color-info)] text-[color:var(--color-text)] rounded-xl border-2 border-black shadow-md hover:brightness-95 transition text-base font-semibold text-center sm:col-auto sm:w-40"
  >
    Receipts
  </Link>

</div>

            {/* Financial Summary - Styled like original boxes */}
            <div className="mb-4 order-2">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {(() => {
                  const totalIncome = Number(totals.income) || 0;
                  const totalExpenses = Number(totals.expenses) || 0;
                  const netProfit = totalIncome - totalExpenses;

                  return (
                    <>
                      <div className="bg-[color:var(--color-accent)] rounded-lg p-2 sm:p-4 text-center text-[color:var(--color-text)] shadow-lg">
                        <div className="text-xs sm:text-sm font-medium opacity-90">Total Income</div>
                        <div className="text-lg sm:text-xl font-bold">{formatCurrency(totalIncome)}</div>
                        <div className="mt-1 text-[10px] sm:text-xs opacity-90">
                          Collected {formatCurrency(totals.cashReceived || 0)}
                          {(totals.outstandingIncome || 0) > 0.01
                            ? ` · Outstanding ${formatCurrency(totals.outstandingIncome)}`
                            : ''}
                        </div>
                      </div>
                      <div className="rounded-lg p-2 sm:p-4 text-center shadow-lg bg-[color:var(--color-info)] text-[color:var(--color-text)]">
                        <div className="text-xs sm:text-sm font-medium opacity-90">Net Profit</div>
                        <div className="text-lg sm:text-xl font-bold">{formatCurrency(netProfit)}</div>
                        <div className="mt-1 text-[10px] sm:text-xs opacity-90">
                          Earned income less incurred expenses
                        </div>
                      </div>
                      <div className="bg-primary-gradient rounded-lg p-2 sm:p-4 text-center text-[color:var(--color-text)] shadow-lg">
                        <div className="text-xs sm:text-sm font-medium opacity-90">Total Expenses</div>
                        <div className="text-lg sm:text-xl font-bold">{formatCurrency(totalExpenses)}</div>
                        <div className="mt-1 text-[10px] sm:text-xs opacity-90">
                          Paid {formatCurrency(totals.cashPaid || 0)}
                          {(totals.unpaidExpenses || 0) > 0.01
                            ? ` · Unpaid ${formatCurrency(totals.unpaidExpenses)}`
                            : ''}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
{/* Filter Dropdowns - Mobile Optimized */}
<div id="filters-reports-section" className="flex flex-col w-full gap-4 mb-6 order-3">
  {/* Mobile: Stack everything vertically, Desktop: Side by side */}
  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
    
    {/* Filter controls and active chips */}
    <div className="flex flex-col gap-4 flex-1 min-w-0">
      {/* Main Filter Selector */}
      <CustomSelect
        options={[
          { value: 'date', label: 'Filter by Date' },
          { value: 'type', label: 'Filter by Transaction Type' },
          { value: 'category', label: 'Filter by Category' },
          { value: 'payment', label: 'Filter by Payment Method' },
          { value: 'search', label: 'Search Transactions' }
        ]}
        placeholder="Add Data Filter"
        value={activeFilterType}
        onChange={(val) => setActiveFilterType(val)}
        className="min-w-[220px] sm:min-w-[260px] w-full"
      />

      {/* Secondary Dropdown - Shows based on selection */}
      {activeFilterType === 'date' && (
        <CustomSelect
  options={[
    { value: '', label: 'All Time' },
    ...(Array.isArray(TIMEFRAMES) ? TIMEFRAMES.map(t => ({ value: t.id, label: t.name })) : [])
  ]}
  value={selectedTimeframe || ''}
  onChange={(val) => {
    setSelectedTimeframe(val);
    if (val !== 'custom') { setStartDate(null); setEndDate(null); }
    setActiveFilterType('');
  }}
/>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="text-sm font-medium text-gray-800">Financial year in view</div>
        <select
          className="px-3 py-2 border rounded-lg text-sm w-full sm:w-auto"
          value={activeFinancialYear}
          onChange={(e) => {
            const year = parseInt(e.target.value, 10) || new Date().getFullYear();
            setActiveFinancialYear(year);
            setSelectedTimeframe(DEFAULT_TIMEFRAME_ID);
          }}
        >
          {availableFinancialYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <span className="text-xs text-gray-600">Default views show the active year. Use a date filter to browse older data.</span>
      </div>

      {activeFilterType === 'type' && (
        <CustomSelect
          options={[
            { value: 'income', label: 'Sales (Income) Only' },
            { value: 'expenses', label: 'Expenses Only' },
          ]}
          placeholder="Select transaction type"
          value={transactionTypeFilter}
          onChange={(val) => { setTransactionTypeFilter(val); setActiveFilterType(''); }}
        />
      )}

      {activeFilterType === 'category' && (
        <div className="w-full bg-white rounded-lg border shadow-sm p-3">
          <div className="text-sm font-medium text-[color:var(--color-text)] mb-2">Select Categories</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-72 overflow-auto pr-1">
            {(
              [
                ...(transactionTypeFilter !== 'expenses' && Array.isArray(CATEGORIES.income) ? CATEGORIES.income : []),
                ...(transactionTypeFilter !== 'income' && Array.isArray(CATEGORIES.expenses) ? CATEGORIES.expenses : [])
              ]
            ).map(cat => (
              <label
                key={cat.id}
                className="flex items-center gap-2 text-[color:var(--color-text)] bg-white border border-gray-200 rounded-full px-3 py-2 text-sm shadow-sm hover:border-[color:var(--color-accent)] transition"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[color:var(--color-accent)]"
                  checked={pendingCategories.includes(cat.id) || selectedCategories.includes(cat.id)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPendingCategories(prev => {
                      const base = new Set(prev.length ? prev : selectedCategories);
                      if (checked) base.add(cat.id); else base.delete(cat.id);
                      return Array.from(base);
                    });
                  }}
                />
                <span className="text-sm truncate">{cat.name}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => { setPendingCategories([]); setActiveFilterType(''); }}
              className="px-3 py-1 bg-gray-100 text-[color:var(--color-text)] rounded-full text-sm hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { setSelectedCategories(pendingCategories); setActiveFilterType(''); }}
              className="px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm hover:brightness-95"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {activeFilterType === 'payment' && (
        <CustomSelect
          options={[
            { value: '', label: 'All Payment Methods' },
            ...PAYMENT_METHODS.map((method) => ({
              value: method.id,
              label: method.name
            }))
          ]}
          value={(Array.isArray(selectedPaymentMethods) && selectedPaymentMethods[0]) || ''}
          onChange={(val) => { setSelectedPaymentMethods(val ? [val] : []); setActiveFilterType(''); }}
        />
      )}

      {activeFilterType === 'search' && (
        <div className="w-full bg-white rounded-lg border shadow-sm p-3">
          <div className="text-sm font-medium text-[color:var(--color-text)] mb-3">Search transactions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keyword</label>
              <input
                type="search"
                value={transactionSearchTerm}
                onChange={(event) => setTransactionSearchTerm(event.target.value)}
                placeholder="Supplier, client, description, notes"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
              <input
                type="search"
                inputMode="decimal"
                value={transactionAmountSearch}
                onChange={(event) => setTransactionAmountSearch(event.target.value)}
                placeholder="Example: 82.41"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setTransactionSearchTerm('');
                setTransactionAmountSearch('');
              }}
              className="px-3 py-1 bg-gray-100 text-[color:var(--color-text)] rounded-full text-sm hover:bg-gray-200"
            >
              Clear search
            </button>
            <button
              type="button"
              onClick={() => setActiveFilterType('')}
              className="px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm hover:brightness-95"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {((selectedTimeframe && selectedTimeframe !== ALL_TIMEFRAME_ID) || transactionTypeFilter !== 'all' || (Array.isArray(selectedCategories) && selectedCategories.length > 0) || (Array.isArray(selectedPaymentMethods) && selectedPaymentMethods.length > 0) || transactionSearchTerm.trim() || transactionAmountSearch.trim()) && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-600 font-medium">Active Filters:</span>

          {selectedTimeframe && selectedTimeframe !== ALL_TIMEFRAME_ID && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
              {TIMEFRAMES.find(t => t.id === selectedTimeframe)?.name || selectedTimeframe}
              <button
                onClick={() => {
                  setSelectedTimeframe(ALL_TIMEFRAME_ID);
                  setStartDate(null);
                  setEndDate(null);
                }}
                className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
              >
                  x
              </button>
            </span>
          )}

          {transactionTypeFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
              {transactionTypeFilter === 'income' ? 'Sales Only' : 'Expenses Only'}
              <button
                onClick={() => setTransactionTypeFilter('all')}
                className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
              >
                  x
              </button>
            </span>
          )}

          {Array.isArray(selectedCategories) && selectedCategories.length > 0 && (
            <>
              {selectedCategories.map((catId) => {
                const cat = (CATEGORIES.income || []).find(c => c.id === catId) || (CATEGORIES.expenses || []).find(c => c.id === catId);
                const label = cat?.name || catId;
                return (
                  <span key={catId} className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
                    {label}
                    <button
                      onClick={() => setSelectedCategories(prev => prev.filter(id => id !== catId))}
                      className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
                    >
                      x
                    </button>
                  </span>
                );
              })}
            </>
          )}

          {Array.isArray(selectedPaymentMethods) && selectedPaymentMethods.length > 0 && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
              {getPaymentMethodLabel(selectedPaymentMethods[0])}
              <button
                onClick={() => setSelectedPaymentMethods([])}
                className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
              >
                  x
              </button>
            </span>
          )}

          {transactionSearchTerm.trim() && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
              Keyword: {transactionSearchTerm.trim()}
              <button
                onClick={() => setTransactionSearchTerm('')}
                className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
              >
                  x
              </button>
            </span>
          )}

          {transactionAmountSearch.trim() && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-full text-sm">
              Amount: {transactionAmountSearch.trim()}
              <button
                onClick={() => setTransactionAmountSearch('')}
                className="ml-1 text-[color:var(--color-text)] hover:opacity-80"
              >
                  x
              </button>
            </span>
          )}

          <button
            onClick={() => {
              setSelectedTimeframe(ALL_TIMEFRAME_ID);
              setTransactionTypeFilter('all');
              setSelectedCategories([]);
              setSelectedPaymentMethods([]);
              setTransactionSearchTerm('');
              setTransactionAmountSearch('');
              setStartDate(null);
              setEndDate(null);
            }}
            className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-gray-200 transition"
          >
            Clear All
          </button>
        </div>
        )}
                    </div>

    {/* Export buttons */}
    <div className="flex flex-col gap-3 lg:items-end lg:min-w-[200px]">
      <div className="w-full space-y-2">
        <div className="flex flex-wrap gap-2">
            <ReportExport
              transactions={getFilteredTransactions}
              categories={CATEGORIES}
              buildTransactionVatEntries={buildTransactionVatEntries}
              selectedTimeframe={selectedTimeframe}
              selectedCategory={selectedCategory}
              selectedType={activeTab}
              startDate={startDate}
              endDate={endDate}
              user={user}
              handleGoogleSignIn={handleGoogleSignIn}
              isCapitalExpense={isCapitalExpense}
              selectedPaymentMethods={selectedPaymentMethods}
              selectedCategories={selectedCategories}
              transactionTypeFilter={transactionTypeFilter}
              reportTypes={['filteredTransactions', 'filteredSummary', 'profitLoss']}
              initialReportType="filteredTransactions"
              dateLocale={dateDisplayLocale}
            />
        </div>
      </div>
    </div>
  </div>

  {/* Custom Date Range */}
  {selectedTimeframe === "custom" && (
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="w-full">
        <label className="block text-xs font-medium text-[color:var(--color-text)] mb-1">Start date</label>
        <div className="relative">
          {isMobile && !startDate && (
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400 text-sm z-10">
              mm/dd/yyyy
            </span>
          )}
          <input
            type="date"
            aria-label="Start date"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder={isMobile ? " " : "mm/dd/yyyy"}
            className="px-4 py-2 bg-white rounded-lg border shadow-sm w-full hover:bg-gray-50 focus:bg-gray-50 relative z-0"
          />
        </div>
      </div>
      <div className="w-full">
        <label className="block text-xs font-medium text-[color:var(--color-text)] mb-1">End date</label>
        <div className="relative">
          {isMobile && !endDate && (
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400 text-sm z-10">
              mm/dd/yyyy
            </span>
          )}
          <input
            type="date"
            aria-label="End date"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder={isMobile ? " " : "mm/dd/yyyy"}
            className="px-4 py-2 bg-white rounded-lg border shadow-sm w-full hover:bg-gray-50 focus:bg-gray-50 relative z-0"
          />
        </div>
      </div>
    </div>
  )}
</div>

{/* Nudges for first-steps */}
{(showGoalNudge || showTxnNudge) && (
  <div className="mb-4 space-y-3">
    {showGoalNudge && (
      <div className="rounded-lg border border-[color:var(--color-info)] bg-[color:var(--color-info)]/60 px-4 py-3 flex items-start gap-3 shadow-sm">
        <div className="w-2 h-2 rounded-full bg-[color:var(--color-accent)] mt-2 animate-pulse"></div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-[color:var(--color-text)]">Set your budget plan</p>
          <p className="text-xs text-[color:var(--color-text)]/80">Tap the Budget tab to set profit, owner pay, and labour cost assumptions.</p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleBudgetNudge}
              className="px-3 py-1.5 rounded-md bg-[color:var(--color-warn)] text-[color:var(--color-text)] text-xs font-semibold shadow-sm hover:brightness-95"
            >
              Show me
            </button>
            <button
              onClick={() => setShowGoalNudge(false)}
              className="text-xs text-gray-600 hover:text-gray-800"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    )}
    {showTxnNudge && (
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3 shadow-sm">
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 animate-pulse"></div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-[color:var(--color-text)]">Add your first sale or expense</p>
          <p className="text-xs text-[color:var(--color-text)]/80">Well guide youtap a button below to start.</p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleAddIncomeNudge}
              className="px-3 py-1.5 rounded-md bg-[color:var(--color-accent)] text-[color:var(--color-text)] text-xs font-semibold shadow-sm hover:brightness-95"
            >
              Add a sale
            </button>
            <button
              onClick={handleAddExpenseNudge}
              className="px-3 py-1.5 rounded-md bg-[#C8A7F6] text-[color:var(--color-text)] text-xs font-semibold shadow-sm hover:brightness-95"
            >
              Add an expense
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
)}

{/* Analytics & Tax Section - Side by Side Layout */}
{user && (

<div id="analytics-section" className="mb-6"> <div className="flex flex-col lg:flex-row gap-6 items-start"> {/* Full Width - Analytics */} <div className="w-full"> <div className="rounded-lg shadow-sm p-4 h-full bg-warn-gradient text-[color:var(--color-text)] border border-[color:var(--color-warn)]"> <div className="mb-4"> {/* Title + badge(s) on one line */} <div className="flex items-center gap-2"> <h3 className="text-2xl font-bold">Business Analytics</h3>
{isPremiumSubscriber && (
  <span className="text-xs bg-[color:var(--color-warn)] text-[color:var(--color-text)] px-2 py-1 rounded-full">
    PREMIUM
  </span>
)}

{isTrialSubscriber && (
  <span className="text-xs bg-info-gradient text-[color:var(--color-text)] px-2 py-1 rounded-full">
    TRIAL
  </span>
)}
      </div>
      {/* Toggle directly below the title */}

      <div className="mt-2 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setAnalyticsExpanded(prev => !prev)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-info-gradient text-[color:var(--color-text)] border border-transparent rounded-full hover:opacity-90 focus:outline-none"
            aria-expanded={analyticsExpanded}
            aria-controls="analytics-panel"
          >
            {analyticsExpanded ? 'Hide Analytics ?' : 'Show Analytics ?'}
          </button>
          {!hideDemoDashboard && (
            <>
          <button
            type="button"
            onClick={() => setShowDemoDashboard(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white/80 text-[color:var(--color-text)] border border-white/80 rounded-full shadow-sm hover:bg-white"
          >
            See example dashboard
          </button>
          <button
            type="button"
            onClick={() => openOnboardingGuide()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white/80 text-[color:var(--color-text)] border border-white/80 rounded-full shadow-sm hover:bg-white"
          >
            Open setup guide
          </button>
          <a
            href="/70Lives%20Beauty%20Finance%20Demo.mp4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white/80 text-[color:var(--color-text)] border border-white/80 rounded-full shadow-sm hover:bg-white"
          >
            Watch onboarding video
          </a>
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem('hide_demo_dashboard', 'true'); } catch {}
              setHideDemoDashboard(true);
                  setShowDemoDashboard(false);
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Don't show again
              </button>
            </>
          )}
        </div>

        {analyticsExpanded && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                {
                  periodKey: 'selected',
                  label: isLimitedCompany ? `${selectedPeriodAnalyticsSnapshot.label} Profit After Tax` : `${selectedPeriodAnalyticsSnapshot.label} Available Take-Home`,
                  value: selectedPeriodAnalyticsSnapshot.netProfit,
                  selectedSnapshot: selectedPeriodAnalyticsSnapshot
                },
                {
                  periodKey: 'today',
                  label: isLimitedCompany ? "Today's Profit After Tax" : "Today's Available Take-Home",
                  value: takeHomeSnapshots.today
                },
                {
                  periodKey: 'week',
                  label: isLimitedCompany ? "This Week's Profit After Tax" : "This Week's Available Take-Home",
                  value: takeHomeSnapshots.week
                },
                {
                  periodKey: 'month',
                  label: isLimitedCompany ? "This Month's Profit After Tax" : "This Month's Available Take-Home",
                  value: takeHomeSnapshots.month
                }
              ].map((item) => {
                const setAside = item.selectedSnapshot
                  ? {
                      incomeTax: item.selectedSnapshot.incomeTax,
                      vatDue: item.selectedSnapshot.vatDue,
                      vatReclaim: item.selectedSnapshot.vatReclaim,
                      totalSetAside: item.selectedSnapshot.totalSetAside
                    }
                  : (taxSetAsideSnapshots[item.periodKey] || { incomeTax: 0, vatDue: 0, vatReclaim: 0, totalSetAside: 0 });
                const payrollSetAside = payrollSetAsideSnapshots[item.periodKey] || { wages: 0, holidayPay: 0, totalSetAside: 0 };
                const totalSetAside = parseFloat((
                  (Number(setAside.totalSetAside) || 0) +
                  (Number(payrollSetAside.totalSetAside) || 0)
                ).toFixed(2));
                const isVatReclaim = setAside.vatReclaim > 0;
                const spendableTakeHome = item.selectedSnapshot
                  ? item.selectedSnapshot.availableTakeHome
                  : parseFloat(((Number(item.value) || 0) - totalSetAside).toFixed(2));
                const isNegative = spendableTakeHome < 0;
                return (
                  <div
                    key={item.label}
                    className="rounded-xl bg-[#F8FAFC] text-[color:var(--color-text)] p-4 shadow-sm border border-slate-200"
                  >
                    <div className="text-xs uppercase tracking-wide text-gray-500">{item.label}</div>
                    <div className={`text-2xl font-semibold mt-1 ${isNegative ? 'text-red-600' : 'text-[color:var(--color-text)]'}`}>
                      {formatCurrency(spendableTakeHome)}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Set aside total</span>
                        <span className="font-semibold">{formatCurrency(totalSetAside)}</span>
                      </div>
                      {item.selectedSnapshot && (
                        <>
                          <div className="flex justify-between">
                            <span>Income</span>
                            <span>{formatCurrency(item.selectedSnapshot.income)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Expenses</span>
                            <span>{formatCurrency(item.selectedSnapshot.expenses)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Net profit</span>
                            <span>{formatCurrency(item.selectedSnapshot.netProfit)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Adjusted net profit</span>
                            <span>{formatCurrency(item.selectedSnapshot.adjustedNetProfit)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span>{isLimitedCompany ? 'Corporation tax' : 'Income tax'}</span>
                        <span>{formatCurrency(setAside.incomeTax)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{isVatReclaim ? `${countryTerminology.taxName} reclaim` : `${countryTerminology.taxName} to pay`}</span>
                        <span>{isVatReclaim ? `-${formatCurrency(setAside.vatReclaim)}` : formatCurrency(setAside.vatDue)}</span>
                      </div>
                      {payrollSetAside.totalSetAside > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span>Wages to set aside</span>
                            <span>{formatCurrency(payrollSetAside.wages)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Holiday pay to set aside</span>
                            <span>{formatCurrency(payrollSetAside.holidayPay)}</span>
                          </div>
                        </>
                      )}
                      {item.selectedSnapshot && (
                        <div className="flex justify-between">
                          <span>Transactions</span>
                          <span>{item.selectedSnapshot.transactionCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {shouldShowAnalyticsUpsell && (
              <div className="bg-info-gradient border border-dashed border-[color:var(--color-accent)] rounded-lg p-4 text-sm text-[color:var(--color-text)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
                <div className="font-medium">
                  Unlock full analytics, AI profit margin suggestions, and premium calculators by upgrading to <strong>Premium</strong>.
                </div>
                <button
                  type="button"
                  onClick={handleUpgradeClick}
                  className="px-4 py-2 rounded-full bg-[color:var(--color-accent)] text-[color:var(--color-text)] font-semibold text-xs shadow hover:brightness-95"
                >
                  {premiumCtaLabel}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>                
       {/* Show appropriate onboarding based on user progress */}
                  {(() => {
                    const filteredTransactions = getFilteredTransactions;
                    const transactionCount = Array.isArray(filteredTransactions) ? filteredTransactions.length : 0;
                    const displaySetupCard = showSetupChecklistCard && (shouldShowSetupChecklist || ((transactionCount === 0 && isNewUser) && !allSetupStepsDone));
                    if (displaySetupCard) {
                      return (
                        <SetupChecklistCard
                          progress={setupProgress}
                          onInstall={handleInstallStep}
                          onDrive={handleDriveConnectStep}
                          onBasics={handleBasicsStep}
                          onDismiss={() => {
                            handleChecklistDismiss();
                            setShowGettingStarted(false);
                            localStorage.setItem('onboarding_completed', 'true');
                          }}
                          hideDemoDashboard={hideDemoDashboard}
                          onDemo={() => setShowDemoDashboard(true)}
                          onHideDemoDashboard={handleHideDemoDashboard}
                        />
                      );
                    }
                    return null;
                  })()}

                  {/* Analytics Charts */}
                  {analyticsExpanded && Array.isArray(getFilteredTransactions) && getFilteredTransactions.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                      {/* Show different content based on subscription tier */}
                      {(() => {
                        if (hasPremiumAnalyticsAccess) {
                        return (
                          <>      
                    {/* Hidden tax calculator instance to keep estimated tax in sync */}
                    <div className="hidden" aria-hidden="true">
                      <TaxCalculation
                        transactions={getFilteredTransactions}
                        allTransactions={transactions}
                        updatedTransactions={updatedTransactions}
                        totals={totals}
                        adjustedNetProfit={taxableAdjustedNetProfit}
                        countryName={currentCountry.name}
                        businessType={businessType}
                        taxYear={activeFinancialYear}
                        isCapitalExpense={isCapitalExpense}
                        categories={transactionInputCategories}
                        rctTracker={rctTracker}
                        supportsRct={supportsRct}
                        buildTransactionVatEntries={buildTransactionVatEntries}
                        categoryVatRateRows={categoryVatRateRows}
                        user={user}
                        handleGoogleSignIn={handleGoogleSignIn}
                        onTaxSummaryChange={setTaxSummary}
                      />
                    </div>
                    {/* Sales & Costs Breakdown Visual */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('revenue')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs sm:text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.revenue}
                        aria-label="Toggle Sales & Costs Breakdown panel"
                      >
                        <span>Sales & Costs Breakdown</span>
                        {analyticsPanels.revenue ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.revenue && (
                        <div className="px-3 pb-3 pt-1">
                          <div className="space-y-3">
                            {(() => {
                              const incomeTransactions = getFilteredTransactions.filter(
                                t => t.type === 'income' && (t.category || '').toLowerCase() !== 'assetsale'
                              );
                              const assetIncomeTransactions = getFilteredTransactions.filter(
                                t => t.type === 'income' && (t.category || '').toLowerCase() === 'assetsale'
                              );

                              const totalServiceProductIncome = incomeTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
                              const totalAssetIncome = assetIncomeTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const expenseTransactions = getFilteredTransactions.filter(t => t.type === 'expenses');
                              const totalExpenses = expenseTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const categoryMatch = (txn, keywords) => {
                                const cat = (txn.category || '').toLowerCase();
                                return keywords.some(k => cat.includes(k));
                              };

                              const productExpenseTotal = expenseTransactions
                                .filter(t => categoryMatch(t, ['supplies', 'materials', 'retail', 'product']))
                                .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const overheadTotal = expenseTransactions
                                .filter(t => categoryMatch(t, ['utility', 'rent', 'lease', 'insurance', 'marketing', 'advert', 'office', 'software', 'subscription']))
                                .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const assetToolTotal = expenseTransactions
                                .filter(t => categoryMatch(t, ['equipment', 'tool', 'asset', 'capital']))
                                .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const labourCostTotal = expenseTransactions
                                .filter(t => categoryMatch(t, ['staffcosts', 'staff', 'payroll', 'salary', 'salaries', 'wage', 'wages', 'employee', 'contractor', 'labour', 'labor', 'prsi', 'employer_prsi', 'employee_holiday_pay', 'holiday pay']))
                                .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

                              const otherExpenses = Math.max(0, totalExpenses - productExpenseTotal - overheadTotal - assetToolTotal - labourCostTotal);

                              // Base income that remains after expenses; includes asset sales to keep totals aligned.
                              const totalIncomeAll = totalServiceProductIncome + totalAssetIncome;
                              const profitBeforeTax = Math.max(0, totalIncomeAll - totalExpenses);
                              const profitBeforeTaxLabel = isLimitedCompany ? 'Profit Before Corporation Tax' : 'Profit Before Tax';

                              const totalForBar = Math.max(totalIncomeAll, 1);

                              const segments = [
                                { label: 'Service/Product Sales', value: totalServiceProductIncome, color: 'bg-[#FCE7F3]' },
                                { label: 'Asset Sales', value: totalAssetIncome, color: 'bg-[#FFE4B5]' },
                                { label: 'Product Cost', value: productExpenseTotal, color: 'bg-[#E0E7FF]' },
                                { label: 'Overheads', value: overheadTotal, color: 'bg-gray-200' },
                                { label: 'Asset & Tool Purchase', value: assetToolTotal, color: 'bg-[#FFE4B5]/70' },
                                { label: 'Labour Costs', value: labourCostTotal, color: 'bg-[#DBEAFE]' },
                                { label: 'Other Expenses', value: otherExpenses, color: 'bg-gray-100' },
                                { label: profitBeforeTaxLabel, value: profitBeforeTax, color: 'bg-[#BBF7D0]' },
                              ].filter(s => s.value > 0);

                              return (
                                <div className="space-y-4">
                                  <div className="h-4 rounded-full bg-gray-200 flex overflow-hidden shadow-inner">
                                    {segments.map(seg => (
                                      <div
                                        key={seg.label}
                                        className={`${seg.color} transition-all duration-300`}
                                        style={{ width: `${(seg.value / totalForBar) * 100}%` }}
                                        title={`${seg.label}: ${formatCurrency ? formatCurrency(seg.value) : seg.value.toFixed(2)}`}
                                      />
                                    ))}
                                  </div>
                                  <div className="space-y-2 text-xs text-gray-700">
                                    {segments.map(seg => (
                                      <div
                                        key={seg.label}
                                        className="flex items-center justify-between rounded border border-gray-100 bg-white px-3 py-2"
                                      >
                                        <div className="font-semibold">{seg.label}</div>
                                        <div className="flex items-center gap-4">
                                          <span className="text-[color:var(--color-text)]">
                                            {formatCurrency ? formatCurrency(seg.value) : seg.value.toFixed(2)}
                                          </span>
                                          <span className="text-[10px] text-gray-500 w-14 text-right">
                                            {(seg.value / totalForBar * 100 || 0).toFixed(1)}%
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="text-[11px] text-gray-500 text-center px-4 leading-snug">
                                    For the most accurate mix, publish prices via the Pricing Calculator; otherwise this view uses totals from service/product sales, asset sales, and expense categories (product costs, overheads, assets/tools, labour, other).
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Goal Progress */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('goal')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs sm:text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.goal}
                        aria-label="Toggle Goal Progress panel"
                      >
                        <span>Goal Progress</span>
                        {analyticsPanels.goal ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.goal && (() => {
                        // Get saved goal data
                        const savedTargetIncomeData = localStorage.getItem('70livesbeautyledgerTargetIncome');
                        let goalData = null;

                        try {
                          if (savedTargetIncomeData) {
                            goalData = JSON.parse(savedTargetIncomeData);
                          }
                        } catch (e) {
                          console.error('Failed to parse goal data', e);
                        }

                        if (!goalData || !goalData.amount || parseFloat(goalData.amount) <= 0) {
                          return (
                            <div className="text-center py-4">
                              <div className="text-gray-400 mb-2 text-lg">??</div>
                              <p className="text-xs text-[color:var(--color-text)]">No budget plan set yet</p>
                              <button
                                onClick={() => setShowBudgetSection(true)}
                                className="text-xs text-[color:var(--color-text)] hover:text-[color:var(--color-primary-800)] mt-1"
                              >
                                Set your first goal
                              </button>
                            </div>
                          );
                        }

// Calculate current period progress
const now = new Date();
let periodStart, periodEnd, targetAmount, periodLabel;

// Check if we should use the filter timeframe instead of goal period
// Replace 'selectedTimeframe' with your actual filter variable name
const shouldUseFilterTimeframe = selectedTimeframe && selectedTimeframe !== 'all';

if (shouldUseFilterTimeframe) {
    // Use the selected filter timeframe (net take-home = income minus expenses)
  targetAmount = parseFloat(goalData.amount);

  switch(selectedTimeframe) {
    case 'financialYear': {
      const start = new Date(activeFinancialYear, 0, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(activeFinancialYear, 11, 31, 23, 59, 59, 999);
      periodStart = start;
      periodEnd = end;
      periodLabel = `FY ${activeFinancialYear}`;
      if (goalData.period === 'monthly') {
        targetAmount = targetAmount * 12;
      } else if (goalData.period === 'weekly') {
        targetAmount = targetAmount * 52;
      }
      break;
    }
    case 'thisWeek':
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      periodStart = startOfWeek;
      periodEnd = new Date(startOfWeek);
      periodEnd.setDate(startOfWeek.getDate() + 6);
      periodEnd.setHours(23, 59, 59, 999);
      periodLabel = 'This Week';

      // Adjust target if goal was set for different period
      if (goalData.period === 'monthly') {
        targetAmount = targetAmount / 4.33; // Convert monthly to weekly
      } else if (goalData.period === 'yearly') {
        targetAmount = targetAmount / 52; // Convert yearly to weekly
      }
      break;

    case 'lastWeek':
      const startOfLastWeek = new Date(now);
      startOfLastWeek.setDate(now.getDate() - now.getDay() - 7);
      startOfLastWeek.setHours(0, 0, 0, 0);
      periodStart = startOfLastWeek;
      periodEnd = new Date(startOfLastWeek);
      periodEnd.setDate(startOfLastWeek.getDate() + 6);
      periodEnd.setHours(23, 59, 59, 999);
      periodLabel = 'Last Week';

      // Adjust target if goal was set for different period
      if (goalData.period === 'monthly') {
        targetAmount = targetAmount / 4.33;
      } else if (goalData.period === 'yearly') {
        targetAmount = targetAmount / 52;
      }
      break;

    case 'thisMonth':
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      periodLabel = 'This Month';

      // Adjust target if goal was set for different period
      if (goalData.period === 'weekly') {
        targetAmount = targetAmount * 4.33; // Convert weekly to monthly
      } else if (goalData.period === 'yearly') {
        targetAmount = targetAmount / 12; // Convert yearly to monthly
      }
      break;

    case 'lastMonth':
      periodStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      periodEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      periodLabel = 'Last Month';

      // Adjust target if goal was set for different period
      if (goalData.period === 'weekly') {
        targetAmount = targetAmount * 4.33;
      } else if (goalData.period === 'yearly') {
        targetAmount = targetAmount / 12;
      }
      break;

    case 'thisYear':
      periodStart = new Date(now.getFullYear() - 1, 0, 1);
      periodEnd = new Date(now.getFullYear() - 1, 11, 31);
      periodLabel = 'Last Year';

      // Adjust target if goal was set for different period
      if (goalData.period === 'weekly') {
        targetAmount = targetAmount * 52; // Convert weekly to yearly
      } else if (goalData.period === 'monthly') {
        targetAmount = targetAmount * 12; // Convert monthly to yearly
      }
      break;

    case 'lastYear':
      periodStart = new Date(now.getFullYear() - 1, 0, 1);
      periodEnd = new Date(now.getFullYear() - 1, 11, 31);
      periodLabel = 'Last Year';

      // Adjust target if goal was set for different period
      if (goalData.period === 'weekly') {
        targetAmount = targetAmount * 52;
      } else if (goalData.period === 'monthly') {
        targetAmount = targetAmount * 12;
      }
      break;

    default:
      // For custom date ranges or other filters, fall back to using filtered transactions
      // but keep original goal period logic
      if (goalData.period === 'weekly') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        periodStart = startOfWeek;
        periodEnd = new Date(startOfWeek);
        periodEnd.setDate(startOfWeek.getDate() + 6);
        periodEnd.setHours(23, 59, 59, 999);
        periodLabel = 'Weekly';
      } else if (goalData.period === 'yearly') {
        periodStart = new Date(now.getFullYear(), 0, 1);
        periodEnd = new Date(now.getFullYear(), 11, 31);
        periodLabel = 'Yearly';
      } else {
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
        periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        periodLabel = 'Monthly';
      }
      break;
  }
} else {
  // Use original goal period logic
  targetAmount = parseFloat(goalData.amount);

  if (goalData.period === 'weekly') {
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    periodStart = startOfWeek;
    periodEnd = new Date(startOfWeek);
    periodEnd.setDate(startOfWeek.getDate() + 6);
    periodEnd.setHours(23, 59, 59, 999);
    periodLabel = 'Weekly';
  } else if (goalData.period === 'yearly') {
    periodStart = new Date(now.getFullYear(), 0, 1);
    periodEnd = new Date(now.getFullYear(), 11, 31);
    periodLabel = 'Yearly';
  } else {
    // Monthly
    periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    periodLabel = 'Monthly';
  }
}

// Use getFilteredTransactions when available, otherwise filter manually (net = income - expenses)
const computeNet = (list = []) => list.reduce((sum, t) => {
  const amt = parseFloat(t.amount) || 0;
  const txType = (t.type || '').toLowerCase();
  if (txType === 'income') return sum + amt;
  if (txType === 'expense' || txType === 'expenses') return sum - amt;
  return sum;
}, 0);

const currentNetIncome = (() => {
  if (shouldUseFilterTimeframe && Array.isArray(getFilteredTransactions)) {
    // If we're using a filter timeframe and have filtered transactions, use those
    return computeNet(getFilteredTransactions);
  } else {
    // Otherwise, filter transactions by the calculated period
    const scoped = Array.isArray(transactions)
      ? transactions.filter(t => {
          const transactionDate = getTransactionDateValue(t);
          return transactionDate ? transactionDate >= periodStart && transactionDate <= periodEnd : false;
        })
      : [];
    return computeNet(scoped);
  }
})();

const progressPercentage = targetAmount > 0 ? (currentNetIncome / targetAmount) * 100 : 0;
const isOnTrack = progressPercentage >= 50; // Simple on-track logic

return (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-600">
        {periodLabel} Target
      </span>
      <span className="text-xs font-medium">
        {formatCurrency(currentNetIncome)} / {formatCurrency(targetAmount)}
      </span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${
          isOnTrack ? 'bg-[color:var(--color-accent)]' : 'bg-[color:var(--color-info)]'
        }`}
        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
      ></div>
    </div>
    <div className="flex justify-between items-center">
      <span className="text-xs text-[color:var(--color-text)]">
        {progressPercentage.toFixed(1)}% complete
      </span>
      <span className={`text-xs font-medium ${
        isOnTrack ? 'text-[color:var(--color-text)]' : 'text-yellow-600'
      }`}>
        {isOnTrack ? '? On track' : 'Behind'}
      </span>
    </div>
    {/* Show adjustment note when target was converted */}
    {shouldUseFilterTimeframe && selectedTimeframe !== goalData.period && (
      <div className="text-xs text-gray-400 italic">
        Target adjusted from {goalData.period} goal
      </div>
    )}
  </div>
);
                      })()}
                    </div>

                    {/* Most Popular Services */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('popularServices')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs sm:text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.popularServices}
                        aria-label="Toggle Most Popular Services panel"
                      >
                        <span>Most Popular Services</span>
                        {analyticsPanels.popularServices ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.popularServices && (
                      <div className="space-y-2 px-3 pb-3 pt-1">
                        {(() => {
                          const serviceTotals = {};
                          if (Array.isArray(getFilteredTransactions)) {
                            getFilteredTransactions
                              .filter(t => t.type === 'income') // Only income transactions (services)
                              .forEach(transaction => {
                              const categoryEntries = getTransactionCategoryEntries(transaction);
                              const normalizedEntries = categoryEntries.length > 0
                                ? categoryEntries
                                : [{
                                    category: transaction.category || '',
                                    amount: parseFloat(transaction.amount) || 0
                                  }];

                              normalizedEntries.forEach((entry) => {
                                if (!entry.category) return;
                                const categoryName = getCategoryDisplayLabel(entry.category);
                                if (!serviceTotals[categoryName]) {
                                  serviceTotals[categoryName] = {
                                    name: categoryName,
                                    total: 0,
                                    count: 0,
                                    icon: getCategoryIconForId(entry.category)
                                  };
                                }
                                serviceTotals[categoryName].total += parseFloat(entry.amount) || 0;
                                serviceTotals[categoryName].count += 1;
                              });
                            });
                          }

                          const sortedServices = Object.values(serviceTotals)
                            .sort((a, b) => b.count - a.count) // Sort by frequency, not amount
                            .slice(0, 4);

                          const maxCount = sortedServices[0]?.count || 1;

                          return sortedServices.map((service, index) => (
                            <div key={index} className="flex items-center gap-2">
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-[color:var(--color-text)] truncate">
                                    {service.name}
                                  </span>
                                  <span className="text-xs font-bold text-[color:var(--color-text)] ml-2">
                                    {service.count}x
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                  <div
                                    className="h-1.5 rounded-full bg-[color:var(--color-accent)]"
                                    style={{ width: `${(service.count / maxCount) * 100}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-[color:var(--color-text)] mt-0.5">
                                  {formatCurrency(service.total)} total
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                      )}
                    </div>

                    {/* Biggest Expenses */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('biggestExpenses')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs sm:text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.biggestExpenses}
                        aria-label="Toggle Biggest Expenses panel"
                      >
                        <span>Biggest Expenses</span>
                        {analyticsPanels.biggestExpenses ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.biggestExpenses && (
                      <div className="space-y-2 px-3 pb-3 pt-1">
                        {(() => {
                          const expenseTotals = {};
                          if (Array.isArray(getFilteredTransactions)) {
                            getFilteredTransactions
                              .filter(t => t.type === 'expenses') // Only expense transactions
                              .forEach(transaction => {
                              const categoryName = CATEGORIES.expenses?.find(c => c.id === transaction.category)?.name || transaction.category;
                              if (!expenseTotals[categoryName]) {
                                expenseTotals[categoryName] = {
                                  name: categoryName,
                                  total: 0,
                                  icon: CATEGORIES.expenses?.find(c => c.id === transaction.category)?.icon || ''
                                };
                              }
                              expenseTotals[categoryName].total += parseFloat(transaction.amount);
                            });
                          }

                          const sortedExpenses = Object.values(expenseTotals)
                            .sort((a, b) => b.total - a.total) // Sort by amount
                            .slice(0, 4);

                          const maxTotal = sortedExpenses[0]?.total || 1;

                          return sortedExpenses.map((expense, index) => (
                            <div key={index} className="flex items-center gap-2">
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-[color:var(--color-text)] truncate">
                                    {expense.name}
                                  </span>
                                  <span className="text-xs font-bold text-[color:var(--color-text)] ml-2">
                                    {formatCurrency(expense.total)}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                  <div
                                    className="h-1.5 rounded-full bg-[color:var(--color-primary)]"
                                    style={{ width: `${(expense.total / maxTotal) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                      )}
                    </div>

                    {/* Adjusted Net Profit - Compact */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('adjustedProfit')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs sm:text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.adjustedProfit}
                        aria-label="Toggle Adjusted Net Profit panel"
                      >
                        <span>Adjusted Net Profit</span>
                        {analyticsPanels.adjustedProfit ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.adjustedProfit && (
                      <div className="text-center px-3 pb-3 pt-1">
                        {(() => {
                          const isSalesTaxCountry = currentCountry?.taxSystem === 'sales';
                          const taxExcludedFromTurnover = Math.max(0, Number(vatPositionForSelectedTransactions.vatCollected || 0));
                          const taxExcludedLabel = isSalesTaxCountry
                            ? 'Sales tax excluded from turnover'
                            : 'VAT excluded from turnover';
                          const currentOutstandingVat = Number(vatOutstandingPositionForSelectedTransactions.netOutstanding || 0);
                          const hasVatSettlementDifference = Math.abs(currentOutstandingVat) > 0.01;
                          const currentVatLabel = currentOutstandingVat < 0
                            ? (isSalesTaxCountry ? 'Sales tax reclaim still due' : 'VAT reclaim still due')
                            : (isSalesTaxCountry ? 'Sales tax still outstanding' : 'VAT still outstanding');

                          return (
                            <>
                              <div className="text-2xl font-bold text-[color:var(--color-text)] mb-2">
                                {formatCurrency(taxableAdjustedNetProfit)}
                              </div>
                              <div className="text-xs text-[color:var(--color-text)] space-y-1">
                                <div className="flex justify-between">
                                  <span>Income (Gross):</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(totals.income)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{taxExcludedLabel}:</span>
                                  <span className="text-[color:var(--color-text)]">-{formatCurrency(taxExcludedFromTurnover)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Net Sales Turnover:</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(totals.taxableIncome)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Expenses (Gross):</span>
                                  <span className="text-[color:var(--color-text)]">-{formatCurrency(totals.expenses)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{isSalesTaxCountry ? 'Sales tax reclaimed on expenses' : 'VAT reclaimed on expenses'}:</span>
                                  <span className="text-[color:var(--color-text)]">+{formatCurrency(Math.max(0, Number(vatPositionForSelectedTransactions.vatPaid || 0)))}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Deductible Expenses:</span>
                                  <span className="text-[color:var(--color-text)]">-{formatCurrency(totals.deductibleExpenses)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Book Depreciation:</span>
                                  <span className="text-[color:var(--color-text)]">-{formatCurrency(totalAnnualDepreciation)}</span>
                                </div>
                                {hasVatSettlementDifference && (
                                  <div className="flex justify-between text-gray-500">
                                    <span>{currentVatLabel}:</span>
                                    <span>{formatCurrency(Math.abs(currentOutstandingVat))}</span>
                                  </div>
                                )}
                                <div className="flex justify-between font-medium border-t pt-1 mt-1">
                                  <span>Adjusted Net Profit Position:</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(taxableAdjustedNetProfit)}</span>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      )}
                    </div>

                    {/* Tax Liability Widget - NEW ADDITION */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleAnalyticsPanel('estimatedTax')}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold text-[color:var(--color-text)] rounded-t-lg bg-warn-gradient"
                        aria-expanded={analyticsPanels.estimatedTax}
                        aria-label="Toggle Estimated Tax panel"
                      >
                        <span>Estimated Tax Liability</span>
                        {analyticsPanels.estimatedTax ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {analyticsPanels.estimatedTax && (
                      <div className="text-center px-3 pb-3 pt-1">
                        {(() => {
                          const incomeTaxTotal = estimatedDirectTaxTotal;
                          const vatLiability = Math.max(0, vatOutstandingPositionForSelectedTransactions.netOutstanding || 0);
                          const capitalGainsTax = Math.max(0, taxSummary.capitalGainsTax || 0);
                          const totalTaxLiability = incomeTaxTotal + vatLiability + capitalGainsTax;
                          const effectiveRateBase = Math.max(0, Number(taxableAdjustedNetProfit) || 0);

                          return (
                            <>
                              <div className="text-2xl font-bold text-[color:var(--color-text)] mb-2">
                                {formatCurrency(totalTaxLiability)}
                              </div>
                              <div className="text-xs text-[color:var(--color-text)] space-y-1">
                                <div className="flex justify-between">
                                  <span>{directTaxLabel}:</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(incomeTaxTotal)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{countryTerminology.taxName} Due (current):</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(vatLiability)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Capital Gains/Loss Tax:</span>
                                  <span className="text-[color:var(--color-text)]">{formatCurrency(capitalGainsTax)}</span>
                                </div>
                                <div className="flex justify-between text-xs border-t pt-1 mt-1">
                                  <span>Effective Rate:</span>
                                  <span className="text-gray-600">
                                    {effectiveRateBase > 0
                                      ? `${((totalTaxLiability / effectiveRateBase) * 100).toFixed(1)}%`
                                      : `${((taxSummary.effectiveTaxRate || 0) * 100).toFixed(1)}%`}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-400 italic">
                                Estimate only
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      )}
                    </div>

                          </>
                        );
                      } else {
                        return (
                          <div className="col-span-full">
                            <div className="bg-white rounded-lg shadow-sm border border-dashed border-[color:var(--color-accent)] p-6 text-center">
                              <h4 className="text-lg font-semibold text-[color:var(--color-text)] mb-2">Unlock Business Analytics</h4>
                              <p className="text-sm text-gray-600 mb-4">
                                {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                                  ? 'Checking this client workspace plan before loading analytics.'
                                  : isExternalSharedWorkspace
                                  ? 'This client workspace needs Premium before accountants can use business analytics and detailed tax insights.'
                                  : 'Upgrade to Premium to view revenue vs expenses, goal tracking, popular services, and detailed tax insights.'}
                              </p>
                              {!isExternalSharedWorkspace && (
                                <button
                                  onClick={handleUpgradeClick}
                                  className="inline-flex items-center justify-center px-4 py-2 bg-[color:var(--color-accent)] text-[color:var(--color-text)] font-semibold rounded-full shadow-sm hover:brightness-95 transition"
                                >
                                  {trialUsedFlag
                                    ? 'Upgrade to Premium'
                                    : (user && user.email ? 'Start Free Trial' : 'Sign Up for Premium')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      }
                      })()}
                      </div>
                    )}
                  </div>
                </div>


              </div>
            </div>
            )}

            {/* Show Voice Input if toggled */}
            {showVoiceInput && (
              <div className="mb-6">
                <VoiceInput
                  onTransactionParsed={handleVoiceTransactionParsed}
                  onClose={() => setShowVoiceInput(false)}
                  className="max-w-md mx-auto"
                  debug={false}
                  categories={CATEGORIES}
                  paymentMethods={PAYMENT_METHODS}
                  defaultType={activeTab === 'income' ? 'income' : 'expenses'}
                />
              </div>
            )}

            {/* Add New Service Form */}
            {isEditingPrices && (
             <div className="p-3 border rounded-lg bg-gray-50 space-y-2">
                <h4 className="font-medium">Add New Service</h4>
                <input
                  type="text"
                  value={newService.name}
                  onChange={(e) => setNewService({...newService, name: e.target.value})}
                  className="w-full p-2 border rounded mb-2"
                  placeholder="Service name"
                />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500">Duration (min)</label>
                    <input
                      type="number"
                      value={newService.duration}
                      onChange={(e) => setNewService({
                        ...newService,
                        duration: e.target.value === '' ? '' : parseInt(e.target.value) || 0
                      })}
                      className="w-full p-2 border rounded"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Cost ({currency?.symbol || '$'})</label>
                    <input
                      type="number"
                      value={newService.cost}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                        setNewService({
                          ...newService,
                          cost: value
                        });
                      }}
                      className="w-full p-2 border rounded"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Price ({currency?.symbol || '$'})</label>
                    <input
                     type="number"
                     value={newService.price}
                     onChange={(e) => setNewService({
                       ...newService,
                       price: e.target.value === '' ? '' : parseFloat(e.target.value) || 0
                     })}
                     className="w-full p-2 border rounded"
                     min="0"
                     step="0.01"
                    />
                  </div>
                </div>
                <button
                  onClick={() => handleSaveService(selectedCategory)}
                  disabled={!newService.name.trim()}
                  className={`w-full mt-2 px-4 py-2 rounded-lg font-semibold ${
                    newService.name.trim()
                      ? 'bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add Service
                </button>
              </div>
            )}

            {/* Right Column - Selected Service Details */}
            <div>
              {selectedService ? (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="text-xl font-semibold mb-3">{selectedService.name}</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="text-xl font-medium">{selectedService.duration} min</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Price</div>
                      <div className="text-xl font-medium text-[color:var(--color-text)]">
                        {formatCurrency(selectedService.price)}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Cost</div>
                      <div className="text-xl font-medium text-[color:var(--color-text)]">
                        {formatCurrency(selectedService.cost)}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Profit</div>
                      <div className="text-xl font-medium text-[color:var(--color-text)]">
                        {formatCurrency(selectedService.price - selectedService.cost)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Profit Margin</div>
                      <div className="text-2xl font-medium text-[color:var(--color-text)]">
                        {((selectedService.price - selectedService.cost) / selectedService.price * 100).toFixed(1)}%
                      </div>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Hourly Rate</div>
                      <div className="text-2xl font-medium text-indigo-600">
                        {formatCurrency((selectedService.price / selectedService.duration) * 60)}/hr
                      </div>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Revenue Goal Analysis</div>
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">To earn {formatCurrency(1000)}:</span>
                          <span className="font-medium">{Math.ceil(1000 / (selectedService.price - selectedService.cost))} services</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">To earn {formatCurrency(5000)}:</span>
                          <span className="font-medium">{Math.ceil(5000 / (selectedService.price - selectedService.cost))} services</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">To earn {formatCurrency(10000)}:</span>
                          <span className="font-medium">{Math.ceil(10000 / (selectedService.price - selectedService.cost))} services</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setActiveTab("income");
                      setSelectedInputMethod('manual');
                      setIsAddingTransaction(true);
                      resetSplitSaleState();
                      resetSplitPaymentState();
                      setCurrentTransaction(createEmptyTransactionDraft('income', {
                        category: selectedCategory,
                        description: selectedService.name,
                        amount: selectedService.price,
                        discountAmount: ''
                      }));
                    }}
                    className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Add as Transaction
                  </button>
                </div>
              ) : (
                <div className="hidden">
                  {/* Empty div that doesn't display anything */}
                </div>
              )}
            </div>

            {/* Dashboard Sections Toggle Bar */}
            {industryId === DEFAULT_INDUSTRY_ID && (
              <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Services provided</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedServiceOfferingLabels.length
                        ? `Showing categories for ${selectedServiceOfferingLabels.join(', ')}.`
                        : 'Showing all Beauty & Wellness categories.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowServicePreferencesEditor((prev) => !prev)}
                    className="self-start sm:self-auto inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    aria-expanded={showServicePreferencesEditor}
                  >
                    {showServicePreferencesEditor ? 'Close' : 'Manage services'}
                  </button>
                </div>

                {showServicePreferencesEditor && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        Choose the services you currently provide. This updates sales and expense category lists immediately.
                      </p>
                      {selectedServiceOfferings.length > 0 && (
                        <button
                          type="button"
                          onClick={resetServiceOfferingPreferences}
                          className="text-xs font-medium text-[color:var(--color-text)] hover:underline whitespace-nowrap"
                        >
                          Show all services
                        </button>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {SERVICE_OFFERING_OPTIONS.map((option) => {
                        const checked = selectedServiceOfferings.includes(option.id);
                        const isOnlySelected = checked && selectedServiceOfferings.length === 1;
                        return (
                          <label
                            key={option.id}
                            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                              checked
                                ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/10 text-[color:var(--color-text)]'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                            title={isOnlySelected ? 'Select another service before removing this one, or use Show all services.' : option.label}
                          >
                            <span className="font-medium">{option.label}</span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleServiceOfferingPreference(option.id)}
                              className="h-4 w-4"
                            />
                          </label>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowServicePreferencesEditor(false)}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
                    >
                      <X size={14} />
                      Close services editor
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              {(() => {
                const sectionButtons = [
                  { key: 'taxCalculation', label: 'Tax Calc', icon: Calculator },
                  { key: 'pricingCalculator', label: 'Pricing', icon: BarChart2 },
                  { key: 'budget', label: 'Budget', icon: Target },
                  { key: 'aiAssistant', label: 'AI Assist', icon: Sparkles },
                  { key: 'categoryTotals', label: 'Categories', icon: BarChart2 },
                  { key: 'capitalReport', label: 'Capital', icon: Scale },
                  { key: 'paymentMethodTotals', label: 'Payments', icon: Calculator },
                  { key: 'recentTransactions', label: 'Recent', icon: Upload },
                  { key: 'staff', label: 'Staff', icon: Users },
                  { key: 'clients', label: 'Clients', icon: Users },
                  { key: 'suppliers', label: 'Suppliers', icon: Users },
                  ...(supportsProjects ? [{ key: 'projects', label: 'Projects', icon: Briefcase }] : []),
                  ...(hasPremiumAnalyticsAccess || isExternalSharedWorkspace
                    ? [{ key: 'accountantWorkspace', label: 'Accountant', icon: FileText }]
                    : [])
                ];

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {sectionButtons.map(({ key, label, icon: Icon }) => {
                      const isVisible = visibleSections[key];
                      return (
                        <button
                          key={key}
                          onClick={() => toggleSection(key)}
                          className={`w-full px-3 py-3 sm:py-4 rounded-xl border text-[color:var(--color-text)] shadow-sm transition transform hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 ${
                            isVisible
                              ? 'bg-info-gradient border-transparent shadow-md'
                              : 'bg-info-gradient border-transparent'
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1">
                            {Icon && <Icon size={18} className="opacity-80" />}
                            <span className="text-sm font-semibold sm:text-base">{label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Two Column Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Column - Main Content */}
              <div className="w-full lg:w-8/12 space-y-6">



              </div>

              {/* Right Column - Sidebars */}
              <div className="w-full lg:w-4/12 space-y-6">

</div>


</div> {/* Closing tag for the right column */}

          {/* Full-Width Dashboard Grid - NEW LAYOUT */}
          <div className="grid grid-cols-1 gap-6 mt-8">

            
            {/* Tax Calculation Section */}
            {visibleSections.taxCalculation && (
              <AdvancedReportsGate
                hasAccessOverride={workspaceAdvancedReportsAccess}
                featureDescription="Upgrade to Premium to unlock automated tax estimates and VAT calculations."
                fallback={
                  <div className="bg-white rounded-lg shadow-sm border border-dashed border-[color:var(--color-accent)] p-6 text-center">
                    <h3 className="text-lg font-semibold text-[color:var(--color-text)] mb-2">Tax Calculation (Premium)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                        ? 'Checking this client workspace plan before loading tax tools.'
                        : isExternalSharedWorkspace
                        ? 'This client workspace needs Premium before accountants can use tax estimates, VAT breakdowns, and capital allowance helpers.'
                        : 'Access smart tax estimates, VAT breakdowns, and capital allowance helpers with a Premium plan.'}
                    </p>
                    {!isExternalSharedWorkspace && (
                      <button
                        onClick={handleUpgradeClick}
                        className="inline-flex items-center justify-center px-4 py-2 bg-[color:var(--color-accent)] text-[color:var(--color-text)] font-semibold rounded-full shadow-sm hover:brightness-95 transition"
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                }
              >
                <div id="tax-section" className="w-full">
                  <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        {isLimitedCompany ? 'Corporation Tax Calculation' : 'Tax Calculation'}
                        <span className="ml-2 text-sm text-gray-500">({currentCountry.name}, {businessTypeLabel})</span>
                      </h3>
                      <button
                        onClick={() => toggleSection('taxCalculation')}
                        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                      >
                        <X size={16} />
                        Close
                      </button>
                    </div>
                    <div className="p-6">
                      <TaxCalculation
                        transactions={getFilteredTransactions}
                        allTransactions={transactions}
                        updatedTransactions={updatedTransactions}
                        totals={totals}
                        adjustedNetProfit={taxableAdjustedNetProfit}
                        countryName={currentCountry.name}
                        businessType={businessType}
                        taxYear={activeFinancialYear}
                        isCapitalExpense={isCapitalExpense}
                        categories={transactionInputCategories}
                        rctTracker={rctTracker}
                        supportsRct={supportsRct}
                        buildTransactionVatEntries={buildTransactionVatEntries}
                        categoryVatRateRows={categoryVatRateRows}
                        user={user}
                        handleGoogleSignIn={handleGoogleSignIn}
                        onTaxSummaryChange={setTaxSummary}
                        openVatSectionSignal={openVatSectionSignal}
                      />
                    </div>
                  </div>
                </div>
              </AdvancedReportsGate>
            )}

            {/* Accountant Workspace Section */}
            {visibleSections.accountantWorkspace && (
              <AdvancedReportsGate
                hasAccessOverride={workspaceAdvancedReportsAccess}
                featureDescription="Upgrade to Premium to unlock accountant-grade reports and compliance workspace tools."
                fallback={
                  <div className="bg-white rounded-lg shadow-sm border border-dashed border-[color:var(--color-accent)] p-6 text-center">
                    <h3 className="text-lg font-semibold text-[color:var(--color-text)] mb-2">Accountant Workspace (Premium)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                        ? 'Checking this client workspace plan before loading accountant reports.'
                        : isExternalSharedWorkspace
                        ? 'This client workspace needs Premium before accountants can use trial balance exports and compliance preparation tools.'
                        : 'Access trial balance exports, accountant review tools, and compliance pack preparation with a Premium plan.'}
                    </p>
                    {!isExternalSharedWorkspace && (
                      <button
                        onClick={handleUpgradeClick}
                        className="inline-flex items-center justify-center px-4 py-2 bg-[color:var(--color-accent)] text-[color:var(--color-text)] font-semibold rounded-full shadow-sm hover:brightness-95 transition"
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                }
              >
                <AccountantWorkspacePanel
                  transactions={transactions}
                  assetTransactions={updatedTransactions}
                  bankAccounts={bankAccounts}
                  journalEntries={journalEntries}
                  taxYear={activeFinancialYear}
                  taxSummary={taxSummary}
                  businessType={businessType}
                  businessProfile={buildBusinessProfileSettings(businessType, industryId, businessProfileDetails)}
                  countryId={currentCountry?.id}
                  formatCurrency={formatCurrency}
                  getTransactionVatAmount={getTransactionVatAmount}
                  isCapitalExpense={isCapitalExpense}
                  onAssetRegisterChange={handleAccountantAssetUpdate}
                  onBankAccountsChange={(nextBankAccounts) => setBankAccounts(normalizeBankAccounts(nextBankAccounts))}
                  onJournalEntriesChange={(nextJournalEntries) => setJournalEntries(normalizeJournalEntries(nextJournalEntries))}
                  onClose={() => toggleSection('accountantWorkspace')}
                />
              </AdvancedReportsGate>
            )}

            {/* Pricing Calculator Section */}
            {visibleSections.pricingCalculator && (
              <AdvancedReportsGate
                hasAccessOverride={workspaceAdvancedReportsAccess}
                featureDescription="Upgrade to Premium to access pricing, cost, and overhead calculators."
                fallback={
                  <div className="bg-white rounded-lg shadow-sm border border-dashed border-[color:var(--color-accent)] p-6 text-center">
                    <h3 className="text-lg font-semibold text-[color:var(--color-text)] mb-2">Pricing Calculator (Premium)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                        ? 'Checking this client workspace plan before loading pricing tools.'
                        : isExternalSharedWorkspace
                        ? 'This client workspace needs Premium before accountants can use pricing, cost, and overhead calculators.'
                        : 'Model profitable prices with cost and overhead calculators when you upgrade to Premium.'}
                    </p>
                    {!isExternalSharedWorkspace && (
                      <button
                        onClick={handleUpgradeClick}
                        className="inline-flex items-center justify-center px-4 py-2 bg-[color:var(--color-accent)] text-[color:var(--color-text)] font-semibold rounded-full shadow-sm hover:brightness-95 transition"
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                }
              >
                <div id="pricing-section" className="w-full">
                  <div className="rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-accent-gradient">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg font-semibold text-[color:var(--color-text)]">Pricing Calculator</h3>
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full sm:w-auto">
                          <button
                            onClick={() => setShowCostModal(true)}
                            className="w-full sm:w-auto px-3 py-1.5 rounded-full bg-warn-gradient border border-black text-[color:var(--color-text)] shadow-sm hover:shadow-md btn-float text-sm"
                          >
                            Cost Calculator
                          </button>
                          <button
                            onClick={() => setShowOverheadModal(true)}
                            className="w-full sm:w-auto px-3 py-1.5 rounded-full bg-warn-gradient border border-black text-[color:var(--color-text)] shadow-sm hover:shadow-md btn-float text-sm"
                          >
                            Overhead Calculator
                          </button>
                          <button
                            onClick={() => toggleSection('pricingCalculator')}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-1 text-sm text-[color:var(--color-text)]/80 hover:text-[color:var(--color-text)] transition"
                          >
                            <X size={16} />
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 bg-white">
                      <PricingCalculator
                        servicePrices={servicePrices}
                        setServicePrices={setServicePrices}
                        CATEGORIES={CATEGORIES}
                        incomeCategories={filteredIncomeCategories}
                        onAddIncomeCategory={() => handleAddCategory('income')}
                        costSummary={costSummary}
                        costItems={costItems}
                        setCostItems={setCostItems}
                        onOpenCostModal={() => setShowCostModal(true)}
                        targetIncome={normalizedTargetIncome}
                        businessProfitHourlyTarget={businessProfitHourlyTarget}
                        weeklyOpenHours={weeklyGoalHours}
                        businessType={businessType}
                        currentCountry={currentCountry}
                        goalHourlyRate={pricingLaborHourlyCost}
                        labourProfiles={targetIncomeData.labourProfiles || []}
                        overheadHourly={overheadHourly}
                        isPremium={hasPremiumAnalyticsAccess}
                        onUpgrade={handleUpgradeClick}
                        onOpenOverheadModal={() => setShowOverheadModal(true)}
                        vatSettings={vatSettings}
                        onSeedMakeupTemplates={seedMakeupServicePresets}
                        onDeleteServiceCategory={handleDeleteServiceCategory}
                        showMakeupTemplates={showMakeupTemplates}
                        industryId={industryId}
                        pricingTerminology={industryProfile.pricingTerminology}
                        industryServicePresets={industryProfile.servicePresets}
                        projectSummaries={projectSummaries}
                        supportsProjects={supportsProjects}
                      />
                    </div>
                  </div>
                </div>
              </AdvancedReportsGate>
            )}

            {/* Budget Section */}
            {visibleSections.budget && (
              <div id="budget-section" className="w-full">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                    <h3 className={`text-lg font-medium text-gray-900 flex items-center ${budgetPulse ? 'animate-pulse' : ''}`}>
                      Budget
                    </h3>
                    <button
                      onClick={() => toggleSection('budget')}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                    >
                      <X size={16} />
                      Close
                    </button>
                  </div>
                  <div className="p-6">
                    <BudgetSection
                      transactions={transactions}
                      CATEGORIES={CATEGORIES}
                      servicePrices={servicePrices}
                      setServicePrices={setServicePrices}
                      targetIncomeData={targetIncomeData}
                      onGoalSaved={handleTargetIncomeSave}
                      businessType={businessType}
                      currentCountry={currentCountry}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* AI Assistant Section */}
            {visibleSections.aiAssistant && (
              <div id="ai-section" className="w-full">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      AI Assistant
                    </h3>
                    <button
                      onClick={() => toggleSection('aiAssistant')}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                    >
                      <X size={16} />
                      Close
                    </button>
                  </div>
                  <div className="p-4 sm:p-6">
                    <AIHelper
                      transactions={transactions}
                      targetIncomeData={targetIncomeData}
                      formatCurrency={formatCurrency}
                      currentPage="aiAssistant"
                      currentCountry={currentCountry}
                      businessType={businessType}
                      vatSettings={vatSettings}
                      pricingLaborHourlyCost={pricingLaborHourlyCost}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Category Totals Section */}
            {visibleSections.categoryTotals && (
              <div id="category-totals-section" className="w-full">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                    <h3 className="text-lg font-medium text-gray-900">Category Totals</h3>
                    <button
                      onClick={() => toggleSection('categoryTotals')}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                    >
                      <X size={16} />
                      Close
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      {!getCategoryTotals || Object.entries(getCategoryTotals).length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-3xl mb-2">??</div>
                          <p className="text-sm">Your category breakdown will appear here once you add transactions</p>
                          <p className="text-xs mt-1 text-gray-400">Track income and expenses by category</p>
                        </div>
                      ) : (
                        Object.entries(getCategoryTotals).map(([category, data]) => {
                        const netAmount = data.income - data.expenses;
                        return (
                          <div key={category} className="flex justify-between bg-gray-50 rounded-lg p-3">
                            <span className="font-medium text-sm">
                              {getCategoryDisplayLabel(category)}
                            </span>
                            <span className={`font-medium text-sm ${
                              netAmount > 0 ? 'text-[color:var(--color-text)]' :
                              netAmount < 0 ? 'text-black' :
                              'text-gray-600'
                            }`}>
                              {formatCurrency(netAmount)}
                            </span>
                          </div>
                        );
                        }))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Full-Width Capital Report */}
            {visibleSections.capitalReport && (
            <div id="capital-report-section" className="w-full bg-white rounded-xl p-6">
  <div className="flex items-start gap-3">
    <button
      onClick={() => setShowCapitalExpenses(!showCapitalExpenses)}
      className="w-full text-left flex justify-between items-center bg-white/40 rounded-lg px-4 py-2"
    >
      <h2 className="text-xl font-semibold text-gray-800">Capital Report</h2>
      <span className="text-gray-600">
        {showCapitalExpenses ? '?' : '?'}
      </span>
    </button>
    <button
      onClick={() => toggleSection('capitalReport')}
      className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition"
    >
      <X size={16} />
      Close
    </button>
  </div>

  {showCapitalExpenses && (
    <div className="mt-4 space-y-6">
      {/* Asset Sales Details */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
          Asset Sales Details
        </h3>

        {(() => {
          const assetSales = updatedTransactions.filter(
            (t) => t.type === "income" && t.category === "assetSale"
          );

          if (assetSales.length === 0) {
            return (
              <p className="text-gray-500 text-sm italic">
                No asset sales recorded yet.
              </p>
            );
          }

          return (
            <table className="w-full text-sm border-t border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">Asset</th>
                  <th className="text-right p-2">Purchase Price</th>
                  <th className="text-right p-2">Book Value</th>
                  <th className="text-right p-2">Sale Price</th>
                  <th className="text-right p-2">Gain / Loss</th>
                </tr>
              </thead>
              <tbody>
                {assetSales.map((sale, index) => {
                  const asset = updatedTransactions.find(
                    (a) => a.id === sale.relatedAssetId
                  );
                  if (!asset) return null;

                  const purchasePrice = parseFloat(asset.amount);
                  const salePrice = parseFloat(sale.amount);
                  const gainLoss = salePrice - asset.currentValue;

                  return (
                    <tr
                      key={index}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="p-2">{asset.description || asset.category}</td>
                      <td className="text-right p-2">{formatCurrency(purchasePrice)}</td>
                      <td className="text-right p-2">{formatCurrency(asset.currentValue)}</td>
                      <td className="text-right p-2">{formatCurrency(salePrice)}</td>
                      <td
                        className={`text-right p-2 ${
                          gainLoss >= 0 ? "text-[color:var(--color-text)]" : "text-[color:var(--color-text)]"
                        }`}
                      >
                        {formatCurrency(gainLoss)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })()}
      </div>

      {/* Capital Assets List */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
          Capital Assets
        </h3>

        {(() => {
          const capitalTransactions = updatedTransactions.filter((t) =>
            isCapitalExpense(t.category)
          );

          if (capitalTransactions.length === 0) {
            return (
              <p className="text-gray-500 text-sm italic">
                No capital assets recorded yet.
              </p>
            );
          }

          return (
            <div className="space-y-3">
              {capitalTransactions.map((transaction, index) => {
                const depreciationPercent = Math.min(
                  (transaction.yearsElapsed / transaction.depreciationYears) * 100,
                  100
                );

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-gray-800">
                        {transaction.description || transaction.category}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          transaction.isStillDepreciating
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {transaction.isStillDepreciating
                          ? "Depreciating"
                          : "Fully Depreciated"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600 mb-2">
                      <div>Purchased: {transaction.date}</div>
                      <div>Depreciation: {transaction.depreciationYears} yrs</div>
                      <div>
                        Annual Depreciation:{" "}
                        {formatCurrency(transaction.annualDepreciation)}
                      </div>
                      <div>
                        Accumulated:{" "}
                        {formatCurrency(transaction.accumulatedDepreciation)}
                      </div>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-[color:var(--color-accent)] h-2 rounded-full"
                        style={{ width: `${depreciationPercent}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">
                        Current Value:{" "}
                        <strong>{formatCurrency(transaction.currentValue)}</strong>
                      </span>

                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setEditingDepreciationId(transaction.id)
                          }
                          className="text-xs text-[color:var(--color-text)] hover:underline"
                        >
                          Edit Depreciation
                        </button>
                        <button
                          onClick={() => {
                            setDisposingAssetId(transaction.id);
                            setDisposalData({
                              ...disposalData,
                              disposalType: 'sold',
                              disposalDate: new Date().toISOString().split('T')[0],
                              disposalAmount: transaction.currentValue || transaction.amount || 0,
                              disposalReason: '',
                              transactionId: transaction.id,
                              originalAmount: transaction.amount,
                              currentValue: transaction.currentValue,
                            });
                          }}
                          className="text-xs text-[color:var(--color-text)] hover:underline"
                        >
                          Mark as Sold
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  )}
</div>
)}

            {/* Payment Method Totals Section */}
            {visibleSections.paymentMethodTotals && (
              <div id="payment-totals-section" className="w-full">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                    <h3 className="text-lg font-medium text-gray-900">Payment Method Totals</h3>
                    <button
                      onClick={() => toggleSection('paymentMethodTotals')}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                    >
                      <X size={16} />
                      Close
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {Object.entries(getPaymentMethodTotals).map(([method, data]) => {
                        const totalAmount = data.income - data.expenses;
                        return (
                          <div key={method} className="flex justify-between bg-gray-50 rounded-lg p-3">
                            <span className="font-medium text-sm">
                              {getPaymentMethodLabel(method)}
                            </span>
                            <span className={`font-medium text-sm ${
                              totalAmount > 0 ? 'text-[color:var(--color-text)]' :
                              totalAmount < 0 ? 'text-[color:var(--color-text)]' :
                              'text-gray-600'
                            }`}>
                              {formatCurrency(totalAmount)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Staff Section */}
            {visibleSections.staff && (
              <div id="staff-section" className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Staff</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Track actual hours worked, holiday taken, sick time, wages earned, and accumulated holiday pay.
                    </p>
                  </div>
                  <button
                    onClick={() => toggleSection('staff')}
                    className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                  >
                    <X size={16} />
                    Close
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Staff pay plan</h4>
                        <p className="mt-1 text-xs text-gray-500">
                          Add employees here so Budget, Pricing, analytics, and holiday pay tracking can use the same staff pay data.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveStaffPlan}
                        className="self-start rounded-lg bg-[color:var(--color-warn)] px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] hover:brightness-95 lg:self-auto"
                      >
                        Save staff plan
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Default employer loading %</label>
                        <input
                          type="number"
                          min="0"
                          value={staffPlanDraft.defaultEmployerCostPercent}
                          onChange={(e) => handleStaffPlanDefaultEmployerChange(e.target.value)}
                          className="w-full rounded-lg border bg-white p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Default holiday reserve %</label>
                        <input
                          type="number"
                          min="0"
                          value={staffPlanDraft.defaultHolidayPayPercent}
                          onChange={(e) => handleStaffPlanDefaultHolidayChange(e.target.value)}
                          className="w-full rounded-lg border bg-white p-2"
                        />
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Weekly staff labour cost</p>
                        <p className="mt-1 text-lg font-bold text-gray-900">{formatCurrency(staffPlanSummary.totalWeeklyLabourCost || 0)}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Weekly holiday reserve</p>
                        <p className="mt-1 text-lg font-bold text-gray-900">{formatCurrency(staffPlanSummary.totalWeeklyHolidayPayReserve || 0)}</p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      {(Array.isArray(staffPlanDraft.staffMembers) ? staffPlanDraft.staffMembers : []).map((member, index) => (
                        <div key={member.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 xl:grid-cols-[1.1fr_0.7fr_0.65fr_0.7fr_0.7fr_0.7fr_auto]">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Staff name</label>
                            <input
                              type="text"
                              value={member.name}
                              placeholder={`Staff ${index + 1}`}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'name', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Hours period</label>
                            <select
                              value={member.hoursPeriod}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'hoursPeriod', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            >
                              {STAFF_HOURS_PERIOD_OPTIONS.map((option) => (
                                <option key={option.id} value={option.id}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Hours</label>
                            <input
                              type="number"
                              min="0"
                              value={member.hours}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'hours', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Hourly pay</label>
                            <input
                              type="number"
                              min="0"
                              value={member.hourlyPay}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'hourlyPay', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Employer loading %</label>
                            <input
                              type="number"
                              min="0"
                              value={member.employerCostPercent}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'employerCostPercent', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Holiday reserve %</label>
                            <input
                              type="number"
                              min="0"
                              value={member.holidayPayPercent}
                              onChange={(e) => handleStaffPlanMemberChange(member.id, 'holidayPayPercent', e.target.value)}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => handleRemoveStaffPlanMember(member.id)}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                              title="Remove staff member"
                              aria-label="Remove staff member"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={handleAddStaffPlanMember}
                        className="inline-flex items-center gap-2 self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      >
                        <Plus size={16} />
                        Add staff member
                      </button>
                      {staffPlanSaveMessage && (
                        <p className={`text-sm ${staffPlanSaveMessage.includes('saved') ? 'text-green-700' : 'text-red-600'}`}>
                          {staffPlanSaveMessage}
                        </p>
                      )}
                    </div>
                  </div>

                  {staffMemberOptions.length === 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      Add and save at least one staff member above to record weekly hours and holiday taken.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs text-gray-500">Actual hours recorded</p>
                          <p className="mt-1 text-2xl font-bold text-gray-900">{staffHrSummary.totals.actualHoursWorked.toFixed(1)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs text-gray-500">Wages / paid labour</p>
                          <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(staffHrSummary.totals.grossWages)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs text-gray-500">Public holiday pay</p>
                          <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(staffHrSummary.totals.publicHolidayPay)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs text-gray-500">Holiday pay accrued</p>
                          <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(staffHrSummary.totals.accruedHolidayPay)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs text-gray-500">Holiday pay still owed</p>
                          <p className={`mt-1 text-2xl font-bold ${staffHrSummary.totals.remainingHolidayPay < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {formatCurrency(staffHrSummary.totals.remainingHolidayPay)}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {editingStaffHrRecordId ? 'Edit weekly staff activity' : 'Record weekly staff activity'}
                          </h4>
                          {editingStaffHrRecordId && (
                            <button
                              type="button"
                              onClick={handleCancelStaffHrEdit}
                              className="self-start rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 sm:self-auto"
                            >
                              Cancel edit
                            </button>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Use actual hours worked for the week. Holiday pay accrues from those hours using each staff member's Staff pay plan holiday reserve percentage.
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Public/bank holiday hours are included as paid labour cost, but they do not reduce annual leave taken or create extra annual leave entitlement in this tracker.
                        </p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-7 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Staff</label>
                            <select
                              value={staffHrDraft.staffMemberId}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, staffMemberId: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            >
                              {staffMemberOptions.map((staffMember) => (
                                <option key={staffMember.id} value={staffMember.id}>{staffMember.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Week start</label>
                            <input
                              type="date"
                              value={staffHrDraft.weekStart}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, weekStart: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Hours worked</label>
                            <input
                              type="number"
                              min="0"
                              value={staffHrDraft.hoursWorked}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, hoursWorked: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Holiday hours taken</label>
                            <input
                              type="number"
                              min="0"
                              value={staffHrDraft.holidayHoursTaken}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, holidayHoursTaken: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Public holiday hours</label>
                            <input
                              type="number"
                              min="0"
                              value={staffHrDraft.publicHolidayHours}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, publicHolidayHours: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Sick hours</label>
                            <input
                              type="number"
                              min="0"
                              value={staffHrDraft.sickHours}
                              onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, sickHours: e.target.value }))}
                              className="w-full rounded-lg border p-2"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={handleSaveStaffHrRecord}
                              className="w-full rounded-lg bg-[color:var(--color-warn)] px-3 py-2 text-sm font-semibold text-[color:var(--color-text)] hover:brightness-95"
                            >
                              {editingStaffHrRecordId ? 'Update week' : 'Save week'}
                            </button>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                          <input
                            type="text"
                            value={staffHrDraft.notes}
                            onChange={(e) => setStaffHrDraft((prev) => ({ ...prev, notes: e.target.value }))}
                            className="w-full rounded-lg border p-2"
                            placeholder="Optional notes, e.g. unpaid sick day or swapped shift"
                          />
                        </div>
                      </div>

                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                            <tr>
                              <th className="px-4 py-3">Staff</th>
                              <th className="px-4 py-3">Actual hours</th>
                              <th className="px-4 py-3">Public holidays</th>
                              <th className="px-4 py-3">Holiday taken</th>
                              <th className="px-4 py-3">Sick hours</th>
                              <th className="px-4 py-3">Accrued holiday pay</th>
                              <th className="px-4 py-3">Holiday pay used</th>
                              <th className="px-4 py-3">Remaining holiday pay</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {staffHrSummary.staff.map((summary) => (
                              <tr key={summary.id}>
                                <td className="px-4 py-3">
                                  <div className="font-semibold text-gray-900">{summary.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {formatCurrency(summary.hourlyPay)}/hr · {summary.holidayPayPercent.toFixed(1)}% holiday reserve
                                  </div>
                                </td>
                                <td className="px-4 py-3">{summary.actualHoursWorked.toFixed(1)}</td>
                                <td className="px-4 py-3">
                                  <div>{summary.publicHolidayHours.toFixed(1)}h</div>
                                  <div className="text-xs text-gray-500">{formatCurrency(summary.publicHolidayPay)}</div>
                                </td>
                                <td className="px-4 py-3">{summary.holidayHoursTaken.toFixed(1)}</td>
                                <td className="px-4 py-3">{summary.sickHours.toFixed(1)}</td>
                                <td className="px-4 py-3">{formatCurrency(summary.accruedHolidayPay)}</td>
                                <td className="px-4 py-3">{formatCurrency(summary.usedHolidayPay)}</td>
                                <td className={`px-4 py-3 font-semibold ${summary.remainingHolidayPay < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                  {formatCurrency(summary.remainingHolidayPay)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="rounded-lg border border-gray-200">
                        <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Profitability by staff member</h4>
                            <p className="mt-1 text-xs text-gray-500">
                              Uses sales and expenses assigned to each staff member, then subtracts planned labour cost.
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {STAFF_PERFORMANCE_PERIOD_OPTIONS.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setStaffProfitabilityPeriod(option.id)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  staffProfitabilityPeriod === option.id
                                    ? 'bg-[color:var(--color-warn)] text-[color:var(--color-text)]'
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-white text-left text-xs uppercase text-gray-500">
                              <tr>
                                <th className="px-4 py-3">Staff</th>
                                <th className="px-4 py-3">Sales</th>
                                <th className="px-4 py-3">Assigned expenses</th>
                                <th className="px-4 py-3">Net before labour</th>
                                <th className="px-4 py-3">Planned labour</th>
                                <th className="px-4 py-3">Profit after labour</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {staffProfitabilityRows.length > 0 ? (
                                staffProfitabilityRows.map((row) => (
                                  <tr key={row.id}>
                                    <td className="px-4 py-3">
                                      <div className="font-semibold text-gray-900">{row.displayName}</div>
                                      <div className="text-xs text-gray-500">
                                        {row.saleCount} sale{row.saleCount === 1 ? '' : 's'} · {row.expenseCount || 0} expense{row.expenseCount === 1 ? '' : 's'}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">{formatCurrency(row.revenue || 0)}</td>
                                    <td className="px-4 py-3">{formatCurrency(row.expenseTotal || 0)}</td>
                                    <td className="px-4 py-3">{formatCurrency(row.netRevenue || 0)}</td>
                                    <td className="px-4 py-3">
                                      {row.plannedLabourCost !== null ? formatCurrency(row.plannedLabourCost) : '-'}
                                    </td>
                                    <td className={`px-4 py-3 font-semibold ${(row.contributionAfterLabour || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                      {row.contributionAfterLabour !== null ? formatCurrency(row.contributionAfterLabour) : '-'}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="px-4 py-4 text-sm text-gray-500">
                                    Assign sales or expenses to staff members to see profitability for {staffProfitabilityMeta.label.toLowerCase()}.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200">
                        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">Weekly staff records</h4>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowStaffHrRecords((prev) => !prev)}
                              className="self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 lg:self-auto"
                            >
                              {showStaffHrRecords ? 'Hide records' : 'View weekly records'}
                            </button>
                          </div>
                        </div>
                        {showStaffHrRecords && (
                          <div>
                            <div className="grid grid-cols-1 gap-3 border-b border-gray-100 bg-white px-4 py-3 md:grid-cols-[1fr_1fr_auto]">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Filter by staff</label>
                                <select
                                  value={staffHrRecordFilters.staffMemberId}
                                  onChange={(e) => setStaffHrRecordFilters((prev) => ({ ...prev, staffMemberId: e.target.value }))}
                                  className="w-full rounded-lg border p-2 text-sm"
                                >
                                  <option value="">All staff</option>
                                  {staffMemberOptions.map((staffMember) => (
                                    <option key={staffMember.id} value={staffMember.id}>{staffMember.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Filter by week</label>
                                <select
                                  value={staffHrRecordFilters.weekStart}
                                  onChange={(e) => setStaffHrRecordFilters((prev) => ({ ...prev, weekStart: e.target.value }))}
                                  className="w-full rounded-lg border p-2 text-sm"
                                >
                                  <option value="">All weeks</option>
                                  {staffHrWeekOptions.map((weekStart) => (
                                    <option key={weekStart} value={weekStart}>
                                      Week of {formatDisplayDate(weekStart, dateDisplayLocale)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex items-end">
                                <button
                                  type="button"
                                  onClick={() => setStaffHrRecordFilters({ staffMemberId: '', weekStart: '' })}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                  Clear filters
                                </button>
                              </div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              {filteredStaffHrRecords.length > 0 ? (
                                filteredStaffHrRecords.map((record) => (
                                  <div key={record.id} className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <div className="font-medium text-gray-900">{record.staffMemberName} · week of {formatDisplayDate(record.weekStart, dateDisplayLocale)}</div>
                                      <div className="text-xs text-gray-500">
                                        Worked {Number(record.hoursWorked || 0).toFixed(1)}h · public holiday {Number(record.publicHolidayHours || 0).toFixed(1)}h · holiday {Number(record.holidayHoursTaken || 0).toFixed(1)}h · sick {Number(record.sickHours || 0).toFixed(1)}h
                                        {record.notes ? ` · ${record.notes}` : ''}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEditStaffHrRecord(record)}
                                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                      >
                                        <Edit2 size={13} />
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteStaffHrRecord(record.id)}
                                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                                      >
                                        <Trash2 size={13} />
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-4 text-sm text-gray-500">No weekly staff records match those filters.</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="hidden">
                        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                          <h4 className="text-sm font-semibold text-gray-900">Recent weekly records</h4>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {(Array.isArray(staffHrRecords) && staffHrRecords.length > 0) ? (
                            staffHrRecords
                              .slice()
                              .sort((a, b) => String(b.weekStart || '').localeCompare(String(a.weekStart || '')))
                              .slice(0, 20)
                              .map((record) => (
                                <div key={record.id} className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <div className="font-medium text-gray-900">{record.staffMemberName} · week of {formatDisplayDate(record.weekStart, dateDisplayLocale)}</div>
                                    <div className="text-xs text-gray-500">
                                      Worked {Number(record.hoursWorked || 0).toFixed(1)}h · public holiday {Number(record.publicHolidayHours || 0).toFixed(1)}h · holiday {Number(record.holidayHoursTaken || 0).toFixed(1)}h · sick {Number(record.sickHours || 0).toFixed(1)}h
                                      {record.notes ? ` · ${record.notes}` : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteStaffHrRecord(record.id)}
                                    className="inline-flex items-center gap-1 self-start rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 sm:self-auto"
                                  >
                                    <Trash2 size={13} />
                                    Delete
                                  </button>
                                </div>
                              ))
                          ) : (
                            <div className="px-4 py-4 text-sm text-gray-500">No weekly staff records yet.</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Recent Transactions Section */}
            {visibleSections.clients && (
              <div id="clients-section" className="w-full">
                <ClientDatabasePanel
                  clients={clientOptions}
                  transactions={transactions}
                  formatCurrency={formatCurrency}
                  onAddClient={handleAddClient}
                  onUpdateClient={handleUpdateClient}
                  onDeleteClient={handleDeleteClient}
                  onImportClients={handleImportClients}
                  onStartSaleForClient={handleStartSaleForClient}
                  onStartInvoiceForClient={handleStartInvoiceForClient}
                  onDownloadInvoice={handleDownloadInvoice}
                  onEditTransaction={handleEditTransactionFromClient}
                  onRecordPartPayment={handleRecordPartPayment}
                  paymentMethods={PAYMENT_METHODS}
                  onClose={() => toggleSection('clients')}
                />
              </div>
            )}

            {visibleSections.suppliers && (
              <div id="suppliers-section" className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Suppliers</h3>
                    <p className="text-sm text-gray-500">
                      {supportsRct
                        ? 'Manage ordinary suppliers and RCT subcontractors.'
                        : 'Manage suppliers and vendors you buy from.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSection('suppliers')}
                    className="self-start rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Hide
                  </button>
                </div>

                <div className={`grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 ${supportsRct ? 'md:grid-cols-[1fr,170px,160px,auto]' : 'md:grid-cols-[1fr,auto]'}`}>
                  <input
                    type="text"
                    value={supplierDraft.name}
                    onChange={(e) => setSupplierDraft((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder={supportsRct ? 'Supplier / subcontractor name' : 'Supplier / vendor name'}
                  />
                  {supportsRct && (
                    <>
                      <select
                        value={supplierDraft.supplierType || 'standard'}
                        onChange={(e) => setSupplierDraft((prev) => ({ ...prev, supplierType: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      >
                        <option value="standard">Standard supplier</option>
                        <option value="rct">RCT subcontractor</option>
                      </select>
                      <select
                        value={String(supplierDraft.rctDeductionRate)}
                        onChange={(e) => setSupplierDraft((prev) => ({ ...prev, rctDeductionRate: Number(e.target.value) }))}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        disabled={supplierDraft.supplierType !== 'rct'}
                      >
                        {supplierDraft.supplierType === 'rct'
                          ? RCT_DEDUCTION_RATES.map((rate) => (
                              <option key={rate.value} value={rate.value}>{rate.label} RCT</option>
                            ))
                          : <option value="0">No RCT</option>}
                      </select>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const savedId = upsertRctSubcontractor(supportsRct
                        ? supplierDraft
                        : { ...supplierDraft, supplierType: 'standard', rctDeductionRate: 0 });
                      if (savedId) {
                        setSupplierDraft({ id: '', name: '', supplierType: 'standard', rctDeductionRate: 0.2 });
                      }
                    }}
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                  >
                    {supplierDraft.id ? 'Update supplier' : 'Add supplier'}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr,260px]">
                  <input
                    type="search"
                    value={supplierSearchTerm}
                    onChange={(event) => setSupplierSearchTerm(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder={supportsRct
                      ? 'Search suppliers, subcontractors, jobs, or invoices'
                      : 'Search suppliers, vendors, or invoices'}
                    aria-label="Search suppliers"
                  />
                  <select
                    value={selectedSupplierId}
                    onChange={(event) => setSelectedSupplierId(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    aria-label="Select supplier"
                  >
                    <option value="">Select supplier</option>
                    {filteredSupplierOptions.length === 0 ? (
                      <option value="">No suppliers found</option>
                    ) : (
                      filteredSupplierOptions.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                      ))
                    )}
                  </select>
                </div>

                <div className="mt-4 space-y-2">
                  {filteredSupplierOptions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">No suppliers added yet.</div>
                  ) : selectedSupplierForDisplay ? (
                      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{selectedSupplierForDisplay.name}</div>
                          <div className="text-sm text-gray-500">
                            {supportsRct && selectedSupplierForDisplay.supplierType === 'rct'
                              ? `RCT deduction: ${Math.round((Number(selectedSupplierForDisplay.rctDeductionRate) || 0) * 100)}%`
                              : 'Standard supplier'}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:flex">
                          <button
                            type="button"
                            onClick={() => setSupplierDraft({
                              id: selectedSupplierForDisplay.id,
                              name: selectedSupplierForDisplay.name,
                              supplierType: supportsRct ? selectedSupplierForDisplay.supplierType || 'rct' : 'standard',
                              rctDeductionRate: supportsRct ? selectedSupplierForDisplay.rctDeductionRate ?? 0.2 : 0
                            })}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteRctSubcontractor(selectedSupplierForDisplay.id)}
                            className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                  ) : null}
                </div>

                <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Supplier liabilities</h4>
                      <p className="text-sm text-gray-500">
                        View supplier details, received invoices, outstanding balances, and payments made.
                      </p>
                    </div>
                  </div>

                  {!selectedSupplierForDisplay ? (
                    null
                  ) : (
                  <>
                  <div className="rounded-lg border border-gray-200 bg-white">
                    <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Supplier invoices and liabilities</div>
                    {!selectedSupplierForDisplay ? (
                      <div className="px-3 py-3 text-sm text-gray-500">Select a supplier to view balances and payments.</div>
                    ) : !selectedSupplierSummary ? (
                      <div className="px-3 py-3 text-sm text-gray-500">No invoices recorded for {selectedSupplierForDisplay.name} yet.</div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                          <div key={selectedSupplierSummary.id} className="p-3">
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <div className="font-semibold text-gray-900">{selectedSupplierSummary.name}</div>
                                <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-3">
                                  <span className="rounded bg-gray-50 px-2 py-1">Gross: <strong>{formatCurrency(selectedSupplierSummary.grossInvoices)}</strong></span>
                                  <span className="rounded bg-gray-50 px-2 py-1">Net: <strong>{formatCurrency(selectedSupplierSummary.netPayable)}</strong></span>
                                  <span className="rounded bg-gray-50 px-2 py-1">Paid: <strong>{formatCurrency(selectedSupplierSummary.paidToSupplier)}</strong></span>
                                  <span className="rounded bg-gray-50 px-2 py-1">Supplier due: <strong>{formatCurrency(selectedSupplierSummary.netOutstanding)}</strong></span>
                                  {supportsRct && (
                                    <>
                                      <span className="rounded bg-gray-50 px-2 py-1">RCT paid: <strong>{formatCurrency(selectedSupplierSummary.rctPaidToRevenue || 0)}</strong></span>
                                      <span className="rounded bg-gray-50 px-2 py-1">RCT due: <strong>{formatCurrency(selectedSupplierSummary.rctOutstanding)}</strong></span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              {selectedSupplierSummary.invoiceRows.map((row) => {
                                const supplierDraft = supplierPaymentDrafts[row.id] || {};
                                const rctDraft = rctPaymentDrafts[row.id] || {};
                                const hasSupplierDue = row.netOutstanding > 0.01;
                                const hasRctDue = row.rctOutstanding > 0.01;
                                return (
                                  <div key={row.id} className="rounded-lg bg-gray-50 p-2 text-sm">
                                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                                      <div className="min-w-0">
                                        <div className="truncate font-medium text-gray-900">{row.jobReference || row.date || 'Supplier invoice'}</div>
                                        <div className="text-xs text-gray-500">
                                          Net outstanding {formatCurrency(row.netOutstanding)}
                                          {supportsRct && row.rctDue > 0 ? `, RCT paid ${formatCurrency(row.rctPaidToRevenue || 0)} of ${formatCurrency(row.rctDue)}` : ''}
                                        </div>
                                      </div>
                                      {!hasSupplierDue && !hasRctDue && (
                                        <div className="rounded bg-green-50 px-2 py-1 text-xs text-green-700">Paid in full</div>
                                      )}
                                    </div>

                                    {hasSupplierDue && (
                                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[1fr,110px,150px,150px,auto] lg:items-center">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Supplier payment</div>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={supplierDraft.amount ?? row.netOutstanding}
                                          onChange={(event) => setSupplierPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], amount: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2 text-right"
                                          aria-label="Supplier payment amount"
                                        />
                                        <input
                                          type="date"
                                          value={supplierDraft.date || new Date().toISOString().split('T')[0]}
                                          onChange={(event) => setSupplierPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], date: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2"
                                          aria-label="Supplier payment date"
                                        />
                                        <select
                                          value={supplierDraft.method || 'transfer'}
                                          onChange={(event) => setSupplierPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], method: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2"
                                          aria-label="Supplier payment method"
                                        >
                                          {PAYMENT_METHODS.filter((method) => method.id !== 'unpaid').map((method) => (
                                            <option key={method.id} value={method.id}>{method.name}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => handleRecordSupplierPayment(row.id, {
                                            amount: supplierDraft.amount ?? row.netOutstanding,
                                            date: supplierDraft.date || new Date().toISOString().split('T')[0],
                                            method: supplierDraft.method || 'transfer'
                                          })}
                                          className="w-full rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                                        >
                                          Supplier payment
                                        </button>
                                      </div>
                                    )}

                                    {supportsRct && hasRctDue && (
                                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[1fr,110px,150px,150px,auto] lg:items-center">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">RCT payment to Revenue</div>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={rctDraft.amount ?? row.rctOutstanding}
                                          onChange={(event) => setRctPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], amount: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2 text-right"
                                          aria-label="RCT payment amount"
                                        />
                                        <input
                                          type="date"
                                          value={rctDraft.date || new Date().toISOString().split('T')[0]}
                                          onChange={(event) => setRctPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], date: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2"
                                          aria-label="RCT payment date"
                                        />
                                        <select
                                          value={rctDraft.method || 'transfer'}
                                          onChange={(event) => setRctPaymentDrafts((prev) => ({
                                            ...prev,
                                            [row.id]: { ...prev[row.id], method: event.target.value }
                                          }))}
                                          className="w-full rounded border border-gray-200 px-2 py-2"
                                          aria-label="RCT payment method"
                                        >
                                          {PAYMENT_METHODS.filter((method) => method.id !== 'unpaid').map((method) => (
                                            <option key={method.id} value={method.id}>{method.name}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => handleRecordRctPayment(row.id, {
                                            amount: rctDraft.amount ?? row.rctOutstanding,
                                            date: rctDraft.date || new Date().toISOString().split('T')[0],
                                            method: rctDraft.method || 'transfer'
                                          })}
                                          className="w-full rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
                                        >
                                          RCT payment
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {selectedSupplierSummary.invoiceRows.every((row) => row.netOutstanding <= 0.01 && (!supportsRct || row.rctOutstanding <= 0.01)) && (
                                <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                                  No supplier balance outstanding.
                                </div>
                              )}
                            </div>
                          </div>
                      </div>
                    )}
                  </div>

                  {supportsRct && (
                  <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Received invoices</div>
                      {!selectedSupplierForDisplay ? (
                        <div className="px-3 py-3 text-sm text-gray-500">Select a supplier to view RCT invoices.</div>
                      ) : selectedSupplierPrincipalRows.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500">No RCT subcontractor invoices recorded for {selectedSupplierForDisplay.name}.</div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {selectedSupplierPrincipalRows.slice(0, 5).map((row) => (
                            <div key={row.id} className="grid grid-cols-1 gap-2 px-3 py-2 text-sm sm:grid-cols-[1fr,90px,90px] sm:items-center">
                              <div className="min-w-0">
                                <div className="truncate font-medium text-gray-900">{row.name}</div>
                                <div className="truncate text-xs text-gray-500">{row.jobReference || row.date || ''}</div>
                              </div>
                              <div className="text-gray-700 sm:text-right">Net due {formatCurrency(row.net)}</div>
                              <div className="font-medium text-gray-900 sm:text-right">RCT withheld {formatCurrency(row.due)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                  </>
                  )}
                </div>
              </div>
            )}

            {supportsProjects && visibleSections.projects && (
              <div id="projects-section" className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Projects / Jobs</h3>
                    <p className="text-sm text-gray-500">
                      Track costs, sales invoices, payments received, and outstanding balances by client and job reference.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      <span className="whitespace-nowrap">Target markup</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={projectPricingMarkup}
                        onChange={(e) => setProjectPricingMarkup(e.target.value)}
                        className="w-20 rounded border border-gray-200 px-2 py-1 text-right"
                      />
                      <span>%</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => toggleSection('projects')}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Hide
                    </button>
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr,280px]">
                  <input
                    type="search"
                    value={projectSearchTerm}
                    onChange={(event) => setProjectSearchTerm(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Search projects, clients, suppliers, or cost categories"
                    aria-label="Search projects"
                  />
                  <select
                    value={selectedProjectKey}
                    onChange={(event) => setSelectedProjectKey(event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    aria-label="Select project"
                  >
                    <option value="">Select project</option>
                    {filteredProjectSummaries.length === 0 ? (
                      <option value="">No projects found</option>
                    ) : (
                      filteredProjectSummaries.map((project) => (
                        <option key={project.key} value={project.key}>
                          {project.jobReference} - {project.clientName}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {projectSummaries.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 p-5 text-sm text-gray-500">
                    No projects yet. Add a client and job reference when recording a sale, supplier invoice, or uploaded receipt to build a project cost view automatically.
                  </div>
                ) : !selectedProjectForDisplay ? (
                  null
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const project = selectedProjectForDisplay;
                      const needsInvoice = project.suggestedCharge > project.invoiceTotal + 0.01;
                      const marginText = project.margin === null ? 'No sales yet' : `${project.margin.toFixed(1)}%`;
                      return (
                        <div key={project.key} className="rounded-lg border border-gray-200 p-4">
                          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-base font-semibold text-gray-900">{project.jobReference}</h4>
                                {project.outstandingIncome > 0.01 && (
                                  <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                                    {formatCurrency(project.outstandingIncome)} outstanding
                                  </span>
                                )}
                                {project.unpaidCosts > 0.01 && (
                                  <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
                                    {formatCurrency(project.unpaidCosts)} supplier bills unpaid
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">{project.clientName}</div>
                              {project.supplierNames.length > 0 && (
                                <div className="mt-1 text-xs text-gray-500">Suppliers: {project.supplierNames.join(', ')}</div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleStartInvoiceForProject(project)}
                                className="rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
                              >
                                Invoice project
                              </button>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                            {[
                              { label: 'Cost', value: formatCurrency(project.costTotal) },
                              { label: 'Billable', value: formatCurrency(project.billableCostTotal) },
                              { label: 'Suggested', value: formatCurrency(project.suggestedCharge) },
                              { label: 'To invoice', value: formatCurrency(Math.max(project.suggestedCharge - project.invoiceTotal, 0)) },
                              { label: 'Invoiced', value: formatCurrency(project.invoiceTotal) },
                              { label: 'Received', value: formatCurrency(project.cashReceived) },
                              { label: 'Profit', value: formatCurrency(project.profit) },
                              { label: 'Margin', value: marginText }
                            ].map((metric) => (
                              <div key={metric.label} className="rounded-lg bg-gray-50 p-3">
                                <div className="text-xs uppercase tracking-wide text-gray-500">{metric.label}</div>
                                <div className="mt-1 text-sm font-semibold text-gray-900">{metric.value}</div>
                              </div>
                            ))}
                          </div>

                          {project.categoryBreakdown.length > 0 && (
                            <div className="mt-4 rounded-lg border border-gray-100">
                              <div className="grid grid-cols-[1fr,120px] border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <span>Cost category</span>
                                <span className="text-right">Amount</span>
                              </div>
                              {project.categoryBreakdown.slice(0, 4).map((entry) => (
                                <div key={entry.label} className="grid grid-cols-[1fr,120px] px-3 py-2 text-sm">
                                  <span className="text-gray-700">{entry.label}</span>
                                  <span className="text-right font-medium text-gray-900">{formatCurrency(entry.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {needsInvoice && (
                            <div className="mt-3 rounded-lg bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                              Suggested charge is higher than the invoices recorded for this job.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {visibleSections.recentTransactions && (
              <div id="recent-transactions-section" className="mx-auto w-full max-w-6xl">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Recent Transactions</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          console.log('[APP] Refreshing transaction list...');
                          setTransactions(prev => [...prev]);
                          setShowRecentTransactions(true);
                          setShowAllTransactions(true);
                        }}
                        className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition text-sm flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </button>
                      <button
                        onClick={() => toggleSection('recentTransactions')}
                        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
                      >
                        <X size={16} />
                        Close
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="text-xs text-gray-500">
                        Showing {displayedRecentTransactions.length}
                        of {Array.isArray(recentTransactions) ? recentTransactions.length : 0} filtered transactions
                        (Total: {Array.isArray(transactions) ? transactions.length : 0})
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={allDisplayedRecentTransactionsSelected}
                            disabled={selectableDisplayedRecentTransactions.length === 0}
                            onChange={(event) => handleToggleAllDisplayedRecentTransactions(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          Select visible
                        </label>
                        {Array.isArray(recentTransactions) && recentTransactions.length > 0 && (
                          <button
                            type="button"
                            onClick={handleSelectAllFilteredRecentTransactions}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Select all filtered
                          </button>
                        )}
                        {selectedRecentTransactionIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedRecentTransactionIds([])}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                          >
                            Clear selection
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleDeleteSelectedTransactions}
                          disabled={selectedRecentTransactionIds.length === 0}
                          className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold ${
                            selectedRecentTransactionIds.length > 0
                              ? 'bg-red-600 text-black hover:bg-red-700'
                              : 'cursor-not-allowed bg-gray-100 text-gray-400'
                          }`}
                        >
                          <Trash2 size={14} />
                          Delete selected{selectedRecentTransactionIds.length > 0 ? ` (${selectedRecentTransactionIds.length})` : ''}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 md:hidden">
                      {Array.isArray(recentTransactions)
                        ? displayedRecentTransactions.map((transaction, index) => {
                          const isEditing = editingTransactionId === transaction.id;
                          const canSelectTransaction = transaction?.id !== undefined && transaction?.id !== null;
                          const isSelected = canSelectTransaction && selectedRecentTransactionIdSet.has(String(transaction.id));
                          const transactionTypeKey = String(transaction.type || '').toLowerCase();
                          const resolvedTransactionTypeKey =
                            transactionTypeKey === 'expense'
                              ? 'expenses'
                              : transactionTypeKey === 'sale'
                                ? 'income'
                                : transactionTypeKey;
                          const visibleCategoryOptions =
                            transactionInputCategories[resolvedTransactionTypeKey] ||
                            transactionInputCategories.expenses ||
                            [];
                          const allCategoryOptions =
                            mergedCategories[resolvedTransactionTypeKey] ||
                            mergedCategories.expenses ||
                            [];
                          const currentCategoryOption = allCategoryOptions.find((cat) => cat.id === transaction.category);
                          const categoryOptions = currentCategoryOption && !visibleCategoryOptions.some((cat) => cat.id === currentCategoryOption.id)
                            ? [currentCategoryOption, ...visibleCategoryOptions]
                            : visibleCategoryOptions;
                          const isEditingSplitTransaction = isEditing && (
                            editingTransaction.isSplitTransaction ||
                            isSplitIncomeTransaction(transaction)
                          );
                          const editingSplitItems = isEditingSplitTransaction && Array.isArray(editingTransaction.splitItems)
                            ? editingTransaction.splitItems
                            : [];
                          const editingSplitTotal = editingSplitItems.reduce((sum, item) => {
                            const amount = parseFloat(sanitizeSignedAmountInput(item?.amount || ''));
                            return sum + (Number.isFinite(amount) ? amount : 0);
                          }, 0);
                          const receiptImageUrl = transaction.receiptImageUrl || transaction.receiptUrl || transaction.imageUrl;
                          const categoryBreakdownPreview = getTransactionCategoryBreakdownPreview(transaction);
                          const transactionVatAmount = getTransactionDisplayVatAmount(transaction);
                          const transactionVatRate = Number(transaction?.vatRate);
                          const transactionOutstandingAmount = getTransactionOutstandingAmount(transaction);
                          const canDownloadInvoice = resolvedTransactionTypeKey === 'income';
                          return (
                            <div key={transaction.id || index} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-gray-900">
                                    {transaction.description || 'No description'}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    {formatDisplayDate(transaction.date, dateDisplayLocale)} · {getTransactionPaymentLabel(transaction)}
                                  </div>
                                </div>
                                <label className="shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(isSelected)}
                                    disabled={!canSelectTransaction}
                                    onChange={(event) => handleToggleRecentTransactionSelection(transaction.id, event.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    aria-label={`Select transaction ${transaction.description || transaction.id}`}
                                  />
                                </label>
                              </div>

                              <div className="mt-3 flex items-start justify-between gap-3">
                                <div className="min-w-0 text-xs text-gray-600">
                                  {categoryBreakdownPreview.length > 1 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {categoryBreakdownPreview.slice(0, 2).map((entry) => (
                                        <span
                                          key={`${transaction.id}-mobile-${entry.category}`}
                                          className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700"
                                        >
                                          {getCategoryDisplayLabel(entry.category)}
                                        </span>
                                      ))}
                                      {categoryBreakdownPreview.length > 2 && (
                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                                          +{categoryBreakdownPreview.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="block truncate">{getTransactionCategoryLabel(transaction)}</span>
                                  )}
                                  {(transaction.clientName || getExpenseAllocationLabel(transaction)) && (
                                    <div className="mt-1 truncate">Client: {getExpenseAllocationLabel(transaction) || transaction.clientName}</div>
                                  )}
                                </div>
                                <div className="shrink-0 text-right">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                  </div>
                                  {transactionVatAmount > 0 && (
                                    <div className="text-[10px] text-gray-500">
                                      {countryTerminology.taxName}: {formatCurrency(transactionVatAmount)}
                                      {Number.isFinite(transactionVatRate) && transactionVatRate > 0
                                        ? ` @ ${(transactionVatRate * 100).toFixed(1)}%`
                                        : ''}
                                    </div>
                                  )}
                                  {transactionOutstandingAmount > 0.01 && (
                                    <div className="text-[10px] font-medium text-amber-700">
                                      Outstanding: {formatCurrency(transactionOutstandingAmount)}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {receiptImageUrl && (
                                  <button
                                    type="button"
                                    onClick={() => window.open(receiptImageUrl, '_blank', 'noopener,noreferrer')}
                                    className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                                  >
                                    <FileText size={13} />
                                    Receipt
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleEditTransaction(transaction)}
                                  className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-100"
                                >
                                  <Edit2 size={13} />
                                  Edit
                                </button>
                                {canDownloadInvoice && (
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadInvoice(transaction)}
                                    className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100"
                                  >
                                    <Download size={13} />
                                    Invoice
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteTransaction(transaction.id)}
                                  className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100"
                                >
                                  <Trash2 size={13} />
                                  Delete
                                </button>
                              </div>

                              {isEditing && (
                                <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                  <div className="grid grid-cols-1 gap-3">
                                    <label className="text-xs font-medium text-gray-600">
                                      Date
                                      <input
                                        type="date"
                                        value={formatDateForInput(editingTransaction.date) || ''}
                                        onChange={(e) => handleEditingFieldChange('date', e.target.value)}
                                        className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                                      />
                                    </label>
                                    <label className="text-xs font-medium text-gray-600">
                                      Description
                                      <textarea
                                        value={editingTransaction.description || ''}
                                        onChange={(e) => handleEditingFieldChange('description', e.target.value)}
                                        className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                                        rows={2}
                                      />
                                    </label>
                                    {isEditingSplitTransaction ? (
                                      <div className="space-y-2 rounded-lg border border-indigo-100 bg-white p-2">
                                        <div className="grid grid-cols-[minmax(0,1fr)_80px_32px] gap-2 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                          <span>Category</span>
                                          <span className="text-right">Amount</span>
                                          <span></span>
                                        </div>
                                        {editingSplitItems.map((item) => (
                                          <div key={item.id} className="grid grid-cols-[minmax(0,1fr)_80px_32px] items-center gap-2">
                                            <select
                                              value={item.category || ''}
                                              onChange={(e) => handleEditingSplitItemChange(item.id, 'category', e.target.value)}
                                              className="min-w-0 rounded border border-gray-300 bg-white px-2 py-2 text-xs"
                                            >
                                              <option value="">Select</option>
                                              {categoryOptions.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                              ))}
                                            </select>
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              pattern="-?\\d*\\.?\\d*"
                                              value={item.amount || ''}
                                              onChange={(e) => handleEditingSplitItemChange(item.id, 'amount', e.target.value)}
                                              className="rounded border border-gray-300 bg-white px-2 py-2 text-right text-xs"
                                              placeholder="0.00"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveEditingSplitItem(item.id)}
                                              className="rounded border border-red-200 px-1 py-1 text-xs text-red-600 hover:bg-red-50"
                                            >
                                              x
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={handleAddEditingSplitItem}
                                          className="text-xs text-indigo-700 hover:underline"
                                        >
                                          + Add split item
                                        </button>
                                        <div className="text-[10px] text-gray-500">Total: {formatCurrency(editingSplitTotal)}</div>
                                      </div>
                                    ) : (
                                      <label className="text-xs font-medium text-gray-600">
                                        Category
                                        <select
                                          value={editingTransaction.category || ''}
                                          onChange={(e) => handleEditingFieldChange('category', e.target.value)}
                                          className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                                        >
                                          <option value="">Select</option>
                                          {categoryOptions.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                          ))}
                                        </select>
                                      </label>
                                    )}
                                    <div className="grid grid-cols-2 gap-3">
                                      <label className="text-xs font-medium text-gray-600">
                                        Amount
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          pattern="\\d*\\.?\\d*"
                                          value={isEditingSplitTransaction ? editingSplitTotal.toFixed(2) : (editingTransaction.amount || '')}
                                          readOnly={isEditingSplitTransaction}
                                          onChange={(e) => handleEditingFieldChange('amount', e.target.value)}
                                          className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                                        />
                                      </label>
                                      <label className="text-xs font-medium text-gray-600">
                                        Payment
                                        <select
                                          value={editingTransaction.paymentMethod || ''}
                                          onChange={(e) => handleEditingFieldChange('paymentMethod', normalizePaymentMethodId(e.target.value))}
                                          className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                                        >
                                          {PAYMENT_METHODS.map(method => (
                                            <option key={method.id} value={method.id}>{method.name}</option>
                                          ))}
                                        </select>
                                      </label>
                                    </div>
                                  </div>

                                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Receipt file</div>
                                    <div className="mt-1 truncate text-sm text-gray-800">
                                      {editingTransaction.receiptAttachmentFile
                                        ? editingTransaction.receiptAttachmentFile.name
                                        : (editingTransaction.removeReceiptAttachment
                                            ? 'Receipt will be removed when you save'
                                            : (editingTransaction.receiptFileName || (editingTransaction.receiptImageUrl || editingTransaction.receiptUrl ? 'Receipt attached' : 'No receipt attached')))}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl) && (
                                        <button
                                          type="button"
                                          onClick={() => window.open(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl, '_blank', 'noopener,noreferrer')}
                                          className="rounded-md border border-blue-200 px-2 py-1 text-xs text-blue-700"
                                        >
                                          View
                                        </button>
                                      )}
                                      {(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl || editingTransaction.receiptAttachmentFile || editingTransaction.receiptStoragePath) && (
                                        <button
                                          type="button"
                                          onClick={handleRemoveEditingReceiptAttachment}
                                          className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                                        >
                                          Remove
                                        </button>
                                      )}
                                      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs text-indigo-700">
                                        <Upload size={13} />
                                        {editingTransaction.receiptImageUrl || editingTransaction.receiptUrl ? 'Replace' : 'Attach'}
                                        <input
                                          type="file"
                                          accept={RECEIPT_ATTACHMENT_ACCEPT}
                                          onChange={handleEditingReceiptAttachmentChange}
                                          className="sr-only"
                                        />
                                      </label>
                                    </div>
                                  </div>

                                  <div className="flex justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={handleCancelEdit}
                                      className="inline-flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700"
                                    >
                                      <X size={14} />
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveTransaction}
                                      disabled={isUploadingReceiptAttachment}
                                      className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                                    >
                                      <Save size={14} />
                                      {isUploadingReceiptAttachment ? 'Uploading...' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }) : null}
                      {(!Array.isArray(recentTransactions) || recentTransactions.length === 0) && (
                        <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                          No transactions yet. Add your first transaction to get started!
                        </div>
                      )}
                    </div>

                    <div className="hidden overflow-hidden rounded-lg border border-gray-100 md:block">
                      <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="w-10 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              <input
                                type="checkbox"
                                checked={allDisplayedRecentTransactionsSelected}
                                disabled={selectableDisplayedRecentTransactions.length === 0}
                                onChange={(event) => handleToggleAllDisplayedRecentTransactions(event.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                aria-label="Select all visible transactions"
                              />
                            </th>
                            <th className="w-24 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                            <th className="w-[22%] px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Description</th>
                            <th className="w-[20%] px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                            <th className="w-32 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                            <th className="w-28 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Payment</th>
                            <th className="w-28 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Array.isArray(recentTransactions)
                            ? displayedRecentTransactions.map((transaction, index) => {
                            const isEditing = editingTransactionId === transaction.id;
                            const canSelectTransaction = transaction?.id !== undefined && transaction?.id !== null;
                            const isSelected = canSelectTransaction && selectedRecentTransactionIdSet.has(String(transaction.id));
                            const transactionTypeKey = String(transaction.type || '').toLowerCase();
                            const resolvedTransactionTypeKey =
                              transactionTypeKey === 'expense'
                                ? 'expenses'
                                : transactionTypeKey === 'sale'
                                  ? 'income'
                                  : transactionTypeKey;
                            const visibleCategoryOptions =
                              transactionInputCategories[resolvedTransactionTypeKey] ||
                              transactionInputCategories.expenses ||
                              [];
                            const allCategoryOptions =
                              mergedCategories[resolvedTransactionTypeKey] ||
                              mergedCategories.expenses ||
                              [];
                            const currentCategoryOption = allCategoryOptions.find((cat) => cat.id === transaction.category);
                            const categoryOptions = currentCategoryOption && !visibleCategoryOptions.some((cat) => cat.id === currentCategoryOption.id)
                              ? [currentCategoryOption, ...visibleCategoryOptions]
                              : visibleCategoryOptions;
                            const isEditingSplitTransaction = isEditing && (
                              editingTransaction.isSplitTransaction ||
                              isSplitIncomeTransaction(transaction)
                            );
                            const isEditingSplitPayment = isEditing && (
                              editingTransaction.isSplitPayment ||
                              (Array.isArray(editingTransaction.paymentBreakdown) && editingTransaction.paymentBreakdown.length > 1)
                            );
                            const editingSplitItems = isEditingSplitTransaction && Array.isArray(editingTransaction.splitItems)
                              ? editingTransaction.splitItems
                              : [];
                            const editingPaymentSplitItems = isEditingSplitPayment && Array.isArray(editingTransaction.paymentBreakdown)
                              ? editingTransaction.paymentBreakdown
                              : [];
                            const editingSplitTotal = editingSplitItems.reduce((sum, item) => {
                              const amount = parseFloat(sanitizeSignedAmountInput(item?.amount || ''));
                              return sum + (Number.isFinite(amount) ? amount : 0);
                            }, 0);
                            const editingPaymentSplitTotal = editingPaymentSplitItems.reduce((sum, item) => {
                              const amount = parseFloat(sanitizeAmountInput(item?.amount || ''));
                              return sum + (Number.isFinite(amount) ? amount : 0);
                            }, 0);
                            const editingTransactionAmount = isEditingSplitTransaction
                              ? editingSplitTotal
                              : parseFloat(sanitizeAmountInput(editingTransaction.amount || '')) || 0;
                            const canSaveEditingSplitItemsToCost = resolvedTransactionTypeKey !== 'income';
                            const receiptImageUrl = transaction.receiptImageUrl || transaction.receiptUrl || transaction.imageUrl;
                            const categoryBreakdownPreview = getTransactionCategoryBreakdownPreview(transaction);
                            const hasSplitCategoryPreview = categoryBreakdownPreview.length > 1;
                            const transactionVatAmount = getTransactionDisplayVatAmount(transaction);
                            const transactionVatRate = Number(transaction?.vatRate);
                            const transactionOutstandingAmount = getTransactionOutstandingAmount(transaction);
                            const canDownloadInvoice = resolvedTransactionTypeKey === 'income';
                            if (isEditing) {
                              return (
                                <tr key={transaction.id || index} className="bg-gray-50 align-top">
                                  <td colSpan={7} className="px-2 py-4 sm:px-3">
                                    <div className="mx-auto max-w-5xl overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                      <div className="grid grid-cols-1 gap-4 md:grid-cols-[130px_minmax(0,1fr)] xl:grid-cols-[130px_minmax(0,1fr)_minmax(220px,0.8fr)_auto]">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                                          <input
                                            type="date"
                                            value={formatDateForInput(editingTransaction.date) || ''}
                                            onChange={(e) => handleEditingFieldChange('date', e.target.value)}
                                            className="w-full rounded-lg border px-3 py-2 text-sm"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                          <textarea
                                            value={editingTransaction.description || ''}
                                            onChange={(e) => handleEditingFieldChange('description', e.target.value)}
                                            className="w-full rounded-lg border px-3 py-2 text-sm"
                                            rows={3}
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          {transaction.type === 'income' && (
                                            <select
                                              value={editingTransaction.clientId || ''}
                                              onChange={(e) => {
                                                const selectedClient = clientById.get(e.target.value);
                                                setEditingTransaction((prev) => ({
                                                  ...prev,
                                                  clientId: selectedClient?.id || '',
                                                  clientName: selectedClient ? getClientDisplayName(selectedClient) : ''
                                                }));
                                              }}
                                              className="w-full rounded-lg border px-3 py-2 text-sm"
                                            >
                                              <option value="">No client selected</option>
                                              {editingTransaction.clientId && !clientById.has(editingTransaction.clientId) && (
                                                <option value={editingTransaction.clientId}>
                                                  {editingTransaction.clientName || 'Archived client'}
                                                </option>
                                              )}
                                              {clientOptions.map((client) => (
                                                <option key={client.id} value={client.id}>
                                                  {getClientDisplayName(client)}
                                                </option>
                                              ))}
                                            </select>
                                          )}
                                          {(transaction.type === 'expenses' || transaction.type === 'expense') && (
                                            <details className="relative">
                                              <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded-lg border bg-white px-3 py-2 text-xs text-gray-700">
                                                <span>{getExpenseAllocationLabel(editingTransaction) || 'No client selected'}</span>
                                                <ChevronDown size={14} className="text-gray-400" />
                                              </summary>
                                              <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                                                <div className="max-h-36 space-y-1 overflow-y-auto pr-1">
                                                  {clientOptions.length > 0 ? clientOptions.map((client) => {
                                                    const selectedIds = getExpenseAllocationIds(editingTransaction);
                                                    const isSelected = selectedIds.includes(client.id);
                                                    return (
                                                      <label key={client.id} className="flex items-center gap-2 text-xs text-gray-700">
                                                        <input
                                                          type="checkbox"
                                                          checked={isSelected}
                                                          onChange={(e) => {
                                                            setEditingTransaction((prev) => {
                                                              const previousIds = getExpenseAllocationIds(prev);
                                                              const nextIds = e.target.checked
                                                                ? [...previousIds, client.id]
                                                                : previousIds.filter((clientId) => clientId !== client.id);
                                                              const clientAllocations = buildClientAllocationsFromIds(nextIds);
                                                              const primaryAllocation = clientAllocations[0];
                                                              return {
                                                                ...prev,
                                                                clientId: primaryAllocation?.clientId || '',
                                                                clientName: primaryAllocation?.clientName || '',
                                                                clientAllocations,
                                                                allocateToClient: clientAllocations.length > 0
                                                              };
                                                            });
                                                          }}
                                                        />
                                                        <span>{getClientDisplayName(client)}</span>
                                                      </label>
                                                    );
                                                  }) : (
                                                    <div className="text-xs text-gray-500">No clients yet.</div>
                                                  )}
                                                </div>
                                                {getExpenseAllocationIds(editingTransaction).length > 1 && (
                                                  <div className="mt-1 text-[10px] text-gray-500">
                                                    Split equally across {getExpenseAllocationIds(editingTransaction).length} clients.
                                                  </div>
                                                )}
                                              </div>
                                            </details>
                                          )}
                                          {staffMemberOptions.length > 0 && (
                                            <select
                                              value={editingTransaction.staffMemberId || ''}
                                              onChange={(e) => {
                                                const selectedStaffMember = staffMemberById.get(e.target.value);
                                                setEditingTransaction((prev) => ({
                                                  ...prev,
                                                  staffMemberId: selectedStaffMember?.id || '',
                                                  staffMemberName: selectedStaffMember?.name || ''
                                                }));
                                              }}
                                              className="w-full rounded-lg border px-3 py-2 text-sm"
                                            >
                                              <option value="">No staff selected</option>
                                              {editingTransaction.staffMemberId && !staffMemberById.has(editingTransaction.staffMemberId) && (
                                                <option value={editingTransaction.staffMemberId}>
                                                  {editingTransaction.staffMemberName || 'Archived staff member'}
                                                </option>
                                              )}
                                              {staffMemberOptions.map((staffMember) => (
                                                <option key={staffMember.id} value={staffMember.id}>
                                                  {staffMember.label}
                                                </option>
                                              ))}
                                            </select>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-start gap-2 xl:justify-end">
                                          <button
                                            onClick={handleSaveTransaction}
                                            disabled={isUploadingReceiptAttachment}
                                            className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700"
                                          >
                                            <Save size={14} />
                                            {isUploadingReceiptAttachment ? 'Uploading...' : 'Save'}
                                          </button>
                                          <button
                                            onClick={handleCancelEdit}
                                            className="inline-flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
                                          >
                                            <X size={14} />
                                            Cancel
                                          </button>
                                        </div>
                                      </div>

                                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                          <div className="min-w-0">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Receipt file</div>
                                            <div className="mt-1 text-sm text-gray-800">
                                              {editingTransaction.receiptAttachmentFile
                                                ? editingTransaction.receiptAttachmentFile.name
                                                : (editingTransaction.removeReceiptAttachment
                                                    ? 'Receipt will be removed when you save'
                                                    : (editingTransaction.receiptFileName || (editingTransaction.receiptImageUrl || editingTransaction.receiptUrl ? 'Receipt attached' : 'No receipt attached')))}
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500">
                                              Attach a PDF or image to this saved transaction.
                                            </div>
                                          </div>
                                          <div className="flex flex-wrap items-center gap-2">
                                            {(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl) && (
                                              <button
                                                type="button"
                                                onClick={() => window.open(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl, '_blank', 'noopener,noreferrer')}
                                                className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                                              >
                                                <FileText size={14} />
                                                View
                                              </button>
                                            )}
                                            {editingTransaction.receiptAttachmentFile && (
                                              <button
                                                type="button"
                                                onClick={handleClearPendingReceiptAttachment}
                                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                              >
                                                <X size={14} />
                                                Clear
                                              </button>
                                            )}
                                            {(editingTransaction.receiptImageUrl || editingTransaction.receiptUrl || editingTransaction.receiptAttachmentFile || editingTransaction.receiptStoragePath) && (
                                              <button
                                                type="button"
                                                onClick={handleRemoveEditingReceiptAttachment}
                                                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                                              >
                                                <Trash2 size={14} />
                                                Remove
                                              </button>
                                            )}
                                            <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                                              <Upload size={14} />
                                              {editingTransaction.receiptImageUrl || editingTransaction.receiptUrl ? 'Replace' : 'Attach'}
                                              <input
                                                type="file"
                                                accept={RECEIPT_ATTACHMENT_ACCEPT}
                                                onChange={handleEditingReceiptAttachmentChange}
                                                className="sr-only"
                                              />
                                            </label>
                                          </div>
                                        </div>
                                        {editingTransaction.receiptAttachmentPreviewUrl && (
                                          <img
                                            src={editingTransaction.receiptAttachmentPreviewUrl}
                                            alt="Receipt preview"
                                            className="mt-3 h-28 w-auto max-w-full rounded-md border border-gray-200 object-contain"
                                          />
                                        )}
                                      </div>

                                      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(180px,0.65fr)_minmax(210px,0.75fr)]">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                                          {isEditingSplitTransaction ? (
                                            <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                                              <div className={`grid ${canSaveEditingSplitItemsToCost ? 'grid-cols-[minmax(0,1fr)_110px_64px_38px]' : 'grid-cols-[minmax(0,1fr)_110px_38px]'} gap-2 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500`}>
                                                <span>Category</span>
                                                <span className="text-right">Amount</span>
                                                {canSaveEditingSplitItemsToCost && <span className="text-center">Save</span>}
                                                <span></span>
                                              </div>
                                              {editingSplitItems.map((item, splitIndex) => (
                                                <div key={item.id} className={`grid ${canSaveEditingSplitItemsToCost ? 'grid-cols-[minmax(0,1fr)_110px_64px_38px]' : 'grid-cols-[minmax(0,1fr)_110px_38px]'} gap-2 items-center`}>
                                                  <select
                                                    value={item.category || ''}
                                                    onChange={(e) => handleEditingSplitItemChange(item.id, 'category', e.target.value)}
                                                    className="rounded border border-gray-300 bg-white px-2 py-2 text-xs"
                                                  >
                                                    <option value="">Select</option>
                                                    {categoryOptions.map(cat => (
                                                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                    ))}
                                                  </select>
                                                  <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    pattern="-?\\d*\\.?\\d*"
                                                    value={item.amount || ''}
                                                    onChange={(e) => handleEditingSplitItemChange(item.id, 'amount', e.target.value)}
                                                    className="rounded border border-gray-300 bg-white px-2 py-2 text-right text-xs"
                                                    placeholder="0.00"
                                                  />
                                                  {canSaveEditingSplitItemsToCost && (
                                                    <label className="flex justify-center">
                                                      <input
                                                        type="checkbox"
                                                        checked={!!item.saveToCost}
                                                        onChange={(e) => handleEditingSplitItemChange(item.id, 'saveToCost', e.target.checked)}
                                                        title={`Save split item ${splitIndex + 1} to Cost Calculator`}
                                                      />
                                                    </label>
                                                  )}
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveEditingSplitItem(item.id)}
                                                    className="rounded border border-red-200 px-1.5 py-1 text-xs text-red-600 hover:bg-red-50"
                                                    title={`Remove split item ${splitIndex + 1}`}
                                                  >
                                                    x
                                                  </button>
                                                </div>
                                              ))}
                                              <button
                                                type="button"
                                                onClick={handleAddEditingSplitItem}
                                                className="pl-1 text-xs text-indigo-700 hover:underline"
                                              >
                                                + Add split item
                                              </button>
                                            </div>
                                          ) : (
                                            <select
                                              value={editingTransaction.category || ''}
                                              onChange={(e) => handleEditingFieldChange('category', e.target.value)}
                                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                            >
                                              <option value="">Select</option>
                                              {categoryOptions.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                              ))}
                                            </select>
                                          )}
                                        </div>

                                        <div className="space-y-2">
                                          <label className="block text-xs font-medium text-gray-600">Amount</label>
                                          {isEditingSplitTransaction ? (
                                            <input
                                              type="text"
                                              value={editingSplitTotal.toFixed(2)}
                                              readOnly
                                              className="w-full rounded-lg border bg-gray-100 px-3 py-2 text-sm text-gray-600"
                                            />
                                          ) : (
                                            <>
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                pattern="\\d*\\.?\\d*"
                                                value={editingTransaction.amount || ''}
                                                onChange={(e) => handleEditingFieldChange('amount', e.target.value)}
                                                className="w-full rounded-lg border px-3 py-2 text-sm"
                                                placeholder="Amount"
                                              />
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                pattern="\\d*\\.?\\d*"
                                                value={editingTransaction.discountAmount || ''}
                                                onChange={(e) => handleEditingFieldChange('discountAmount', e.target.value)}
                                                className="w-full rounded-lg border px-3 py-2 text-xs"
                                                placeholder="Discount"
                                              />
                                            </>
                                          )}
                                          <div>
                                            <label className="mb-1 block text-xs font-medium text-gray-600">
                                              {countryTerminology.taxName} amount
                                            </label>
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              pattern="-?\\d*\\.?\\d*"
                                              value={editingTransaction.vatAmount || ''}
                                              onChange={(e) => handleEditingFieldChange('vatAmount', e.target.value)}
                                              className="w-full rounded-lg border px-3 py-2 text-xs"
                                              placeholder="0.00"
                                            />
                                          </div>
                                          <div>
                                            <label className="mb-1 block text-xs font-medium text-gray-600">
                                              {countryTerminology.taxName} rate %
                                            </label>
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              pattern="\\d*\\.?\\d*"
                                              value={editingTransaction.vatRate || ''}
                                              onChange={(e) => handleEditingFieldChange('vatRate', e.target.value)}
                                              className="w-full rounded-lg border px-3 py-2 text-xs"
                                              placeholder="0"
                                            />
                                          </div>
                                        </div>

                                        <div>
                                          <label className="mb-2 flex items-center gap-2 text-xs text-gray-700">
                                            <input
                                              type="checkbox"
                                              id={`split-payment-edit-${transaction.id}`}
                                              checked={isEditingSplitPayment}
                                              onChange={(e) => {
                                                const checked = e.target.checked;
                                                setEditingTransaction((prev) => {
                                                  const sourceAmount = isEditingSplitTransaction
                                                    ? editingSplitTotal
                                                    : parseFloat(sanitizeAmountInput(prev.amount || '')) || 0;
                                                  const defaultMethod = normalizePaymentMethodId(prev.paymentMethod || 'cash') || 'cash';
                                                  return {
                                                    ...prev,
                                                    isSplitPayment: checked,
                                                    paymentMethod: defaultMethod,
                                                    paymentBreakdown: checked
                                                      ? [createSplitPaymentItem({
                                                          method: defaultMethod,
                                                          amount: sourceAmount > 0 ? sanitizeAmountInput(sourceAmount.toString()) : ''
                                                        })]
                                                      : []
                                                  };
                                                });
                                              }}
                                            />
                                            Split or part payment
                                          </label>
                                          {isEditingSplitPayment ? (
                                            <div className="space-y-2 rounded-lg border bg-gray-50 p-3">
                                              {editingPaymentSplitItems.map((item) => (
                                                <div key={item.id} className="grid grid-cols-[minmax(0,1fr)_90px_32px] gap-2 items-center">
                                                  <select
                                                    value={item.method || ''}
                                                    onChange={(e) => handleEditingPaymentSplitChange(item.id, 'method', e.target.value)}
                                                    className="rounded border px-2 py-2 text-xs"
                                                  >
                                                    <option value="">Method</option>
                                                    {PAYMENT_METHODS.map((method) => (
                                                      <option key={method.id} value={method.id}>{method.name}</option>
                                                    ))}
                                                  </select>
                                                  <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    pattern="\\d*\\.?\\d*"
                                                    value={item.amount || ''}
                                                    onChange={(e) => handleEditingPaymentSplitChange(item.id, 'amount', e.target.value)}
                                                    className="rounded border px-2 py-2 text-right text-xs"
                                                    placeholder="0.00"
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveEditingPaymentSplitItem(item.id)}
                                                    className="rounded border border-red-200 px-1 py-1 text-xs text-red-600 hover:bg-red-50"
                                                    title="Remove split payment line"
                                                  >
                                                    x
                                                  </button>
                                                </div>
                                              ))}
                                              <button
                                                type="button"
                                                onClick={handleAddEditingPaymentSplitItem}
                                                className="text-xs text-indigo-700 hover:underline"
                                              >
                                                + Add payment
                                              </button>
                                              <div className={`text-[10px] ${editingPaymentSplitTotal - editingTransactionAmount > 0.01 ? 'text-red-600' : 'text-gray-500'}`}>
                                                {formatCurrency(editingPaymentSplitTotal)} / {formatCurrency(editingTransactionAmount)}
                                                {editingTransactionAmount - editingPaymentSplitTotal > 0.01
                                                  ? ` (${formatCurrency(editingTransactionAmount - editingPaymentSplitTotal)} outstanding)`
                                                  : ''}
                                              </div>
                                            </div>
                                          ) : (
                                            <select
                                              value={editingTransaction.paymentMethod || ''}
                                              onChange={(e) => handleEditingFieldChange('paymentMethod', normalizePaymentMethodId(e.target.value))}
                                              className="w-full rounded-lg border px-3 py-2 text-sm"
                                            >
                                              {PAYMENT_METHODS.map(method => (
                                                <option key={method.id} value={method.id}>{method.name}</option>
                                              ))}
                                            </select>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={transaction.id || index} className="hover:bg-gray-50 align-top">
                                <td className="px-3 py-4 text-sm text-gray-500">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(isSelected)}
                                    disabled={!canSelectTransaction}
                                    onChange={(event) => handleToggleRecentTransactionSelection(transaction.id, event.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    aria-label={`Select transaction ${transaction.description || transaction.id}`}
                                  />
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-900">
                                  {isEditing ? (
                                    <input
                                      type="date"
                                      value={formatDateForInput(editingTransaction.date) || ''}
                                      onChange={(e) => handleEditingFieldChange('date', e.target.value)}
                                      className="border rounded px-2 py-1 text-sm w-full"
                                    />
                                  ) : (
                                    formatDisplayDate(transaction.date, dateDisplayLocale)
                                  )}
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-900">
                                  {isEditing ? (
                                    <div className="space-y-2">
                                      <textarea
                                        value={editingTransaction.description || ''}
                                        onChange={(e) => handleEditingFieldChange('description', e.target.value)}
                                        className="w-full border rounded px-2 py-1 text-sm"
                                        rows={2}
                                      />
                                      {transaction.type === 'income' && (
                                        <>
                                          <select
                                            value={editingTransaction.clientId || ''}
                                            onChange={(e) => {
                                              const selectedClient = clientById.get(e.target.value);
                                              setEditingTransaction((prev) => ({
                                                ...prev,
                                                clientId: selectedClient?.id || '',
                                                clientName: selectedClient ? getClientDisplayName(selectedClient) : ''
                                              }));
                                            }}
                                            className="w-full border rounded px-2 py-1 text-xs"
                                          >
                                            <option value="">No client selected</option>
                                            {editingTransaction.clientId && !clientById.has(editingTransaction.clientId) && (
                                              <option value={editingTransaction.clientId}>
                                                {editingTransaction.clientName || 'Archived client'}
                                              </option>
                                            )}
                                            {clientOptions.map((client) => (
                                              <option key={client.id} value={client.id}>
                                                {getClientDisplayName(client)}
                                              </option>
                                            ))}
                                          </select>
                                        </>
                                      )}
                                      {(transaction.type === 'expenses' || transaction.type === 'expense') && (
                                        <details className="relative">
                                          <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded border bg-white px-2 py-1 text-xs text-gray-700">
                                            <span>{getExpenseAllocationLabel(editingTransaction) || 'No client selected'}</span>
                                            <ChevronDown size={14} className="text-gray-400" />
                                          </summary>
                                          <div className="absolute z-30 mt-1 w-full rounded border border-gray-200 bg-white p-2 shadow-lg">
                                            <div className="max-h-36 space-y-1 overflow-y-auto pr-1">
                                              {clientOptions.length > 0 ? clientOptions.map((client) => {
                                                const selectedIds = getExpenseAllocationIds(editingTransaction);
                                                const isSelected = selectedIds.includes(client.id);
                                                return (
                                                  <label key={client.id} className="flex items-center gap-2 text-xs text-gray-700">
                                                    <input
                                                      type="checkbox"
                                                      checked={isSelected}
                                                      onChange={(e) => {
                                                        setEditingTransaction((prev) => {
                                                          const previousIds = getExpenseAllocationIds(prev);
                                                          const nextIds = e.target.checked
                                                            ? [...previousIds, client.id]
                                                            : previousIds.filter((clientId) => clientId !== client.id);
                                                          const clientAllocations = buildClientAllocationsFromIds(nextIds);
                                                          const primaryAllocation = clientAllocations[0];
                                                          return {
                                                            ...prev,
                                                            clientId: primaryAllocation?.clientId || '',
                                                            clientName: primaryAllocation?.clientName || '',
                                                            clientAllocations,
                                                            allocateToClient: clientAllocations.length > 0
                                                          };
                                                        });
                                                      }}
                                                    />
                                                    <span>{getClientDisplayName(client)}</span>
                                                  </label>
                                                );
                                              }) : (
                                                <div className="text-xs text-gray-500">No clients yet.</div>
                                              )}
                                            </div>
                                            {getExpenseAllocationIds(editingTransaction).length > 1 && (
                                              <div className="mt-1 text-[10px] text-gray-500">
                                                Split equally across {getExpenseAllocationIds(editingTransaction).length} clients.
                                              </div>
                                            )}
                                          </div>
                                        </details>
                                      )}
                                      {staffMemberOptions.length > 0 && (
                                        <select
                                          value={editingTransaction.staffMemberId || ''}
                                          onChange={(e) => {
                                            const selectedStaffMember = staffMemberById.get(e.target.value);
                                            setEditingTransaction((prev) => ({
                                              ...prev,
                                              staffMemberId: selectedStaffMember?.id || '',
                                              staffMemberName: selectedStaffMember?.name || ''
                                            }));
                                          }}
                                          className="w-full border rounded px-2 py-1 text-xs"
                                        >
                                          <option value="">No staff selected</option>
                                          {editingTransaction.staffMemberId && !staffMemberById.has(editingTransaction.staffMemberId) && (
                                            <option value={editingTransaction.staffMemberId}>
                                              {editingTransaction.staffMemberName || 'Archived staff member'}
                                            </option>
                                          )}
                                          {staffMemberOptions.map((staffMember) => (
                                            <option key={staffMember.id} value={staffMember.id}>
                                              {staffMember.label}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="min-w-0">
                                      <div className="truncate font-medium text-gray-900" title={transaction.description || 'No description'}>
                                        {transaction.description || 'No description'}
                                      </div>
                                      {(transaction.clientName || getExpenseAllocationLabel(transaction)) && (
                                        <div className="mt-1 text-xs text-gray-500">
                                          Client: {getExpenseAllocationLabel(transaction) || transaction.clientName}
                                        </div>
                                      )}
                                      {transaction.staffMemberName && (
                                        <div className="mt-1 text-xs text-gray-500">
                                          Staff: {transaction.staffMemberName}
                                        </div>
                                      )}
                                      {transaction.receiptId && (
                                        <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-[color:var(--color-primary-800)] rounded-full">
                                          Receipt
                                        </span>
                                      )}
                                      {!transaction.receiptId && receiptImageUrl && (
                                        <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-[color:var(--color-primary-800)] rounded-full">
                                          Receipt
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-600">
                                  {isEditing ? (
                                    isEditingSplitTransaction ? (
                                      <div className="space-y-2 min-w-[340px] rounded-lg border border-indigo-100 bg-indigo-50/40 p-2">
                                        <div className={`grid ${canSaveEditingSplitItemsToCost ? 'grid-cols-[minmax(0,1fr)_110px_64px_38px]' : 'grid-cols-[minmax(0,1fr)_110px_38px]'} gap-2 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500`}>
                                          <span>Category</span>
                                          <span className="text-right">Amount</span>
                                          {canSaveEditingSplitItemsToCost && <span className="text-center">Save</span>}
                                          <span></span>
                                        </div>
                                        {editingSplitItems.map((item, splitIndex) => (
                                          <div key={item.id} className={`grid ${canSaveEditingSplitItemsToCost ? 'grid-cols-[minmax(0,1fr)_110px_64px_38px]' : 'grid-cols-[minmax(0,1fr)_110px_38px]'} gap-2 items-center`}>
                                            <select
                                              value={item.category || ''}
                                              onChange={(e) => handleEditingSplitItemChange(item.id, 'category', e.target.value)}
                                              className="border border-gray-300 bg-white rounded px-2 py-1.5 text-xs"
                                            >
                                              <option value="">Select</option>
                                              {categoryOptions.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                              ))}
                                            </select>
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              pattern="-?\\d*\\.?\\d*"
                                              value={item.amount || ''}
                                              onChange={(e) => handleEditingSplitItemChange(item.id, 'amount', e.target.value)}
                                              className="border border-gray-300 bg-white rounded px-2 py-1.5 text-xs text-right"
                                              placeholder="0.00"
                                            />
                                            {canSaveEditingSplitItemsToCost && (
                                              <label className="flex justify-center">
                                                <input
                                                  type="checkbox"
                                                  checked={!!item.saveToCost}
                                                  onChange={(e) => handleEditingSplitItemChange(item.id, 'saveToCost', e.target.checked)}
                                                  title={`Save split item ${splitIndex + 1} to Cost Calculator`}
                                                />
                                              </label>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveEditingSplitItem(item.id)}
                                              className="text-red-600 border border-red-200 rounded px-1.5 py-1 text-xs hover:bg-red-50"
                                              title={`Remove split item ${splitIndex + 1}`}
                                            >
                                              x
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={handleAddEditingSplitItem}
                                          className="text-xs text-indigo-700 hover:underline pl-1"
                                        >
                                          + Add split item
                                        </button>
                                      </div>
                                    ) : (
                                      <select
                                        value={editingTransaction.category || ''}
                                        onChange={(e) => handleEditingFieldChange('category', e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full min-w-[220px]"
                                      >
                                        <option value="">Select</option>
                                        {categoryOptions.map(cat => (
                                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                      </select>
                                    )
                                  ) : (
                                    hasSplitCategoryPreview ? (
                                      <div className="space-y-1.5">
                                        <div className="text-xs font-semibold text-indigo-700">
                                          Split across {categoryBreakdownPreview.length} categories
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                          {categoryBreakdownPreview.slice(0, 3).map((entry) => (
                                            <span
                                              key={`${transaction.id}-${entry.category}`}
                                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[11px] text-indigo-700"
                                            >
                                              <span>{getCategoryDisplayLabel(entry.category)}</span>
                                              <span className="font-medium">{formatCurrency(entry.amount)}</span>
                                            </span>
                                          ))}
                                          {categoryBreakdownPreview.length > 3 && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600">
                                              +{categoryBreakdownPreview.length - 3} more
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="block truncate" title={String(getTransactionCategoryLabel(transaction) || '')}>
                                        {getTransactionCategoryLabel(transaction)}
                                      </span>
                                    )
                                  )}
                                </td>
                                <td className="px-3 py-4 text-sm font-medium">
                                  {isEditing ? (
                                    isEditingSplitTransaction ? (
                                      <div className="w-44 space-y-2">
                                        <input
                                          type="text"
                                          value={editingSplitTotal.toFixed(2)}
                                          readOnly
                                          className="border rounded px-2 py-1 text-sm w-full bg-gray-100 text-gray-600"
                                        />
                                        <div className="text-[10px] text-gray-400 mt-1">Auto from split items</div>
                                        <div>
                                          <div className="text-[10px] text-gray-500 mb-1">{countryTerminology.taxName} amount</div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="-?\\d*\\.?\\d*"
                                            value={editingTransaction.vatAmount || ''}
                                            onChange={(e) => handleEditingFieldChange('vatAmount', e.target.value)}
                                            className="border rounded px-2 py-1.5 text-xs w-full"
                                            placeholder={`${countryTerminology.taxName} amount`}
                                          />
                                        </div>
                                        <div>
                                          <div className="text-[10px] text-gray-500 mb-1">{countryTerminology.taxName} rate %</div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="\\d*\\.?\\d*"
                                            value={editingTransaction.vatRate || ''}
                                            onChange={(e) => handleEditingFieldChange('vatRate', e.target.value)}
                                            className="border rounded px-2 py-1.5 text-xs w-full"
                                            placeholder={`${countryTerminology.taxName} rate %`}
                                          />
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="w-44 space-y-2">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          pattern="\\d*\\.?\\d*"
                                          value={editingTransaction.amount || ''}
                                          onChange={(e) => handleEditingFieldChange('amount', e.target.value)}
                                          className="border rounded px-2 py-1 text-sm w-full"
                                          placeholder="Amount"
                                        />
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          pattern="\\d*\\.?\\d*"
                                          value={editingTransaction.discountAmount || ''}
                                          onChange={(e) => handleEditingFieldChange('discountAmount', e.target.value)}
                                          className="border rounded px-2 py-1.5 text-xs w-full"
                                          placeholder="Discount"
                                        />
                                        <div>
                                          <div className="text-[10px] text-gray-500 mb-1">{countryTerminology.taxName} amount</div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="-?\\d*\\.?\\d*"
                                            value={editingTransaction.vatAmount || ''}
                                            onChange={(e) => handleEditingFieldChange('vatAmount', e.target.value)}
                                            className="border rounded px-2 py-1.5 text-xs w-full"
                                          placeholder={`${countryTerminology.taxName} amount`}
                                          />
                                        </div>
                                        <div>
                                          <div className="text-[10px] text-gray-500 mb-1">{countryTerminology.taxName} rate %</div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="\\d*\\.?\\d*"
                                            value={editingTransaction.vatRate || ''}
                                            onChange={(e) => handleEditingFieldChange('vatRate', e.target.value)}
                                            className="border rounded px-2 py-1.5 text-xs w-full"
                                            placeholder={`${countryTerminology.taxName} rate %`}
                                          />
                                        </div>
                                      </div>
                                    )
                                  ) : (
                                    <div className="flex flex-col">
                                      <span className="text-[color:var(--color-text)]">
                                        {transaction.type === 'income' ? '+' : '-'}
                                        {formatCurrency(transaction.amount)}
                                      </span>
                                      {(Number(transaction.discountAmount) || 0) > 0 && (
                                        <span className="text-[10px] text-gray-400">
                                          Discount: -{formatCurrency(Number(transaction.discountAmount) || 0)}
                                        </span>
                                      )}
                                      {transactionVatAmount > 0 && (
                                        <span className="text-[10px] text-gray-500">
                                          {countryTerminology.taxName}: {formatCurrency(transactionVatAmount)}
                                          {Number.isFinite(transactionVatRate) && transactionVatRate > 0
                                            ? ` @ ${(transactionVatRate * 100).toFixed(1)}%`
                                            : ''}
                                        </span>
                                      )}
                                      {transactionOutstandingAmount > 0.01 && (
                                        <span className="text-[10px] font-medium text-amber-700">
                                          Outstanding: {formatCurrency(transactionOutstandingAmount)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-500">
                                  {isEditing ? (
                                    <div className="space-y-2 min-w-[250px]">
                                      <div className="flex items-center gap-2 text-xs">
                                        <input
                                          type="checkbox"
                                          id={`split-payment-edit-${transaction.id}`}
                                          checked={isEditingSplitPayment}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setEditingTransaction((prev) => {
                                              const sourceAmount = isEditingSplitTransaction
                                                ? editingSplitTotal
                                                : parseFloat(sanitizeAmountInput(prev.amount || '')) || 0;
                                              const defaultMethod = normalizePaymentMethodId(prev.paymentMethod || 'cash') || 'cash';
                                              return {
                                                ...prev,
                                                isSplitPayment: checked,
                                                paymentMethod: defaultMethod,
                                                paymentBreakdown: checked
                                                  ? [createSplitPaymentItem({
                                                      method: defaultMethod,
                                                      amount: sourceAmount > 0 ? sanitizeAmountInput(sourceAmount.toString()) : ''
                                                    })]
                                                  : []
                                              };
                                            });
                                          }}
                                        />
                                        <label htmlFor={`split-payment-edit-${transaction.id}`} className="cursor-pointer">
                                          Split or part payment
                                        </label>
                                      </div>
                                      {isEditingSplitPayment ? (
                                        <div className="space-y-2 border rounded p-2 bg-gray-50">
                                          {editingPaymentSplitItems.map((item) => (
                                            <div key={item.id} className="grid grid-cols-[1fr_90px_32px] gap-2 items-center">
                                              <select
                                                value={item.method || ''}
                                                onChange={(e) => handleEditingPaymentSplitChange(item.id, 'method', e.target.value)}
                                                className="border rounded px-2 py-1.5 text-xs"
                                              >
                                                <option value="">Method</option>
                                                {PAYMENT_METHODS.map((method) => (
                                                  <option key={method.id} value={method.id}>{method.name}</option>
                                                ))}
                                              </select>
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                pattern="\\d*\\.?\\d*"
                                                value={item.amount || ''}
                                                onChange={(e) => handleEditingPaymentSplitChange(item.id, 'amount', e.target.value)}
                                                className="border rounded px-2 py-1.5 text-xs text-right"
                                                placeholder="0.00"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveEditingPaymentSplitItem(item.id)}
                                                className="text-red-600 border border-red-200 rounded px-1 py-1 text-xs hover:bg-red-50"
                                                title="Remove split payment line"
                                              >
                                                x
                                              </button>
                                            </div>
                                          ))}
                                          <button
                                            type="button"
                                            onClick={handleAddEditingPaymentSplitItem}
                                            className="text-xs text-indigo-700 hover:underline"
                                          >
                                            + Add payment
                                          </button>
                                          <div className={`text-[10px] ${editingPaymentSplitTotal - editingTransactionAmount > 0.01 ? 'text-red-600' : 'text-gray-500'}`}>
                                            {formatCurrency(editingPaymentSplitTotal)} / {formatCurrency(editingTransactionAmount)}
                                            {editingTransactionAmount - editingPaymentSplitTotal > 0.01
                                              ? ` (${formatCurrency(editingTransactionAmount - editingPaymentSplitTotal)} outstanding)`
                                              : ''}
                                          </div>
                                        </div>
                                      ) : (
                                        <select
                                          value={editingTransaction.paymentMethod || ''}
                                          onChange={(e) => handleEditingFieldChange('paymentMethod', normalizePaymentMethodId(e.target.value))}
                                          className="border rounded px-2 py-1.5 text-sm w-full"
                                        >
                                          {PAYMENT_METHODS.map(method => (
                                            <option key={method.id} value={method.id}>{method.name}</option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="block truncate" title={String(getTransactionPaymentLabel(transaction) || '')}>
                                      {getTransactionPaymentLabel(transaction)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-4 text-sm">
                                  {isEditing ? (
                                    <div className="flex flex-col items-start gap-2">
                                      <button
                                        onClick={handleSaveTransaction}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                      >
                                        <Save size={14} />
                                        Save
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                      >
                                        <X size={14} />
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {receiptImageUrl && (
                                        <button
                                          type="button"
                                          onClick={() => window.open(receiptImageUrl, '_blank', 'noopener,noreferrer')}
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
                                        >
                                          View Receipt
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleEditTransaction(transaction)}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs hover:bg-indigo-100"
                                      >
                                        <Edit2 size={14} />
                                        Edit
                                      </button>
                                      {canDownloadInvoice && (
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadInvoice(transaction)}
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs hover:bg-emerald-100"
                                        >
                                          <Download size={14} />
                                          Invoice
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteTransaction(transaction.id)}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100"
                                      >
                                        <Trash2 size={14} />
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          }) : null}
                          {(!Array.isArray(recentTransactions) || recentTransactions.length === 0) && (
                            <tr>
                              <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                No transactions yet. Add your first transaction to get started!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {Array.isArray(recentTransactions) && recentTransactions.length > 10 && (
                      <div className="text-center mt-4">
                        <button
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                          onClick={() => setShowAllTransactions((prev) => !prev)}
                        >
                          {showAllTransactions
                            ? 'Show only 10'
                            : `View all ${recentTransactions.length} transactions`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      ) : (
        authView === 'signin' ? (
          <SimpleSignIn
            isOpen={true}
            variant="page"
            onGoogleSignIn={handleGoogleSignIn}
            rememberMe={rememberMe}
            onRememberChange={setRememberMe}
            onSwitchToSignUp={() => navigateAuth('signup')}
          />
        ) : (
          <EnhancedAuthCheck
            isOpen={true}
            onClose={() => {}}
            onGoogleSignIn={handleGoogleSignIn}
            showPricing={true}
            rememberMe={rememberMe}
            onRememberChange={setRememberMe}
            onNavigate={handleAuthNavigate}
          />
        )
      )}
        {/* legacy login removed */ false && (<>
            <div className="space-y-4 mb-6">
            {/* Free Plan */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="font-bold text-[color:var(--color-text)]">Basic Plan</span>
              </div>
              <p className="text-sm text-[color:var(--color-text)] mb-2">Essential features to get started:</p>
              <p className="text-xs text-[color:var(--color-text)] font-medium mb-3">? Always free to use</p>
              <ul className="space-y-2 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Add transactions manually</span>
                    <span className="text-[color:var(--color-text)]"> - Type in what you earned and spent</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">See how much you're really making</span>
                    <span className="text-[color:var(--color-text)]"> - Automatic profit & tax calculations</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Download reports anytime</span>
                    <span className="text-[color:var(--color-text)]"> - Easily share with your accountant</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Know what to charge</span>
                    <span className="text-[color:var(--color-text)]"> - Use our built-in pricing helper</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Set money goals & track progress</span>
                    <span className="text-[color:var(--color-text)]"> - Know if you're hitting your targets</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Keep your data safe</span>
                    <span className="text-[color:var(--color-text)]"> - Syncs with Google Drive</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Premium Trial */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-6 h-6 bg-[color:var(--color-text)] rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="font-bold text-[color:var(--color-text)]">14-Day Free Trial</span>
              </div>
              <p className="text-sm text-[color:var(--color-text)] mb-3">Get full access to premium features:</p>
              <ul className="space-y-2 text-left">
                <li className="flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 p-2 rounded-lg">
                  <span className="text-lg">?</span>
                  <span className="font-semibold text-[color:var(--color-text)]">Everything in Basic Plan</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-[color:var(--color-text)]">Talk instead of typing</span>
                    <span className="text-[color:var(--color-text)]"> - Add income or expenses just by speaking</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-[color:var(--color-text)]">Snap & save receipts</span>
                    <span className="text-[color:var(--color-text)]"> - Take a picture, and we'll fill in the details for you</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-[color:var(--color-text)]">See your money clearly</span>
                    <span className="text-[color:var(--color-text)]"> - Simple charts that show where your money goes</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <div className="text-sm">
                    <span className="font-semibold text-[color:var(--color-text)]">Track growth easily</span>
                    <span className="text-[color:var(--color-text)]"> - Understand what's working in your business</span>
                  </div>
                </li>
                <li className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 p-2 rounded-lg">
                  <span className="text-lg">•</span>
                  <span className="font-semibold text-[color:var(--color-text)]">No credit card required</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-3 mb-6">
              <button
                onClick={handleUpgradeClick}
                className="w-full px-4 py-3 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-lg hover:brightness-95 transition font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                </svg>
                      {premiumCtaLabel}
              </button>

            <button
              onClick={() => handleGoogleSignIn('free')}
              className="w-full px-4 py-3 bg-accent-gradient text-[color:var(--color-text)] rounded-lg hover:brightness-95 transition font-medium"
            >
              Continue with Basic Plan
            </button>

            <button
              onClick={handleGoogleSignIn}
              className="w-full px-4 py-3 border border-gray-300 text-[color:var(--color-text)] rounded-lg hover:bg-gray-50 transition"
            >
              Sign In (Existing Users)
            </button>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg text-left">
            <h3 className="text-[color:var(--color-text)] font-medium mb-2">Your Data Privacy</h3>
            <ul className="list-disc pl-5 text-sm text-[color:var(--color-text)]">
              <li>Your financial data is stored in your own Google Drive</li>
              <li>We never store your data on our servers</li>
              <li>Your data is only accessible to you</li>
              <li>Data persists across all your devices</li>
            </ul>
            </div>
        </>) }
    </div>

  {/* Modals - These should be outside the main-content div but inside the main container */}
  {showDemoDashboard && !hideDemoDashboard && (
    <DemoDashboardModal
      onClose={() => setShowDemoDashboard(false)}
      onDontShow={handleHideDemoDashboard}
    />
  )}

  {/* Cost Calculator Modal (placeholder) */}
  {showCostModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-[color:var(--color-text)]">Cost Calculator</h3>
          <button onClick={() => setShowCostModal(false)} className="p-1 rounded hover:bg-gray-100">?</button>
        </div>
        <div className="p-6 space-y-5 text-[color:var(--color-text)]">
          <p className="text-sm">
            Add every product you use per service. Enter the unit cost (e.g. per bottle/tube) and how many units you use per service to see true cost per appointment.
          </p>

            <div className="space-y-3">
              <div className="hidden sm:grid sm:grid-cols-4 gap-2 text-xs font-semibold text-gray-500 px-1">
                <span>Product</span>
                <span className="text-center">Units per service</span>
                <span className="text-center">Total per service</span>
              <span className="text-center">Actions</span>
            </div>
            {costItems.length === 0 && (
              <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                Start by adding a product below. You can also add the disposable supplies bundle as a quick preset.
              </div>
            )}
            {costItems.map((item) => {
              const unit = getUnitCost(item);
              const usage = parseFloat(item.usagePerService) || 0;
              const total = unit * usage;
              const unitType = item.unitType || 'piece';
              const shouldShowPackSize = ['ml', 'g', 'pump'].includes(unitType);
              const packUnits = (() => {
                const primary = parseFloat(shouldShowPackSize ? item.packSize : item.unitsPerPack);
                const fallback = parseFloat(shouldShowPackSize ? item.unitsPerPack : item.packSize);
                return primary || fallback || 0;
              })();
              const approxApplications = usage > 0 && packUnits > 0 ? Math.floor(packUnits / usage) : null;
              const searchTerm = productSearchTerms[item.id] ?? item.name ?? '';
              const filteredPresets = PRESET_PRODUCTS_SORTED.filter((preset) =>
                preset.name.toLowerCase().includes((searchTerm || '').toLowerCase())
              );
              return (
                <div key={item.id} className="grid grid-cols-1 gap-3 border rounded-lg p-4 bg-white shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-start">
                    <div className="sm:col-span-2 space-y-2">
                      <label className="text-xs font-semibold text-gray-600">Product (search or add custom)</label>
                      <div className="relative">
                        <input
                          type="search"
                          inputMode="search"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          placeholder="Type to search or name a custom item"
                          value={searchTerm}
                          onChange={(e) => handleProductInput(item.id, e.target.value)}
                          list={`product-options-${item.id}`}
                        />
                        <datalist id={`product-options-${item.id}`}>
                          {filteredPresets.map((preset) => (
                            <option key={preset.id} value={preset.name} />
                          ))}
                        </datalist>
                        <p className="text-[11px] text-gray-500 mt-1">Prefills cost fields when an existing product is chosen.</p>
                      </div>
                    </div>
                    <div className="space-y-2 sm:justify-self-center w-full">
                      <label className="text-xs font-semibold text-gray-600">Units per service</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="w-full px-3 py-2 border rounded-lg text-sm text-right"
                        placeholder="e.g. 0.25"
                        value={item.usagePerService}
                        onChange={(e) => updateCostItem(item.id, 'usagePerService', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col items-end gap-2 text-right">
                      <label className="text-xs font-semibold text-gray-600 w-full text-right">Total per service</label>
                      <div className="text-lg font-semibold text-[color:var(--color-text)]">{formatCurrency ? formatCurrency(total) : total.toFixed(2)}</div>
                      <button
                        onClick={() => removeCostItem(item.id)}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs text-gray-700">
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500">Pack price</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-right"
                        placeholder="e.g. 40"
                        value={item.packCost || ''}
                        onChange={(e) => updateCostItem(item.id, 'packCost', e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500">Unit type</span>
                      <select
                        className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
                        value={unitType}
                        onChange={(e) => updateCostItem(item.id, 'unitType', e.target.value)}
                      >
                        {['ml', 'g', 'pump', 'piece', 'wipe', 'set'].map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500">
                        {shouldShowPackSize ? 'Pack size' : 'Units per pack'}
                      </span>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-right"
                        placeholder={shouldShowPackSize ? 'e.g. 30' : 'e.g. 50'}
                        value={shouldShowPackSize ? (item.packSize || item.unitsPerPack || '') : (item.unitsPerPack || item.packSize || '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (shouldShowPackSize) {
                            updateCostItem(item.id, 'packSize', val);
                          } else {
                            updateCostItem(item.id, 'unitsPerPack', val);
                          }
                        }}
                      />
                    </div>
                    <div className="flex items-end text-gray-600">
                      <div className="w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500">Auto unit cost</div>
                        <div className="text-sm font-medium">
                          {formatCurrency ? formatCurrency(unit || 0) : (unit || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isFoundationProduct(item.name) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end text-xs text-gray-700">
                      <div>
                        <span className="block text-[10px] uppercase tracking-wide text-gray-500">Coverage</span>
                        <select
                          className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
                          value={item.coveragePreset || 'medium'}
                          onChange={(e) => handleCoveragePresetChange(item.id, e.target.value)}
                        >
                          <option value="light">Light (0.4–0.5 ml)</option>
                          <option value="medium">Medium (0.6–0.8 ml)</option>
                          <option value="full">Full (1.0–1.2 ml)</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2 text-[11px] text-gray-600 leading-relaxed">
                        Auto-sets usage for foundation so coverage matches client expectations. You can still edit the field above.
                      </div>
                    </div>
                  )}

                  <div className="w-full text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    Auto-calculated: {formatCurrency ? formatCurrency(total) : total.toFixed(2)} per service
                    {approxApplications ? ` (~${approxApplications} applications per pack)` : ''}
                    . These are estimated averages. Your actual cost may vary based on products, technique, and coverage.
                  </div>

                  <div className="w-full">
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Notes (optional)</label>
                    <input
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="Add quick notes about the product usage"
                      value={item.notes}
                      onChange={(e) => updateCostItem(item.id, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={addCostItem}
              className="px-3 py-2 rounded-lg bg-[color:var(--color-warn)] text-[color:var(--color-text)] hover:brightness-95 text-sm"
            >
                + Add item
              </button>
              <button
                onClick={() => {
                  const existing = costItems.find((c) => (c.name || '').toLowerCase().includes('disposable supplies'));
                  if (existing) return;
                  const now = Date.now();
                  const preset = {
                    id: now,
                    name: 'Disposable supplies (per client)',
                    unitCost: 1,
                    packCost: '',
                    packSize: '',
                    unitsPerPack: '',
                    usagePerService: 1,
                    unitType: 'piece',
                    coveragePreset: '',
                    notes: 'Preset bundle: cotton pads, micellar, brush cleanser, spoolies, lip wands, tissues, alcohol wipes.'
                  };
                  setCostItems((prev) => [...prev, preset]);
                  setProductSearchTerms((prev) => ({ ...prev, [now]: preset.name }));
                }}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white text-[color:var(--color-text)] hover:bg-gray-50"
              >
                + Add disposable supplies bundle (€0.50–€1.50)
              </button>
              <button
                onClick={() => {
                  setCostItems([]);
                  setProductSearchTerms({});
                }}
                className="px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white text-[color:var(--color-text)] hover:bg-gray-50"
              >
                Clear all
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-100 rounded-lg p-3 text-sm">
            <div className="flex flex-col">
              <span className="font-medium">Total cost per service</span>
              <span className="text-xs text-gray-500">Auto-applies in Pricing Calculator.</span>
            </div>
            <span className="font-semibold">
              {formatCurrency ? formatCurrency(costSummary.perService || 0) : (costSummary.perService ? costSummary.perService.toFixed(2) : '0.00')}
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCostModal(false)} className="px-3 py-2 rounded-lg bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95">Save</button>
          </div>
        </div>
      </div>
    </div>
  )}
  {/* Overhead Calculator Modal */}
  {showOverheadModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[color:var(--color-text)]">Overhead Calculator</h3>
          <button onClick={() => setShowOverheadModal(false)} className="p-1 rounded hover:bg-gray-100">?</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-sm font-medium text-[color:var(--color-text)]">Pick a period to calculate overheads</div>
            <button
              type="button"
              onClick={setLast30Days}
              className="self-start sm:self-auto px-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 shadow-sm"
            >
              Use last 30 days
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1">From</label>
              <input type="date" value={overheadStart || ''} onChange={e => setOverheadStart(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">To</label>
              <input type="date" value={overheadEnd || ''} onChange={e => setOverheadEnd(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Days per week</label>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                max="7"
                value={workDaysPerWeek}
                onChange={e => setWorkDaysPerWeek(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Hours per day</label>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                max="16"
                value={hoursPerDay}
                onChange={e => setHoursPerDay(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input type="radio" name="premises" value="separate" checked={premisesType==='separate'} onChange={() => setPremisesType('separate')} />
              <span>Separate premises</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="premises" value="home" checked={premisesType==='home'} onChange={() => setPremisesType('home')} />
              <span>Work from home (30% utilities)</span>
            </label>
          </div>
          <p className="text-xs text-gray-600">We auto-pull Utilities and Rent/Lease expenses in the selected dates to build your overhead rate.</p>
          <div className="flex justify-end gap-2">
            <button onClick={computeOverhead} className="px-3 py-2 rounded-lg bg-[color:var(--color-warn)] text-[color:var(--color-text)] hover:brightness-95">Calculate</button>
            <button onClick={() => setShowOverheadModal(false)} className="px-3 py-2 rounded-lg bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95">Save</button>
          </div>
          {overheadBreakdown && (
            <div className="mt-2 space-y-3 text-[color:var(--color-text)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Utilities in period</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.utilitiesExpense || 0)}</div>
                </div>
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Rent/Lease in period</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.rentExpense || 0)}</div>
                </div>
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Staff/Payroll</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.staffExpense || 0)}</div>
                </div>
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Insurance</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.insuranceExpense || 0)}</div>
                </div>
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Marketing/Ads</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.marketingExpense || 0)}</div>
                </div>
                <div className="rounded-lg p-3 bg-white border border-gray-200 shadow-sm">
                  <div className="text-xs uppercase text-gray-500">Professional Fees</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.professionalExpense || 0)}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg p-3 bg-info-gradient">
                  <div className="text-xs uppercase">Total Period Expense</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.totalExpense)}</div>
                </div>
                <div className="rounded-lg p-3 bg-info-gradient">
                  <div className="text-xs uppercase">Adjusted ({overheadBreakdown.adjustmentLabel})</div>
                  <div className="text-lg font-bold">{formatCurrency(overheadBreakdown.adjusted)}</div>
                </div>
                <div className="rounded-lg p-3 bg-info-gradient">
                  <div className="text-xs uppercase">Hourly Overhead</div>
                  <div className="text-lg font-bold">{(overheadHourly ?? 0).toFixed(2)}</div>
                </div>
              </div>
              <div className="text-sm text-[color:var(--color-text)] bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="font-medium">Work Hours Summary</div>
                <p>
                  Total Working Hours for Selected Period:&nbsp;
                  <strong>{(overheadBreakdown.totalWorkHours ?? 0).toFixed(1)} hours</strong>
                  {overheadBreakdown.workingDays ? (
                    <> ({overheadBreakdown.workingDays} working days)</>
                  ) : null}
                </p>
                <p className="mt-1">
                  Each service you perform costs <strong>{formatCurrency(overheadHourly ?? 0)}</strong> in overhead before product and labour.
                </p>
              </div>
            </div>
          )}
          {overheadBreakdown && Number(overheadBreakdown.totalExpense) === 0 && (
            <div className="mt-3 p-3 rounded-lg bg-[color:var(--color-warn)] text-[color:var(--color-text)]">
              No Utilities or Rent/Lease expenses found for the selected period. Add those expenses to calculate overheads.
            </div>
          )}
          <div className="text-xs text-gray-500">Note: Overhead uses expenses in categories like <strong>Utilities</strong>/<strong>Electricity</strong>/<strong>Water</strong>/<strong>Internet</strong>, <strong>Rent/Lease</strong>, <strong>Staff/Payroll</strong>, <strong>Insurance</strong>, <strong>Marketing/Ads</strong>, and <strong>Professional Fees</strong> for the selected dates. If you work from home, utilities are adjusted to 30% for business use.</div>
          <div className="mt-2">
            <button
              onClick={() => { if (overheadHourly != null) setShowOverheadModal(false); }}
              className="px-4 py-2 rounded-full bg-white border shadow-sm hover:shadow-md btn-float"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
  {/* Depreciation Edit Modal */}
  {editingDepreciationId && (() => {
      const asset = transactions.find(t => t.id === editingDepreciationId);
      const currentValue = customDepreciationPeriods[editingDepreciationId];
      const defaultPeriod = asset?.depreciationYears ?? 8;
      const inputValue = currentValue === '' ? '' : (currentValue ?? defaultPeriod);

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold">Edit Depreciation Period</h2>
            <input
              type="number"
              min="1"
              max="8"
              value={inputValue}
              placeholder={defaultPeriod}
              onChange={(e) => {
                const rawValue = e.target.value;
                setCustomDepreciationPeriods(prev => ({
                  ...prev,
                  [editingDepreciationId]: rawValue === '' ? '' : Number(rawValue)
                }));
              }}
              className="w-full p-3 border rounded-lg mt-4"
            />
            <div className="flex justify-end gap-4 mt-4">
              <button
                onClick={() => {
                  setCustomDepreciationPeriods(prev => {
                    if (prev[editingDepreciationId] === '') {
                      const next = { ...prev };
                      delete next[editingDepreciationId];
                      return next;
                    }
                    return prev;
                  });
                  setEditingDepreciationId(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const yearsToSave = customDepreciationPeriods[editingDepreciationId];
                  const valueToPersist = yearsToSave === '' || yearsToSave == null
                    ? defaultPeriod
                    : Number(yearsToSave);
                  handleUpdateDepreciation(editingDepreciationId, valueToPersist);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* Asset Disposal Modal (Sell or Write Off) */}
    {disposingAssetId && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md">
          <h2 className="text-xl font-semibold mb-4">
            {disposalData.disposalType === 'sold' ? 'Sell Asset' : 'Write Off Asset'}
          </h2>

          <div className="space-y-4">
            {/* Asset being disposed */}
            {(() => {
              const asset = transactions.find(t => t.id === disposingAssetId);
              if (!asset) return null;

              return (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium">
                    {CATEGORIES.expenses.find(c => c.id === asset.category)?.name}
                    {asset.description && ` - ${asset.description}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    Purchase Price: {formatCurrency(asset.amount)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Current Value: {formatCurrency(asset.amount)}
                  </div>
                </div>
              );
            })()}

            {/* Disposal Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Disposal Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="disposalType"
                    value="sold"
                    checked={disposalData.disposalType === 'sold'}
                    onChange={() => setDisposalData({...disposalData, disposalType: 'sold'})}
                    className="mr-2"
                  />
                  Sold
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="disposalType"
                    value="written-off"
                    checked={disposalData.disposalType === 'written-off'}
                    onChange={() => setDisposalData({...disposalData, disposalType: 'written-off'})}
                    className="mr-2"
                  />
                  Write Off
                </label>
              </div>
            </div>

            {/* Disposal Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {disposalData.disposalType === 'sold' ? 'Sale Date' : 'Write-off Date'}
              </label>
              <input
                type="date"
                value={disposalData.disposalDate}
                onChange={(e) => setDisposalData({...disposalData, disposalDate: e.target.value})}
                className="w-full p-3 border rounded-lg"
              />
            </div>

            {/* Sale Amount (only for sold assets) */}
            {disposalData.disposalType === 'sold' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sale Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={disposalData.disposalAmount}
                  onChange={(e) => setDisposalData({...disposalData, disposalAmount: e.target.value})}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
            )}

            {/* Reason for disposal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for {disposalData.disposalType === 'sold' ? 'Sale' : 'Write-off'}
              </label>
              <textarea
                value={disposalData.disposalReason}
                onChange={(e) => setDisposalData({...disposalData, disposalReason: e.target.value})}
                className="w-full p-3 border rounded-lg"
                rows="3"
                placeholder={disposalData.disposalType === 'sold'
                  ? "e.g., Upgraded to newer model"
                  : "e.g., Damaged beyond repair, Lost, No longer functional"
                }
              ></textarea>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setDisposingAssetId(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAssetDisposal}
                className={`px-4 py-2 text-white rounded-lg transition ${
                  disposalData.disposalType === 'sold'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-[color:var(--color-text)] hover:opacity-90'
                }`}
              >
                {disposalData.disposalType === 'sold' ? 'Complete Sale' : 'Write Off Asset'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Input Method Selection Modal */}
    {showInputMethodSelection && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 w-full max-w-lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Add {activeTab === 'income' ? 'Sale' : 'Expense'}
            </h2>
            <p className="text-gray-600">
              Choose how you'd like to add your {activeTab === 'income' ? 'sale' : 'expense'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-6">
            {/* Manual Entry Option */}
            <button
              onClick={() => {
                setSelectedInputMethod('manual');
                setShowInputMethodSelection(false);
                setIsAddingTransaction(true);
                resetSplitSaleState();
                resetSplitPaymentState();
                // Set up the current transaction based on active tab
                setCurrentTransaction(createEmptyTransactionDraft(activeTab, {
                  category: activeTab === 'income' ? 'nail_services' : 'supplies',
                  receiptUrl: ''
                }));
              }}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200 text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <BarChart2 size={20} className="text-gray-800" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Manual Entry</h3>
                  <p className="text-sm text-gray-600">Fill out the form manually with all details</p>
                </div>
              </div>
            </button>

            {/* Voice Input Option - Premium Feature */}
            <VoiceToTextGate
              hasAccessOverride={workspaceVoiceAccess}
              fallback={
                <div className="relative">
                  <button
                    disabled
                    className="p-6 border-2 border-gray-200 rounded-xl bg-gray-50 opacity-60 cursor-not-allowed text-left group w-full"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <BarChart2 size={20} className="text-gray-800" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-600 mb-1">Voice Input</h3>
                        <p className="text-sm text-gray-500">
                          {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                            ? 'Checking client workspace plan'
                            : isExternalSharedWorkspace
                            ? 'Client workspace Premium required'
                            : 'Premium feature - Start your free trial'}
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="absolute top-3 right-3">
                    <span className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] text-xs px-3 py-1 rounded-lg font-medium shadow-sm border border-yellow-300">
                      Premium
                    </span>
                  </div>
                </div>
              }
            >
              <button
                onClick={() => {
                  setSelectedInputMethod('voice');
                  setShowInputMethodSelection(false);
                  setShowModalVoiceInput(true);
                }}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all duration-200 text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <BarChart2 size={20} className="text-gray-800" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">Voice Input</h3>
                    <p className="text-sm text-gray-600">Speak your transaction naturally</p>
                  </div>
                </div>
              </button>
            </VoiceToTextGate>

            {/* Receipt / sales invoice upload option - Premium Feature */}
            <ReceiptScannerGate
                hasAccessOverride={workspaceReceiptScannerAccess}
                fallback={
                <div className="relative">
                  <button
                    disabled
                    className="p-6 border-2 border-gray-200 rounded-xl bg-gray-50 opacity-60 cursor-not-allowed text-left group w-full"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <BarChart2 size={20} className="text-gray-800" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-600 mb-1">
                          {activeTab === 'income' ? 'Upload Sales Invoice' : 'Upload Receipt'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {isExternalSharedWorkspace && isWorkspaceSubscriptionLoading
                            ? 'Checking client workspace plan'
                            : isExternalSharedWorkspace
                            ? 'Client workspace Premium required'
                            : 'Premium feature - Start your free trial'}
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="absolute top-3 right-3">
                    <span className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] text-xs px-3 py-1 rounded-lg font-medium shadow-sm border border-yellow-300">
                      Premium
                    </span>
                  </div>
                </div>
                }
              >
                <button
                  onClick={() => {
                    setSelectedInputMethod('receipt');
                    setShowInputMethodSelection(false);
                    setShowReceiptModal(true);
                  }}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <BarChart2 size={20} className="text-gray-800" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">
                        {activeTab === 'income' ? 'Upload Sales Invoice' : 'Upload Receipt'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {activeTab === 'income'
                          ? 'Scan or upload a sales invoice'
                          : 'Scan or upload a receipt photo'}
                      </p>
                    </div>
                  </div>
                </button>
            </ReceiptScannerGate>

            <button
              onClick={handleSpreadsheetImportClick}
              disabled={isSpreadsheetImporting}
              className={`p-6 border-2 border-gray-200 rounded-xl transition-all duration-200 text-left group ${
                isSpreadsheetImporting
                  ? 'bg-gray-50 opacity-70 cursor-not-allowed'
                  : 'hover:border-cyan-300 hover:bg-cyan-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                  <Upload size={20} className="text-gray-800" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {activeTab === 'income' ? 'Upload Spreadsheet' : 'Upload Statement'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isSpreadsheetImporting
                      ? 'Importing file...'
                      : (activeTab === 'income'
                        ? 'Import sales from Excel or Google Sheets export'
                        : 'Import expenses from bank statement CSV, spreadsheet, or PDF')}
                  </p>
                </div>
              </div>
            </button>
            <input
              ref={spreadsheetFileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.pdf,application/pdf"
              onChange={handleSpreadsheetFileSelected}
              className="hidden"
            />
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => setShowInputMethodSelection(false)}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Spreadsheet Import Review Modal */}
    {showSpreadsheetReviewModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">
                Review Imported {spreadsheetImportMeta.defaultImportType === 'income' ? 'Sales' : 'Expenses'}
              </h2>
              <p className="text-sm text-gray-600">
                {spreadsheetImportMeta.fileName || 'Spreadsheet'} • {spreadsheetImportDrafts.length} row(s) ready
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancelSpreadsheetReview}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              aria-label="Close import review"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {(spreadsheetImportMeta.sourceVariant === 'sales_summary' || spreadsheetImportMeta.parserSkippedRows > 0 || spreadsheetImportMeta.typeFilteredRows > 0 || spreadsheetImportMeta.autoCalculatedVatRows > 0 || (spreadsheetImportMeta.suggestedCategories || []).length > 0) && (
            <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
              {spreadsheetImportMeta.sourceVariant === 'sales_summary' && (
                <div>
                  Detected Fresha sales summary format. Date was inferred from the file name; review before saving.
                </div>
              )}
              {spreadsheetImportMeta.autoCalculatedVatRows > 0 && (
                <div>
                  {countryTerminology.taxName} was auto-calculated for {spreadsheetImportMeta.autoCalculatedVatRows} row(s) using your registered {countryTerminology.taxName} rates.
                </div>
              )}
              {spreadsheetImportMeta.parserSkippedRows > 0 && (
                <div>{spreadsheetImportMeta.parserSkippedRows} source row(s) were skipped during parsing.</div>
              )}
              {spreadsheetImportMeta.typeFilteredRows > 0 && (
                <div>{spreadsheetImportMeta.typeFilteredRows} row(s) were skipped because they belong to the other tab.</div>
              )}
              {(spreadsheetImportMeta.suggestedCategories || []).length > 0 && (
                <div className="mt-2">
                  <div>
                    Suggested new categories based on imported descriptions:
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleApplyAllSuggestedImportCategories}
                      className="px-2 py-1 border border-amber-400 rounded hover:bg-amber-100"
                    >
                      Add all suggestions
                    </button>
                    {(spreadsheetImportMeta.suggestedCategories || []).map((suggestedCategory) => (
                      <button
                        key={suggestedCategory.id}
                        type="button"
                        onClick={() => handleApplySuggestedImportCategory(suggestedCategory)}
                        className="px-2 py-1 border border-amber-400 rounded hover:bg-amber-100"
                      >
                        Add {suggestedCategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="border rounded-lg overflow-auto max-h-[58vh]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-700">#</th>
                  <th className="text-left p-2 font-medium text-gray-700">Date</th>
                  <th className="text-left p-2 font-medium text-gray-700">Description</th>
                  <th className="text-left p-2 font-medium text-gray-700">Category</th>
                  <th className="text-left p-2 font-medium text-gray-700">Amount</th>
                  <th className="text-left p-2 font-medium text-gray-700">Allocate to client</th>
                  <th className="text-left p-2 font-medium text-gray-700">{countryTerminology.taxName}</th>
                  <th className="text-left p-2 font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {spreadsheetImportDrafts.map((row) => {
                  const visibleCategoryOptions = transactionInputCategories[row.type] || [];
                  const currentCategoryOption = (mergedCategories[row.type] || []).find((cat) => cat.id === row.category);
                  const categoryOptions = currentCategoryOption && !visibleCategoryOptions.some((cat) => cat.id === currentCategoryOption.id)
                    ? [currentCategoryOption, ...visibleCategoryOptions]
                    : visibleCategoryOptions;
                  return (
                    <tr key={row.draftId} className="border-t align-top">
                      <td className="p-2 text-gray-500">{row.rowNumber || '-'}</td>
                      <td className="p-2">
                        <input
                          type="date"
                          value={row.date || ''}
                          onChange={(e) => handleSpreadsheetDraftFieldChange(row.draftId, 'date', e.target.value)}
                          className="w-36 p-2 border rounded"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          value={row.description || ''}
                          onChange={(e) => handleSpreadsheetDraftFieldChange(row.draftId, 'description', e.target.value)}
                          className="w-64 p-2 border rounded"
                          placeholder="Description"
                        />
                      </td>
                      <td className="p-2">
                        <select
                          value={row.category || ''}
                          onChange={(e) => handleSpreadsheetDraftFieldChange(row.draftId, 'category', e.target.value)}
                          className="w-48 p-2 border rounded"
                        >
                          <option value="">Select category</option>
                          {categoryOptions.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => handleAddSpreadsheetDraftCategory(row.draftId, row.type)}
                          className="mt-1 text-xs font-medium text-[color:var(--color-text)] hover:underline"
                        >
                          + New category
                        </button>
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={row.amount || ''}
                          onChange={(e) => handleSpreadsheetDraftFieldChange(row.draftId, 'amount', e.target.value)}
                          className="w-28 p-2 border rounded"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2">
                        {row.type === 'expenses' || row.type === 'expense' ? (
                          <details className="relative">
                            <summary className="flex w-56 cursor-pointer list-none items-center justify-between rounded border bg-white px-3 py-2 text-sm text-gray-700">
                              <span className="truncate">{getExpenseAllocationLabel(row) || 'No client selected'}</span>
                              <ChevronDown size={14} className="text-gray-400" />
                            </summary>
                            <div className="absolute z-40 mt-1 w-56 rounded border border-gray-200 bg-white p-2 shadow-lg">
                              <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                                {clientOptions.length > 0 ? clientOptions.map((client) => {
                                  const selectedIds = getExpenseAllocationIds(row);
                                  const isSelected = selectedIds.includes(client.id);
                                  return (
                                    <label key={client.id} className="flex items-center gap-2 text-xs text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const previousIds = getExpenseAllocationIds(row);
                                          const nextIds = e.target.checked
                                            ? [...previousIds, client.id]
                                            : previousIds.filter((clientId) => clientId !== client.id);
                                          const clientAllocations = buildClientAllocationsFromIds(nextIds);
                                          const primaryAllocation = clientAllocations[0];
                                          setSpreadsheetImportDrafts((prev) => prev.map((draft) => (
                                            draft.draftId === row.draftId
                                              ? {
                                                  ...draft,
                                                  clientId: primaryAllocation?.clientId || '',
                                                  clientName: primaryAllocation?.clientName || '',
                                                  clientAllocations,
                                                  allocateToClient: clientAllocations.length > 0
                                                }
                                              : draft
                                          )));
                                        }}
                                      />
                                      <span>{getClientDisplayName(client)}</span>
                                    </label>
                                  );
                                }) : (
                                  <div className="text-xs text-gray-500">No clients yet.</div>
                                )}
                              </div>
                              {getExpenseAllocationIds(row).length > 1 && (
                                <div className="mt-1 text-[10px] text-gray-500">
                                  Split equally across {getExpenseAllocationIds(row).length} clients.
                                </div>
                              )}
                            </div>
                          </details>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={row.vatAmount || ''}
                          onChange={(e) => handleSpreadsheetDraftFieldChange(row.draftId, 'vatAmount', e.target.value)}
                          className="w-24 p-2 border rounded"
                          placeholder="optional"
                        />
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => handleSpreadsheetDraftDelete(row.draftId)}
                          className="px-2 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancelSpreadsheetReview}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmSpreadsheetImport}
              className="px-4 py-2 rounded-lg bg-info-gradient text-[color:var(--color-text)] hover:opacity-90"
            >
              Save {spreadsheetImportDrafts.length} Transaction{spreadsheetImportDrafts.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Voice Input Modal (Standalone) */}
    {showModalVoiceInput && !isAddingTransaction && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Voice Input - {activeTab === 'income' ? 'Sale' : 'Expense'}
            </h2>
            <button
              onClick={() => setShowModalVoiceInput(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              type="button"
              aria-label="Close voice input"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

            <div className="p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-2 mb-3 text-sm text-gray-700">
              <input
                type="checkbox"
                id="autosave-cost-voice"
                checked={autoSaveCostItems}
                onChange={(e) => setAutoSaveCostItems(e.target.checked)}
              />
              <label htmlFor="autosave-cost-voice" className="cursor-pointer">
                Save parsed line items to Cost Calculator
              </label>
            </div>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => handleAddCategory(activeTab)}
                className="text-xs text-[color:var(--color-text)] hover:underline"
              >
                + New category
              </button>
            </div>
            <VoiceInput
              onTransactionParsed={async (transaction) => {
                const wasSaved = await handleVoiceTransactionParsed(transaction);
                if (!wasSaved) {
                  return;
                }
                setShowModalVoiceInput(false);
                setSelectedInputMethod(null);
              }}
              onClose={() => {
                setShowModalVoiceInput(false);
                setSelectedInputMethod(null);
              }}
              className="w-full"
              debug={false}
              categories={transactionInputCategories}
              paymentMethods={PAYMENT_METHODS}
              defaultType={activeTab === 'income' ? 'income' : 'expenses'}
            />
          </div>
        </div>
      </div>
    )}

    {/* Receipt / sales invoice scanner modal */}
    {showReceiptModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {activeTab === 'income' ? 'Upload Sales Invoice' : 'Upload Expense Receipt'}
            </h2>
            <button
              onClick={() => {
                setShowReceiptModal(false);
                setSelectedInputMethod(null);
              }}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              type="button"
              aria-label="Close receipt upload"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg border p-4">
            {activeTab !== 'income' && (
              <div className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  id="autosave-cost-receipt"
                  checked={autoSaveCostItems}
                  onChange={(e) => setAutoSaveCostItems(e.target.checked)}
                />
                <label htmlFor="autosave-cost-receipt" className="cursor-pointer">
                  Save receipt line items to Cost Calculator
                </label>
              </div>
            )}
              <ReceiptScannerProvider>
                <ReceiptScannerIntegration
                  categories={transactionInputCategories}
                  onAddCategory={handleAddCategory}
                  onAddClient={handleAddClient}
                  onAddSupplier={(supplier) => {
                    const supplierId = upsertRctSubcontractor({
                      ...(supplier || {}),
                      supplierType: 'standard',
                      rctDeductionRate: 0
                    });
                    return supplierId ? { ...(supplier || {}), id: supplierId, supplierType: 'standard' } : null;
                  }}
                  transactionType={activeTab === 'income' ? 'income' : 'expenses'}
                  clients={clientOptions}
                  paymentMethods={PAYMENT_METHODS}
                  rctSubcontractors={rctSubcontractorOptions}
                  supportsRct={supportsRct}
                  supportsProjects={supportsProjects}
                  activeWorkspace={activeWorkspace}
                  onAddTransaction={async (transaction) => {
                    console.log('[APP] Receipt scanner transaction received:', transaction);

                  // Convert receipt transaction to our format
                  const convertedTransaction = {
                    ...transaction,
                    type: activeTab, // Use the current active tab (income/expenses)
                    date: transaction.date || new Date().toISOString().split('T')[0],
                    id: transaction.id || transaction.transactionId || transaction.timestamp || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                  };

                  const wasSaved = await handleVoiceTransactionParsed(convertedTransaction, { sourceLabel: 'receipt' });
                  if (!wasSaved) {
                    return;
                  }
                  if (transaction.keepReceiptScannerOpen) {
                    return;
                  }
                  setShowReceiptModal(false);
                  setSelectedInputMethod(null);
                }}
                onUpdateTransaction={(transaction) => {
                  console.log('[APP] Receipt scanner transaction updated:', transaction);
                }}
                userRole={user?.email?.includes('admin') ? 'admin' : 'user'}
              />
            </ReceiptScannerProvider>
          </div>
        </div>
      </div>
    )}

    {/* AI Chat Modal */}
    {showAIChat && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-purple-800">
              AI Assistant - Business Finance Helper
            </h2>
            <button
              onClick={() => setShowAIChat(false)}
              className="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 overflow-hidden text-[0]"
              aria-label="Close AI assistant"
            >
              <X size={20} className="text-gray-500" />
              ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¾Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â
            </button>
          </div>

          <div className="bg-accent-gradient rounded-lg p-6">
            <div className="text-center">
              <Sparkles size={48} className="text-purple-400 mx-auto" />
              <h3 className="text-lg font-semibold text-[color:var(--color-text)] mb-2">
                AI Assistant Coming Soon!
              </h3>
              <p className="text-[color:var(--color-text)]/80 mb-4">
                Your intelligent business finance assistant will help you with:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart2 size={20} className="text-gray-800" />
                    <h4 className="font-semibold">Business Analytics</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Get insights about your revenue, expenses, and profitability
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart2 size={20} className="text-gray-800" />
                    <h4 className="font-semibold">Smart Suggestions</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Receive personalized recommendations for your business
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart2 size={20} className="text-gray-800" />
                    <h4 className="font-semibold">Trend Analysis</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Understand your business patterns and seasonal trends
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart2 size={20} className="text-gray-800" />
                    <h4 className="font-semibold">Goal Setting</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Set and track financial goals for your business
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    <FuelVatDecisionModal
      isOpen={Boolean(pendingFuelVatReview)}
      review={pendingFuelVatReview}
      rememberChoice={rememberFuelVatPreference}
      onRememberChoiceChange={setRememberFuelVatPreference}
      onCancel={() => resolvePendingFuelVatReview('cancel')}
      onChooseOtherTravel={() => resolvePendingFuelVatReview('other_travel')}
      onChooseDiesel={() => resolvePendingFuelVatReview('diesel')}
      onChoosePetrol={() => resolvePendingFuelVatReview('petrol')}
    />

    {/* Add Transaction Modal - Manual Entry Only */}
    {isAddingTransaction && selectedInputMethod === 'manual' && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Manual Entry - {activeTab === 'income' ? 'Sale' : 'Expense'}
            </h2>
            <button
              onClick={() => {
                setIsAddingTransaction(false);
                setSelectedInputMethod(null);
                resetSplitSaleState();
                resetSplitPaymentState();
              }}
                className="text-gray-500 hover:text-gray-700 text-2xl">
                x
              </button>
          </div>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>

              <select
                value={currentTransaction.category || ""}
                onChange={(e) => {
                  setCurrentTransaction((prev) => ({
                    ...prev,
                    category: e.target.value,
                    service: '',
                    description: '',
                    amount: '',
                    discountAmount: ''
                  }));
                  setSelectedService(null);
                }}
                className={`w-full p-3 border rounded-lg ${
                  splitSaleMode ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                disabled={splitSaleMode}
                required
              >
                <option value="">Select category</option>
                {activeCategoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleAddCategory(activeTab)}
                className="mt-2 text-sm text-[color:var(--color-text)] hover:underline"
                disabled={splitSaleMode}
              >
                + New category
              </button>
              <button
                type="button"
                onClick={() => setShowCategoryManager((prev) => !prev)}
                className="mt-2 ml-4 text-sm text-gray-600 hover:text-[color:var(--color-text)] hover:underline"
                disabled={splitSaleMode}
              >
                {showCategoryManager ? 'Done managing' : 'Manage categories'}
              </button>

              {showCategoryManager && !splitSaleMode && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">
                        {activeCategoryType === 'income' ? 'Sales categories' : 'Expense categories'}
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        Delete custom categories or hide built-in categories from future entries. Existing transactions keep their saved category.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCategoryManager(false)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-700"
                      aria-label="Close category manager"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="mt-3 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
                    {activeCategoryOptions.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500">No visible categories.</div>
                    ) : (
                      activeCategoryOptions.map((cat) => {
                        const isCustomCategory = (extraCategories[activeCategoryType] || [])
                          .some((entry) => entry.id === cat.id);
                        return (
                          <div key={cat.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-gray-800">{cat.icon} {cat.name}</div>
                              <div className="text-xs text-gray-500">{isCustomCategory ? 'Custom' : 'Built-in'}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(activeCategoryType, cat.id)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                              title={isCustomCategory ? 'Delete category' : 'Hide category'}
                            >
                              <Trash2 size={13} />
                              {isCustomCategory ? 'Delete' : 'Hide'}
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {activeHiddenCategoryOptions.length > 0 && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Hidden built-in categories</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {activeHiddenCategoryOptions.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleRestoreCategory(activeCategoryType, cat.id)}
                            className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Restore {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Service Selection for Income Categories */}
            {activeTab === 'income' && !splitSaleMode && currentTransaction.category && servicePrices[currentTransaction.category] && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service
                </label>
                <select
                  value={currentTransaction.service || ""}
                  onChange={(e) => {
                    const selectedServiceName = e.target.value;
                    if (selectedServiceName) {
                      const service = servicePrices[currentTransaction.category]?.find(
                        s => s.name === selectedServiceName
                      );
                      if (service) {
                        setCurrentTransaction((prev) => ({
                          ...prev,
                          service: selectedServiceName,
                          description: selectedServiceName,
                          amount: service.price.toString(),
                          discountAmount: ''
                        }));
                      }
                    } else {
                      setCurrentTransaction((prev) => ({
                        ...prev,
                        service: '',
                        description: '',
                        amount: '',
                        discountAmount: ''
                      }));
                    }
                  }}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="">Select service (optional)</option>
                  {Array.isArray(servicePrices[currentTransaction.category]) && servicePrices[currentTransaction.category].map((service, index) => (
                    <option key={index} value={service.name}>
                      {service.name} - {formatCurrency(service.price)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(activeTab === 'income' || activeTab !== 'income') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {activeTab === 'income' ? 'Client' : 'Allocate to client'}
                  </label>
                  {activeTab === 'income' ? (
                    <select
                      value={currentTransaction.clientId || ''}
                      onChange={(e) => {
                        const selectedClient = clientById.get(e.target.value);
                        setCurrentTransaction((prev) => ({
                          ...prev,
                          clientId: selectedClient?.id || '',
                          clientName: selectedClient ? getClientDisplayName(selectedClient) : '',
                          clientAllocations: []
                        }));
                      }}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="">No client selected</option>
                      {clientOptions.map((client) => (
                        <option key={client.id} value={client.id}>
                          {getClientDisplayName(client)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <details className="relative">
                      <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded-lg border bg-white p-3 text-sm text-gray-700">
                        <span>{getExpenseAllocationLabel(currentTransaction) || 'No client selected'}</span>
                        <ChevronDown size={16} className="text-gray-400" />
                      </summary>
                      <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                        <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                          {clientOptions.length > 0 ? clientOptions.map((client) => {
                            const selectedIds = getExpenseAllocationIds(currentTransaction);
                            const isSelected = selectedIds.includes(client.id);
                            return (
                              <label key={client.id} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setCurrentTransaction((prev) => {
                                      const previousIds = getExpenseAllocationIds(prev);
                                      const nextIds = e.target.checked
                                        ? [...previousIds, client.id]
                                        : previousIds.filter((clientId) => clientId !== client.id);
                                      const clientAllocations = buildClientAllocationsFromIds(nextIds);
                                      const primaryAllocation = clientAllocations[0];
                                      return {
                                        ...prev,
                                        clientId: primaryAllocation?.clientId || '',
                                        clientName: primaryAllocation?.clientName || '',
                                        clientAllocations,
                                        allocateToClient: clientAllocations.length > 0
                                      };
                                    });
                                  }}
                                />
                                <span>{getClientDisplayName(client)}</span>
                              </label>
                            );
                          }) : (
                            <div className="text-sm text-gray-500">No clients yet.</div>
                          )}
                        </div>
                        {getExpenseAllocationIds(currentTransaction).length > 1 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Split equally across {getExpenseAllocationIds(currentTransaction).length} clients.
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={handleQuickAddManualClient}
                      className="font-medium text-[color:var(--color-text)] hover:underline"
                    >
                      + New client
                    </button>
                  </div>
                </div>

                {activeTab !== 'income' && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={!!currentTransaction.billableToClient}
                      onChange={(e) => setCurrentTransaction((prev) => ({
                        ...prev,
                        billableToClient: e.target.checked,
                        allocateToClient: e.target.checked ? getExpenseAllocationIds(prev).length > 0 : prev.allocateToClient
                      }))}
                    />
                    Mark this expense as billable / rechargeable to this client
                  </label>
                )}

                {supportsProjects && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job / Estimate Reference
                    </label>
                    <input
                      type="text"
                      value={currentTransaction.jobReference || ''}
                      onChange={(e) => setCurrentTransaction((prev) => ({
                        ...prev,
                        jobReference: e.target.value,
                        allocateToClient: getExpenseAllocationIds(prev).length > 0
                      }))}
                      className="w-full p-3 border rounded-lg"
                      placeholder="e.g. Smith hallway repaint"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      Optional reference for client/job costing.
                    </div>
                  </div>
                )}

                {activeTab !== 'income' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supplier
                    </label>
                    <select
                      value={currentTransaction.supplierId || ''}
                      onChange={(e) => {
                        const selectedSupplier = rctSubcontractorById.get(e.target.value);
                        setCurrentTransaction((prev) => ({
                          ...prev,
                          supplierId: selectedSupplier?.id || '',
                          supplierName: selectedSupplier?.name || '',
                          description: prev.description || selectedSupplier?.name || '',
                          rctSubcontractorId: selectedSupplier?.supplierType === 'rct' ? selectedSupplier.id : prev.rctSubcontractorId,
                          rctSubcontractorName: selectedSupplier?.supplierType === 'rct' ? selectedSupplier.name : prev.rctSubcontractorName,
                          rctDeductionRate: selectedSupplier?.supplierType === 'rct' ? selectedSupplier.rctDeductionRate ?? 0.2 : prev.rctDeductionRate,
                          rctReverseCharge: selectedSupplier?.supplierType === 'rct' ? true : prev.rctReverseCharge
                        }));
                      }}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="">No supplier selected</option>
                      {rctSubcontractorOptions
                        .filter((supplier) => supportsRct || supplier.supplierType !== 'rct')
                        .map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} {supportsRct && supplier.supplierType === 'rct' ? `(RCT ${Math.round((supplier.rctDeductionRate || 0) * 100)}%)` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={handleQuickAddManualSupplier}
                        className="font-medium text-[color:var(--color-text)] hover:underline"
                      >
                        + New supplier
                      </button>
                    </div>
                  </div>
                )}

                {staffMemberOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Staff member
                    </label>
                    <select
                      value={currentTransaction.staffMemberId || ''}
                      onChange={(e) => {
                        const selectedStaffMember = staffMemberById.get(e.target.value);
                        setCurrentTransaction((prev) => ({
                          ...prev,
                          staffMemberId: selectedStaffMember?.id || '',
                          staffMemberName: selectedStaffMember?.name || ''
                        }));
                      }}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="">No staff selected</option>
                      {staffMemberOptions.map((staffMember) => (
                        <option key={staffMember.id} value={staffMember.id}>
                          {staffMember.label}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 text-xs text-gray-500">
                      Add or rename staff in the Staff tab.
                    </div>
                  </div>
                )}

                {supportsRct && activeTab === 'income' && normalizeCategoryId(currentTransaction.category) === 'rct_contract_income' && (
                  <div className="md:col-span-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          RCT contract income
                        </label>
                        <p className="text-xs text-gray-500">
                          For subcontractor invoices where the principal withholds RCT.
                        </p>
                      </div>
                      <span className="text-xs font-medium text-gray-600">Rates: 0%, 20%, 35%</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">RCT deduction rate</label>
                        <select
                          value={String(currentTransaction.rctDeductionRate ?? 0.2)}
                          onChange={(e) => setCurrentTransaction((prev) => ({
                            ...prev,
                            rctDeductionRate: Number(e.target.value),
                            rctRole: 'subcontractor'
                          }))}
                          className="w-full p-2 border rounded"
                        >
                          {RCT_DEDUCTION_RATES.map((rate) => (
                            <option key={rate.value} value={rate.value}>{rate.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="text-xs text-gray-600 flex items-end">
                        Enter the gross invoice amount above. If the invoice is paid now, the app records net cash received and the RCT credit separately.
                      </div>
                    </div>
                  </div>
                )}

                {supportsRct && activeTab !== 'income' && normalizeCategoryId(currentTransaction.category) === 'subcontractor_rct' && (
                  <div className="md:col-span-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          RCT subcontractor
                        </label>
                        <p className="text-xs text-gray-500">
                          For Irish RCT reverse-charge subcontractor invoices.
                        </p>
                      </div>
                      <span className="text-xs font-medium text-gray-600">Rates: 0%, 20%, 35%</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Saved subcontractor</label>
                        <select
                          value={currentTransaction.rctSubcontractorId || ''}
                          onChange={(e) => {
                            const selected = rctSubcontractorById.get(e.target.value);
                            setCurrentTransaction((prev) => ({
                              ...prev,
                              rctSubcontractorId: selected?.id || '',
                              rctSubcontractorName: selected?.name || prev.rctSubcontractorName || '',
                              rctDeductionRate: selected?.rctDeductionRate ?? prev.rctDeductionRate ?? 0.2,
                              rctReverseCharge: true
                            }));
                          }}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select or add below</option>
                          {rctSubcontractorOptions.filter((subcontractor) => subcontractor.supplierType === 'rct').map((subcontractor) => (
                            <option key={subcontractor.id} value={subcontractor.id}>
                              {subcontractor.name} ({Math.round((subcontractor.rctDeductionRate || 0) * 100)}%)
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Subcontractor name</label>
                        <input
                          type="text"
                          value={currentTransaction.rctSubcontractorName || ''}
                          onChange={(e) => setCurrentTransaction((prev) => ({
                            ...prev,
                            rctSubcontractorName: e.target.value,
                            rctReverseCharge: true
                          }))}
                          className="w-full p-2 border rounded"
                          placeholder="Subcontractor name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">RCT deduction rate</label>
                        <select
                          value={String(currentTransaction.rctDeductionRate ?? 0.2)}
                          onChange={(e) => setCurrentTransaction((prev) => ({
                            ...prev,
                            rctDeductionRate: Number(e.target.value),
                            rctReverseCharge: true
                          }))}
                          className="w-full p-2 border rounded"
                        >
                          {RCT_DEDUCTION_RATES.map((rate) => (
                            <option key={rate.value} value={rate.value}>{rate.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => upsertRctSubcontractor({
                          id: currentTransaction.rctSubcontractorId || '',
                          name: currentTransaction.rctSubcontractorName,
                          rctDeductionRate: Number(currentTransaction.rctDeductionRate ?? 0.2)
                        })}
                        className="px-3 py-2 text-sm border rounded-lg bg-white hover:bg-gray-100"
                      >
                        Save subcontractor
                      </button>
                      <span className="text-xs text-gray-600">
                        The app will split the invoice into net payable to the subcontractor and RCT due to Revenue, and add 13.5% reverse-charge VAT to both VAT on sales and purchases.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={splitSaleMode}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setSplitSaleMode(enabled);
                      if (enabled) {
                        setCurrentTransaction((prev) => ({ ...prev, service: '' }));
                        if (!splitSaleItems.length) {
                          setSplitSaleItems([createSplitSaleItem()]);
                        }
                      }
                    }}
                  />
                  Split this {activeTab === 'income' ? 'sale' : 'expense'} into multiple category items
                </label>

                {splitSaleMode && (
                  <div className="mt-3 space-y-3">
                    {splitSaleItems.map((item, index) => (
                      <div key={item.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                        <div className="sm:col-span-5">
                          <label className="block text-xs text-gray-500 mb-1">Item description</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateSplitSaleItemField(item.id, 'description', e.target.value)}
                            className="w-full p-2 border rounded"
                            placeholder={`Item ${index + 1}`}
                          />
                        </div>
                        <div className="sm:col-span-4">
                          <label className="block text-xs text-gray-500 mb-1">Category</label>
                          <select
                            value={item.category}
                            onChange={(e) => updateSplitSaleItemField(item.id, 'category', e.target.value)}
                            className="w-full p-2 border rounded"
                          >
                            <option value="">Select</option>
                            {activeCategoryOptions.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.icon} {cat.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Amount</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.amount}
                            onChange={(e) => updateSplitSaleItemField(item.id, 'amount', e.target.value)}
                            className="w-full p-2 border rounded"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="sm:col-span-1">
                          <button
                            type="button"
                            onClick={() => removeSplitSaleItem(item.id)}
                            className="w-full p-2 text-red-600 border border-red-200 rounded hover:bg-red-50 overflow-hidden text-[0]"
                            title="Remove item"
                            aria-label="Remove item"
                          >
                            <X size={16} className="mx-auto text-red-600" />
                            ×
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={addSplitSaleItem}
                        className="text-sm text-[color:var(--color-text)] hover:underline"
                      >
                        + Add item
                      </button>
                      <span className="text-sm font-medium text-gray-700">
                        Split total: {formatCurrency(splitSaleTotal)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

            {/* Amount Input */}
            <div>
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  {activeTab === 'income'
                    ? `Amount (${currentTransaction.amountBasis === 'net' ? `net before ${countryTerminology.taxName}` : `gross incl. ${countryTerminology.taxName}`})`
                    : 'Amount (gross)'}
                </label>
                {activeTab === 'income' && !splitSaleMode && (
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setCurrentTransaction((prev) => ({ ...prev, amountBasis: 'gross' }))}
                      className={`rounded-md px-2 py-1 font-medium ${
                        currentTransaction.amountBasis !== 'net'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Gross
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentTransaction((prev) => ({ ...prev, amountBasis: 'net' }))}
                      className={`rounded-md px-2 py-1 font-medium ${
                        currentTransaction.amountBasis === 'net'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Net + {countryTerminology.taxName}
                    </button>
                  </div>
                )}
              </div>
              <input
                type="text"
                inputMode="decimal"
                pattern="\\d*\\.?\\d*"
                value={currentTransaction.amount}
                onChange={(e) => {
                  const newAmount = sanitizeAmountInput(e.target.value);

                  // Update the transaction amount
                  setCurrentTransaction((prev) => {
                    // Create a new transaction object with the updated amount
                    const updatedTransaction = {
                      ...prev,
                      amount: newAmount,
                    };

                    // If there are VAT components, recalculate their amounts
                    if (prev.vatComponents && prev.vatComponents.length > 0) {
                      const grossAmount = parseFloat(newAmount) || 0;
                      const updatedComponents = prev.vatComponents.map(component => {
                        const rate = parseFloat(component.rate) || 0;
                        // Calculate VAT amount based on the new gross amount and the component's rate
                        const calculatedVatAmount = rate > 0
                          ? grossAmount - (grossAmount / (1 + rate))
                          : 0;

                        return {
                          ...component,
                          amount: parseFloat(calculatedVatAmount.toFixed(2))
                        };
                      });

                      updatedTransaction.vatComponents = updatedComponents;
                    }

                    return updatedTransaction;
                  });
                }}
                className={`w-full p-3 border rounded-lg ${
                  splitSaleMode ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                placeholder="0.00"
                disabled={splitSaleMode}
                required
              />
              {splitSaleMode && (
                <p className="mt-1 text-xs text-gray-500">
                  Amount is calculated from the split {activeTab === 'income' ? 'sale' : 'expense'} items above.
                </p>
              )}
              {activeTab === 'income' && !splitSaleMode && (
                <p className="mt-1 text-xs text-gray-500">
                  Choose Gross if the price already includes {countryTerminology.taxName}. Choose Net + {countryTerminology.taxName} if you quote before {countryTerminology.taxName}.
                </p>
              )}
              {activeTab !== 'income' && vatSettings.enabled && vatSettings.isRegistered && (
                <>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the total paid including {countryTerminology.taxName}. If you select a primary {countryTerminology.taxName} rate below, {countryTerminology.taxName} is calculated automatically.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Open {countryTerminology.taxName} Components only if this expense mixes different {countryTerminology.taxName} rates or you need to adjust the {countryTerminology.taxName} split manually.
                  </p>
                  {selectedPrimaryExpenseVatRate && primaryExpenseVatPreviewAmount > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Current {countryTerminology.taxName} estimate from the selected rate: {formatCurrency(primaryExpenseVatPreviewAmount)} on {formatCurrency(manualPaymentTargetAmount)}.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Discount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount (optional)
              </label>
              <input
                type="text"
                inputMode="decimal"
                pattern="\\d*\\.?\\d*"
                value={currentTransaction.discountAmount || ''}
                onChange={(e) =>
                  setCurrentTransaction((prev) => ({
                    ...prev,
                    discountAmount: sanitizeAmountInput(e.target.value)
                  }))
                }
                className="w-full p-3 border rounded-lg"
                placeholder="0.00"
              />
              {manualDiscountAmount > 0 && (
                <p className={`mt-1 text-xs ${manualDiscountAmount > manualGrossAmount ? 'text-red-600' : 'text-gray-500'}`}>
                  Net {activeTab === 'income' ? 'sale' : 'expense'} amount after discount: {formatCurrency(manualPaymentTargetAmount)}
                </p>
              )}
            </div>

            {activeTab === 'income' && manualIncomeVatPreview && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900">Sale breakdown before saving</div>
                  <div className="text-xs text-gray-500">
                    {manualIncomeVatPreview.isVatRegistered
                      ? manualIncomeVatPreview.rateLabel || `${(manualIncomeVatPreview.rate * 100).toFixed(1)}% ${countryTerminology.taxName}`
                      : `No ${countryTerminology.taxName}`}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-white p-2">
                    <div className="text-[11px] font-medium uppercase text-gray-500">Net</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{formatCurrency(manualIncomeVatPreview.netAmount)}</div>
                  </div>
                  <div className="rounded-md bg-white p-2">
                    <div className="text-[11px] font-medium uppercase text-gray-500">{countryTerminology.taxName}</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{formatCurrency(manualIncomeVatPreview.vatAmount)}</div>
                  </div>
                  <div className="rounded-md bg-white p-2">
                    <div className="text-[11px] font-medium uppercase text-gray-500">Gross</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{formatCurrency(manualIncomeVatPreview.grossAmount)}</div>
                  </div>
                </div>
                {manualIncomeVatPreview.amountBasis === 'net' && manualIncomeVatPreview.vatAmount > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    This sale will be saved as gross {formatCurrency(manualIncomeVatPreview.grossAmount)}.
                  </p>
                )}
              </div>
            )}

            {/* Description Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={currentTransaction.description || ""}
                onChange={(e) =>
                  setCurrentTransaction((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full p-3 border rounded-lg"
                placeholder="Enter description"
                required
              />
            </div>

            {/* Date Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={currentTransaction.date || ""}
                onChange={(e) =>
                  setCurrentTransaction((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
                className="w-full p-3 border rounded-lg"
                required
              />
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              {!splitPaymentMode ? (
                <select
                  value={currentTransaction.paymentMethod || ""}
                  onChange={(e) =>
                    setCurrentTransaction((prev) => ({
                      ...prev,
                      paymentMethod: normalizePaymentMethodId(e.target.value),
                    }))
                  }
                  className="w-full p-3 border rounded-lg"
                  required
                >
                  <option value="">Select payment method</option>
                  {PAYMENT_METHODS?.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.icon} {method.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
                  {splitPaymentItems.map((entry) => (
                    <div key={entry.id} className="grid grid-cols-[1fr_100px_34px] gap-2 items-center">
                      <select
                        value={entry.method || ''}
                        onChange={(e) => updateSplitPaymentItemField(entry.id, 'method', e.target.value)}
                        className="border rounded px-2 py-2 text-sm"
                      >
                        <option value="">Method</option>
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method.id} value={method.id}>{method.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="\\d*\\.?\\d*"
                        value={entry.amount || ''}
                        onChange={(e) => updateSplitPaymentItemField(entry.id, 'amount', e.target.value)}
                        className="border rounded px-2 py-2 text-sm text-right"
                        placeholder="0.00"
                      />
                      <button
                        type="button"
                        onClick={() => removeSplitPaymentItem(entry.id)}
                        className="text-red-600 border border-red-200 rounded px-1 py-2 text-xs hover:bg-red-50"
                        title="Remove split payment line"
                      >
                        x
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSplitPaymentItem}
                    className="text-sm text-indigo-700 hover:underline"
                  >
                    + Add payment method
                  </button>
                  <div className={`text-xs ${splitPaymentTotal - manualPaymentTargetAmount > 0.01 ? 'text-red-600' : 'text-gray-500'}`}>
                    Payments recorded: {formatCurrency(splitPaymentTotal)} of {formatCurrency(manualPaymentTargetAmount)}
                    {manualPaymentTargetAmount - splitPaymentTotal > 0.01
                      ? ` (${formatCurrency(manualPaymentTargetAmount - splitPaymentTotal)} outstanding)`
                      : ''}
                  </div>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  id="split-payment-manual"
                  checked={splitPaymentMode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSplitPaymentMode(checked);
                    if (checked) {
                      const seedAmount = manualPaymentTargetAmount > 0
                        ? sanitizeAmountInput(manualPaymentTargetAmount.toString())
                        : '';
                      const seedMethod = normalizePaymentMethodId(currentTransaction.paymentMethod || 'cash') || 'cash';
                      setSplitPaymentItems([createSplitPaymentItem({ method: seedMethod, amount: seedAmount })]);
                    } else {
                      resetSplitPaymentState();
                    }
                  }}
                />
                <label htmlFor="split-payment-manual" className="cursor-pointer">
                  Split or part payment
                </label>
              </div>
            </div>

            {activeTab !== 'income' && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  id="autosave-cost-manual"
                  checked={!!currentTransaction.saveToCost}
                  onChange={(e) => setCurrentTransaction((prev) => ({
                    ...prev,
                    saveToCost: e.target.checked
                  }))}
                />
                <label htmlFor="autosave-cost-manual" className="cursor-pointer">
                  Save to Cost Calculator
                </label>
              </div>
            )}

           {/* VAT Section */}
           {vatSettings.enabled && (
             <div className="mt-4 border-t pt-4">
               <h3 className="text-md font-medium mb-2">
                 {countryTerminology.taxName} Information
               </h3>

               {/* For Income transactions - show info message */}
                {activeTab === 'income' ? (
                  <div className="bg-blue-50 p-3 rounded-lg mb-4">
                    <p className="text-sm text-blue-700">
                      {!isVatRegisteredForTransactionDate(currentTransaction.date)
                        ? `No ${countryTerminology.taxName} will be added because this sale date is outside your ${countryTerminology.taxName} registration periods.`
                        : currentCountry.taxSystem === 'vat'
                          ? splitSaleMode
                            ? `${countryTerminology.taxName} will be automatically calculated per split line item based on each line item category.`
                            : `${countryTerminology.taxName} will be automatically calculated and added to this sale based on your sales ${countryTerminology.taxName} rate.`
          : `${countryTerminology.taxName} will be automatically calculated and added to this sale based on the transaction category.`}
                                   </p>
                    <p className="text-xs text-[color:var(--color-text)] mt-1">
                      {!isVatRegisteredForTransactionDate(currentTransaction.date) && vatSettings.registrationDate
                        ? `First registration date: ${formatDisplayDate(vatSettings.registrationDate, dateDisplayLocale)}.`
                        : currentCountry.taxSystem === 'vat'
                        ? splitSaleMode && splitSaleVatPreview?.rateBreakdown?.length
                          ? `${currentCountry.name} split ${countryTerminology.taxName}: ${splitSaleVatPreview.rateBreakdown
                              .map((entry) => `${getCategoryDisplayLabel(entry.category || currentTransaction.category)} ${formatTaxRatePercent(entry.rate)} on ${formatCurrency(entry.grossAmount)}`)
                              .join('; ')}. Estimated ${countryTerminology.taxName} total: ${formatCurrency(splitSaleVatPreview.totalVatAmount)}.`
                          : `${currentCountry.name} ${countryTerminology.taxName} rates: ${(currentCountry.vatRates || [])
                              .map((rate) => `${rate.name} ${rate.rate}%`)
                              .join(', ')}. This category will use ${formatTaxRatePercent(
                                getIncomeTaxRateForCategory(currentTransaction.category, resolveIncomeTaxJurisdiction(), currentTransaction.date)
                              )}.`
                        : `${currentCountry.name} ${countryTerminology.taxName}: varies by location (typically ${currentCountry.defaultSalesTax}%)`}
                    </p>
                  </div>
                ) : (
                 /* Tax Rate Selection - only for expenses */
                 <div className="mb-4">
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     Primary {countryTerminology.taxName} Rate
                   </label>
                   <select
                     value={currentTransaction.vatRate !== null ? currentTransaction.vatRate : ""}
                     onChange={(e) =>
                       setCurrentTransaction((prev) => ({
                         ...prev,
                         vatRate: e.target.value === "" ? null : parseFloat(e.target.value),
                       }))
                     }
                     className="w-full p-3 border rounded-lg"
                   >
                     <option value="">No Tax</option>
    {currentCountry.taxSystem === 'vat'
                       ? currentCountry.vatRates.map(rate => (
                           <option key={rate.id} value={rate.rate / 100}>
                             {rate.name} ({rate.rate}%)
                           </option>
                         ))
                       : currentCountry.salesTaxRates.map(rate => (
                                            <option key={rate.id} value={rate.rate / 100}>
                             {rate.name} ({rate.rate}%)
                           </option>
                         ))
                     }
                   </select>
                   <p className="mt-2 text-xs text-gray-500">
                     Choose the main {countryTerminology.taxName} rate for this expense. The app calculates {countryTerminology.taxName} automatically from the gross amount above.
                   </p>
                   <p className="mt-1 text-xs text-gray-500">
                     Use No Tax when no {countryTerminology.taxName} should be reclaimed. Open {countryTerminology.taxName} Components only for mixed rates or manual adjustments.
                   </p>
                 </div>
               )}

               {/* VAT Components Section - only for expenses */}
               {activeTab !== 'income' && (
                   <div className="mb-4">
                 <div className="flex justify-between items-center mb-2">
                     <label className="block text-sm font-medium text-gray-700">
                       {countryTerminology.taxName} Components
                     </label>
                     <button
                       type="button"
                       onClick={() => {
                         const rate = currentCountry.taxSystem === 'vat'
                           ? getStandardCountryVatRate()
                           : ((Number(currentCountry.defaultSalesTax) || 0) / 100);
                         const grossAmount = parseFloat(currentTransaction.amount) || 0;
                         // Calculate VAT amount based on the gross amount and default rate
                         const calculatedVatAmount = grossAmount - (grossAmount / (1 + rate));

                         setCurrentTransaction((prev) => ({
                           ...prev,
                           vatComponents: [
                             ...prev.vatComponents,
                             {
                               rate: rate,
                               amount: parseFloat(calculatedVatAmount.toFixed(2)),
                               description: getTaxComponentDescription(rate)
                             }
                           ]
                         }));
                       }}
                       className="text-sm bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition">
                     
                       + Add Component
                     </button>
                   </div>

                   {Array.isArray(currentTransaction.vatComponents) && currentTransaction.vatComponents.length > 0 ? (
                     <div className="space-y-2">
                       {Array.isArray(currentTransaction.vatComponents) && currentTransaction.vatComponents.map((component, index) => (
                         <div key={index} className="flex space-x-2 items-center p-2 bg-gray-50 rounded">
                           <div className="flex-1">
                             <label className="block text-xs text-gray-500">Rate</label>
                             <select
                               value={component.rate}
                               onChange={(e) => {
                                 const rate = parseFloat(e.target.value);
                                 const description = e.target.options[e.target.selectedIndex].text;
                                 // Calculate VAT amount based on the gross amount and selected rate
                                 const grossAmount = parseFloat(currentTransaction.amount) || 0;
                                 // Formula: VAT amount = gross amount - (gross amount / (1 + VAT rate))
                                 const calculatedVatAmount = rate > 0
                                   ? grossAmount - (grossAmount / (1 + rate))
                                   : 0;

                                 const newComponents = [...currentTransaction.vatComponents];
                                 newComponents[index] = {
                                   ...component,
                                   rate: rate,
                                   description: description,
                                   amount: parseFloat(calculatedVatAmount.toFixed(2))
                                 };
                                 setCurrentTransaction((prev) => ({
                                   ...prev,
                                   vatComponents: newComponents
                                 }));
                               }}
                               className="w-full p-2 border rounded"
                             >
                               <option value="0">No Tax (0%)</option>
                               {(currentCountry.taxSystem === 'vat'
                                 ? (currentCountry.vatRates || [])
                                 : (currentCountry.salesTaxRates || [])
                               ).map((rate) => (
                                 <option key={rate.id} value={rate.rate / 100}>
                                   {rate.name} ({rate.rate}%)
                                 </option>
                               ))}
                             </select>
                           </div>
                           <div className="flex-1">
                             <label className="block text-xs text-gray-500">Amount</label>
                             <input
                               type="number"
                               step="0.01"
                               value={component.amount}
                               onChange={(e) => {
                                 const newComponents = [...currentTransaction.vatComponents];
                                 newComponents[index] = {
                                   ...component,
                                   amount: parseFloat(e.target.value) || 0
                                 };
                                 setCurrentTransaction((prev) => ({
                                   ...prev,
                                   vatComponents: newComponents
                                 }));
                               }}
                               className="w-full p-2 border rounded"
                               placeholder="0.00"
                             />
                           </div>
                           <button
                             type="button"
                             onClick={() => {
                               const newComponents = currentTransaction.vatComponents.filter((_, i) => i !== index);
                               setCurrentTransaction((prev) => ({
                                 ...prev,
                                 vatComponents: newComponents
                               }));
                             }}
                              className="mt-4 inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden text-[0] text-[color:var(--color-text)] hover:opacity-80"
                              aria-label={`Remove ${countryTerminology.taxName} component`}
                           >
                              <span className="sr-only">Remove</span>
                              <X size={16} className="text-[color:var(--color-text)]" />
                             ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¾Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â
                           </button>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <div className="space-y-1">
                       <p className="text-sm text-gray-500 italic">
                         No {countryTerminology.taxName} components added. That is fine for a single {countryTerminology.taxName} rate.
                       </p>
                       <p className="text-xs text-gray-500">
                         If you selected a primary {countryTerminology.taxName} rate above, the app will calculate {countryTerminology.taxName} automatically when you save.
                       </p>
                     </div>
                   )}

                   <p className="text-xs text-gray-500 mt-2">
                     Add multiple {countryTerminology.taxName} components if your transaction includes items with different {countryTerminology.taxName} rates.
                   </p>
                 </div>
               )}
             </div>
           )}

           {(!vatSettings.enabled || !vatSettings.isRegistered) && (
             <div className="mt-4">
               <p className="text-xs text-gray-500">
                 {countryTerminology.taxName} features are disabled. Enable {countryTerminology.taxName} calculations and register for {countryTerminology.taxName} in settings to use these features.
               </p>
             </div>
           )}

            {/* Modal Actions */}
            <div className="flex justify-end gap-4 pt-6">
              <button
                type="button"
                onClick={() => {
                  setIsAddingTransaction(false);
                  setSelectedInputMethod(null);
                  resetSplitSaleState();
                  resetSplitPaymentState();
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isManualSaveDisabled}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  isManualSaveDisabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-warn-gradient text-[color:var(--color-text)] hover:brightness-95'
                }`}
              >
                Save {activeTab === "income" ? "Income" : "Expense"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

{user && (
  <>
    <DriveDisconnectWarningModal
      isOpen={showDriveDisconnectWarning}
      onReconnect={handleReconnectDriveWarning}
      onDismiss={dismissDriveDisconnectWarning}
      isReconnecting={isReconnectingDriveWarning}
    />

    <AccountantAccessModal
      isOpen={showAccountantAccessModal}
      onClose={() => setShowAccountantAccessModal(false)}
      user={user}
      activeWorkspace={activeWorkspace}
      ownWorkspace={collaborationState.ownWorkspace}
      acceptedWorkspaces={collaborationState.acceptedWorkspaces}
      pendingInvites={collaborationState.pendingInvites}
      sentInvites={collaborationState.sentInvites}
      onSendInvite={handleSendAccountantInvite}
      onAcceptInvite={handleAcceptAccountantInvite}
      onRevokeInvite={handleRevokeAccountantInvite}
      onSelectWorkspace={handleWorkspaceSelectionChange}
      onRecoverLocalTransactions={handleRecoverLocalTransactionsToWorkspace}
      onReplaceSharedLedgerFromLocal={handleReplaceSharedLedgerFromLocalTransactions}
      canRecoverLocalTransactions={Boolean(
        collaborationState.ownWorkspace?.id &&
        collaborationState.ownWorkspace?.ownerId === user?.uid
      )}
      inviteDisabledReason={
        isExternalSharedWorkspace
          ? 'Switch back to your own data before inviting an accountant from this account.'
          : ''
      }
      isBusy={isCollaborationBusy}
      isRefreshing={isRefreshingCollaboration}
    />

    {/* Sign In Modal - For Returning Users */}
    <SimpleSignIn
      isOpen={showAuthRequired}
      onClose={() => setShowAuthRequired(false)}
      onGoogleSignIn={handleGoogleSignIn}
      rememberMe={rememberMe}
      onRememberChange={setRememberMe}
    />

    {/* Sign Up Modal */}
    <EnhancedAuthCheck
      isOpen={showSignUpModal}
      onClose={() => setShowSignUpModal(false)}
      onGoogleSignIn={handleGoogleSignIn}
      showPricing={true}
      rememberMe={rememberMe}
      onRememberChange={setRememberMe}
    />
  </>
)}

{/* Country Selection Modal for New Users */}
<CountrySelectionModal
    isOpen={showCountrySelection}
    onClose={handleCloseCountrySelection}
    onCountrySelect={handleCountrySelect}
    currentCountry={currentCountry}
    isNewUser={isNewUser || requiresInitialBusinessSetup}
    requireBusinessSetup={requiresInitialBusinessSetup}
    businessType={businessType}
    businessTypeOptions={localizedBusinessTypeOptions}
    industryId={industryId}
    industryOptions={INDUSTRY_OPTIONS}
    serviceOfferingOptions={SERVICE_OFFERING_OPTIONS}
    selectedServiceOfferings={selectedServiceOfferings}
    vatRegistered={Boolean(vatSettings?.isRegistered)}
    vatAccountingBasis={vatSettings?.accountingBasis || 'cash'}
  />
</div>
      )}
    </VatContext.Provider>
);
}
// Wrapper component that provides all necessary contexts
const AppWithProviders = () => {
  const [user, setUser] = useState(null);

  // Create a callback to update user that can be passed down
  const updateUser = (newUser) => {
    console.log('Updating user in AppWithProviders:', newUser);
    setUser(newUser);
  };

  return (
    <CurrencyProvider>
      <SubscriptionProvider user={user}>
        <AppWithCurrency user={user} setUser={updateUser} />
      </SubscriptionProvider>
    </CurrencyProvider>
  );
};

// Component that can use the useCurrency hook
const AppWithCurrency = ({ user, setUser }) => {
  const { currency } = useCurrency();

  return <App user={user} setUser={setUser} currency={currency} />;
};

export default AppWithProviders;






